/**
 * Central translation source for the whole application.
 *
 * Structure: { [langCode]: { [namespace]: { [key]: 'translated text' } } }
 *
 * Default language: German (de). English (en) is the primary translation target.
 * Czech (cs) and Spanish (es) are additional supported languages.
 *
 * How to add a new string:
 *   1. Pick (or create) a namespace — e.g. `sidebar`, `homepage`, `settings`.
 *   2. Add the German text under `de.<namespace>.<key>`.
 *   3. Add the English text under `en.<namespace>.<key>`.
 *   4. In the component:
 *        import { useTranslation } from '../i18n/LanguageContext';
 *        const { t } = useTranslation();
 *        <button>{t('sidebar.fragen')}</button>
 *
 * Missing keys automatically fall back to the German entry, then to the key
 * itself — so adding German first never breaks the UI in other languages.
 */

const translations = {
  de: {
    common: {
      save: 'Speichern',
      cancel: 'Abbrechen',
      delete: 'Löschen',
      edit: 'Bearbeiten',
      close: 'Schließen',
      confirm: 'Bestätigen',
      back: 'Zurück',
      next: 'Weiter',
      previous: 'Vorherige',
      search: 'Suchen',
      loading: 'Wird geladen...',
      upload: 'Hochladen',
      download: 'Herunterladen',
      open: 'Öffnen',
      yes: 'Ja',
      no: 'Nein',
      add: 'Hinzufügen',
      remove: 'Entfernen',
      create: 'Erstellen',
      continue: 'Fortfahren',
      proceed: 'Fortsetzen',
      retry: 'Wiederholen',
      refresh: 'Aktualisieren',
      error: 'Fehler',
      success: 'Erfolg',
      warning: 'Warnung',
      info: 'Information',
    },

    nav: {
      homepage: 'Startseite',
      projects: 'KI-Anfragen',
      agents: 'Agenten',
      notifications: 'Benachrichtigungen',
      helpCenter: 'Hilfecenter',
      recentProjects: 'Letzte KI-Anfragen',
      settings: 'Einstellungen',
      account: 'Konto',
      logout: 'Abmelden',
      logoutConfirm: 'Sind Sie sicher, dass Sie sich abmelden möchten?',
    },

    sidebar: {
      fragen: 'Anweisungen',
      dateien: 'Dateien',
      uploadFile: 'Datei hochladen',
      loadingQuestions: 'Anweisungen werden geladen…',
      refreshFiles: 'Dateien aktualisieren',
      closeSidebar: 'Seitenleiste schließen',
      openSidebar: 'Seitenleiste öffnen',
      noFilesUploaded: 'Noch keine Dateien hochgeladen',
      searchFilesPlaceholder: 'Dateien suchen',
      sharepointSection: 'SharePoint',
      noKnowledgeArea: 'Kein Wissensbereich',
      noFilesFound: 'Keine Dateien gefunden',
      maxFilesInfo: 'Maximal {count} Dateien für die Analyse zulässig ({used}/{count})',
      selectedFiles: 'Ausgewählte Dateien:',
      addFiles: 'Dateien hinzufügen',
      standardBadge: 'Standard',
      sharepointBadge: 'SharePoint',
      uploadedBadge: 'Hochgeladen',
      deleteFileTitle: 'Datei löschen?',
      deleteFileMessage: 'Möchten Sie "{filename}" wirklich aus der Analyse entfernen? Diese Aktion kann nicht rückgängig gemacht werden.',
      statusPending: 'Ausstehend',
      statusDone: 'Fertig',
      standardSection: 'Standard',
      // Upload states
      uploadingState: 'Wird hochgeladen...',
      convertingState: 'Wird konvertiert...',
      // Group headers
      youSection: 'Sie',
      // Selected files area
      totalPages: 'Gesamtzahl der Seiten: {count}',
      // PDF viewer
      pdfLoading: 'PDF wird geladen...',
      imageLoading: 'Bild wird geladen...',
      imageLoadError: 'Bild konnte nicht geladen werden',
      sourceFileNotFound: 'Datei konnte nicht geöffnet werden. Sie ist möglicherweise nicht mehr verfügbar.',
      sourceFileStale: 'Diese SharePoint-Datei wurde seit der Erstellung dieser Antwort aktualisiert oder an der Quelle entfernt.',
      sourceFileTimeout: 'Das Laden der Datei hat zu lange gedauert. Bitte versuchen Sie es erneut.',
      sourceFileLoadFailed: 'Die Datei konnte nicht geladen werden. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
      sourceFileDecryptionFailed: 'Diese Datei konnte nicht entschlüsselt werden. Der Schlüssel hat sich möglicherweise geändert oder die Datei ist beschädigt — bitte wenden Sie sich an einen Administrator.',
      backToFiles: 'Zurück zu Dateien',
      refreshAlt: 'Aktualisieren',
      // Error messages / validation
      noFileSelected: 'Keine Datei ausgewählt',
      invalidFileType: 'Bitte wählen Sie nur {types} Dateien aus',
      pdfProcessingError: 'Fehler bei der Verarbeitung von {filename}: Ungültige PDF-Datei',
      fileValidationError: 'Fehler bei {filename}: {error}',
      convertFileError: 'Datei konnte nicht konvertiert werden',
      noOrganization: 'Der Benutzer ist keiner Organisation zugeordnet',
      uploadExceedsPages: 'Der Upload überschreitet die verfügbaren Seiten',
      selectFilesToUpload: 'Bitte wählen Sie Dateien zum Hochladen aus',
      uploadFilesError: 'Dateien konnten nicht hochgeladen werden',
      cannotRefresh: 'Dateien können nicht aktualisiert werden: Benutzer- oder Verlaufsinformationen fehlen',
      refreshFilesError: 'Dateien konnten nicht aktualisiert werden',
      deleteFileError: 'Datei konnte nicht gelöscht werden',
      filesLoadError: 'Fehler: {error}',
      maxFilesReached: 'Nur {max} Dateien dürfen analysiert werden. Sie haben bereits {uploaded} Datei(en) hochgeladen. {rejected} Datei(en) wurden nicht hinzugefügt.',
      maxFilesReachedShort: 'Nur {max} Dateien dürfen analysiert werden. Sie haben bereits {uploaded} Datei(en) hochgeladen.',
      // Toast success
      uploadSuccess: 'Dateien erfolgreich hochgeladen',
      refreshSuccess: 'Dateien erfolgreich aktualisiert',
      // Generated files tab
      uploadedTab: 'Hochgeladen',
      generatedFilesTab: 'Generierte Dateien {count}',
      tableName: 'Name',
      tableCreated: 'Erstellt',
      tableSize: 'Größe',
      tableActions: 'Aktionen',
      noGeneratedFiles: 'Noch keine generierten Dateien',
      today: 'Heute',
      yesterday: 'Gestern',
      previewAction: 'Vorschau',
      downloadWordAction: 'Word herunterladen',
      downloadPdfAction: 'PDF herunterladen',
      deleteGeneratedFileTitle: 'Datei löschen?',
      deleteGeneratedFileMessage: 'Möchten Sie „{filename}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteGeneratedFileError: 'Datei konnte nicht gelöscht werden',
    },

    homepage: {
      title: 'Startseite',
      recentProjects: 'Letzte KI-Anfragen',
      createNewProject: 'Neue KI-Anfrage erstellen',
      noProjectsCreated: 'Keine KI-Anfragen erstellt',
      noProjectsHint: 'Erstellen Sie eine KI-Anfrage, um Dateien hochzuladen und Analysen durchzuführen.',
      tableTitle: 'Titel',
      tableAgents: 'Agenten',
      tableCreatedAt: 'Erstelldatum',
      tableProgress: 'Q&A Fortschritt',
      tableAnswerStatus: 'Antwort-Status',
      answeredOf: '{done} / {total} Beantwortet',
      agentsTitle: 'Agenten',
      agentsSubtitleLine1: 'Entdecken und arbeiten Sie mit KI-Agenten, die für verschiedene Benutzergruppen entwickelt wurden.',
      agentsSubtitleLine2: 'Jeder Agent hilft Ihnen, Dokumente, Daten und Entscheidungen schneller zu analysieren.',
      noAgentsAvailable: 'Keine Agenten verfügbar. Bitte wenden Sie sich an Ihren Administrator.',
      noAgentsYet: 'Noch keine Agenten',
      noAgentsYetHint: 'Erstellen Sie einen Agenten, um mit der Analyse von Dateien und Daten zu beginnen.',
      createAgentFirst: 'Bitte erstellen Sie zuerst einen Agenten.',
      agentCardDefaultDescription: 'KI-Agent zur Unterstützung Ihrer Aufgaben.',
      newChatPlaceholderName: 'Neuer Chat',
      historyLimitReached: 'Sie haben die maximale Anzahl von {limit} Chatverläufen erreicht. Bitte löschen Sie einen bestehenden Verlauf, bevor Sie einen neuen erstellen.',
      createProjectError: 'Projekt konnte nicht erstellt werden.',
      startDirectChatError: 'Chat konnte nicht gestartet werden.',
      startingDirectChat: 'Chat wird gestartet...',
      showAll: 'Alle anzeigen',
      showLess: 'Weniger anzeigen',
      noDepartment: 'Ohne Abteilung',
    },

    history: {
      pageTitle: 'KI-Anfragen',
      tableTitle: 'Titel',
      tableAgents: 'Agenten',
      tableCreatedAt: 'Erstellungsdatum',
      tableProgress: 'Fortschritt der Q&A',
      tableAnswerStatus: 'Antwort-Status',
      answeredOf: '{done} / {total} beantwortet',
      agentLabel: 'Agenten',
      titleLabel: 'Titel',
      creationDateLabel: 'Erstellungsdatum',
      progressLabel: 'Fortschritt der Q&A',
      noProjectsFound: 'Keine KI-Anfragen gefunden',
      noProjectsCreated: 'Keine KI-Anfragen erstellt',
      noProjectsSearchHint: 'Keine KI-Anfragen gefunden, die Ihrer Suche entsprechen.',
      createForAgent: 'Erstellen Sie eine KI-Anfrage für {agent}.',
      createHint: 'Erstellen Sie eine KI-Anfrage, um Dateien hochzuladen und Analysen durchzuführen.',
      continue: 'Fortfahren',
      view: 'Ansehen',
      projectDetails: 'KI-Anfrage-Details',
      deleteAll: 'Alle löschen',
      newProjectTitle: 'Neue KI-Anfrage erstellen',
      createProject: 'KI-Anfrage erstellen',
      agentProjects: 'Agent-KI-Anfragen',
      clearFilter: 'Filter löschen',
      filter: 'Filter',
      tableAccess: 'Zugriff',
      accessOwner: 'Eigentümer',
      accessViewOnly: 'Geteilt',
      accessInvited: 'Eingeladen',
      accessCanEdit: 'Kann bearbeiten',
      tabMyProjects: 'Meine KI-Anfragen',
      tabSharedProjects: 'Geteilte KI-Anfragen',
      tabInvitedProjects: 'Eingeladene KI-Anfragen',
    },

    analysis: {
      breadcrumbHome: 'Startseite',
      breadcrumbProjects: 'KI-Anfragen',
      breadcrumbProjectDetail: 'Zur KI-Anfrage-Übersicht',
      breadcrumbChatLeaf: 'Chat',
      export: 'Exportieren',
      exportWithComments: 'Export mit Kommentaren',
      exportWithoutComments: 'Export ohne Kommentare',
      agentFallback: 'Agent',
      projectFallback: 'KI-Anfrage',
      openSidebar: 'Seitenleiste öffnen',
      chatPlaceholder: 'Frage etwas',
      sources: 'Quellen',
      reference: 'Referenz',
      webSourcesCount: '{count} Web-Quellen',
      sourcesCount: '{count} Quellen',
      document: 'Dokument',
      documentNumber: 'Dokument {index}',
      source: 'Quelle',
      sourceLabel: 'Quelle: {filename} (Seite {page})',
      page: 'Seite',
      pageWithNumber: 'Seite {page}',
      questionTab: 'Fragen',
      fileTab: 'Dateien',
      projectLoading: 'KI-Anfrage wird geladen...',
      pdfBrowserTitle: 'AI Spaces KI-Zusammenfassung',
      pdfSummarySuffix: 'KI-Zusammenfassung',
      pdfUploadedDocsHeading: 'Hochgeladene Dokumente:',
    },

    chatActions: {
      listen: 'Anhören',
      stop: 'Stopp',
      loading: 'Wird geladen...',
      copy: 'Kopieren',
      addComment: 'Kommentar hinzufügen',
      editComment: 'Kommentar bearbeiten',
      quickActions: 'Schnellaktionen',
      quickActionsResult: 'Schnellaktions-Ergebnis',
      setStatus: 'Status festlegen',
      statusSelect: 'Status auswählen',
      aiDisclaimer: 'KI-generierte Antwort. Bitte prüfen Sie immer die Ergebnisse.',
      wordToggle: 'Word-Dokument generieren',
      wordCreating: 'Dokument wird erstellt…',
      wordCreatingSubtitle: 'Basierend auf der aktuellen Anfrage und den verfügbaren Quellen',
      wordCreatingStatusPill: 'Wird erstellt',
      wordCreatingRendering: 'Word-Dokument wird erstellt…',
      wordCreatingInProgress: 'Läuft',
      wordCreatingHelper: 'Dies kann einige Minuten dauern. Sie können währenddessen weiter chatten.',
      wordNothingFound: 'Das Thema wurde in den verfügbaren Dokumenten nicht gefunden.',
      wordTruncated: 'Das Dokument konnte nicht vollständig erstellt werden. Bitte versuchen Sie es erneut.',
      wordFailed: 'Die Dokumentenerstellung ist fehlgeschlagen.',
      wordDownloadWord: 'Word herunterladen',
      wordDownloadPdf: 'PDF herunterladen',
      wordDocumentCreated: 'Word-Dokument erstellt',
      wordReady: 'Bereit',
      wordCompletedLabel: 'Abgeschlossen',
      wordReadyHelper: 'Das Dokument ist zum Download bereit.',
      wordPreview: 'Vorschau',
      wordDownloadWordShort: 'Word',
      wordDownloadPdfShort: 'PDF',
      wordDownloadError: 'Download fehlgeschlagen. Bitte versuchen Sie es erneut.',
      wordHint: 'Als Word-Dokument',
      wordModeLabel: 'Word',
      wordComposerMeta: "Nur eine Anfrage · erstellt ein Word-Dokument",
      wordNotEnabledError: 'Die Word-Dokumentenerstellung ist für diesen Agenten nicht aktiviert.',
      wordAlreadyRunningError: 'Für diesen Agenten wird bereits ein Word-Dokument erstellt. Bitte warten Sie, bis es fertig ist.',
      saving: 'Wird gespeichert...',
      saveComment: 'Speichern',
      commentBy: 'Kommentar von {name}',
      readAloud: 'Vorlesen',
      close: 'Schließen',
      commentPlaceholder: 'Kommentar hinzufügen',
      defaultUser: 'Benutzer',
      askPlaceholder: 'Fragen Sie etwas',
      chatDisabled: 'Chat ist deaktiviert',
      voiceInput: 'Spracheingabe',
      uploadFile: 'Datei hochladen',
      enterProjectName: 'KI-Anfrage benennen',
      convertToPdf: 'Zu PDF konvertieren...',
      processing: 'Verarbeiten...',
      uploading: 'Hochladen...',
      startAnalysis: 'Analyse starten',
      addFiles: 'Dateien hinzufügen',
      newAnalysisTitle: 'Neue Analyse?',
      newAnalysisConfirm: 'Sind Sie sicher, dass Sie die aktuelle Analyse schliessen und eine neue starten möchten?',
      removeFile: 'Datei entfernen',
      filePickerRoot: 'Hauptordner',
      filePickerClear: 'Auswahl entfernen',
      filePickerTrigger: 'Datei auswählen (/)',
      filePickerSearchPlaceholder: 'In diesem Ordner suchen',
      filePickerSelected: 'ausgewählt',
      onlyOneFileAllowed: 'Nur eine Datei ist für die erste Analyse zulässig.',
      tooManyImages: 'Maximal {max} Bilder pro Upload.',
      filesStillProcessing: 'Bitte warten Sie, bis die hochgeladenen Dateien fertig verarbeitet sind, bevor Sie eine Frage dazu stellen.',
      defaultImageOnlyMessage: 'Bitte analysieren Sie das angehängte Bild.',
      analyzingImage: 'Bild wird analysiert…',
      transcriptionFailed: 'Transkription fehlgeschlagen',
      microphoneDenied: 'Mikrofonzugriff verweigert',
      uploadSuccess: 'Datei erfolgreich hochgeladen',
      uploadFailed: 'Hochladen fehlgeschlagen',
      uploadExceedsPages: 'Upload überschreitet verfügbare Seiten',
      uploadPageLimitExceeded: 'Das Upload-Limit für diese Sitzung beträgt {limit} Seiten. Sie haben versucht, {attempted} Seiten hochzuladen. Bitte versuchen Sie es erneut und laden Sie nur {limit} Seiten hoch.',
      pageLimitsVerificationFailed: 'Seitenlimits konnten nicht überprüft werden',
      invalidFileType: 'Bitte wählen Sie nur {types} Dateien',
      invalidPdfProcessing: 'Fehler beim Verarbeiten von {name}: Ungültige PDF-Datei',
      fileConvertFailed: 'Datei konnte nicht konvertiert werden',
      noFileSelected: 'Keine Datei ausgewählt',
      fileErrorFor: 'Fehler bei {name}: {error}',
      genericError: 'Ein Fehler ist aufgetreten.',
      noOrganization: 'Benutzer ist keiner Organisation zugeordnet',
      tasksLoadFailed: 'Aufgaben konnten nicht geladen werden: {error}',
      agentQuestionPrefix: 'F: {title}',
      webSearchStep1: 'Ihre Frage wird verstanden',
      webSearchStep2: 'Durchsuche das Internet',
      webSearchStep3: 'Relevante Ergebnisse werden geprüft',
      webSearchStep4: 'Nützliche Quellen werden geprüft',
      webSearchStep5: 'Antwort wird vorbereitet',
      webSearchFoundQuery: 'Gesucht nach: „{query}"',
      aiThinkingLabel: 'KI denkt nach:',
      retryTimeoutLabel: 'Erneut versuchen',
    },

    answerStatus: {
      accepted: 'Akzeptiert',
      review: 'Weitere Prüfung erforderlich',
      missing: 'Information fehlt',
      rejected: 'Abgelehnt',
    },

    projectDetail: {
      breadcrumb: 'KI-Anfrage-Details',
      tabOverview: 'Übersicht',
      tabDocuments: 'Dokumente',
      tabQuestions: 'Anweisungen',
      tabActivities: 'Aktivitäten',
      agentLabel: 'Agent: {name}',
      defaultAgentName: 'Vertragsprüfungsagent',
      aiSummary: 'KI-Zusammenfassung',
      checklistProgress: 'Checklisten-Fortschritt',
      projectLocation: 'KI-Anfrage-Standort',
      analysisScore: 'Analyse-Bewertung',
      documentCount: 'Dokumentanzahl',
      participants: 'Teilnehmer',
      documents: 'Dokumente',
      noLocationExtracted: 'Noch nicht extrahiert',
      questionsCategory: 'Kategorien',
      questionsTitle: 'Fragentitel',
      questionsDescription: 'Beschreibung',
      questionsAnswer: 'KI-Antwort',
      questionsStatus: 'Status',
      questionsLoading: 'Fragen werden geladen...',
      defaultCategory: 'Allgemein',
      acceptedOrOpen: 'Akzeptiert',
      openAction: 'Öffnen',
      projectCreated: 'KI-Anfrage erstellt',
      newProjectStarted: 'Neue KI-Anfrage gestartet',
      activitiesEmpty: 'KI-Anfrage-Aktivitäten werden hier angezeigt, sobald Dokumente hochgeladen, analysiert und Fragen beantwortet werden.',
      activitiesEmptyTitle: 'Noch keine Aktivität',
      activityFileUploaded: 'Datei hochgeladen',
      activityFileProcessed: 'Verarbeitet',
      activityQuestionAsked: 'Frage gestellt',
      activityQuestionAskedFallback: 'Benutzer hat eine Frage gestellt',
      activityAnalysisPerformed: 'Analyse durchgeführt',
      activityAnalysisFallback: 'KI-Analyse abgeschlossen',
      activityDefaultUser: 'Benutzer',
      activityAiAgent: 'KI-Agent',
      activityTodayLabel: 'Heute',
      activityYesterdayLabel: 'Gestern',
      activityShowOlder: 'Ältere anzeigen',
      activityShowLess: 'Weniger anzeigen',
      uploadFilesHint: 'Laden Sie Dateien in diese KI-Anfrage hoch, damit der Agent sie überprüfen und analysieren kann.',
      yourDocuments: 'Ihre Dokumente',
      uploadDocument: 'Dokument hochladen',
      uploading: 'Wird hochgeladen...',
      agentReferenceFiles: 'Agenten Referenzdateien',
      documentName: 'Dokumentenname',
      pages: 'Seiten',
      uploadDate: 'Hochladedatum',
      documentsLoading: 'Dokumente werden geladen...',
      noDocumentsUploaded: 'Keine Dokumente hochgeladen',
      noQuestionsAssigned: 'Keine Fragen zugewiesen',
      noQuestionsHint: 'Fragen werden hier angezeigt, nachdem sie diesem Agenten hinzugefügt wurden.',
    },

    agentSettings: {
      breadcrumbAgents: 'Agenten',
      breadcrumbSettings: 'Agenteneinstellungen',
      agentFallback: 'Agent',
      departmentFallback: 'Benutzergruppe',
      tabGeneral: 'Allgemeine Einstellungen',
      tabQuestions: 'Anweisungen',
      tabAiProfile: 'KI-Profil',
      tabTools: 'Tools',
      tabFiles: 'Standard-Dateien',
      tabProjectOverview: 'KI-Anfrage-Übersicht',
      tabProjectSetup: 'KI-Anfrage-Setup',
      tabShortcuts: 'Shortcuts',
      // Tools tab — Word generation
      toolsTitle: 'Tools',
      toolsSubtitle: 'Aktivieren Sie optionale Funktionen für diesen Agenten. Die Word-Generierung verwendet die vorhandene Dokumenten-, Web- oder kombinierte Abrufkonfiguration des Agenten.',
      toolsWordGenTitle: 'Word-Generierung',
      toolsWordGenSubtitle: 'Nutzer können ein gebrandetes Word-Dokument erstellen, indem sie Word für eine einzelne Chat-Anfrage aktivieren.',
      toolsCodeInterpreterTitle: "Code Interpreter (Datenanalyse)",
      toolsCodeInterpreterSubtitle: "Ermöglicht exakte Berechnungen, Tabellen und Diagramme aus hochgeladenen Dateien – pro Chat einschaltbar, bleibt bis zum Ausschalten aktiv.",
      toolsCiInstructionLabel: "Analyseanweisungen",
      toolsCiInstructionPlaceholder: "z. B. Alle Beträge in CHF angeben und jede Tabelle zusätzlich als Excel-Datei ausgeben.",
      toolsCiInstructionHelp: "Ergänzt die festen Analyseregeln dieses Agenten.",
      toolsCiMaxOutputTokensLabel: "Maximale Antwortlänge (Tokens)",
      toolsCiMaxOutputTokensPlaceholder: "Kein Limit",
      toolsCiMaxOutputTokensHelp: "Leer lassen für keine Begrenzung. Sehr niedrige Werte können die Analyse vorzeitig beenden.",
      toolsCiSessionScopeLabel: "Arbeitsumgebung",
      toolsCiSessionScopeSession: "Im Chat wiederverwenden (empfohlen)",
      toolsCiSessionScopeRequest: "Für jede Anfrage neu",
      toolsCiSessionScopeSessionDesc: "Folgefragen nutzen dieselben Dateien und sind deutlich schneller.",
      toolsCiSessionScopeRequestDesc: "Jede Analyse startet in einer neuen Umgebung und lädt die Dateien erneut.",
      toolsInstructionLabel: 'Anweisung',
      toolsInstructionPlaceholder: 'Anweisung für die Word-Generierung',
      toolsInstructionHelp: 'Dies ersetzt den antwortformenden Teil des normalen Agenten-Prompts für die Word-Generierung. Der Abruf selbst bleibt unverändert.',
      toolsMaxOutputTokensLabel: 'Maximale Ausgabe-Tokens',
      toolsMaxOutputTokensHelp: 'Separates Ausgabebudget für die Word-Generierung. Ein großzügiger Standardwert verhindert stillschweigend abgeschnittene Dokumente; das Backend validiert den konfigurierten Wert dennoch gegen das unterstützte Modell-Limit.',
      toolsBehaviourTitle: 'Verhalten der Word-Generierung',
      toolsBehaviourItem1: 'Word wird im Chat-Eingabefeld nur angezeigt, wenn dieses Tool aktiviert ist.',
      toolsBehaviourItem2: 'Der Word-Schalter gilt für eine Nachricht und wird nach dem Senden oder beim Öffnen eines anderen Chats zurückgesetzt.',
      toolsBehaviourItem3: 'Ausgewählte Dokumente beschränken die für das generierte Dokument verwendeten sachlichen Quellen.',
      toolsBehaviourItem4: 'Das fertige DOCX und die PDF-Vorschau werden unter Generierte Dateien gespeichert und nie in den Suchindex aufgenommen.',
      toolsFutureToolsNote: 'Zukünftige Tools: Datenanalyse / Code Interpreter können hier später als weiteres eigenständiges Tool ergänzt werden.',
      toolsFixedLayoutTitle: 'Festes Dokumentlayout · keine Konfiguration erforderlich',
      toolsBrandingCardTitle: 'Organisations-Branding',
      toolsBrandingCardText: 'Logo, Organisationsname und Primärfarbe werden automatisch angewendet.',
      toolsSourcesCardTitle: 'Quellen & Sprache',
      toolsSourcesCardText: 'Validierte Quellen und feste Beschriftungen folgen der Dokument-/Anfragesprache.',
      toolsTransparencyCardTitle: 'KI-Transparenz',
      toolsTransparencyCardText: 'Ein Hinweis auf KI-Generierung wird in der Fußzeile und den Word-Dateieigenschaften angegeben.',
      toolsLayoutFooterNote: 'V1 verwendet das standardmäßige, gebrandete Layout. Eigene Vorlagen, mehrere Vorlagen und ein Dokumenteneditor sind bewusst nicht enthalten.',
      toolsSaveButton: 'Änderungen speichern',
      // General (Overview) header
      generalTitle: 'Allgemeine Einstellungen',
      generalSubtitle: 'Definieren Sie, wie dieser Agent funktioniert und welches Geschäftsproblem er löst.',
      unsavedChanges: 'Ungespeicherte Änderungen',
      cancel: 'Abbrechen',
      save: 'Speichern',
      saving: 'Speichern...',
      // Basic information card
      basicInfoTitle: 'Grundinformationen',
      basicInfoSubtitle: 'Grundlegende Details zur Organisation und Identifizierung dieses Agenten.',
      agentImageLabel: 'Agentenbild',
      editImage: 'Bild bearbeiten',
      removeImage: 'Bild entfernen',
      noFileSelected: 'Keine Datei ausgewählt',
      agentNameLabel: 'Agentenname',
      agentNamePlaceholder: 'Agentenname eingeben',
      organizationLabel: 'Organisation',
      selectOrganization: 'Organisation auswählen',
      departmentLabel: 'Benutzergruppe',
      selectDepartment: 'Benutzergruppe auswählen',
      descriptionLabel: 'Beschreibung',
      descriptionPlaceholder: 'Beschreiben Sie den Hauptnutzen dieses Agenten...',
      descriptionHelp: 'Ein oder zwei Sätze, die erklären, wie dieser Agent den Benutzern hilft.',
      // Use case card
      useCaseTitle: 'Anwendungsfall',
      useCaseSubtitle: 'Beschreiben Sie das Problem, das dieser Agent löst, und wie er Ihrem Team hilft.',
      problemLabel: 'Problembeschreibung*',
      problemPlaceholder: 'Welche geschäftliche Herausforderung adressiert dieser Agent?',
      problemHelp: 'Beispiel: Rechtsteams verbringen Stunden damit, Verträge manuell auf Compliance-Probleme zu überprüfen...',
      solutionLabel: 'Lösungsbeschreibung*',
      solutionPlaceholder: 'Wie löst dieser Agent das Problem?',
      solutionHelp: 'Beispiel: Dieser KI-Agent scannt automatisch Verträge und identifiziert Schlüsselbegriffe...',
      // Goals & KPIs card
      goalsTitle: 'Ziele & KPIs',
      goalsSubtitle: 'Definieren Sie Erfolgsmetriken und wer für diesen Anwendungsfall verantwortlich ist.',
      goalLabel: 'Ziel des Anwendungsfalls*',
      goalPlaceholder: 'z.B. Vertragsüberprüfungszeit um 80% reduzieren bei 99% Genauigkeit',
      goalHelp: 'Dies ist ein Hinweistext zur Unterstützung des Benutzers.',
      kpisLabel: 'Key Performance Indicators (KPIs)',
      kpiPlaceholder: 'Neuen KPI eingeben...',
      kpiAdd: 'Hinzufügen',
      kpiHelp: 'Drücken Sie Enter oder klicken Sie auf Hinzufügen, um einen neuen KPI hinzuzufügen.',
      managerLabel: 'Prozessmanager',
      managerPlaceholder: 'Name des Prozessmanagers',
      managerHelp: 'Wer ist für diesen Prozess verantwortlich?',
      // Toasts
      toastNoTask: 'Keine Aufgabe ausgewählt',
      toastNoPermission: 'Sie haben keine Berechtigung, diese Aufgabe zu bearbeiten',
      toastUpdateSuccess: 'Aufgabe erfolgreich aktualisiert',
      toastUpdateError: 'Fehler beim Aktualisieren',
      // Add Category modal
      addCategoryTitle: 'Neue Kategorie erstellen',
      addCategorySubtitle: 'Fügen Sie eine Kategorie hinzu, um Anweisungen zu organisieren und die Analyse einfacher zu verwalten und zu filtern.',
      addCategoryNameLabel: 'Kategoriename',
      addCategoryNamePlaceholder: 'Kategoriename',
      addCategoryEnterHint: 'Drücken Sie Enter, um eine neue Kategorie hinzuzufügen.',
      addCategoryEmptyError: 'Bitte geben Sie einen Kategorienamen ein.',
      addCategoryExistsError: 'Diese Kategorie existiert bereits.',
      addCategoryNoAgentError: 'Kein Agent ausgewählt.',
      addCategoryCreateError: 'Fehler beim Erstellen der Kategorie.',
      addCategoryDeleteError: 'Fehler beim Löschen der Kategorie.',
      addCategoryConfirm: 'Kategorie hinzufügen',
      // Add/Edit Question modal
      addQuestionTitle: 'Neue Anweisung erstellen',
      editQuestionTitle: 'Anweisung bearbeiten',
      questionTitleLabel: 'Anweisungstitel',
      questionTitlePlaceholder: 'Anweisungstitel',
      questionCategoryLabel: 'Kategorie',
      questionCategoryPlaceholder: 'Kategorie',
      questionDescriptionLabel: 'Beschreibung',
      questionDescriptionPlaceholder: 'Beschreibung eingeben...',
      questionEmptyTitleError: 'Bitte geben Sie einen Anweisungstitel ein.',
      questionEmptyDescriptionError: 'Bitte geben Sie eine Beschreibung ein.',
      questionOrgPermissionError: 'Sie können nur Anweisungen für Aufgaben Ihrer Organisation bearbeiten.',
      questionGenericError: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
      addQuestionConfirm: 'Anweisung hinzufügen',
      updateQuestionConfirm: 'Anweisung aktualisieren',
      // Shared
      edit: 'Bearbeiten',
      delete: 'Löschen',
      searchPlaceholder: 'Suche',
      previous: 'Vorherige',
      next: 'Weiter',
      pageOf: 'Seite {current} von {total}',
      // StandarQustions (Anweisungen tab)
      showProjectDetailTitle: 'Anweisungen aktivieren',
      showProjectDetailSubtitle: 'Wenn deaktiviert, wird der Anweisungen-Tab im Chat ausgeblendet.',
      showFilesTabTitle: 'Dateien-Tab im Chat anzeigen',
      showFilesTabSubtitle: 'Wenn deaktiviert, wird der Dateien-Tab in der Chat-Seitenleiste für diesen Agenten ausgeblendet.',
      allowLocalFilesTitle: 'Lokale Datei-Uploads erlauben',
      allowLocalFilesSubtitle: 'Wenn deaktiviert, können Nutzer keine lokalen Dateien hochladen. Es werden nur SharePoint-Dateien angezeigt.',
      questionsSelectTaskHint: 'Bitte wählen Sie eine Aufgabe aus, um ihre Anweisungen anzuzeigen',
      questionsAddCategoriesButton: 'Kategorien hinzufügen',
      questionsAddQuestionButton: 'Anweisung hinzufügen',
      questionsReorderButton: 'Reihenfolge ändern',
      questionsReorderDoneButton: 'Fertig',
      questionsReorderHint: 'Alle Anweisungen werden angezeigt. Ziehen Sie eine Anweisung an die gewünschte Position, auch über mehrere Seiten hinweg.',
      questionsColCategories: 'Kategorien',
      questionsColTitle: 'Anweisungstitel',
      questionsColDescription: 'Beschreibung',
      questionsEmptyTitle: 'Keine Anweisungen gefunden',
      questionsEmptyHint: 'Es wurden noch keine Anweisungen hinzugefügt. Klicken Sie auf „Anweisung hinzufügen", um eine neue Anweisung zu erstellen.',
      questionsDeleteTitle: 'Anweisung löschen',
      questionsDeleteMessage: 'Sind Sie sicher, dass Sie diese Anweisung löschen möchten?',
      // SystemPrompts (KI-Profil tab)
      aiProfileLoadError: 'Fehler beim Laden des KI-Profils',
      aiProfileSaveError: 'Fehler beim Speichern des KI-Profils',
      aiProfileNoPermission: 'Sie können nur KI-Profile für Aufgaben Ihrer Organisation bearbeiten',
      aiProfileSaveSuccess: 'KI-Profil erfolgreich gespeichert',
      aiProfileLoading: 'KI-Profil wird geladen...',
      aiProfileHeaderTitle: 'KI-Profil',
      aiProfileHeaderSubtitle: 'Konfigurieren Sie, wie der KI-Agent Kontext versteht, Antworten generiert und sich während der Analyse verhält.',
      aiProfileModelDefaultLabel: 'Default (from environment variables)',
      aiProfileModelDefaultDesc: 'Verwendet die Standardkonfiguration',
      aiProfileModelGpt51Label: 'GPT-5.1',
      aiProfileModelGpt51Desc: 'Leistungsstärkste, ausgewogene Leistung',
      aiProfileModelGpt56LunaLabel: 'GPT-5.6-Luna',
      aiProfileModelGpt56LunaDesc: 'Experimentell, nur für Web-Suche',
      aiProfileModelGpt56TerraLabel: 'GPT-5.6-Terra',
      aiProfileModelGpt56TerraDesc: 'Experimentell, nur für Web-Suche',
      aiProfileReasoningLowLabel: 'Low',
      aiProfileReasoningLowDesc: 'Schnellste Antworten, grundlegendes Reasoning',
      aiProfileReasoningStandardLabel: 'Standard',
      aiProfileReasoningStandardDesc: 'Standardmäßiges Reasoning',
      aiProfileReasoningMediumLabel: 'Medium',
      aiProfileReasoningMediumDesc: 'Ausgewogene Geschwindigkeit und Qualität',
      aiProfileReasoningHighLabel: 'High',
      aiProfileReasoningHighDesc: 'Beste Genauigkeit, langsamere Antworten',
      aiProfileSearchHybrid: 'Hybrid',
      aiProfileSearchSemantic: 'Hybrid + Semantisch',
      aiProfileSearchVector: 'Vector',
      aiProfileSearchKeyword: 'Keyword',
      aiProfileKnowledgeSourceTitle: 'Wissensquelle',
      aiProfileKnowledgeSourceSubtitle: 'Wählen Sie aus, welche Quellen dieses AI-Profil verwenden kann.',
      aiProfileModeDocumentsLabel: 'Nur Dokumente',
      aiProfileModeDocumentsDesc: 'Antworten nur mit indexierten Dokumenten und Q/A-Paaren.',
      aiProfileModeWebLabel: 'Nur Web',
      aiProfileModeWebDesc: 'Antworten nur mit Web-Suchergebnissen.',
      aiProfileModeHybridLabel: 'Dokumente + Web',
      aiProfileModeHybridDesc: 'Antworten mit Dokumenten und Web-Suchergebnissen.',
      aiProfileBadgeRag: 'RAG',
      aiProfileBadgeNoRag: 'Kein RAG',
      aiProfileBadgeWeb: 'Web',
      aiProfileBadgeNoWeb: 'Kein Web',
      aiProfileIdentityTitle: 'AI Identity',
      aiProfileIdentitySubtitle: 'Definieren Sie die Persönlichkeit und Anweisungen für das Verhalten der KI.',
      aiProfileIdentitySubtitleDocuments: 'Dedizierter System-Prompt für dokumentenbasierte Antworten.',
      aiProfileIdentitySubtitleWeb: 'Dedizierter System-Prompt für webbasierte Antworten.',
      aiProfileIdentitySubtitleHybrid: 'Dedizierter System-Prompt für Antworten mit Dokumenten und Web.',
      aiProfileDifferentPromptInfoTitle: 'Unterschiedlicher System-Prompt pro Wissensquelle',
      aiProfileDifferentPromptInfoDesc: 'Beim Wechsel des ausgewählten Modus wird der dazugehörige System-Prompt geladen.',
      aiProfileActivePromptBadge: 'Aktiver Prompt: {mode}',
      aiProfileSystemPromptLabel: 'System-Prompt: {mode}',
      aiProfileSystemPromptLabelPlain: 'System-Prompt',
      aiProfileRegexDetectionLabel: 'Referenz-UI anzeigen',
      disclaimerToggleLabel: 'Datenschutzhinweis',
      disclaimerToggleDescription: 'Zeigt einen Datenschutzhinweis über dem Chat-Eingabefeld an.',
      disclaimerTextLabel: 'Hinweistext',
      disclaimerTextHelp: 'Der Hinweis erscheint direkt über dem Eingabefeld.',
      disclaimerLearnMoreContentLabel: 'Inhalt für „Mehr erfahren"',
      disclaimerLearnMoreContentHelp: 'Inhalt, der im Modal nach Klick auf „Mehr erfahren" angezeigt wird.',
      disclaimerIconLabel: 'Symbol',
      disclaimerInfoNotice: 'Der Hinweis wird jedes Mal angezeigt, wenn ein Chat geöffnet wird. Nutzer können ihn für den aktuellen Besuch schließen; beim erneuten Öffnen des Chats erscheint er wieder.',
      disclaimerPreviewLabel: 'Vorschau',
      disclaimerLearnMoreLink: 'Mehr erfahren',
      disclaimerModalTitle: 'Weitere Informationen',
      toggleOn: 'An',
      toggleOff: 'Aus',
      aiProfileSystemPromptHelp: 'Technischer Prompt, der genau steuert, wie die KI in diesem Modus antwortet. Leer lassen, um den Standard zu verwenden.',
      aiProfileSystemPromptPlaceholder: 'System-Prompt für {mode}',
      aiProfileNameLabel: 'AI Profile Name',
      aiProfileNamePlaceholder: 'Vertragsprüfungs-Agent',
      aiProfileDescriptionLabel: 'Beschreibung',
      aiProfileDescriptionPlaceholder: 'Sie sind ein erfahrener Vertragsanalyst KI, der rechtliche Dokumente mit Präzision und Detailgenauigkeit überprüft. Geben Sie klare, strukturierte Antworten basierend ausschließlich auf dem Dokumentinhalt. Heben Sie Risiken, Verpflichtungen und Schlüsselbegriffe hervor. Formatieren Sie Ihre Antworten übersichtlich mit Aufzählungspunkten und Abschnittsüberschriften.',
      aiProfileDescriptionHelp: 'Dieser Prompt legt den Ton und das Verhalten für alle KI-Antworten fest',
      aiProfileModelTitle: 'Model & Intelligence Settings',
      aiProfileModelSubtitle: 'Wählen Sie das KI-Modell und die Reasoning-Stufe, die am besten zu Ihrem Anwendungsfall passt.',
      aiProfileModelLabel: 'Modell',
      aiProfileModelSelectPlaceholder: 'Modell auswählen',
      aiProfileReasoningLabel: 'Reasoning Effort',
      aiProfileResponseLengthLabel: 'Antwortlängenlimit',
      aiProfileResponseLengthHelp: 'Maximale Anzahl von Tokens in der Antwort. Höhere Werte ermöglichen längere Antworten. GPT-5.1 unterstützt bis zu 65.536 Tokens. Standard: 1.000',
      aiProfileResponseLengthExceeded: 'Maximal erlaubter Wert ist {max} — höhere Werte können nicht eingegeben werden.',
      aiProfileSourceSettingsTitle: 'Quelleneinstellungen',
      aiProfileSourceSettingsSubtitleDocuments: 'Konfigurieren Sie die Einstellungen für den Dokumentenabruf.',
      aiProfileSourceSettingsSubtitleWeb: 'Konfigurieren Sie die Web-Sucheinstellungen.',
      aiProfileSourceSettingsSubtitleHybrid: 'Konfigurieren Sie den Dokumentenabruf und die Web-Sucheinstellungen.',
      aiProfileWebSourceSettingsTitle: 'Web-Sucheinstellungen',
      aiProfileWebSourceSettingsSubtitle: 'Steuert die von der KI verwendeten öffentlichen Web-Suchergebnisse.',
      aiProfileWebSourceBadge: 'WEB-QUELLE',
      aiProfileWebSearchProviderLabel: 'Web-Suchanbieter',
      aiProfileWebSearchProviderValue: 'Azure OpenAI Web Search',
      aiProfileSearchContextSizeLabel: 'Such-Kontextgröße',
      aiProfileSearchContextSizeHelp: 'Steuert, wie viel Web-Inhalt Azure zur Beantwortung nutzt – keine exakte Anzahl an Quellen oder Zitaten.',
      aiProfileSearchContextLowLabel: 'Niedrig',
      aiProfileSearchContextLowDesc: 'Schnellere, oberflächliche Suche',
      aiProfileSearchContextMediumLabel: 'Mittel',
      aiProfileSearchContextMediumDesc: 'Ausgewogene Standardeinstellung',
      aiProfileSearchContextHighLabel: 'Hoch',
      aiProfileSearchContextHighDesc: 'Gründlichere, detailliertere Suche',
      aiProfileUseWebChatHistoryLabel: 'Chatverlauf für Web-Suche verwenden',
      aiProfileUseWebChatHistoryHelp: 'Bezieht frühere Fragen und Antworten in die Web-Suche ein. Standardmäßig deaktiviert, da dies die Antwortqualität verschlechtern kann.',
      aiProfileVerbosityLabel: 'Ausführlichkeit',
      aiProfileVerbosityOffLabel: 'Aus',
      aiProfileVerbosityOffDesc: 'Nicht gesendet',
      aiProfileVerbosityLowLabel: 'Niedrig',
      aiProfileVerbosityLowDesc: 'Kurze Antworten',
      aiProfileVerbosityMediumLabel: 'Mittel',
      aiProfileVerbosityMediumDesc: 'Ausgewogene Antworten',
      aiProfileVerbosityHighLabel: 'Hoch',
      aiProfileVerbosityHighDesc: 'Ausführliche Antworten',
      aiProfileAdvancedWebSearchTitle: 'Erweiterte Web-Suchparameter',
      aiProfileForceWebSearchLabel: 'Web-Suche erzwingen',
      aiProfileForceWebSearchHelp: 'Ein: Jede Anfrage durchsucht das Web (tool_choice: required). Aus: Das Modell entscheidet selbst (tool_choice: auto).',
      aiProfileStreamResponseLabel: 'Antwort streamen',
      aiProfileStreamResponseHelp: 'Ein (Standard): zeigt die Fortschrittsanzeige „Suche läuft“/„Antwort wird erstellt“ während der Anfrage. Aus: eine einzelne, nicht gestreamte Anfrage.',
      aiProfileIncludeSearchSourcesLabel: 'Suchquellen protokollieren',
      aiProfileIncludeSearchSourcesHelp: 'Ein: Die vom Modell durchsuchten Quellen werden zusätzlich serverseitig protokolliert (nur zur Fehlersuche, ändert nicht die angezeigten Quellen).',
      aiProfileShowReasoningLabel: 'Denkprozess anzeigen',
      aiProfileShowReasoningHelp: 'Ein: Zeigt dem Nutzer live an, worüber das Modell gerade nachdenkt, bevor die Antwort erscheint (wie bei Claudes „Denken"-Anzeige).',
      aiProfileShowReasoningEffortWarning: 'Wirkt nur bei Reasoning-Aufwand „Mittel" oder „Hoch" — bei „Standard" oder „Niedrig" liefert Azure keinen Denkprozess.',
      aiProfileShowReasoningStreamWarning: 'Benötigt zusätzlich „Antwort streamen" (oben) — ohne Streaming kann der Denkprozess dem Nutzer nicht live angezeigt werden.',
      aiProfileMaxToolCallsLabel: 'Maximale Tool-Aufrufe',
      aiProfileMaxToolCallsPlaceholder: 'Unbegrenzt',
      aiProfileMaxToolCallsHelp: 'Begrenzt, wie viele Suchen/Seitenaufrufe Azure pro Antwort durchführen darf. Leer: keine Begrenzung gesendet.',
      aiProfileAzureTimeoutLabel: 'Zeitüberschreitung (Sekunden)',
      aiProfileAzureTimeoutHelp: 'Wie lange auf eine Azure-Antwort gewartet wird, bevor abgebrochen wird. Standard: 180 Sekunden.',
      aiProfileAzureMaxRetriesLabel: 'Maximale Wiederholungen',
      aiProfileAzureMaxRetriesHelp: 'Wie oft Azure eine fehlgeschlagene Anfrage automatisch wiederholt. Standard: 0 (kein automatisches Wiederholen — jede Wiederholung nutzt das volle Zeitlimit erneut).',
      aiProfileMaxOutputTokensLabel: 'Max. Ausgabe-Tokens: {mode}',
      aiProfileMaxOutputTokensHelp: 'Begrenzt die Antwortlänge für diesen Modus. Standard: 1500. Maximal erlaubt: 65536 — höhere Werte werden automatisch darauf begrenzt.',
      aiProfileMaxOutputTokensExceeded: 'Maximal erlaubter Wert ist {max} — höhere Werte werden beim Speichern automatisch darauf begrenzt.',
      aiProfileAllowedDomainsLabel: 'Erlaubte Domains',
      aiProfileBlockedDomainsLabel: 'Gesperrte Domains',
      aiProfileDomainsPlaceholder: 'z. B. wikipedia.org, example.com',
      aiProfileDomainsHelp: 'Kommagetrennt oder eine Domain pro Zeile. Leer: kein Filter gesendet.',
      aiProfileUserLocationLabel: 'Standort-Ausrichtung',
      aiProfileUserLocationHelp: 'Ein: Ein ungefährer Standort wird an die Web-Suche übergeben, um lokale Ergebnisse zu verbessern.',
      aiProfileUserLocationCountryLabel: 'Land (ISO-Code)',
      aiProfileUserLocationCountryPlaceholder: 'z. B. DE',
      aiProfileUserLocationCityLabel: 'Stadt',
      aiProfileUserLocationCityPlaceholder: 'z. B. Berlin',
      aiProfileUserLocationRegionLabel: 'Region',
      aiProfileUserLocationRegionPlaceholder: 'z. B. Berlin',
      aiProfileUserLocationTimezoneLabel: 'Zeitzone',
      aiProfileUserLocationTimezonePlaceholder: 'z. B. Europe/Berlin',
      aiProfileContextTitle: 'Kontext- & Abrufeinstellungen',
      aiProfileContextSubtitle: 'Definiert, wie viel Kontext die KI vor der Generierung von Antworten verwendet.',
      aiProfileSearchStrategyLabel: 'Suchstrategie',
      aiProfileSearchStrategyPlaceholder: 'Strategie auswählen',
      aiProfileTopKLabel: 'Dokument-Kontexte (Top K)',
      aiProfileUseCustom: 'Use Custom',
      aiProfileTopKDefaultHint: 'Wenn deaktiviert, wird die Standardanzahl von Kontexten verwendet',
      aiProfileVectorKLabel: 'Kandidatenpool (Vector K)',
      aiProfileVectorKDefaultHint: 'Wenn deaktiviert, wird der globale Standardwert verwendet',
      aiProfileQaPairsLabel: 'Q/A-Paare als Kontext',
      aiProfileQaPairsDefaultHint: 'Wenn deaktiviert, werden keine Q/A-Paare als Kontext verwendet',
      aiProfileWebSearchLabel: 'Web-Suche',
      aiProfileWebSearchDescription: 'Durchsucht das Internet nach aktuellen Informationen. Dateien und Anweisungen werden im Chat ausgeblendet.',
      aiProfileWebSearchEnabled: 'Aktiviert',
      aiProfileWebSearchDisabled: 'Deaktiviert',
      aiProfileWebSearchCustomPromptLabel: 'Benutzerdefinierter Such-Prompt',
      aiProfileWebSearchCustomPromptDescription: 'Wird bei jeder Web-Suche vor die Eingabe des Nutzers gestellt. Nur für Super-Administratoren sichtbar.',
      aiProfileWebSearchCustomPromptPlaceholder: 'z. B. Suche nur nach Quellen aus den letzten 12 Monaten...',
      // DefaultFiles (Standard-Dateien tab)
      filesImportSuccess: '{count} Datei(en) erfolgreich importiert',
      filesImportError: 'Fehler beim Importieren',
      filesAutoSyncSubfoldersEnabled: 'Auto-Sync (mit Unterordnern) aktiviert',
      filesAutoSyncFolderOnlyEnabled: 'Auto-Sync (nur Ordner) aktiviert',
      filesAutoSyncEnableError: 'Fehler beim Aktivieren von Auto-Sync',
      filesInheritedSyncNotice: 'Dieses Element wird durch einen übergeordneten Ordner synchronisiert. Deaktivieren Sie den übergeordneten Ordner.',
      filesAutoSyncDisabled: 'Auto-Sync deaktiviert',
      filesAutoSyncDisableError: 'Fehler beim Deaktivieren',
      filesSpItemDeleted: 'Element erfolgreich gelöscht',
      filesSpItemDeleteError: 'Fehler beim Löschen',
      filesLastSyncNever: 'Nicht synchronisiert',
      filesLastSyncJustNow: 'Gerade eben',
      filesLastSyncMinutes: 'vor {mins} Min.',
      filesLastSyncHours: 'vor {hrs} Std.',
      filesLastSyncDays: 'vor {days} Tag(en)',
      filesRelToday: 'Heute',
      filesRelYesterday: 'Gestern',
      filesRelDaysAgo: 'vor {days} Tagen',
      filesRelWeekAgo: 'vor 1 Woche',
      filesRelWeeksAgo: 'vor {weeks} Wochen',
      filesRelMonthAgo: 'vor 1 Monat',
      filesRelMonthsAgo: 'vor {months} Monaten',
      filesRelYearAgo: 'vor 1 Jahr',
      filesRelYearsAgo: 'vor {years} Jahren',
      filesGeneralFolder: 'Allgemein',
      filesInvalidFileType: '{count} Datei(en) übersprungen. Nur PDF, DOC und DOCX sind erlaubt.',
      filesFolderNameRequired: 'Bitte geben Sie einen Ordnernamen ein',
      filesAddAtLeastOne: 'Bitte fügen Sie mindestens eine Datei hinzu',
      filesSelectTaskRequired: 'Bitte wählen Sie eine Aufgabe aus',
      filesAddedToFolderSuccess: '{count} Datei(en) erfolgreich zu „{folderName}" hinzugefügt',
      filesFolderCreatedSuccess: 'Ordner „{folderName}" mit {count} Datei(en) erfolgreich erstellt',
      filesPartialSuccess: '{success} Dateien erfolgreich, {errors} Fehler',
      filesUploadError: 'Fehler beim Hochladen der Dateien',
      filesMonthlyPageLimitExceeded: 'Das Upload-Limit für diesen Monat beträgt {limit} Seiten. Sie haben versucht, {attempted} Seiten hochzuladen. Bitte versuchen Sie es erneut und laden Sie nur {limit} Seiten hoch.',
      filesCreateFolderError: 'Fehler beim Erstellen des Ordners',
      filesDeleteFileSuccess: 'Datei erfolgreich gelöscht',
      filesDeleteFileError: 'Fehler beim Löschen der Datei',
      filesDeleteFolderSuccess: 'Ordner „{folderName}" erfolgreich gelöscht',
      filesSelectTaskHint: 'Bitte wählen Sie eine Aufgabe aus, um Standard-Dateien zu verwalten',
      filesTabAll: 'Alle Dateien',
      filesTabLocal: 'Lokale Dateien',
      filesTabSharePoint: 'SharePoint',
      filesSubTabOverview: 'Sites',
      filesSubTabImportReports: 'Import-Berichte',
      filesImportReportsSubtitle: 'Ein Bericht pro Import, neueste zuerst.',
      filesImportReportsRefresh: 'Aktualisieren',
      filesImportReportsEmpty: 'Noch keine Import-Berichte vorhanden',
      filesImportReportsEmptyHint: 'Berichte werden nach jedem Import automatisch gespeichert.',
      filesImportReportsColSite: 'Site',
      filesImportReportsColStarted: 'Gestartet',
      filesSubTabProtocol: 'Löschprotokoll',
      filesDeletionReportsSubtitle: 'Ein Bericht pro Löschvorgang, neueste zuerst.',
      filesDeletionReportsEmpty: 'Noch keine Löschberichte vorhanden',
      filesDeletionReportsEmptyHint: 'Berichte werden nach jedem "Importierte Daten löschen" oder "Site entfernen" automatisch gespeichert.',
      filesViewDeletionReport: 'Löschbericht anzeigen',
      filesCountSingular: 'Datei',
      filesCountPlural: 'Dateien',
      filesItemSingular: 'Element',
      filesItemPlural: 'Elemente',
      filesHeaderAllSubtitle: 'Lokale und SharePoint Dateien in einer vereinten Liste.',
      filesHeaderLocalSubtitle: 'Dateien, die direkt in DocAI gespeichert und verwaltet werden.',
      filesConnectedSitesSingular: '{count} Site diesem Agenten zugewiesen',
      filesConnectedSitesPlural: '{count} Sites diesem Agenten zugewiesen',
      filesUploadFile: 'Datei hochladen',
      filesAddFolder: 'Ordner hinzufügen',
      filesSearchPlaceholder: 'Suchen',
      filesFilterLabel: 'Filter',
      filesFilterLabelWithCount: 'Filter (1)',
      filesNoResultsFound: 'Keine Ergebnisse gefunden',
      filesNoFiles: 'Keine Dateien vorhanden',
      filesColName: 'Dateiname',
      filesColType: 'Dateityp',
      filesColCreated: 'Erstellt',
      filesColModified: 'Geändert',
      filesTypeSharePoint: 'SharePoint File',
      filesTypeLocal: 'Local File',
      filesDelete: 'Löschen',
      filesPageOf: 'Seite {current} von {total}',
      filesPrevious: 'Zurück',
      filesNext: 'Weiter',
      filesSyncProtocolTitle: 'Löschprotokoll',
      filesSyncProtocolSubtitle: 'Audit-Log aller Löschvorgänge über alle Sites',
      filesNoSyncActions: 'Keine Sync-Aktionen protokolliert',
      filesColSite: 'Site',
      filesColPath: 'Pfad',
      filesColTimestamp: 'Zeitstempel',
      filesColRuleType: 'Regel-Typ',
      filesColEvent: 'Ereignis',
      filesColStatus: 'Status',
      filesSiteOverview: 'SharePoint-Sites',
      filesFilesAndFolders: 'Dateien & Ordner',
      filesSyncedSuffix: '{count} synchronisiert',
      filesImporting: 'Importieren...',
      filesImport: 'Import',
      filesImportWithCount: 'Import ({count})',
      filesNoItems: 'Keine Dateien oder Ordner',
      filesDisableSync: 'Disable Sync',
      filesEnableAutoSyncFolderOnly: 'Enable Auto-Sync (folder only)',
      filesEnableAutoSync: 'Enable Auto-Sync',
      filesItemTypeFolder: 'Ordner',
      filesItemTypeFile: 'Datei',
      filesMetaModified: 'Geändert: {date}',
      filesColNameSp: 'Name',
      filesColTypeSp: 'Typ',
      filesColSpFolder: 'folder',
      filesColSpFile: 'File',
      filesNoConnectedSites: 'Noch keine SharePoint-Sites',
      filesNoConnectedSitesHint: 'Fügen Sie eine von Ihrem Organisationsadmin verbundene Site hinzu, wählen Sie die zu verwendenden Bibliotheken und Listen und starten Sie dann einen Import.',
      filesAddSiteButton: 'Site hinzufügen',
      filesAddSiteTitle: 'SharePoint-Site hinzufügen',
      filesAddSiteSubtitle: 'Sites, die Ihr Organisationsadmin verbunden hat. Das Hinzufügen einer Site importiert nichts.',
      filesScheduleSyncButtonTitle: 'Synchronisierung planen',
      filesScheduleSyncTitle: 'Sync-Zeitplan',
      filesScheduleSyncSubtitle: 'Wählen Sie Datum und Uhrzeit, zu der alle konfigurierten Sites automatisch synchronisiert werden.',
      filesScheduleSyncDateLabel: 'Datum',
      filesScheduleSyncTimeLabel: 'Uhrzeit',
      filesScheduleSyncRepeatLabel: 'Automatisch wiederholen',
      filesScheduleSyncRepeatDaysLabel: 'Alle (Tage)',
      filesScheduleSyncSaveButton: 'Zeitplan speichern',
      filesScheduleSyncRemoveButton: 'Zeitplan entfernen',
      filesScheduleSyncNextRunLabel: 'Nächste Synchronisierung:',
      filesScheduleSyncNoScheduleLabel: 'Kein Zeitplan festgelegt',
      filesScheduleSyncPastDateError: 'Datum und Uhrzeit müssen in der Zukunft liegen',
      filesScheduleSyncSelectDatePlaceholder: 'Datum wählen',
      filesScheduleSyncSelectTimePlaceholder: 'Uhrzeit wählen',
      filesScheduleSyncClearButton: 'Löschen',
      filesScheduleSyncTodayButton: 'Heute',
      filesAddSiteNoKnowledgeArea: 'Kein Wissensbereich',
      filesAddSiteEmpty: 'Keine weiteren Sites verfügbar — entweder wurden alle bereits hinzugefügt, oder Ihr Organisationsadmin hat noch keine für Ihren Bereich passende Site verbunden.',
      filesAddSiteSubmit: 'Site hinzufügen',
      filesSiteAddedToAgent: 'Site zum Agenten hinzugefügt',
      filesSourcesUpdated: 'Quellen aktualisiert',
      filesImportedBrowserTitle: 'Importierte Dateien — {siteName}',
      filesImportedBrowserRoot: 'Alle Ordner',
      filesImportedBrowserBack: 'Zurück',
      filesImportedBrowserEmpty: 'Keine Dateien an diesem Ort',
      filesImportedBrowserFileCount: '{count} Dateien',
      filesAddSiteNext: 'Weiter',
      filesAddSiteSourcesTitle: 'Quellen in {siteName}',
      filesAddSiteSourcesSubtitle: 'Dokumentbibliotheken und Listen mit Anhängen auf dieser Site. Nur was Sie auswählen, kann importiert werden.',
      filesAddSiteDocumentLibraries: 'Dokumentbibliotheken',
      filesAddSiteItemCount: '{count} Elemente',
      filesAddSiteLists: 'Listen mit Anhängen',
      filesAddSiteListsHint: 'Listen können noch nicht ausgewählt werden. Der Import erfordert eine zertifikatsbasierte Authentifizierung, die noch nicht eingerichtet ist — diese Auswahl wird aktiviert, sobald das verfügbar ist.',
      filesAddSiteNoSources: 'Keine Dokumentbibliotheken oder Listen auf dieser Site gefunden.',
      filesAddSiteSourcesFooterHint: 'Das Speichern merkt sich die Auswahl. Es wird erst importiert, wenn Sie einen Import starten.',
      filesAddSiteSelectAtLeastOne: 'Wählen Sie mindestens eine Quelle aus.',
      filesOpenSite: 'Site öffnen',
      filesStatsFiles: 'Dateien',
      filesStatsFolders: 'Ordner',
      filesStatsSubfolders: 'Unterordner',
      filesStatsLastSync: 'Letzte Sync',
      filesStatusImported: 'Importiert',
      filesSourcesChangedBadge: 'Quellen geändert',
      filesSourcesChangedHint: 'Die ausgewählten Quellen wurden seit dem letzten Import geändert. Erneut importieren, um die Änderung zu übernehmen.',
      filesEditSources: 'Quellen bearbeiten',
      filesImportAgain: 'Erneut importieren',
      filesStatsFilesImported: 'Dateien importiert',
      filesStatsSourceCountSingular: '{count} Quelle',
      filesStatsSourceCountPlural: '{count} Quellen',
      filesStatsLastImport: 'Letzter Import',
      filesStatsSkippedFailed: '{skipped} übersprungen · {failed} fehlgeschlagen',
      filesViewImportReport: 'Import-Bericht anzeigen',
      filesImportInfoBanner: 'Imports werden nur ausgeführt, wenn Sie sie starten. Dateien, die danach in SharePoint hinzugefügt, geändert oder gelöscht werden, werden erst beim nächsten Import erfasst.',
      filesDeleteImportedData: 'Importierte Daten löschen',
      filesRemoveSiteFromAgent: 'Site vom Agenten entfernen',
      filesSiteRemovedFromAgent: 'Site vom Agenten entfernt',
      filesRemoveSiteError: 'Site konnte nicht vom Agenten entfernt werden',
      filesDeleteImportedDataConfirmTitle: 'Importierte Daten löschen?',
      filesDeleteImportedDataConfirmMessage: 'Alle aus {siteName} importierten Dateien werden entfernt — Embeddings, Dateieinträge und Zuordnungen. Diese Aktion kann nicht rückgängig gemacht werden.',
      filesDeleteImportedDataError: 'Importierte Daten konnten nicht gelöscht werden. Bitte versuchen Sie es später erneut.',
      filesRemoveSiteConfirmTitle: 'Site entfernen?',
      filesRemoveSiteConfirmMessage: 'Die Site {siteName} wird vom Agenten entfernt und alle daraus importierten Daten — Dokumente, OCR-Ergebnisse, Embeddings und Struktur — werden gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.',
      filesRemoveSiteConfirmButton: 'Site entfernen',
      filesImportReportTitle: 'Import-Bericht — {siteName}',
      filesImportReportStarted: 'Gestartet {date}',
      filesImportReportSources: 'Quellen',
      filesImportReportFileTypes: 'Dateitypen',
      filesImportReportPageLimit: 'Seitenlimit',
      filesImportReportMaxPages: 'Max. {max}',
      filesImportReportNoLimit: 'Kein Limit',
      filesImportReportImported: 'Importiert',
      filesImportReportSkipped: 'Übersprungen',
      filesImportReportFailed: 'Fehlgeschlagen',
      filesImportReportFileCount: '{count} Dateien',
      filesImportReportDuration: 'Dauer',
      filesImportReportStoppedNote: 'Dieser Import wurde vorzeitig gestoppt. {count} Datei(en) wurden übernommen, der Rest wurde übersprungen.',
      filesImportReportStoppedNoteWithTotal: 'Dieser Import wurde vorzeitig gestoppt. {count} Datei(en) wurden in diesem Durchlauf übernommen (die Website hat jetzt insgesamt {total} Dateien).',
      filesImportReportDiscardedNote: 'Dieser Import wurde abgebrochen und verworfen. Es wurde nichts aus diesem Durchlauf übernommen.',
      filesImportReportBadgeStopped: 'Gestoppt',
      filesImportReportBadgeDiscarded: 'Verworfen',
      embedErrorNoTextExtracted: 'Kein Text extrahiert (wahrscheinlich ein gescanntes PDF)',
      filesImportReportLastImport: 'Letzter Import',
      filesImportReportNoDetailHint: 'Diese Dateien wurden einzeln importiert — dafür gibt es keinen detaillierten Bericht mit Dateitypen, Seitenlimit oder Einzeldateiverlauf.',
      filesImportReportSkippedTitle: 'Übersprungen aufgrund Ihrer Import-Einstellungen',
      filesImportReportSkippedHint: 'Diese wurden absichtlich ausgeschlossen. Ändern Sie die Einstellungen und importieren Sie erneut, um sie einzuschließen.',
      filesImportReportFailedTitle: 'Fehlgeschlagen',
      filesImportReportFailedHint: 'Diese konnten nicht verarbeitet werden. Beim erneuten Versuch werden nur diese Dateien gelesen.',
      filesDeletionReportTitle: 'Löschbericht — {siteName}',
      filesImportReportRemoved: 'Entfernt',
      filesDeletionReportRemovedTitle: 'Entfernte Dateien',
      filesDeletionReportFailedTitle: 'Fehlgeschlagen',
      filesDeletionReportFailedHint: 'Diese Dateien konnten nicht gelöscht werden.',
      filesRetryFailedFiles: 'Fehlgeschlagene Dateien erneut versuchen ({count})',
      filesRetryReportNotCurrent: 'Dieser Bericht beschreibt nicht mehr den aktuellen Stand der Site und kann nicht erneut versucht werden.',
      filesRetryImportRunning: 'Für diese Site läuft bereits ein Import. Bitte warten Sie, bis er abgeschlossen ist.',
      filesRetryFailedError: 'Erneuter Versuch fehlgeschlagen. Bitte versuchen Sie es später erneut.',
      filesStartImportTitle: 'Import starten',
      filesStartImportFileTypes: 'Dateitypen',
      filesStartImportPageLimit: 'Seitenlimit pro Datei',
      filesStartImportNoLimit: 'Kein Limit',
      filesStartImportPageLimitHint: 'Dateien mit mehr Seiten werden übersprungen und im Import-Bericht aufgeführt.',
      filesStartImportReplaceWarningBold: 'Diese Site wurde am {date} importiert.',
      filesStartImportReplaceWarningRest: 'Ein erneuter Import ersetzt die {count} Dokumente, OCR-Ergebnisse und Embeddings, die derzeit dafür gespeichert sind.',
      filesStartImportSourcesAddedLabel: 'Hinzugefügte Quellen:',
      filesStartImportSourcesRemovedLabel: 'Entfernte Quellen (deren Dateien werden gelöscht):',
      filesStartImportInfoStatic: 'Der Import liest die ausgewählten Quellen vollständig ein. Dateien, die danach in SharePoint gelöscht oder geändert werden, bleiben hier unverändert, bis Sie erneut importieren.',
      filesStartImportSubmit: 'Import starten',
      filesImportCompleted: 'Import für {site} abgeschlossen',
      filesImportFailed: 'Import fehlgeschlagen',
      filesImportRunning: 'Import läuft',
      filesImportReconnecting: 'Verbindung wird wiederhergestellt…',
      filesImportProcessedOf: '{processed} von {total} Dateien verarbeitet',
      filesImportCancel: 'Abbrechen',
      filesImportCancelling: 'Wird abgebrochen…',
      filesImportCancelTitle: 'Import abbrechen?',
      filesImportCancelMessage: 'Möchten Sie die bereits importierten Dateien behalten und nur den Rest stoppen, oder den gesamten Import bisher rückgängig machen?',
      filesImportCancelStop: 'Stoppen (bisherige Dateien behalten)',
      filesImportCancelDiscard: 'Alles verwerfen',
      filesImportCancelBack: 'Zurück',
      filesImportCancelError: 'Import konnte nicht abgebrochen werden',
      filesImportCancelProcessing: 'Wird verarbeitet…',
      filesEmptyInFolder: 'Keine Dateien in diesem Ordner',
      filesEmptyFolders: 'Keine Ordner gefunden',
      filesEmptyLocalHint: 'Klicken Sie auf „Ordner hinzufügen", um einen neuen Ordner mit Dateien zu erstellen.',
      filesEmptySharePointHint: 'Importieren Sie Dateien aus SharePoint, um sie hier anzuzeigen.',
      filesCreatedOn: 'Erstellt: {date}',
      filesModifiedOn: 'Geändert: {date}',
      filesColFileName: 'Dateiname',
      filesColFolderName: 'Ordnername',
      filesColUploadedAt: 'Hochgeladen am',
      filesColSize: 'Größe',
      filesColUsedIn: 'Verwendet in',
      filesColFiles: 'Dateien',
      filesColCreatedAt: 'Erstellt am',
      filesColLastModified: 'Zuletzt geändert',
      filesSourceDeleted: 'Quelle gelöscht',
      filesProjectSingular: 'KI-Anfrage',
      filesProjectPlural: 'KI-Anfragen',
      filesAddToFolderTitle: 'Dateien zu „{folderName}" hinzufügen',
      filesCreateNewFolderTitle: 'Neuen Ordner erstellen',
      filesAddToFolderSubtitle: 'Laden Sie neue Dateien in diesen Ordner hoch.',
      filesCreateNewFolderSubtitle: 'Erstellen Sie einen Ordner und fügen Sie Dateien hinzu.',
      filesFolderNameLabel: 'Ordnername *',
      filesFolderNamePlaceholder: 'z.B. Verträge, Vorlagen, Berichte...',
      filesAddFilesLabel: 'Dateien hinzufügen *',
      filesDragOrClick: 'Dateien hierher ziehen oder klicken zum Auswählen',
      filesSupportedTypes: 'PDF, DOC, DOCX (Mehrere Dateien möglich)',
      filesSelectedFiles: 'Ausgewählte Dateien ({count})',
      filesRemoveAll: 'Alle entfernen',
      filesCancel: 'Abbrechen',
      filesUploadingStatus: 'Wird hochgeladen...',
      filesCreatingStatus: 'Wird erstellt...',
      filesUpload: 'Hochladen',
      filesCreateFolder: 'Ordner erstellen',
      filesUploadProgressUploading: 'Dateien werden hochgeladen...',
      filesUploadProgressCreating: 'Ordner wird erstellt...',
      filesFileOfTotal: 'Datei {current} von {total}',
      filesPreparing: 'Vorbereitung...',
      filesPercentDone: '{percent}% abgeschlossen',
      filesDeleteFolderTitle: 'Ordner löschen',
      filesDeleteFileTitle: 'Datei löschen',
      filesDeleteFolderMessage: 'Sind Sie sicher, dass Sie den Ordner „{name}" mit {count} Datei(en) löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      filesDeleteSpItemMessage: 'Sind Sie sicher, dass Sie „{name}" löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      filesDeleteFileMessage: 'Sind Sie sicher, dass Sie „{name}" löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      filesDeleting: 'Wird gelöscht...',
      filesFilterTitle: 'Filter',
      filesFilterFileTypeLabel: 'Dateityp',
      filesFilterOptLocal: 'Local File',
      filesFilterOptSharePoint: 'SharePoint File',
      filesFilterSortByLabel: 'Sortieren nach',
      filesSortNewest: 'Neueste zuerst',
      filesSortOldest: 'Älteste zuerst',
      filesSortMostProgress: 'Most progress',
      filesSortLeastProgress: 'Least progress',
      filesResetAll: 'Alle zurücksetzen',
      filesApply: 'Anwenden',
      // TaskShortcuts (Shortcuts tab)
      shortcutsTabEmoji: 'Emoji',
      shortcutsTabCustom: 'Benutzerdefiniert',
      shortcutsCustomEmojiLabel: 'Beliebiges Emoji eingeben:',
      shortcutsCustomEmojiPlaceholder: 'Emoji hier einfügen...',
      shortcutsUseButton: 'Verwenden',
      shortcutsModalEditTitle: 'Shortcut bearbeiten',
      shortcutsModalAddTitle: 'Neuen Shortcut hinzufügen',
      shortcutsNameLabel: 'Shortcut-Name',
      shortcutsNamePlaceholder: 'z.B. Sprache vereinfachen',
      shortcutsIconLabel: 'Icon',
      shortcutsIconChange: 'Klicken um Icon zu ändern',
      shortcutsDescriptionLabel: 'Beschreibung',
      shortcutsDescriptionPlaceholder: 'Beschreiben Sie, was dieser Shortcut tut...',
      shortcutsOrderLabel: 'Anzeigereihenfolge',
      shortcutsActiveLabel: 'Aktiv',
      shortcutsCancel: 'Abbrechen',
      shortcutsSaving: 'Speichern...',
      shortcutsSaveButton: 'Shortcut speichern',
      shortcutsUpdateSuccess: 'Shortcut erfolgreich aktualisiert',
      shortcutsCreateSuccess: 'Shortcut erfolgreich erstellt',
      shortcutsSaveError: 'Fehler beim Speichern des Shortcuts',
      shortcutsDeleteSuccess: 'Shortcut erfolgreich gelöscht',
      shortcutsDeleteError: 'Fehler beim Löschen des Shortcuts',
      shortcutsOrderSaveSuccess: 'Reihenfolge erfolgreich gespeichert',
      shortcutsOrderSaveError: 'Fehler beim Speichern der Reihenfolge',
      shortcutsSelectTaskHint: 'Bitte wählen Sie eine Aufgabe aus, um Shortcuts zu verwalten',
      shortcutsHeaderTitle: 'Shortcuts',
      shortcutsHeaderSubtitle: 'Erstellen und verwalten Sie wiederverwendbare KI-Shortcuts, um Inhalte während der Analyse schnell zu transformieren.',
      shortcutsListTitle: 'Shortcut-Liste',
      shortcutsAddButton: 'Shortcut hinzufügen',
      shortcutsEmptyTitle: 'Keine Shortcuts vorhanden',
      shortcutsEmptyHint: 'Klicken Sie auf „Shortcut hinzufügen", um Ihren ersten Shortcut zu erstellen.',
      shortcutsInactiveBadge: 'Inaktiv',
      shortcutsEditTooltip: 'Bearbeiten',
      shortcutsDeleteTooltip: 'Löschen',
      shortcutsInfoTitle: 'Wie Shortcuts funktionieren',
      shortcutsInfoText: 'Shortcuts erscheinen unter den KI-Antworten im Analysebereich. Wenn Sie darauf klicken, verarbeitet das System die Antwort mit dem Shortcut-Prompt neu und zeigt das Ergebnis neben der ursprünglichen Antwort an. So können Benutzer schnell Übersetzungen, Vereinfachungen, Zusammenfassungen oder andere von Ihnen konfigurierte Transformationen erhalten.',
      shortcutsDeleteModalTitle: 'Shortcut löschen',
      shortcutsDeleteModalMessage: 'Möchten Sie „{name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      // ProjectOverviewBuilder (Project Overview tab)
      overviewAdded: 'Hinzugefügt',
      overviewAdd: 'Hinzufügen',
      overviewNoAgent: 'Kein Agent ausgewählt.',
      overviewEnableTitle: 'KI-Anfrage-Übersicht aktivieren',
      overviewEnableSubtitle: 'Wenn deaktiviert, wird der KI-Anfrage-Übersicht-Tab im KI-Anfrage für diesen Agenten ausgeblendet und Nutzer gelangen direkt in den Chat.',
      overviewAvailableWidgets: 'Verfügbare Widgets',
      overviewAvailableSubtitle: 'Durchsuchen und fügen Sie Widgets hinzu, um das KI-Anfrage-Übersicht-Layout anzupassen.',
      overviewPreviewTitle: 'KI-Anfrage-Übersicht-Vorschau',
      overviewPreviewSubtitle: 'Dieses Layout wird auf alle KI-Anfragen angewendet, die diesen Agenten verwenden.',
      overviewUnsavedChanges: 'Ungespeicherte Änderungen',
      overviewCancel: 'Abbrechen',
      overviewSaving: 'Wird gespeichert…',
      overviewSaveLayout: 'Layout speichern',
      overviewEmpty: 'Keine Widgets hinzugefügt. Füge oben Widgets aus der Liste hinzu.',
    },

    projectSetup: {
      title: 'KI-Anfrage-Setup',
      subtitle: 'Felder konfigurieren, die im KI-Anfrage-Erstellungsformular angezeigt werden.',
      formBuilderTitle: 'Formular-Builder',
      addField: 'Feld hinzufügen',
      emptyState: 'Noch keine Felder definiert. Klicken Sie auf „Feld hinzufügen", um zu starten.',
      saveChanges: 'Änderungen speichern',
      savedSuccess: 'Felder erfolgreich gespeichert',
      saveError: 'Fehler beim Speichern der Felder',
      moveUp: 'Nach oben',
      moveDown: 'Nach unten',
      addFieldTitle: 'Feld hinzufügen',
      editFieldTitle: 'Feld bearbeiten',
      fieldTypeLabel: 'Feldtyp',
      fieldLabelLabel: 'Feldbezeichnung',
      fieldLabelPlaceholder: 'z. B. KI-Anfrage-Name',
      requiredLabel: 'Pflichtfeld',
      requiredHelp: 'Benutzer müssen dieses Feld ausfüllen.',
      requiredError: 'Bitte alle Pflichtfelder ausfüllen.',
      helperLabel: 'Hinweistext',
      helperPlaceholder: 'Kurze Beschreibung für Benutzer',
      defaultValueLabel: 'Standardwert',
      defaultValuePlaceholder: 'Optionaler Standardwert',
      optionsLabel: 'Optionen',
      optionPlaceholder: 'Option hinzufügen',
      addOption: 'Hinzufügen',
      saveField: 'Feld speichern',
      fieldTypeText: 'Texteingabe',
      fieldTypeTextarea: 'Mehrzeiliger Text',
      fieldTypeNumber: 'Zahl',
      fieldTypeDate: 'Datum',
      fieldTypeDropdown: 'Dropdown',
      fieldTypeImage: 'Bild-Upload',
      fieldTypeFile: 'Datei-Upload',
      directChatModeLabel: 'Direkter Chat-Modus',
      directChatModeEnabledBadge: 'Aktiviert',
      directChatModeDesc: 'Wenn ein Nutzer diesen Agenten auswählt, gelangt er direkt in einen Live-Chat — kein Anfrage-Setup erforderlich.',
      fieldProjectName: 'KI-Anfrage-Name',
      fieldProjectNameDesc: 'Textfeld für den KI-Anfrage-Namen – wird als KI-Anfrage-Titel gespeichert.',
      fieldProjectImage: 'KI-Anfrage-Bild',
      fieldProjectImageDesc: 'Bild-Upload für das KI-Anfrage-Bild.',
      fieldUploadMethods: 'Upload-Methoden',
      fieldUploadMethodsDesc: 'Konfigurieren Sie, wie Benutzer Dokumente hochladen können.',
      uploadSourceComputer: 'Computer',
      uploadSourceSharePoint: 'SharePoint',
      uploadSourceBoth: 'Beide',
      uploadSourceLabel: 'Upload-Quelle',
      uploadSourceHelp: 'Wählen Sie, wie Benutzer Dateien für dieses Feld bereitstellen.',
      sourceComputerTitle: 'Vom Computer hochladen',
      sourceComputerSub: 'Benutzer lädt Dateien direkt vom lokalen Gerät hoch.',
      sourceSharePointTitle: 'SharePoint-Ordner',
      sourceSharePointSub: 'Benutzer wählt einen Ordner aus einem verbundenen SharePoint.',
      sourceBothTitle: 'Beide Methoden',
      sourceBothSub: 'Benutzer kann zwischen lokalem Upload oder SharePoint wählen.',
      // Image upload settings
      imageUploadSettings: 'Bild-Upload-Einstellungen',
      acceptedFormatsLabel: 'Akzeptierte Formate',
      acceptedFormatsHelp: 'Wählen Sie, welche Bildformate hochgeladen werden dürfen.',
      multipleLabel: 'Mehrere Bilder erlauben',
      multipleHelp: 'Benutzer kann mehr als ein Bild hochladen.',
      maxImageCountLabel: 'Maximale Bildanzahl',
      imagesMax: 'Bilder max.',
      imageConstraintsLabel: 'Bild-Einschränkungen',
      optional: 'optional',
      aspectRatioLabel: 'Seitenverhältnis',
      arAny: 'Beliebig', arAnySub: 'Keine Einschränkung',
      ar11: '1:1', ar11Sub: 'Quadrat',
      ar43: '4:3', ar43Sub: 'Standard',
      ar169: '16:9', ar169Sub: 'Breitbild',
      ar32: '3:2', ar32Sub: 'Foto',
      minDimensionsLabel: 'Mindestabmessungen',
      widthPx: 'Breite (px)',
      heightPx: 'Höhe (px)',
      outputQualityLabel: 'Ausgabequalität',
      qOriginal: 'Original', qOriginalSub: 'Keine Komprimierung',
      qHigh: 'Hoch', qHighSub: '~80% Qualität',
      qMedium: 'Mittel', qMediumSub: '~60% Qualität',
      qLow: 'Niedrig', qLowSub: '~40% Qualität',
      maxFileSizeLabel: 'Max. Dateigröße',
      uploadImageBtn: 'Bild hochladen',
      uploadImagesBtn: '{remaining} weitere Bilder hochladen',
      errMaxCount: 'Maximal {count} Bilder erlaubt.',
      errFormat: 'Format nicht erlaubt: {name}',
      errSize: '{name} überschreitet {mb} MB.',
      errMinDim: '{name}: Mindestens {w}×{h} px erforderlich.',
      errAspect: '{name}: Seitenverhältnis {ratio} erforderlich.',
    },

    widgets: {
      notGenerated: 'Noch nicht generiert.',
      noSubmissionDate: 'Kein Einreichungsdatum extrahiert',
      documents: 'Dokumente',
      titles: {
        summary: 'KI-Zusammenfassung',
        checklist_progress: 'Checklisten-Fortschritt',
        location: 'KI-Anfrage-Standort',
        analysis_score: 'Analyse-Bewertung',
        document_count: 'Dokumentanzahl',
        participants: 'Teilnehmer',
        submission_date: 'Abgabefrist-Countdown',
      },
    },

    settings: {
      title: 'Einstellungen',
      tabAdministrator: 'Administrator',
      tabOrganization: 'Organisation',
      tabLanguage: 'Sprache',
      tabManageTasks: 'Aufgaben verwalten',
    },

    organization: {
      // Toolbar
      searchPlaceholder: 'Suchen',
      addButton: 'Neue Organisation',
      // Table headers
      columnLogo: 'Logo',
      columnName: 'Organisationsname',
      columnDescription: 'Beschreibung',
      columnSharePoint: 'SharePoint',
      columnColor: 'Farbe',
      columnSubscription: 'Abonnement',
      // Cell values
      logoFallback: 'Logo',
      spConnected: 'Verbunden',
      spError: 'Fehler',
      spNotConnected: 'Nicht verbunden',
      subscriptionActive: 'Aktiv',
      subscriptionInactive: 'Inaktiv',
      // Actions
      actionManage: 'Verwalten',
      actionEdit: 'Organisation bearbeiten',
      actionDelete: 'Organisation löschen',
      // Pagination
      pageOf: 'Seite {current} von {total}',
      paginationPrev: 'Zurück',
      paginationNext: 'Weiter',
      // Create/Edit Modal
      modalCreateTitle: 'Neue Organisation erstellen',
      modalEditTitle: 'Organisation bearbeiten',
      logoLabel: 'Organisationslogo',
      logoAlt: 'Logo',
      logoNone: 'Kein Logo',
      logoUpload: 'Logo hochladen',
      nameLabel: 'Organisationsname *',
      namePlaceholder: 'Organisationsname eingeben',
      descriptionLabel: 'Beschreibung (Optional)',
      descriptionPlaceholder: 'Kurze Beschreibung, die Benutzern angezeigt wird',
      footerAddressLabel: 'Fußzeilen-Adresse (Optional)',
      footerAddressPlaceholder: 'Firma AG\nStraße 1, Stadt\nTelefon 000 000 00 00\nE-Mail info@firma.ch',
      footerAddressHelp: 'Wird in der Fußzeile der von dieser Organisation generierten Word-Dokumente angezeigt. Jede Zeile erscheint als eigene Zeile.',
      primaryColorLabel: 'Primärfarbe',
      uploadingState: 'Wird hochgeladen...',
      savingState: 'Wird gespeichert...',
      submitCreate: 'Organisation erstellen',
      submitSave: 'Speichern',
      // Delete modal
      deleteTitle: 'Organisation löschen',
      deleteMessage: 'Möchten Sie die Organisation "{name}" wirklich löschen?',
      // Subscription assignment modal
      subModalTitle: 'Abonnement für "{name}" zuweisen',
      subModalSelectLabel: 'Abonnement auswählen',
      subModalSelectPlaceholder: 'Abonnement auswählen...',
      subModalNone: 'Kein Abonnement',
      subModalOptionInfo: '{name} - {tasks} Aufgaben, {pages} Seiten',
      // Toasts
      toastCreated: 'Organisation erfolgreich erstellt',
      toastUpdated: 'Organisation erfolgreich aktualisiert',
      toastDeleted: 'Organisation gelöscht',
      toastSaveError: 'Fehler beim Speichern',
      toastDeleteError: 'Fehler beim Löschen',
      toastSubAssigned: 'Abonnement erfolgreich zugewiesen',
      toastSubAssignError: 'Fehler beim Zuweisen des Abonnements',
    },

    manageOrganization: {
      // Breadcrumb / header
      breadcrumbSettings: 'Einstellungen',
      breadcrumbManage: 'Organisation verwalten',
      loading: 'Laden...',
      // Summary card
      summaryDepartments: 'Benutzergruppen',
      summaryUsers: 'Benutzer',
      summarySubscription: 'Abonnement',
      summaryActive: 'Aktiv',
      summaryInactive: 'Inaktiv',
      // Tabs
      tabDepartments: 'Benutzergruppen',
      tabSubscriptions: 'Abonnements',
      tabAgentOrder: 'Agenten-Reihenfolge',
      tabSharepoint: 'SharePoint verknüpfen',
      // Departments tab
      deptSearchPlaceholder: 'Suchen',
      deptAddButton: 'Benutzergruppe hinzufügen',
      deptEmptyTitle: 'Noch keine Benutzergruppen',
      deptEmptyHint: 'Erstellen Sie Ihre erste Benutzergruppe.',
      noResultsTitle: 'Keine Ergebnisse gefunden',
      noResultsHint: 'Versuchen Sie andere Suchbegriffe.',
      deptColumnName: 'Benutzergruppe',
      deptColumnDescription: 'Beschreibung',
      deptColumnCreated: 'Erstellt',
      deptActionEdit: 'Benutzergruppe bearbeiten',
      deptActionDelete: 'Benutzergruppe löschen',
      // Department modal
      deptModalCreateTitle: 'Benutzergruppe erstellen',
      deptModalEditTitle: 'Benutzergruppe bearbeiten',
      deptModalNameLabel: 'Benutzergruppenname*',
      deptModalNamePlaceholder: 'z.B. Vertrieb',
      deptModalDescriptionLabel: 'Beschreibung (Optional)',
      deptModalDescriptionPlaceholder: 'Kurze Beschreibung des Zwecks dieser Benutzergruppe.',
      deptModalSubmitCreate: 'Benutzergruppe erstellen',
      deptModalSubmitSave: 'Speichern',
      deptModalCreating: 'Erstellen...',
      deptModalSaving: 'Speichern...',
      // Department delete
      deptDeleteTitle: 'Benutzergruppe löschen',
      deptDeleteMessage: 'Durch das Löschen dieser Benutzergruppe wird die Zuordnung zu Agenten aufgehoben. Die Agenten bleiben aktiv, gehören aber nicht mehr zu dieser Benutzergruppe.',
      // Department toasts
      deptToastCreated: 'Benutzergruppe erfolgreich erstellt',
      deptToastUpdated: 'Benutzergruppe erfolgreich aktualisiert',
      deptToastDeleted: 'Benutzergruppe erfolgreich gelöscht',
      deptToastSaveError: 'Fehler beim Speichern der Benutzergruppe',
      deptToastDeleteError: 'Fehler beim Löschen der Benutzergruppe',
      // Subscriptions tab
      subSearchPlaceholder: 'Suchen',
      subAddButton: 'Neues Abonnement',
      subEmptyTitle: 'Noch keine Abonnements',
      subEmptyHint: 'Erstellen Sie Ihr erstes Abonnement.',
      subColumnName: 'Abonnementname',
      subColumnDescription: 'Beschreibung',
      subActionViewCredits: 'Kreditnutzung anzeigen',
      subActionViewUsage: 'Nutzung anzeigen',
      subActionEdit: 'Bearbeiten',
      subActionDelete: 'Löschen',
      // Subscription modal
      subModalCreateTitle: 'Neues Abonnement erstellen',
      subModalEditTitle: 'Abonnement bearbeiten',
      subModalInfoSection: 'Abonnement-Informationen',
      subModalNameLabel: 'Abonnementname *',
      subModalNamePlaceholder: 'z.B. xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
      subModalDescriptionLabel: 'Beschreibung (Optional)',
      subModalDescriptionPlaceholder: 'Beschreibung eingeben',
      subModalOrgLabel: 'Organisation *',
      subModalOrgPlaceholder: 'Organisation auswählen...',
      subModalNoOrgs: 'Keine Organisationen verfügbar',
      subModalUsageSection: 'Nutzungseinstellungen',
      subModalUsageHint: 'Steuern Sie das Nutzungsverhalten für dieses Abonnement.',
      subModalTransferableLabel: 'Ungenutztes Guthaben auf den nächsten Monat übertragen',
      subModalTransferableHint: 'Wenn aktiviert, wird ungenutztes Guthaben in den nächsten Abrechnungszeitraum übertragen.',
      subModalMaxPagesLabel: 'Maximale Seiten pro Sitzung',
      subModalAgentLimitsSection: 'Agenten-Limits',
      subModalAgentLimitsHint: 'Legen Sie für jeden KI-Agenten die beiden Nutzungseinstellungen fest. Leer lassen bedeutet unbegrenzt.',
      subModalAgentColumn: 'Agent',
      subModalMaxRunsColumn: 'Max Läufe',
      subModalMaxPagesColumn: 'Max Seiten',
      subModalStandardPagesColumn: 'Standarddatei-Seiten pro Monat',
      subModalUploadPagesColumn: 'Upload-Seiten pro Sitzung',
      subModalUnlimitedPlaceholder: 'Unbegrenzt',
      subModalNoAgents: 'Keine Agenten für diese Organisation gefunden.',
      subModalSaving: 'Speichern...',
      subModalSubmit: 'Speichern',
      subModalOcrSection: 'OCR-Seitenlimits',
      subModalOcrHint: 'Globale Limits für OCR-Verarbeitungen. Leer lassen für kein Limit.',
      subModalOcrDaily: 'Täglich (Seiten)',
      subModalOcrWeekly: 'Wöchentlich (Seiten)',
      subModalOcrMonthly: 'Monatlich (Seiten)',
      subModalOcrPlaceholder: 'Kein Limit',
      // Subscription delete
      subDeleteTitle: 'Abonnement löschen',
      subDeleteMessage: 'Möchten Sie das Abonnement "{name}" wirklich löschen? Alle zugehörigen Limits werden ebenfalls entfernt.',
      // Subscription toasts
      subToastCreated: 'Abonnement erfolgreich erstellt',
      subToastUpdated: 'Abonnement erfolgreich aktualisiert',
      subToastDeleted: 'Abonnement erfolgreich gelöscht',
      subToastSaveError: 'Fehler beim Speichern des Abonnements',
      subToastDeleteError: 'Fehler beim Löschen des Abonnements',
      // Credit usage view
      creditBackButton: 'Abonnements',
      creditTitle: 'Kreditnutzung',
      creditSubtitle: 'Aktueller Stand der Standarddatei-Seiten und Upload-Einstellung je Agent.',
      creditGrantButton: 'Temporäre Gutschriften vergeben',
      creditColumnAgent: 'Agent',
      creditColumnRunsUsed: 'Läufe verbraucht',
      creditColumnTempRuns: 'Temp. Läufe vergeben',
      creditColumnPagesUsed: 'Seiten verbraucht',
      creditColumnTempPages: 'Temp. Seiten vergeben',
      creditColumnStandardPages: 'Standarddatei-Seiten (diesen Monat)',
      creditUnlimitedLabel: '(unbegrenzt)',
      creditTaskFallback: 'Aufgabe {id}',
      creditBreakdown: '({monthly} monatl. + {temp} temp.)',
      creditEmptyTitle: 'Keine Agenten-Limits',
      creditEmptyHint: 'Diesem Abonnement sind keine Agenten-Limits zugewiesen.',
      // Grant credits modal
      grantTitle: 'Temporäre Gutschriften vergeben',
      grantSubscriptionLabel: 'Abonnement',
      grantAgentLabel: 'Agent *',
      grantAgentPlaceholder: 'Agent auswählen...',
      grantNoAgents: 'Keine Agenten verfügbar',
      grantRunsLabel: 'Temporäre Läufe',
      grantRunsHint: 'Zusätzliche Läufe für diesen Agenten vergeben.',
      grantPagesLabel: 'Temporäre Seiten',
      grantPagesHint: 'Zusätzliche Seiten für diesen Agenten vergeben.',
      grantNoteTitle: 'Hinweis',
      grantNoteBody: 'Temporäre Gutschriften ändern nicht die Abonnement-Limits und werden zuerst verbraucht, bevor die reguläre Zuweisung genutzt wird.',
      grantSubmit: 'Gutschrift vergeben',
      grantSaving: 'Vergeben...',
      grantToastSuccess: 'Temporäre Gutschrift erfolgreich vergeben',
      grantToastError: 'Fehler beim Vergeben der Gutschrift',
      // SharePoint tab
      spSearchPlaceholder: 'Suchen',
      spEditButton: 'SharePoint bearbeiten',
      spConnectSiteButton: 'Site verbinden',
      spConnectButton: 'SharePoint verbinden',
      spEmptyTitle: 'Keine verbundenen Sites',
      spEmptyHint: 'Erstellen Sie eine KI-Anfrage, um Dateien hochzuladen und Analysen durchzuführen.',
      spColumnSiteName: 'Site-Name',
      spColumnUrl: 'URL',
      spColumnStatus: 'Status',
      spColumnSyncInterval: 'Sync-Intervall',
      spColumnSite: 'Site',
      spColumnKnowledgeArea: 'Wissensbereich',
      spColumnUsedByAgents: 'Verwendet von Agenten',
      spAllKnowledgeAreas: 'Alle Wissensbereiche',
      spUsedByAgentsSingular: '{count} Agent',
      spUsedByAgentsPlural: '{count} Agenten',
      spNotUsed: 'Nicht verwendet',
      spCanBeDisconnected: 'Kann getrennt werden',
      spInfoBannerTitle: 'Das Verbinden einer Site gewährt nur Zugriff.',
      spInfoBannerBody: 'Es wird nichts automatisch gescannt, überwacht oder importiert — keine geplanten Läufe, keine Änderungsbenachrichtigungen, keine OCR, keine Embeddings. Inhalte werden nur importiert, wenn ein Agent-Administrator einen Import startet, und bleiben auf dem Stand dieses Imports, bis jemand einen neuen startet.',
      spStatusSynced: 'Synchronisiert',
      spStatusError: 'Fehler',
      spStatusPending: 'Ausstehend',
      spStatusConnected: 'Verbunden',
      spStatusReadOnlyAccess: 'Nur-Lese-Zugriff',
      spIntervalMinutes: '{count} Min.',
      spActionEditInterval: 'Sync-Intervall bearbeiten',
      spActionDelete: 'Löschen',
      spActionViewDetails: 'Site-Details anzeigen',
      spActionEditNameArea: 'Name und Wissensbereich bearbeiten',
      spActionCheckAccessNow: 'Zugriff jetzt prüfen',
      spActionCopySiteId: 'Site-ID kopieren',
      spActionDisconnect: 'Site trennen',
      spToastAccessConfirmed: 'Zugriff bestätigt — nur Lesezugriff, kein Inhalt gelesen',
      spToastAccessFailed: 'Zugriffsprüfung fehlgeschlagen: {error}',
      spToastSiteIdCopied: 'Site-ID kopiert',
      spToastDisconnectBlocked: 'Kann nicht getrennt werden — wird noch von folgenden Agenten verwendet: {agents}',
      spConnLoading: 'Laden...',
      spConnEditTitle: 'SharePoint bearbeiten',
      spConnCreateTitle: 'SharePoint verbinden',
      spConnTenantIdLabel: 'Tenant ID',
      spConnTenantIdPlaceholder: 'z.B. xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
      spConnClientIdLabel: 'Client ID',
      spConnClientIdPlaceholder: 'Azure App Registration Client ID eingeben',
      spConnClientSecretLabel: 'Client Secret',
      spConnClientSecretPlaceholderEdit: '••••••••',
      spConnClientSecretPlaceholderCreate: 'Azure App Client Secret eingeben',
      spConnSecurityNote: 'Diese Anmeldedaten werden sicher gespeichert und ausschließlich zur Verbindung mit Ihrer SharePoint-Site verwendet.',
      spConnDisconnectButton: 'SharePoint trennen',
      spConnSavingButton: 'Speichern...',
      spConnSaveButton: 'Verbindung speichern',
      spConnDisconnectConfirmTitle: 'SharePoint wirklich trennen?',
      spConnDisconnectConfirmMessage: 'Alle verbundenen Sites und deren Zuordnungen werden entfernt. Bereits importierte Dateien und Embeddings bleiben erhalten, aber es kann nichts mehr aus SharePoint synchronisiert werden, bis eine neue Verbindung hergestellt wird.',
      spConnDisconnectingButton: 'Wird getrennt...',
      spConnDisconnectConfirmButton: 'Ja, trennen',
      spErrorInvalidSecret: 'Der SharePoint Client Secret ist ungültig oder abgelaufen. Bitte aktualisieren Sie die Verbindungsdaten.',
      spErrorAppNotFound: 'Die Azure-App-Registrierung wurde für diesen Tenant nicht gefunden. Bitte überprüfen Sie Tenant ID und Client ID.',
      spErrorTenantNotFound: 'Die Tenant ID wurde nicht gefunden. Bitte überprüfen Sie sie und versuchen Sie es erneut.',
      spErrorAuthFailed: 'Die Anmeldung bei SharePoint ist fehlgeschlagen. Bitte überprüfen Sie die Verbindungsdaten.',
      spErrorPermissionDenied: 'Zugriff verweigert. Der Azure-App fehlen die erforderlichen SharePoint-Berechtigungen.',
      spErrorSiteNotFound: 'Die SharePoint-Site konnte nicht gefunden werden.',
      spErrorGeneric: 'Verbindung zu SharePoint fehlgeschlagen. Bitte überprüfen Sie die Verbindungseinstellungen.',
      spDetailsSubtitle: 'Gespeicherte Verbindungsdaten. Nichts hier wird im Hintergrund aktualisiert.',
      spDetailsSetInPlatform: 'In dieser Plattform festgelegt',
      spDetailsKnowledgeArea: 'Wissensbereich',
      spDetailsSharePointName: 'SharePoint-Name',
      spDetailsIdentifiesSite: 'Identifiziert die Site',
      spDetailsKeyBadge: 'SCHLÜSSEL',
      spDetailsAccessScope: 'Nur Lesezugriff · Sites.Selected',
      spDetailsConnectedBy: 'Verbunden von',
      spDetailsConnectedOn: 'Verbunden am',
      spDetailsAccessLastChecked: 'Zugriff zuletzt geprüft',
      spDetailsCheckedByFormat: '{datetime} von {name}',
      spNeverChecked: 'Nie geprüft',
      spDetailsCheckAccessNote: 'Zugriffsprüfungen bestätigen die Berechtigungsfreigabe und aktualisieren den SharePoint-Namen und die URL. Es wird kein Inhalt gelesen.',
      spEditSiteSaveError: 'Änderungen konnten nicht gespeichert werden',
      spAgentsModalViewTitle: 'Agenten, die diese Site verwenden',
      spAgentsModalViewSubtitle: 'Öffnen Sie einen dieser Agenten direkt.',
      spAgentsModalBlockedTitle: 'Site wird noch verwendet',
      spAgentsModalBlockedSubtitle: 'Entfernen Sie diese Site zuerst aus den untenstehenden Agenten, bevor Sie sie trennen.',
      spAgentsModalWarningSingular: '{siteName} ist {count} Agent zugewiesen. Ein Trennen jetzt würde dessen importierte Inhalte beschädigen.',
      spAgentsModalWarningPlural: '{siteName} ist {count} Agenten zugewiesen. Ein Trennen jetzt würde deren importierte Inhalte beschädigen.',
      spAgentsModalFileCountSingular: '{count} Datei',
      spAgentsModalFileCountPlural: '{count} Dateien',
      spAgentsModalImportedOn: 'importiert am {date}',
      spAgentsModalOpenAgent: 'Agent öffnen',
      spAgentsModalDeleteNote: 'Das Löschen eines Agenten gibt dessen Site-Zuweisungen automatisch frei.',
      spAgentsModalGoToFirst: 'Zum ersten Agenten',
      // Connect site modal
      connectSiteTitle: 'Site verbinden',
      connectSiteUrlPlaceholder: '',
      connectSiteInvalidIdFormat: 'Ungültiges Site-ID-Format. Erwartet: hostname.sharepoint.com,siteCollectionGuid,webGuid',
      connectSiteAdd: 'Hinzufügen',
      connectSiteSaveCredentials: 'Anmeldedaten speichern',
      connectSiteSubtitle: 'Es können nur Sites verbunden werden, für die Ihr Microsoft-365-Administrator Lesezugriff gewährt hat.',
      connectSiteStep1: 'Site-ID eingeben',
      connectSiteStep2: 'Bestätigen und benennen',
      connectSiteIdLabel: 'Site-ID',
      connectSiteIdHint: 'Jede Site benötigt ihre eigene ID und ihre eigene Zugriffsfreigabe — auch Sites, die sich unter einer Hub-Site befinden.',
      connectSiteHelpTitle: 'Wo finde ich die Site-ID?',
      connectSiteHelpStep1: 'Öffnen Sie die SharePoint-Site in Ihrem Browser.',
      connectSiteHelpStep2: 'Fügen Sie /_api/site/id an die Site-Adresse an und drücken Sie Enter, z. B. https://contoso.sharepoint.com/sites/Finance/_api/site/id.',
      connectSiteHelpStep3: 'Kopieren Sie die GUID aus der Antwort und fügen Sie sie, durch ein Komma getrennt, hinter den Hostnamen ein.',
      connectSiteCheckAccess: 'Zugriff prüfen',
      connectSiteUrlColumn: 'URL',
      connectSiteAccessColumn: 'Zugriff',
      connectSiteAccessConfirmed: 'Nur Lesezugriff · bestätigt',
      connectSiteDisplayNameLabel: 'Anzeigename',
      connectSiteDisplayNameHint: 'Wird Agent-Administratoren und im Chat angezeigt.',
      connectSiteKnowledgeAreaLabel: 'Wissensbereich (optional)',
      connectSiteKnowledgeAreaSelectPrompt: 'Wissensbereich auswählen',
      connectSiteKnowledgeAreaEmpty: 'Noch keine Wissensbereiche — unten erstellen.',
      connectSiteKnowledgeAreaHint: 'Gruppiert verwandte Sites in dieser Liste und in den Agent-Einstellungen. Hat keinen Einfluss auf Zugriff oder Importe.',
      knowledgeAreaCreateNew: 'Neuen Wissensbereich erstellen',
      knowledgeAreaManageButton: 'Wissensbereiche verwalten',
      knowledgeAreaManagerTitle: 'Wissensbereiche',
      knowledgeAreaManagerSubtitle: 'Wissensbereiche organisieren SharePoint-Sites und sind unabhängig von User Groups — jeder Agent kann jeden Wissensbereich sehen.',
      knowledgeAreaNameLabel: 'Name des Wissensbereichs',
      knowledgeAreaNamePlaceholder: 'z. B. IT, Finanzen, Personalwesen',
      knowledgeAreaEnterHint: 'Drücken Sie Enter oder klicken Sie auf „Erstellen", um hinzuzufügen.',
      knowledgeAreaCreateButton: 'Erstellen',
      knowledgeAreaEmptyError: 'Bitte geben Sie einen Namen ein',
      knowledgeAreaExistsError: 'Dieser Wissensbereich existiert bereits',
      knowledgeAreaCreateError: 'Wissensbereich konnte nicht erstellt werden',
      connectSiteConsentLabel: 'Mir ist bewusst, dass nach dem Import dieser Site in einen Agenten jede Person mit Zugriff auf diesen Agenten jede importierte Datei abrufen kann — auch Dateien, die in SharePoint nur für bestimmte Personen freigegeben sind.',
      connectSiteSubmit: 'Site verbinden',
      connectSiteDuplicateSite: 'Diese Site ist bereits verbunden: {name}',
      // Sync interval modal
      syncIntervalTitle: 'Sync-Intervall',
      syncIntervalLabel: 'Intervall (Minuten)',
      syncIntervalHint: 'Empfohlen: 15 Minuten für normale Sites, 5 Minuten für häufig aktualisierte Sites',
      syncIntervalSaving: 'Speichern...',
      syncIntervalSubmit: 'Speichern',
      // Delete site modal
      deleteSiteTitle: 'Site trennen',
      deleteSiteMessage: 'Sind Sie sicher, dass Sie "{name}" trennen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteSiteDeleting: 'Wird getrennt...',
    },

    users: {
      // Page toolbar
      pageSearchPlaceholder: 'Suchen',
      pageFilterButton: 'Filter',
      // Table column headers
      columnFullName: 'Vollständiger Name',
      columnEmail: 'E-Mail',
      columnOrganization: 'Organisation',
      columnUserGroup: 'Benutzergruppe',
      columnCreatedAt: 'Erstellungsdatum',
      columnRole: 'Rolle',
      columnAuthProvider: 'Anbieter',
      authProviderEmail: 'E-Mail',
      authProviderMicrosoft: 'Microsoft',
      authProviderLocal: 'Lokal',
      // Role badges
      roleSuperAdmin: 'Super Admin',
      roleAdmin: 'Admin',
      roleNormal: 'Normal',
      roleNoLicense: 'Keine Lizenz',
      // Row action menu
      actionAssignRole: 'Rolle zuweisen',
      actionAssignOrganization: 'Orga + Benutzergruppe zuweisen',
      actionAssignDepartment: 'Benutzergruppe zuweisen',
      actionCheckActivity: 'Aktivität prüfen',
      actionResendInvite: 'Einladung erneut senden',
      actionDeactivateUser: 'Benutzer deaktivieren',
      actionActivateUser: 'Benutzer aktivieren',
      statusInactive: 'Inaktiv',
      deactivateConfirmTitle: 'Benutzer deaktivieren',
      deactivateConfirmMessage: 'Möchten Sie {name} wirklich deaktivieren?',
      activateConfirmTitle: 'Benutzer aktivieren',
      activateConfirmMessage: 'Möchten Sie {name} wirklich aktivieren?',
      toastUserDeactivated: 'Benutzer wurde deaktiviert',
      toastUserActivated: 'Benutzer wurde aktiviert',
      toastStatusChangeError: 'Status konnte nicht geändert werden',
      activityModalTitle: 'Anmeldeaktivität',
      activityColMethod: 'Methode',
      activityColStatus: 'Status',
      activityColIp: 'IP-Adresse',
      activityColTime: 'Zeitpunkt',
      activityStatusSuccess: 'Erfolgreich',
      activityStatusFailed: 'Fehlgeschlagen',
      activityEmpty: 'Keine Anmeldeversuche gefunden.',
      activityError: 'Aktivität konnte nicht geladen werden.',
      activityMethodPassword: 'Passwort',
      activityMethodOtp: 'E-Mail-Code',
      activityMethodMicrosoft: 'Microsoft',
      activityMethodMfa: 'Zwei-Faktor-Code',
      actionResetMfa: "2FA zurücksetzen",
      activityMethodMfaRecovery: "Wiederherstellungscode",
      activityMethodMfaReset: "2FA zurückgesetzt",
      activityMethodMfaSetup: "2FA eingerichtet",
      activityPerformedBy: "durch {email}",
      resetMfaConfirmMessage: "Die Zwei-Faktor-Authentifizierung von {name} wird entfernt. Beim nächsten Login muss die Authenticator-App neu eingerichtet werden.",
      resetMfaConfirmTitle: "Zwei-Faktor-Authentifizierung zurücksetzen",
      toastMfaReset: "Zwei-Faktor-Authentifizierung wurde zurückgesetzt",
      // Filter modal
      filterModalTitle: 'Filter',
      filterUserGroupLabel: 'Benutzergruppe',
      filterUserGroupPlaceholder: 'Benutzergruppen auswählen',
      filterOrganizationLabel: 'Organisation',
      filterOrganizationPlaceholder: 'Organisationen auswählen',
      filterRoleLabel: 'Rolle',
      filterSortByLabel: 'Sortieren nach',
      filterSortNewest: 'Neueste zuerst',
      filterSortOldest: 'Älteste zuerst',
      filterSortNameAsc: 'Name (A–Z)',
      filterSortNameDesc: 'Name (Z–A)',
      filterClearAll: 'Alle zurücksetzen',
      filterApply: 'Anwenden',
      filterNoOptions: 'Keine Optionen verfügbar',
      // Status
      errorPrefix: 'Fehler: {error}',
      // Role modal
      roleModalTitle: 'Rolle zuweisen',
      roleModalNameLabel: 'Name:',
      roleModalCurrentRoleLabel: 'Aktuelle Rolle:',
      roleModalSelectLabel: 'Neue Rolle auswählen',
      roleModalSelectPlaceholder: 'Neue Rolle auswählen',
      roleModalNoOtherRoles: 'Keine anderen Rollen verfügbar',
      roleModalSaving: 'Wird gespeichert...',
      // Organization modal
      orgModalTitle: 'Organisation zuweisen',
      orgModalSelectLabel: 'Organisation auswählen',
      orgModalNoOrganization: 'Keine Organisation',
      orgModalAssignUserGroupLabel: 'Benutzergruppe zuweisen',
      orgModalAssignUserGroupPlaceholder: 'Benutzergruppe zuweisen',
      orgModalSaving: 'Wird gespeichert...',
      userGroupMoreLabel: '+{count} weitere',
      // Toasts
      toastRoleUpdated: 'Rolle erfolgreich aktualisiert',
      toastRoleUpdateError: 'Fehler beim Aktualisieren der Rolle',
      toastOrgAssigned: 'Organisation erfolgreich zugewiesen',
      toastOrgAssignError: 'Fehler beim Zuweisen der Organisation',
      // Invite modal
      inviteButtonLabel: 'Benutzer einladen',
      inviteModalTitle: 'Benutzer einladen',
      inviteModalSubtitle: 'Der Benutzer erhält eine Einladungs-E-Mail und kann sich danach registrieren.',
      inviteModalEmailLabel: 'E-Mail-Adresse',
      inviteModalRoleLabel: 'Rolle',
      inviteModalRolePlaceholder: 'Rolle auswählen',
      inviteModalSendButton: 'Einladung senden',
      inviteModalSending: 'Wird gesendet...',
      inviteModalSuccessToast: 'Einladung erfolgreich gesendet',
      inviteModalErrorToast: 'Einladung konnte nicht gesendet werden',
      resendInviteSuccessToast: 'Einladung erneut gesendet',
      resendInviteErrorToast: 'Einladung konnte nicht erneut gesendet werden',
      inviteModalFieldRequired: 'Bitte E-Mail-Adresse und Rolle angeben',
      inviteEmailAlreadyExists: 'Dieser Benutzer existiert bereits im System',
      addUserMenuLabel: 'Benutzer hinzufügen',
      inviteUserMenuLabel: 'Benutzer einladen',
      newUserDefaultsAllGroups: 'Alle Gruppen',
      newUserDefaultsTitle: 'Standardwerte für neue Benutzer',
      newUserDefaultsSubtitle: 'Organisation, Rolle und Gruppen, die bei der ersten Microsoft-Anmeldung vergeben werden.',
      newUserDefaultsButton: 'Standardwerte',
      addUserModalTitle: 'Neuen Benutzer hinzufügen',
      addUserModalSubtitle: 'Benutzer wird direkt erstellt und der Organisation zugewiesen.',
      addUserFullnameLabel: 'Vollständiger Name',
      addUserOrganizationLabel: 'Organisation',
      addUserGenderLabel: 'Geschlecht',
      addUserGenderPlaceholder: 'Bitte auswählen',
      addUserPasswordLabel: 'Passwort',
      addUserRoleLabel: 'Rolle',
      addUserRolePlaceholder: 'Rolle auswählen',
      addUserSubmitButton: 'Benutzer erstellen',
      addUserSubmitting: 'Wird erstellt...',
      addUserSuccessToast: 'Benutzer erfolgreich erstellt',
      addUserErrorToast: 'Benutzer konnte nicht erstellt werden',
      addUserFieldRequired: 'Bitte alle Felder ausfüllen',
      addUserNoOrgError: 'Ihrem Konto ist keine Organisation zugewiesen. Bitte bitten Sie einen Super-Administrator, zuerst Ihre Organisation festzulegen.',
      addUserSelectOrgPlaceholder: 'Organisation auswählen',
      addUserSelectOrgRequired: 'Bitte eine Organisation auswählen',
      changePasswordTitle: 'Passwort festlegen',
      changePasswordSubtitle: 'Legen Sie ein neues Passwort für Ihr Konto fest.',
      changePasswordButton: 'Passwort speichern',
      statusPending: 'Eingeladen',
      statusExpired: 'Abgelaufen',
    },

    language: {
      pageTitle: 'Spracheinstellungen',
      pageSubtitle: 'Wählen Sie Ihre bevorzugte Sprache für die Benutzeroberfläche',
      unsavedChanges: 'Ungespeicherte Änderungen',
      saveButton: 'Spracheinstellung speichern',
      cancelButton: 'Abbrechen',
      searchPlaceholder: 'Suchen',
      currentBadge: 'Aktuell',
      defaultBadge: 'Standard',
    },

    pagination: {
      pageOf: 'Seite {current} von {total}',
    },

    toast: {
      commentSaved: 'Kommentar gespeichert',
      fileUploaded: 'Dateien erfolgreich hochgeladen',
      fileUpdated: 'Dateien erfolgreich aktualisiert',
      projectCreated: 'KI-Anfrage erfolgreich erstellt!',
      projectFailed: 'KI-Anfrage konnte nicht erstellt werden',
      chooseAgent: 'Bitte wählen Sie einen Agenten und geben Sie einen KI-Anfrage-Namen ein',
    },

    deleteModal: {
      singleTitle: 'KI-Anfrage löschen',
      bulkTitle: '{count} KI-Anfragen löschen',
      singleDescription: 'Sind Sie sicher, dass Sie diese KI-Anfrage löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      bulkDescription: 'Sind Sie sicher, dass Sie {count} ausgewählte KI-Anfragen löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleting: 'Wird gelöscht...',
      defaultTitle: 'Löschen bestätigen',
      defaultMessage: 'Sind Sie sicher, dass Sie dieses Element löschen möchten?',
      cancelButton: 'Abbrechen',
      deleteButton: 'Löschen',
    },

    agentOrder: {
      hint: 'Ziehen Sie die Agenten per Drag & Drop oder geben Sie eine Positionsnummer ein.',
      unsavedChanges: 'Ungespeicherte Änderungen',
      cancel: 'Abbrechen',
      save: 'Speichern',
      colPosition: 'Position',
      colAgent: 'Agent',
      colPlacement: 'Platz auf der Startseite',
      posTopLeft: 'Oben links',
      posTopRight: 'Oben rechts',
      posSecondLeft: 'Zweite Reihe links',
      posSecondRight: 'Zweite Reihe rechts',
      posBehindShowAll: 'Unter „Alle anzeigen“',
      noDepartments: 'Diese Organisation hat noch keine Benutzergruppen.',
      noAgents: 'Dieser Benutzergruppe sind keine Agenten zugeordnet.',
      toastSaved: 'Reihenfolge wurde gespeichert',
      toastError: 'Reihenfolge konnte nicht gespeichert werden',
      loadError: 'Daten konnten nicht geladen werden',
      retry: 'Erneut versuchen',
      discardConfirm: 'Es gibt ungespeicherte Änderungen an der Reihenfolge. Möchten Sie diese verwerfen?',
      toastNoPermission: 'Sie haben keine Berechtigung, die Reihenfolge dieser Agenten zu ändern.',
    },
    usageHistory: {
      title: 'Nutzungs-Historie',
      subtitle: 'Monatliche Nutzung pro Agent — nur für Super-Admins sichtbar',
      cardRunsLabel: 'Läufe',
      cardRunsSub: 'von {max} Läufen in {year}',
      cardQuestionsLabel: 'Fragen',
      cardTokensLabel: 'Tokens',
      cardTokensSub: 'Ein- und Ausgabe zusammen',
      cardUserPagesLabel: 'Nutzer-hochgeladene Seiten',
      cardStandardPagesLabel: 'Standarddatei-Seiten',
      cardPagesLabel: 'Verbrauchte Seiten',
      cardPagesSub: 'von {max} Seiten in {year}',
      cardCreditsLabel: 'Temp. Gutschriften',
      cardCreditsSub: 'zusätzlich vergeben',
      orgPlaceholder: 'Organisation auswählen...',
      allMonths: 'Alle Monate',
      refresh: 'Aktualisieren',
      colMonth: 'Monat',
      colAgent: 'Agent',
      colRuns: 'Läufe',
      colQuestions: 'Fragen',
      colTokens: 'Tokens',
      colUserPages: 'Nutzer-Seiten',
      colStandardPages: 'Standarddatei-Seiten',
      unlimitedSuffix: '(unbegrenzt)',
      colRunsUsed: 'Verbrauchte Läufe',
      colPagesUsed: 'Verbrauchte Seiten',
      colTempCredits: 'Temp. Credits',
      colTempPages: 'Temp. Seiten',
      colAction: 'Aktion',
      entry: 'Eintrag',
      entries: 'Einträge',
      remaining: 'übrig',
      actionCarryOver: 'Übertrag',
      actionReset: 'Zurückgesetzt',
      emptyOrgTitle: 'Organisation auswählen',
      emptyOrgSubtitle: 'Wählen Sie eine Organisation, um die monatliche Nutzungshistorie anzuzeigen.',
      emptyDataTitle: 'Keine Daten für',
      emptyDataSubtitle: 'Für diesen Zeitraum wurden keine Nutzungsdaten aufgezeichnet.',
      loadError: 'Fehler beim Laden der Nutzungshistorie',
      months: 'Januar,Februar,März,April,Mai,Juni,Juli,August,September,Oktober,November,Dezember',
      unknownAgent: 'Unbekannter Agent',
      liveTitle: 'Aktuelle Nutzung',
      liveBadge: 'Live',
      liveSubtitle: 'Echtzeit · Laufender Monat',
      liveUpdated: 'Aktualisiert',
      deletedBadge: 'Gelöscht',
    },

    notifications: {
      pageTitle: 'Benachrichtigungen',
      subtitle: 'Alle Aktivitäten über Ihre KI-Anfragen, Dokumente, Agenten und Integrationen.',
      markAllRead: 'Alle als gelesen markieren',
      tabAll: 'Alle',
      tabUnread: 'Ungelesen',
      tabSystem: 'System',
      empty: 'Keine Benachrichtigungen',
      emptyHint: 'Sie sind auf dem neuesten Stand.',
      groupToday: 'HEUTE',
      groupYesterday: 'GESTERN',
      groupEarlier: 'FRÜHER',
      // Action labels
      viewDocument: 'Dokument anzeigen',
      viewSyncReport: 'Sync-Bericht anzeigen',
      viewChanges: 'Änderungen anzeigen',
      viewFile: 'Datei anzeigen',
      openProject: 'KI-Anfrage öffnen',
      viewSite: 'Site anzeigen',
      viewNews: 'Neuigkeiten ansehen',
      fixIssue: 'Problem beheben',
      reviewActivity: 'Aktivität prüfen',
      // Time phrases
      justNow: 'Gerade eben',
      minuteAgo: 'vor 1 Minute',
      minutesAgo: 'vor {m} Minuten',
      hourAgo: 'vor 1 Stunde',
      hoursAgo: 'vor {h} Stunden',
      yesterdayAt: 'Gestern, {time}',
      // Sample titles & descriptions
      t1Title: 'Dokumentenanalyse abgeschlossen',
      t1Desc: 'KI hat die Analyse von Service_Contract.pdf für City Center Agreement Review abgeschlossen.',
      t2Title: 'SharePoint-Sync abgeschlossen',
      t2Desc: '143 Dateien erfolgreich synchronisiert aus Contracts / Legal Agreements Ordner.',
      t3Title: 'Agenteneinstellungen aktualisiert',
      t3Desc: 'Änderungen am Contract Review Agent vorgenommen — Temperatur und Prompt aktualisiert.',
      t4Title: 'Neue KI-Anfrage erstellt',
      t4Desc: 'KI-Anfrage "Downtown Plaza" wurde erstellt und der Contracts Benutzergruppe zugewiesen.',
      t5Title: 'Dokument hochgeladen',
      t5Desc: 'Lease_Agreement_2024.docx wurde in die Standard-Dateien Bibliothek hochgeladen.',
      t6Title: 'Analyse abgeschlossen',
      t6Desc: 'Vollständige Analyse des KI-Anfrages "Skyline Tower" abgeschlossen — 98% Konfidenzscore.',
      t7Title: 'SharePoint-Site verbunden',
      t7Desc: 'Contoso Legal Hub wurde erfolgreich als Datenquelle verbunden.',
      t8Title: 'Dokument verarbeitet',
      t8Desc: 'NDA_Template_v3.pdf wurde verarbeitet und ist bereit für die KI-Analyse.',
      t9Title: 'Plattform-Update — v2.4.0',
      t9Desc: 'Neue Funktionen: verbesserte SharePoint-Sync-Geschwindigkeit, erweiterte Zitations-Badges und KI-Profil-Builder.',
      t10Title: 'Neuer Agent erstellt',
      t10Desc: '"Compliance Checker" Agent wurde von Olivia Rhye im Contracts Workspace erstellt.',
      t11Title: 'Sync-Fehler erkannt',
      t11Desc: 'Synchronisierung von 3 Dateien aus dem HR-Dokumentenordner fehlgeschlagen. Zugriff verweigert.',
      t12Title: 'Sicherheitshinweis',
      t12Desc: 'Eine neue Anmeldung von einem unbekannten Gerät wurde erkannt. Bitte überprüfen Sie Ihr Konto.',
      t13Title: 'Geplante Wartung',
      t13Desc: 'Die Plattform wird am 10. März 2026 von 02:00–04:00 UTC gewartet. Dienste können kurzzeitig nicht verfügbar sein.',
    },

    dataAnalysis: {
      hint: "Als Datenanalyse",
      activeHint: "Datenanalyse ist für diese Anfrage aktiv",
      preview: "Vorschau",
      closePreview: "Vorschau schließen",
      previewLoading: "Vorschau wird geladen…",
      previewError: "Die Vorschau konnte nicht geladen werden. Sie können die Datei herunterladen.",
      skippedLabel: "Nicht verwendet",
      skipReasonMissing: "Datei nicht gefunden",
      skipReasonUnreadable: "konnte nicht gelesen werden",
      skipReasonTooLarge: "zu groß",
      skipReasonStandardFile: "Standarddatei, nicht unterstützt",
      modeName: "Datenanalyse",
      tooltip: "Dateien mit erweiterten Berechnungen und Datenwerkzeugen analysieren",
      composerMeta: "Bleibt aktiv, bis Sie es ausschalten · ausgewählte Dateien begrenzen den Kontext",
      usingSessionFiles: "Verwendet passende Dateien aus diesem Chat",
      usingSelectedFiles: "Verwendet nur ausgewählte Dateien",
      selectedForRequest: "Für diese Anfrage ausgewählt",
      sessionForRequest: "Passende Dateien aus diesem Chat",
      noFilesForRequest: "Keine Dateien – nur Berechnungen",
      workingTitle: "Daten werden analysiert…",
      completedTitle: "Datenanalyse abgeschlossen",
      statusAnalyzing: "Analysiert",
      statusReady: "Fertig",
      statusFailed: "Fehlgeschlagen",
      stepPreparing: "Dateien werden vorbereitet…",
      stepInspecting: "Datenstruktur wird geprüft…",
      stepCalculating: "Berechnungen werden ausgeführt…",
      stepResults: "Ergebnisse werden aufbereitet…",
      stepRetrying: "Erneuter Versuch…",
      progressCompleted: "Analyse abgeschlossen",
      completedHelper: "Das Ergebnis erscheint als normale Chat-Antwort; erstellte Dateien werden unter Generierte Dateien abgelegt.",
      failedTitle: "Die Datenanalyse ist fehlgeschlagen",
      failedHelper: "Bitte versuchen Sie es erneut.",
      contentFilterHelper: "Die Antwort wurde vom Inhaltsfilter blockiert. Bitte formulieren Sie die Anfrage um oder wählen Sie andere Dateien.",
      filesUnavailableHelper: "Die Dateien für diese Analyse konnten nicht gelesen werden. Bitte laden Sie sie erneut hoch.",
      tokenLimitHelper: "Die Antwort hat das Token-Limit des Agenten erreicht. Bitte erhöhen Sie das Limit in den Agenten-Einstellungen.",
      retry: "Erneut versuchen",
      basedOnSelected: "Analyse basiert auf den ausgewählten Dateien",
      basedOnSession: "Analyse basiert auf den Dateien dieses Chats",
      basedOnNone: "Analyse ohne Dateien",
      filesUsedLabel: "Verwendete Dateien",
      generatedFilesLabel: "Erstellte Dateien",
      download: "Herunterladen",
      downloadError: "Die Datei konnte nicht heruntergeladen werden.",
      alreadyRunningError: "In diesem Chat läuft bereits eine Datenanalyse. Bitte warten Sie, bis sie abgeschlossen ist.",
      notEnabledError: "Die Datenanalyse ist für diesen Agenten deaktiviert.",
      modeSaveError: "Der Datenanalyse-Schalter konnte nicht gespeichert werden.",
      startFailedError: "Die Datenanalyse konnte nicht gestartet werden.",
    },
    chatZoom: {
      open: 'Zoom-Steuerung öffnen',
      close: 'Zoom-Steuerung schließen',
      zoomIn: 'Vergrößern',
      zoomOut: 'Verkleinern',
      reset: 'Auf 100 % zurücksetzen',
    },
    share: {
      button: 'Teilen',
      shareProject: 'KI-Anfrage teilen',
      modalTitle: 'KI-Anfrage teilen',
      modalSubtitle: 'Personen hinzufügen oder Link kopieren',
      modalDescription: 'Teilen Sie diese KI-Anfrage im Nur-Lese-Modus. Jeder mit diesem Link kann KI-Anfrage-Details, Dateien, KI-Antworten und Quellen einsehen, aber keine Änderungen vornehmen.',
      notifOnly: 'Nur In-App-Benachrichtigung · Kein E-Mail-Versand',
      peopleLabel: 'Personen',
      permissionLabel: 'Berechtigung',
      searchPlaceholder: 'Name oder E-Mail suchen...',
      noResults: 'Keine Benutzer gefunden',
      addedLabel: 'Hinzugefügt',
      shareButton: 'Link erstellen & kopieren',
      shareAndNotify: 'Teilen & benachrichtigen',
      cancel: 'Schließen',
      copyLink: 'Link kopieren',
      linkCopied: 'Kopiert!',
      shareLinkLabel: 'Freigabelink',
      shareLinkPlaceholder: 'Link wird nach dem Teilen angezeigt...',
      linkNote: 'Jeder mit diesem Link kann das KI-Anfrage nur ansehen. Bearbeiten, Dateien hochladen, Kommentare hinzufügen oder KI-Fragen stellen ist nicht möglich.',
      shareSuccess: 'KI-Anfrage erfolgreich geteilt',
      removeFailed: 'Empfänger konnte nicht entfernt werden',
      readOnlyBadge: 'Nur lesen',
      commentsTitle: 'Kommentare',
      commentsEmpty: 'Noch keine Kommentare.',
      commentsLoading: 'Kommentare werden geladen …',
      commentsPlaceholder: 'Kommentar schreiben …',
      commentsAdd: 'Kommentieren',
      commentsSave: 'Speichern',
      commentsCancel: 'Abbrechen',
      commentsEdit: 'Bearbeiten',
      commentsDelete: 'Löschen',
      commentsDeleteConfirm: 'Diesen Kommentar löschen?',
      commentsEdited: 'bearbeitet',
      commentsUnknownAuthor: 'Unbekannter Benutzer',
      commentsLoadError: 'Kommentare konnten nicht geladen werden.',
      commentsSaveError: 'Kommentar konnte nicht gespeichert werden.',
      sharedBy: 'Geteilt von',
      readOnlyHint: 'Diese Ansicht ist schreibgeschützt – Fragen können nicht gestellt werden.',
      openProject: 'KI-Anfrage anzeigen',
      loadingError: 'Freigegebenes KI-Anfrage konnte nicht geladen werden.',
      notFound: 'Dieser Freigabelink ist ungültig oder abgelaufen.',
      accessRevoked: 'Ihr Zugriff auf diese KI-Anfrage wurde entfernt.',
      currentRecipients: 'Aktuell geteilt mit',
      noRecipients: 'Noch nicht geteilt',
      revokeAccess: 'Zugriff entziehen',
      revokeShare: 'Freigabe beenden',
      revokeConfirm: 'Möchten Sie die Freigabe für diese KI-Anfrage wirklich beenden? Alle Empfänger verlieren den Zugriff.',
      revokeSuccess: 'Freigabe erfolgreich beendet',
      inviteDescription: 'Laden Sie Personen ein, indem Sie deren Name oder E-Mail eingeben.',
      invitePeople: 'Personen einladen',
      sendInvite: 'Einladung senden',
      whoCanView: 'Wer kann diese KI-Anfrage ansehen',
      viewAll: 'Alle anzeigen',
      allViewers: 'Alle Betrachter',
      allViewersDesc: 'Personen, die derzeit Zugriff auf diese KI-Anfrage haben.',
      removeAccess: 'Zugriff entfernen',
      youLabel: 'Sie',
      nPeople: '{count} Personen',
      viewPermission: 'Nur ansehen',
      viewButton: 'Ansehen',
      alreadyShared: 'Bereits geteilt',
      done: 'Fertig',
    },

    agents: {
      pageTitle: 'Agenten',
      createAgent: 'Agent erstellen',
      selectAll: 'Alle auswählen',
      loading: 'Agenten werden geladen...',
      noneFound: 'Keine Agenten gefunden',
      noneYet: 'Noch keine Agenten',
      noneFoundHint: 'Keine Agenten gefunden, die Ihrer Suche entsprechen.',
      noneYetHint: 'Erstellen Sie einen Agenten, um mit der Analyse von Dateien und Daten zu beginnen.',
      duplicate: 'Agent duplizieren',
      agentNameLabel: 'Agentenname',
      userGroupLabel: 'Benutzergruppe',
      organizationLabel: 'Organisation',
      lastUpdatedLabel: 'Zuletzt aktualisiert',
      createModalTitle: 'Neuen Agenten erstellen',
      createModalImageLabel: 'Agentenbild',
      createModalUploadImage: 'Bild hochladen',
      createModalChangeImage: 'Neues Bild hochladen',
      createModalRemoveImage: 'Löschen',
      createModalNoFileSelected: 'Keine Datei ausgewählt',
      createModalSelectOrg: 'Organisation auswählen',
      createModalSelectDept: 'Benutzergruppe auswählen',
      createModalDescLabel: 'Beschreibung',
      createModalDescPlaceholder: 'Beschreiben Sie den Hauptnutzen dieses Agenten...',
      createModalDescHelp: 'Ein oder zwei Sätze, die erklären, wie dieser Agent den Benutzern hilft.',
      createModalCancel: 'Abbrechen',
      createModalSubmit: 'Agent hinzufügen',
      createModalSubmitting: 'Wird erstellt...',
      createModalNameRequired: 'Bitte geben Sie einen Agentennamen ein',
    },

    filterModal: {
      title: 'Filter',
      agentsLabel: 'Agenten',
      agentsPlaceholder: 'Agenten auswählen',
      creationDateLabel: 'Erstellungsdatum',
      today: 'Heute',
      last7days: 'Letzte 7 Tage',
      last30days: 'Letzte 30 Tage',
      monthPlaceholder: 'Monat auswählen',
      progressLabel: 'Fortschritt der Q&A',
      low: 'Niedrig',
      medium: 'Mittel',
      high: 'Hoch',
      sortByLabel: 'Sortieren nach',
      newestFirst: 'Neueste zuerst',
      oldestFirst: 'Älteste zuerst',
      mostProgress: 'Größter Fortschritt',
      leastProgress: 'Geringster Fortschritt',
      resetAll: 'Alle zurücksetzen',
      apply: 'Anwenden',
      organizationLabel: 'Organisation',
      organizationPlaceholder: 'Organisation auswählen',
      userGroupLabel: 'Benutzergruppe',
      userGroupPlaceholder: 'Benutzergruppe auswählen',
      lastUpdateLabel: 'Letzte Aktualisierung',
      clearAll: 'Alles löschen',
    },

    months: {
      january: 'Januar',
      february: 'Februar',
      march: 'März',
      april: 'April',
      may: 'Mai',
      june: 'Juni',
      july: 'Juli',
      august: 'August',
      september: 'September',
      october: 'Oktober',
      november: 'November',
      december: 'Dezember',
    },

    createProjectModal: {
      title: 'Neue KI-Anfrage erstellen',
      agentLabel: 'Agent',
      agentPlaceholder: 'Agent auswählen',
      noAgentsAvailable: 'Keine Agenten verfügbar',
      projectImageLabel: 'KI-Anfrage-Bild',
      uploadImage: 'Bild hochladen',
      uploadNewImage: 'Neues Bild hochladen',
      noFileSelected: 'Keine Datei ausgewählt',
      projectNameLabel: 'KI-Anfrage-Name',
      projectNamePlaceholder: 'KI-Anfrage-Name',
      uploadMethodLabel: 'Upload-Methode',
      uploadFromDevice: 'Von meinem Gerät hochladen',
      uploadFromDeviceHint: 'Dateien direkt von Ihrem Computer hinzufügen',
      connectSharePoint: 'SharePoint-Ordner verbinden',
      connectSharePointHint: 'Dateien aus Ihrem KI-Anfrage-Arbeitsbereich verwenden',
      analyze: 'Analysieren',
      creating: 'Wird erstellt...',
    },

    helpCenter: {
      title: 'Help Center',
      tabFaqs: "FAQ's",
      tabSupport: 'Support kontaktieren',
      sidebarFaqs: 'FAQs',
      sidebarSupport: 'Support',
      sidebarFooterCopyright: '© 2026 AI Spaces GmbH',

      supportInfoTitle: 'Support-Informationen',
      supportInfoSubtitle: 'Stellen Sie Informationen bereit, die Nutzer im Hilfecenter sehen, wenn sie Unterstützung benötigen.',
      supportMessageLabel: 'Support-Nachricht',
      supportMessageDefault: 'Hier finden Sie Informationen zu unserem Support. Unser Team hilft Ihnen gerne weiter.',
      supportRequestLinkLabel: 'Link für Support-Anfrage',
      supportNoLinkConfigured: 'Kein Link konfiguriert',
      supportHoursLabel: 'Support-Zeiten',
      supportHoursDaysDefault: 'Montag - Freitag',
      supportHoursTimeDefault: '07:00 - 17:00',
      supportErrorPrefix: 'Fehler: {message}',
      supportMessagePlaceholder: 'Schreiben Sie eine kurze Nachricht, in der Sie erklären, wie Nutzer Hilfe von Ihrem Support-Team erhalten können.',
      supportCharactersRemaining: '{count} Zeichen übrig',
      supportLinkPlaceholder: 'Fügen Sie den Link zum Support-Formular oder zur Feedback-Anfrage ein',
      supportDaysLabel: 'Tage',
      supportTimeLabel: 'Uhrzeit',
      supportDaysPlaceholder: 'z. B. Montag - Freitag',
      supportTimePlaceholder: 'z. B. 07:00 - 17:00',
      supportSaving: 'Speichern...',
      // Per-day hours (toggle-based UI)
      supportDayMonday: 'Montag',
      supportDayTuesday: 'Dienstag',
      supportDayWednesday: 'Mittwoch',
      supportDayThursday: 'Donnerstag',
      supportDayFriday: 'Freitag',
      supportDaySaturday: 'Samstag',
      supportDaySunday: 'Sonntag',
      supportClosed: 'Geschlossen',
      supportOpenLabel: 'Öffnung',
      supportCloseLabel: 'Schließung',
      supportAddHours: 'Zeitfenster hinzufügen',
      supportRemoveHours: 'Zeitfenster entfernen',
      // Time & Date Settings card + modal
      tdsCardTitle: 'Zeit- & Datumseinstellungen',
      tdsCardSubtitle: 'Konfigurieren Sie, wie Zeit, Datum und Wocheninformationen auf der Plattform angezeigt werden.',
      tdsTimezoneLabel: 'Zeitzone',
      tdsDateFormatLabel: 'Datumsformat',
      tdsTimeFormatLabel: 'Zeitformat',
      tdsWeekStartsOnLabel: 'Woche beginnt am',
      tdsTime12Label: '12-Stunden',
      tdsTime12Example: '14:30 Uhr',
      tdsTime24Label: '24-Stunden',
      tdsTime24Example: '14:30',
      tdsModalTitle: 'Zeit- & Datumseinstellungen',
      tdsSaving: 'Speichern...',

      faqPageTitle: 'Häufig gestellte Fragen',
      faqPageSubtitle: 'Finden Sie Antworten auf häufige Fragen zur Nutzung von DocAI, zur Verwaltung von Agenten und zur Analyse von Dokumenten.',
      faqManageTitle: 'FAQs verwalten',
      faqAddButton: 'Frage hinzufügen',
      faqEmptyTitle: 'Noch keine FAQs',
      faqEmptySubtitle: 'Es wurden noch keine Fragen hinzugefügt.',
      faqDrawerAddTitle: 'Frage hinzufügen',
      faqDrawerEditTitle: 'Frage bearbeiten',
      faqQuestionLabel: 'Frage',
      faqQuestionPlaceholder: 'Frage',
      faqAnswerLabel: 'Antwort',
      faqAnswerPlaceholder: 'Antwort',
      faqMediaLabel: 'Video/Bild hochladen (Optional)',
      faqUploading: 'Wird hochgeladen...',
      faqFileUploaded: 'Datei hochgeladen',
      faqRemoveFile: 'Entfernen',
      faqDropHint: 'Ziehen Sie Ihr Video/Bild hierher oder {browse}',
      faqBrowse: 'durchsuchen',
      faqSaving: 'Speichern...',
      faqDeleteTitle: 'FAQ löschen',
      faqDeleteMessage: 'Sind Sie sicher, dass Sie diese FAQ löschen möchten?',
      faqMediaAlt: 'FAQ-Medien',
      faqToastFetchError: 'FAQs konnten nicht geladen werden',
      faqToastDeleted: 'FAQ erfolgreich gelöscht',
      faqToastDeleteError: 'FAQ konnte nicht gelöscht werden',
      faqToastFileUploaded: 'Datei erfolgreich hochgeladen',
      faqToastUploadError: 'Fehler beim Hochladen der Datei',
      faqValidationRequired: 'Frage und Antwort sind erforderlich',
      faqValidationUploadWait: 'Bitte warten Sie, bis der Upload abgeschlossen ist.',
      faqToastUpdated: 'FAQ erfolgreich aktualisiert',
      faqToastCreated: 'FAQ erfolgreich erstellt',
      faqToastSaveError: 'Fehler beim Speichern der FAQ',
      faqToastChangesSaved: 'Änderungen gespeichert',
      faqToastChangesSaveError: 'Fehler beim Speichern',
    },

    auth: {
      loginTitle: 'Anmelden oder registrieren',
      loginSubtitle: 'Melden Sie sich an, um auf Ihre KI-Anfragen zuzugreifen.',
      continueWithMicrosoft: 'Mit Microsoft fortfahren',
      or: 'ODER',
      orContinueWith: 'ODER FORTFAHREN MIT',
      emailPlaceholder: 'E-Mail-Adresse',
      checkInboxTitle: 'Postfach prüfen',
      checkInboxSubtitle: 'Geben Sie den Bestätigungscode ein, den wir an {email} gesendet haben',
      codePlaceholder: 'Code',
      resendEmail: 'E-Mail erneut senden',
      continueWithPassword: 'Mit Passwort fortfahren',
      enterPasswordTitle: 'Passwort eingeben',
      emailLabel: 'E-Mail-Adresse',
      edit: 'Bearbeiten',
      passwordLabel: 'Passwort',
      forgotPassword: 'Passwort vergessen?',
      loginWithOtp: 'Mit einmaligem Code anmelden',
      resetPasswordTitle: 'Passwort vergessen?',
      resetPasswordSubtitle: 'Klicken Sie auf "Fortfahren", um Ihr Passwort zurückzusetzen für {email}',
      resetPasswordEmailPrompt: 'Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Reset-Link.',
      setNewPasswordSubtitle: 'Muss mindestens 8 Zeichen lang sein.',
      setNewPasswordTitle: 'Neues Passwort festlegen',
      newPasswordPlaceholder: 'Neues Passwort',
      confirmPasswordPlaceholder: 'Passwort bestätigen',
      passwordMismatch: 'Die Passwörter stimmen nicht überein.',
      sendResetLinkButton: 'Reset-Link senden',
      resetPasswordButton: 'Passwort zurücksetzen',
      footerPrivacyPolicy: 'Datenschutz',
      footerTermsOfService: 'Nutzungsbedingungen',
      footerSupport: 'Support',
      resetLinkSentTitle: 'Überprüfen Sie Ihre E-Mails',
      resetLinkSentSubtitle: 'Wir haben einen Link zum Zurücksetzen des Passworts an {email} gesendet. Bitte überprüfen Sie Ihren Posteingang.',
      backToLogin: 'Zurück zur Anmeldung',
      invalidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
      otpSent: 'Code wurde gesendet',
      otpResent: 'Code wurde erneut gesendet',
      genericError: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
      fieldRequired: 'Bitte füllen Sie alle Felder aus.',
      invalidPhone: 'Bitte geben Sie eine gültige Telefonnummer ein.',
      passwordTooShort: 'Das Passwort muss mindestens 8 Zeichen lang sein.',
      today: 'Heute',
      clear: 'Löschen',
      completeProfileTitle: 'Profil vervollständigen',
      completeProfileSubtitle: 'Nur noch ein paar Angaben, um Ihr Konto einzurichten.',
      fullnameLabel: 'Vollständiger Name',
      genderLabel: 'Geschlecht',
      genderPlaceholder: 'Bitte auswählen',
      genderMale: 'Männlich',
      genderFemale: 'Weiblich',
      genderDiverse: 'Divers',
      dobLabel: 'Geburtsdatum',
      mobileLabel: 'Mobiltelefonnummer',
      passwordOptionalLabel: 'Passwort (optional)',
      loginButton: 'Anmelden',
      welcomeBack: 'Willkommen zurück',
      loginPageSubtitle: 'Melden Sie sich bei Ihrem Konto an, um fortzufahren.',
      rememberMe: 'Angemeldet bleiben',
      noAccount: 'Noch kein Konto?',
      signUpLink: 'Registrieren',
      noPasswordHint: 'Kein Passwort? Wir senden Ihnen einen Bestätigungscode per E-Mail.',
      loginWithPasswordLink: 'Mit Passwort anmelden',
      setPasswordTitle: 'Passwort festlegen',
      setPasswordSubtitle: 'Willkommen zurück, {fullname}. Legen Sie ein Passwort fest, um sich künftig direkt mit Ihrer E-Mail-Adresse anzumelden.',
      mfaVerifyTitle: 'Zwei-Faktor-Authentifizierung',
      mfaAuthenticatorRowTitle: "Authenticator-App",
      mfaConfirmText: "Geben Sie den 6-stelligen Code ein, den Ihre App jetzt anzeigt.",
      mfaConfirmTitle: "Code bestätigen",
      mfaEnrollStep: "Schritt {current} von {total}",
      mfaInstallText: "Installieren Sie eine Authenticator-App wie Microsoft Authenticator oder Google Authenticator auf Ihrem Smartphone.",
      mfaInstallTitle: "Authenticator-App installieren",
      mfaManualKeyButton: "Schlüssel manuell eingeben",
      mfaManualKeyText: "Geben Sie diesen Schlüssel in Ihrer App ein (zeitbasiert):",
      mfaRecoveryCopied: "Kopiert",
      mfaRecoveryCopy: "Kopieren",
      mfaRecoveryDone: "Fertig",
      mfaRecoveryDownload: "Herunterladen",
      mfaRecoveryFileHeader: "DocAI Plattform – Wiederherstellungscodes (jeder Code ist nur einmal gültig)",
      mfaRecoverySavedCheckbox: "Ich habe die Codes sicher gespeichert",
      mfaRecoverySectionText: "Melden Sie sich mit einem dieser Codes an, falls Sie keinen Zugriff auf Ihre Authenticator-App haben.",
      mfaRecoverySectionTitle: "Wiederherstellungscodes",
      mfaRecoveryText: "Falls Sie keinen Zugriff auf Ihre App haben, können Sie sich mit einem dieser Codes anmelden. Jeder Code ist nur einmal gültig. Bewahren Sie die Codes sicher auf.",
      mfaRecoveryTitle: "Wiederherstellungscodes speichern",
      mfaRecoveryVerifySubtitle: "Geben Sie einen Ihrer Wiederherstellungscodes ein.",
      mfaRegenerateButton: "Neue Codes erstellen",
      mfaRegenerateConfirmTitle: "Neue Wiederherstellungscodes erstellen",
      mfaRegenerateSubmit: "Codes erstellen",
      mfaRegenerateSubtitle: "Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.",
      mfaRegenerateSuccess: "Neue Wiederherstellungscodes wurden erstellt.",
      mfaRegenerateWarning: "Ihre bisherigen Wiederherstellungscodes werden dabei ungültig.",
      mfaRequiredForAll: "Zwei-Faktor-Authentifizierung ist für alle Benutzer verpflichtend und kann nicht deaktiviert werden.",
      mfaScanText: "Öffnen Sie die App, fügen Sie ein neues Konto hinzu und scannen Sie diesen QR-Code. Der Eintrag erscheint als „{issuer}“.",
      mfaScanTitle: "QR-Code scannen",
      mfaUseAuthenticatorCode: "Code aus der Authenticator-App verwenden",
      mfaUseRecoveryCode: "Wiederherstellungscode verwenden",
      mfaVerifySubtitle: 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.',
      mfaCodeLabel: 'Code',
      mfaSetupTitle: 'Zwei-Faktor-Authentifizierung einrichten',
      mfaSetupSubtitle: 'Scannen Sie diesen QR-Code mit einer Authenticator-App (z. B. Google oder Microsoft Authenticator) und geben Sie den angezeigten Code ein, um fortzufahren.',
      mfaSectionTitle: 'Zwei-Faktor-Authentifizierung',
      mfaSectionDescription: 'Schützen Sie Ihr Konto zusätzlich mit einer Authenticator-App.',
      mfaStatusEnabled: 'Aktiviert',
      mfaStatusDisabled: 'Deaktiviert',
      mfaEnableButton: 'Aktivieren',
      mfaDisableButton: 'Deaktivieren',
      mfaEnabledSuccess: 'Zwei-Faktor-Authentifizierung wurde aktiviert.',
      mfaDisabledSuccess: 'Zwei-Faktor-Authentifizierung wurde deaktiviert.',
      mfaRequiredForRole: 'Zwei-Faktor-Authentifizierung ist für Ihre Rolle erforderlich und kann nicht deaktiviert werden.',
    },
  },

  en: {
    common: {
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      close: 'Close',
      confirm: 'Confirm',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      search: 'Search',
      loading: 'Loading...',
      upload: 'Upload',
      download: 'Download',
      open: 'Open',
      yes: 'Yes',
      no: 'No',
      add: 'Add',
      remove: 'Remove',
      create: 'Create',
      continue: 'Continue',
      proceed: 'Resume',
      retry: 'Retry',
      refresh: 'Refresh',
      error: 'Error',
      success: 'Success',
      warning: 'Warning',
      info: 'Information',
    },

    nav: {
      homepage: 'Homepage',
      projects: 'AI Requests',
      agents: 'Agents',
      notifications: 'Notifications',
      helpCenter: 'Help Center',
      recentProjects: 'Recent AI requests',
      settings: 'Settings',
      account: 'Account',
      logout: 'Log out',
      logoutConfirm: 'Are you sure you want to log out?',
    },

    sidebar: {
      fragen: 'Instructions',
      dateien: 'Files',
      uploadFile: 'Upload file',
      loadingQuestions: 'Loading instructions…',
      refreshFiles: 'Refresh files',
      closeSidebar: 'Close sidebar',
      openSidebar: 'Open sidebar',
      noFilesUploaded: 'No files uploaded yet',
      searchFilesPlaceholder: 'Search files',
      sharepointSection: 'SharePoint',
      noKnowledgeArea: 'No knowledge area',
      noFilesFound: 'No files found',
      maxFilesInfo: 'Maximum {count} files allowed for analysis ({used}/{count})',
      selectedFiles: 'Selected files:',
      addFiles: 'Add files',
      standardBadge: 'Standard',
      sharepointBadge: 'SharePoint',
      uploadedBadge: 'Uploaded',
      deleteFileTitle: 'Delete file?',
      deleteFileMessage: 'Are you sure you want to remove "{filename}" from the analysis? This action cannot be undone.',
      statusPending: 'Pending',
      statusDone: 'Done',
      standardSection: 'Standard',
      // Upload states
      uploadingState: 'Uploading...',
      convertingState: 'Converting...',
      // Group headers
      youSection: 'You',
      // Selected files area
      totalPages: 'Total pages: {count}',
      // PDF viewer
      pdfLoading: 'Loading PDF...',
      imageLoading: 'Loading image...',
      imageLoadError: 'Failed to load image',
      sourceFileNotFound: 'Could not open the file. It may no longer be available.',
      sourceFileStale: 'This SharePoint file has been updated or removed at the source since this answer was generated.',
      sourceFileTimeout: 'Loading the file took too long. Please try again.',
      sourceFileLoadFailed: 'The file could not be loaded. Please check your connection and try again.',
      sourceFileDecryptionFailed: 'This file could not be decrypted. The encryption key may have changed or the file may be corrupted — please contact an administrator.',
      backToFiles: 'Back to files',
      refreshAlt: 'Refresh',
      // Error messages / validation
      noFileSelected: 'No file selected',
      invalidFileType: 'Please select only {types} files',
      pdfProcessingError: 'Error processing {filename}: Invalid PDF file',
      fileValidationError: 'Error for {filename}: {error}',
      convertFileError: 'Failed to convert file',
      noOrganization: 'User is not associated with any organization',
      uploadExceedsPages: 'Upload exceeds available pages',
      selectFilesToUpload: 'Please select files to upload',
      uploadFilesError: 'Failed to upload files',
      cannotRefresh: 'Cannot refresh files: missing user or history information',
      refreshFilesError: 'Failed to refresh files',
      deleteFileError: 'Failed to delete file',
      filesLoadError: 'Error: {error}',
      maxFilesReached: 'Only {max} files may be analyzed. You have already uploaded {uploaded} file(s). {rejected} file(s) were not added.',
      maxFilesReachedShort: 'Only {max} files may be analyzed. You have already uploaded {uploaded} file(s).',
      // Toast success
      uploadSuccess: 'Files uploaded successfully',
      refreshSuccess: 'Files refreshed successfully',
      // Generated files tab
      uploadedTab: 'Uploaded',
      generatedFilesTab: 'Generated files {count}',
      tableName: 'Name',
      tableCreated: 'Created',
      tableSize: 'Size',
      tableActions: 'Actions',
      noGeneratedFiles: 'No generated files yet',
      today: 'Today',
      yesterday: 'Yesterday',
      previewAction: 'Preview',
      downloadWordAction: 'Download Word',
      downloadPdfAction: 'Download PDF',
      deleteGeneratedFileTitle: 'Delete file?',
      deleteGeneratedFileMessage: 'Are you sure you want to delete "{filename}"? This action cannot be undone.',
      deleteGeneratedFileError: 'Failed to delete file',
    },

    homepage: {
      title: 'Homepage',
      recentProjects: 'Recent AI requests',
      createNewProject: 'Create new AI request',
      noProjectsCreated: 'No AI requests created',
      noProjectsHint: 'Create an AI request to upload files and run analyses.',
      tableTitle: 'Title',
      tableAgents: 'Agents',
      tableCreatedAt: 'Creation date',
      tableProgress: 'Q&A progress',
      tableAnswerStatus: 'Response status',
      answeredOf: '{done} / {total} answered',
      agentsTitle: 'Agents',
      agentsSubtitleLine1: 'Discover and work with AI agents designed for different user groups.',
      agentsSubtitleLine2: 'Each agent is designed to help you analyze documents, data, and decisions faster.',
      noAgentsAvailable: 'No agents available. Please contact your administrator.',
      noAgentsYet: 'No Agents Yet',
      noAgentsYetHint: 'Create an agent to start analyzing files and data.',
      createAgentFirst: 'Please create an agent first.',
      agentCardDefaultDescription: 'AI agent to support your tasks.',
      newChatPlaceholderName: 'New Chat',
      historyLimitReached: 'You have reached the maximum number of {limit} chat histories. Please delete an existing history before creating a new one.',
      createProjectError: 'Failed to create project.',
      startDirectChatError: 'Failed to start chat.',
      startingDirectChat: 'Starting chat...',
      showAll: 'Show All',
      showLess: 'Show Less',
      noDepartment: 'No Department',
    },

    history: {
      pageTitle: 'AI Requests',
      tableTitle: 'Title',
      tableAgents: 'Agents',
      tableCreatedAt: 'Creation date',
      tableProgress: 'Q&A progress',
      tableAnswerStatus: 'Response status',
      answeredOf: '{done} / {total} answered',
      agentLabel: 'Agents',
      titleLabel: 'Title',
      creationDateLabel: 'Creation date',
      progressLabel: 'Q&A progress',
      noProjectsFound: 'No AI requests found',
      noProjectsCreated: 'No AI requests created',
      noProjectsSearchHint: 'No AI requests match your search.',
      createForAgent: 'Create an AI request for {agent}.',
      createHint: 'Create an AI request to upload files and run analyses.',
      continue: 'Continue',
      view: 'View',
      projectDetails: 'AI request details',
      deleteAll: 'Delete all',
      newProjectTitle: 'Create new AI request',
      createProject: 'Create AI request',
      agentProjects: 'Agent AI requests',
      clearFilter: 'Clear filter',
      filter: 'Filter',
      tableAccess: 'Access',
      accessOwner: 'Owner',
      accessViewOnly: 'Shared',
      accessInvited: 'Invited',
      accessCanEdit: 'Can Edit',
      tabMyProjects: 'My AI Requests',
      tabSharedProjects: 'Shared AI Requests',
      tabInvitedProjects: 'Invited AI Requests',
    },

    analysis: {
      breadcrumbHome: 'Homepage',
      breadcrumbProjects: 'AI Requests',
      breadcrumbProjectDetail: 'Open AI request overview',
      breadcrumbChatLeaf: 'Chat',
      export: 'Export',
      exportWithComments: 'Export with comments',
      exportWithoutComments: 'Export without comments',
      agentFallback: 'Agent',
      projectFallback: 'AI Request',
      openSidebar: 'Open sidebar',
      chatPlaceholder: 'Ask something',
      sources: 'Sources',
      reference: 'Reference',
      webSourcesCount: '{count} Web Sources',
      sourcesCount: '{count} Sources',
      document: 'Document',
      documentNumber: 'Document {index}',
      source: 'Source',
      sourceLabel: 'Source: {filename} (page {page})',
      page: 'Page',
      pageWithNumber: 'Page {page}',
      questionTab: 'Questions',
      fileTab: 'Files',
      projectLoading: 'Loading AI request...',
      pdfBrowserTitle: 'AI Spaces AI summary',
      pdfSummarySuffix: 'AI summary',
      pdfUploadedDocsHeading: 'Uploaded documents:',
    },

    chatActions: {
      listen: 'Listen',
      stop: 'Stop',
      loading: 'Loading...',
      copy: 'Copy',
      addComment: 'Add comment',
      editComment: 'Edit comment',
      quickActions: 'Quick actions',
      quickActionsResult: 'Quick action result',
      setStatus: 'Set status',
      statusSelect: 'Select status',
      aiDisclaimer: 'AI-generated answer. Please always verify the results.',
      wordToggle: 'Generate a Word document',
      wordCreating: 'Creating document…',
      wordCreatingSubtitle: 'Using the current request and available sources',
      wordCreatingStatusPill: 'Creating',
      wordCreatingRendering: 'Rendering Word document…',
      wordCreatingInProgress: 'In progress',
      wordCreatingHelper: 'This can take a few minutes. You can continue chatting while the document is being created.',
      wordNothingFound: 'The topic was not found in the available documents.',
      wordTruncated: 'The document could not be completed. Please try again.',
      wordFailed: 'Document generation failed.',
      wordDownloadWord: 'Download Word',
      wordDownloadPdf: 'Download PDF',
      wordDocumentCreated: 'Word document created',
      wordReady: 'Ready',
      wordCompletedLabel: 'Completed',
      wordReadyHelper: 'The document is ready to download.',
      wordPreview: 'Preview',
      wordDownloadWordShort: 'Word',
      wordDownloadPdfShort: 'PDF',
      wordDownloadError: 'Download failed. Please try again.',
      wordHint: 'As a Word document',
      wordModeLabel: 'Word',
      wordComposerMeta: "One request only · creates a Word document",
      wordNotEnabledError: 'Word document generation is not enabled for this agent.',
      wordAlreadyRunningError: 'A Word document is already being generated for this agent. Please wait for it to finish.',
      saving: 'Saving...',
      saveComment: 'Save',
      commentBy: 'Comment by {name}',
      readAloud: 'Read aloud',
      close: 'Close',
      commentPlaceholder: 'Add a comment',
      defaultUser: 'User',
      askPlaceholder: 'Ask something',
      chatDisabled: 'Chat is disabled',
      voiceInput: 'Voice input',
      uploadFile: 'Upload file',
      enterProjectName: 'Name the AI request',
      convertToPdf: 'Converting to PDF...',
      processing: 'Processing...',
      uploading: 'Uploading...',
      startAnalysis: 'Start analysis',
      addFiles: 'Add files',
      newAnalysisTitle: 'New analysis?',
      newAnalysisConfirm: 'Are you sure you want to close the current analysis and start a new one?',
      removeFile: 'Remove file',
      filePickerRoot: 'Root folder',
      filePickerClear: 'Clear selection',
      filePickerTrigger: 'Select a file (/)',
      filePickerSearchPlaceholder: 'Search in this folder',
      filePickerSelected: 'selected',
      onlyOneFileAllowed: 'Only one file is allowed for the initial analysis.',
      tooManyImages: 'Maximum {max} images per upload.',
      filesStillProcessing: 'Please wait until the uploaded files finish processing before asking a question about them.',
      defaultImageOnlyMessage: 'Please analyze the attached image.',
      analyzingImage: 'Analyzing image…',
      transcriptionFailed: 'Transcription failed',
      microphoneDenied: 'Microphone access denied',
      uploadSuccess: 'File uploaded successfully',
      uploadFailed: 'Upload failed',
      uploadExceedsPages: 'Upload exceeds available pages',
      uploadPageLimitExceeded: 'The file upload limit for this session is {limit} pages. You have tried to upload {attempted} pages. Please try again and only upload {limit} pages.',
      pageLimitsVerificationFailed: 'Failed to verify page limits',
      invalidFileType: 'Please select only {types} files',
      invalidPdfProcessing: 'Error processing {name}: invalid PDF file',
      fileConvertFailed: 'Failed to convert file',
      noFileSelected: 'No file selected',
      fileErrorFor: 'Error for {name}: {error}',
      genericError: 'An error occurred.',
      noOrganization: 'User is not associated with any organization',
      tasksLoadFailed: 'Failed to load tasks: {error}',
      agentQuestionPrefix: 'Q: {title}',
      webSearchStep1: 'Understanding your question',
      webSearchStep2: 'Searching the web',
      webSearchStep3: 'Reviewing relevant results',
      webSearchStep4: 'Checking useful sources',
      webSearchStep5: 'Preparing response',
      webSearchFoundQuery: 'Searched for: "{query}"',
      aiThinkingLabel: 'AI is thinking:',
      retryTimeoutLabel: 'Retry',
    },

    answerStatus: {
      accepted: 'Accepted',
      review: 'Further review required',
      missing: 'Information is missing',
      rejected: 'Rejected',
    },

    projectDetail: {
      breadcrumb: 'AI request details',
      tabOverview: 'Overview',
      tabDocuments: 'Documents',
      tabQuestions: 'Instructions',
      tabActivities: 'Activities',
      agentLabel: 'Agent: {name}',
      defaultAgentName: 'Contract Review Agent',
      aiSummary: 'AI summary',
      checklistProgress: 'Checklist progress',
      projectLocation: 'AI request location',
      analysisScore: 'Analysis score',
      documentCount: 'Document count',
      participants: 'Participants',
      documents: 'Documents',
      noLocationExtracted: 'Not yet extracted',
      questionsCategory: 'Categories',
      questionsTitle: 'Question title',
      questionsDescription: 'Description',
      questionsAnswer: 'AI answer',
      questionsStatus: 'Status',
      questionsLoading: 'Loading questions...',
      defaultCategory: 'General',
      acceptedOrOpen: 'Accepted',
      openAction: 'Open',
      projectCreated: 'AI Request created',
      newProjectStarted: 'New AI request started',
      activitiesEmpty: 'AI Request activities will appear here once documents are uploaded, analyzed and questions answered.',
      activitiesEmptyTitle: 'No activity yet',
      activityFileUploaded: 'File uploaded',
      activityFileProcessed: 'Processed',
      activityQuestionAsked: 'Question asked',
      activityQuestionAskedFallback: 'User asked a question',
      activityAnalysisPerformed: 'Analysis performed',
      activityAnalysisFallback: 'AI analysis completed',
      activityDefaultUser: 'User',
      activityAiAgent: 'AI Agent',
      activityTodayLabel: 'Today',
      activityYesterdayLabel: 'Yesterday',
      activityShowOlder: 'Show older',
      activityShowLess: 'Show less',
      uploadFilesHint: 'Upload files into this AI request so the agent can review and analyze them.',
      yourDocuments: 'Your documents',
      uploadDocument: 'Upload document',
      uploading: 'Uploading...',
      agentReferenceFiles: 'Agent reference files',
      documentName: 'Document name',
      pages: 'Pages',
      uploadDate: 'Upload date',
      documentsLoading: 'Loading documents...',
      noDocumentsUploaded: 'No documents uploaded',
      noQuestionsAssigned: 'No questions assigned',
      noQuestionsHint: 'Questions will appear here after they are added to this agent.',
    },

    agentSettings: {
      breadcrumbAgents: 'Agents',
      breadcrumbSettings: 'Agent settings',
      agentFallback: 'Agent',
      departmentFallback: 'User group',
      tabGeneral: 'General settings',
      tabQuestions: 'Instructions',
      tabAiProfile: 'AI profile',
      tabTools: 'Tools',
      tabFiles: 'Standard files',
      tabProjectOverview: 'AI request overview',
      tabProjectSetup: 'AI request setup',
      tabShortcuts: 'Shortcuts',
      // Tools tab — Word generation
      toolsTitle: 'Tools',
      toolsSubtitle: "Enable optional capabilities for this agent. Word generation uses the agent's existing document, web, or combined retrieval configuration.",
      toolsWordGenTitle: 'Word generation',
      toolsWordGenSubtitle: 'Users can create a branded Word document by turning on Word for an individual chat request.',
      toolsCodeInterpreterTitle: "Code Interpreter (Data Analysis)",
      toolsCodeInterpreterSubtitle: "Enables exact calculations, tables and charts from uploaded files – switched on per chat and stays on until you turn it off.",
      toolsCiInstructionLabel: "Analysis instructions",
      toolsCiInstructionPlaceholder: "e.g. Show all amounts in CHF and also export every table as an Excel file.",
      toolsCiInstructionHelp: "Added to this agent's built-in analysis rules.",
      toolsCiMaxOutputTokensLabel: "Maximum answer length (tokens)",
      toolsCiMaxOutputTokensPlaceholder: "No limit",
      toolsCiMaxOutputTokensHelp: "Leave empty for no limit. Very low values may stop the analysis early.",
      toolsCiSessionScopeLabel: "Workspace",
      toolsCiSessionScopeSession: "Reuse within the chat (recommended)",
      toolsCiSessionScopeRequest: "New for every request",
      toolsCiSessionScopeSessionDesc: "Follow-up requests reuse the same files and are much faster.",
      toolsCiSessionScopeRequestDesc: "Each analysis starts in a fresh workspace and uploads the files again.",
      toolsInstructionLabel: 'Instruction',
      toolsInstructionPlaceholder: 'Instruction for Word generation',
      toolsInstructionHelp: 'This replaces the answer-shaping part of the normal agent prompt for Word generation. Retrieval itself is unchanged.',
      toolsMaxOutputTokensLabel: 'Max output tokens',
      toolsMaxOutputTokensHelp: 'Separate output budget for Word generation. A generous default prevents silently truncated documents; the backend still validates the configured value against the supported model limit.',
      toolsBehaviourTitle: 'Word generation behaviour',
      toolsBehaviourItem1: 'Word is shown in the chat input only when this tool is enabled.',
      toolsBehaviourItem2: 'The Word toggle applies to one message and resets after sending or opening another chat.',
      toolsBehaviourItem3: 'Selected documents restrict the factual sources used for the generated document.',
      toolsBehaviourItem4: 'The finished DOCX and PDF preview are stored under Generated files and are never added to the search index.',
      toolsFutureToolsNote: 'Future tools: Data analysis / Code Interpreter can be added here as another independent tool later.',
      toolsFixedLayoutTitle: 'Fixed document layout · no configuration required',
      toolsBrandingCardTitle: 'Organisation branding',
      toolsBrandingCardText: 'Logo, organisation name and primary colour are applied automatically.',
      toolsSourcesCardTitle: 'Sources & language',
      toolsSourcesCardText: 'Validated sources and fixed labels follow the document/request language.',
      toolsTransparencyCardTitle: 'AI transparency',
      toolsTransparencyCardText: 'AI-generated notice is included in the footer and Word file properties.',
      toolsLayoutFooterNote: 'V1 uses the standard branded layout. Custom templates, multiple templates and a document editor are intentionally out of scope.',
      toolsSaveButton: 'Save changes',
      // General (Overview) header
      generalTitle: 'General settings',
      generalSubtitle: 'Define how this agent works and which business problem it solves.',
      unsavedChanges: 'Unsaved changes',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving...',
      // Basic information card
      basicInfoTitle: 'Basic information',
      basicInfoSubtitle: 'Basic details for organizing and identifying this agent.',
      agentImageLabel: 'Agent image',
      editImage: 'Edit image',
      removeImage: 'Remove image',
      noFileSelected: 'No file selected',
      agentNameLabel: 'Agent name',
      agentNamePlaceholder: 'Enter agent name',
      organizationLabel: 'Organization',
      selectOrganization: 'Select organization',
      departmentLabel: 'User group',
      selectDepartment: 'Select user group',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Describe the primary value of this agent...',
      descriptionHelp: 'One or two sentences explaining how this agent helps users.',
      // Use case card
      useCaseTitle: 'Use case',
      useCaseSubtitle: 'Describe the problem this agent solves and how it helps your team.',
      problemLabel: 'Problem description*',
      problemPlaceholder: 'Which business challenge does this agent address?',
      problemHelp: 'Example: Legal teams spend hours manually reviewing contracts for compliance issues...',
      solutionLabel: 'Solution description*',
      solutionPlaceholder: 'How does this agent solve the problem?',
      solutionHelp: 'Example: This AI agent automatically scans contracts and identifies key terms...',
      // Goals & KPIs card
      goalsTitle: 'Goals & KPIs',
      goalsSubtitle: 'Define success metrics and who is responsible for this use case.',
      goalLabel: 'Use case goal*',
      goalPlaceholder: 'e.g. Reduce contract review time by 80% with 99% accuracy',
      goalHelp: 'This is helper text to assist the user.',
      kpisLabel: 'Key Performance Indicators (KPIs)',
      kpiPlaceholder: 'Enter a new KPI...',
      kpiAdd: 'Add',
      kpiHelp: 'Press Enter or click Add to add a new KPI.',
      managerLabel: 'Process manager',
      managerPlaceholder: 'Name of the process manager',
      managerHelp: 'Who is responsible for this process?',
      // Toasts
      toastNoTask: 'No task selected',
      toastNoPermission: 'You do not have permission to edit this task',
      toastUpdateSuccess: 'Task updated successfully',
      toastUpdateError: 'Error updating',
      // Add Category modal
      addCategoryTitle: 'Create new category',
      addCategorySubtitle: 'Add a category to organize instructions and make analysis easier to manage and filter.',
      addCategoryNameLabel: 'Category name',
      addCategoryNamePlaceholder: 'Category name',
      addCategoryEnterHint: 'Press Enter to add a new category.',
      addCategoryEmptyError: 'Please enter a category name.',
      addCategoryExistsError: 'This category already exists.',
      addCategoryNoAgentError: 'No agent selected.',
      addCategoryCreateError: 'Error creating the category.',
      addCategoryDeleteError: 'Error deleting the category.',
      addCategoryConfirm: 'Add category',
      // Add/Edit Question modal
      addQuestionTitle: 'Create new instruction',
      editQuestionTitle: 'Edit instruction',
      questionTitleLabel: 'Instruction title',
      questionTitlePlaceholder: 'Instruction title',
      questionCategoryLabel: 'Category',
      questionCategoryPlaceholder: 'Category',
      questionDescriptionLabel: 'Description',
      questionDescriptionPlaceholder: 'Enter description...',
      questionEmptyTitleError: 'Please enter an instruction title.',
      questionEmptyDescriptionError: 'Please enter a description.',
      questionOrgPermissionError: 'You can only edit instructions for tasks belonging to your organization.',
      questionGenericError: 'An error occurred. Please try again.',
      addQuestionConfirm: 'Add instruction',
      updateQuestionConfirm: 'Update instruction',
      // Shared
      edit: 'Edit',
      delete: 'Delete',
      searchPlaceholder: 'Search',
      previous: 'Previous',
      next: 'Next',
      pageOf: 'Page {current} of {total}',
      // StandarQustions (Instructions tab)
      showProjectDetailTitle: 'Enable instructions',
      showProjectDetailSubtitle: 'When off, the instructions tab is hidden in chat.',
      showFilesTabTitle: 'Show files tab in chat',
      showFilesTabSubtitle: 'When off, the files tab in the chat sidebar is hidden for this agent.',
      allowLocalFilesTitle: 'Allow local file uploads',
      allowLocalFilesSubtitle: 'When off, users cannot upload local files. Only SharePoint files are shown.',
      questionsSelectTaskHint: 'Please select a task to view its instructions',
      questionsAddCategoriesButton: 'Add categories',
      questionsAddQuestionButton: 'Add instruction',
      questionsReorderButton: 'Reorder',
      questionsReorderDoneButton: 'Done',
      questionsReorderHint: 'All instructions are shown. Drag an instruction to the position you want, even across former pages.',
      questionsColCategories: 'Categories',
      questionsColTitle: 'Instruction title',
      questionsColDescription: 'Description',
      questionsEmptyTitle: 'No instructions found',
      questionsEmptyHint: 'No instructions have been added yet. Click "Add instruction" to create a new instruction.',
      questionsDeleteTitle: 'Delete instruction',
      questionsDeleteMessage: 'Are you sure you want to delete this instruction?',
      // SystemPrompts (AI profile tab)
      aiProfileLoadError: 'Error loading AI profile',
      aiProfileSaveError: 'Error saving AI profile',
      aiProfileNoPermission: 'You can only edit AI profiles for tasks of your organization',
      aiProfileSaveSuccess: 'AI profile saved successfully',
      aiProfileLoading: 'Loading AI profile...',
      aiProfileHeaderTitle: 'AI profile',
      aiProfileHeaderSubtitle: 'Configure how the AI agent understands context, generates responses, and behaves during analysis.',
      aiProfileModelDefaultLabel: 'Default (from environment variables)',
      aiProfileModelDefaultDesc: 'Uses the default configuration',
      aiProfileModelGpt51Label: 'GPT-5.1',
      aiProfileModelGpt51Desc: 'Most powerful, balanced performance',
      aiProfileModelGpt56LunaLabel: 'GPT-5.6-Luna',
      aiProfileModelGpt56LunaDesc: 'Experimental, web search only',
      aiProfileModelGpt56TerraLabel: 'GPT-5.6-Terra',
      aiProfileModelGpt56TerraDesc: 'Experimental, web search only',
      aiProfileReasoningLowLabel: 'Low',
      aiProfileReasoningLowDesc: 'Fastest responses, basic reasoning',
      aiProfileReasoningStandardLabel: 'Standard',
      aiProfileReasoningStandardDesc: 'Standard reasoning',
      aiProfileReasoningMediumLabel: 'Medium',
      aiProfileReasoningMediumDesc: 'Balanced speed and quality',
      aiProfileReasoningHighLabel: 'High',
      aiProfileReasoningHighDesc: 'Best accuracy, slower responses',
      aiProfileSearchHybrid: 'Hybrid',
      aiProfileSearchSemantic: 'Hybrid + Semantic',
      aiProfileSearchVector: 'Vector',
      aiProfileSearchKeyword: 'Keyword',
      aiProfileKnowledgeSourceTitle: 'Knowledge Source',
      aiProfileKnowledgeSourceSubtitle: 'Choose which sources this AI profile can use.',
      aiProfileModeDocumentsLabel: 'Documents only',
      aiProfileModeDocumentsDesc: 'Answers only with indexed documents and Q/A pairs.',
      aiProfileModeWebLabel: 'Web only',
      aiProfileModeWebDesc: 'Answers only with web search results.',
      aiProfileModeHybridLabel: 'Documents + Web',
      aiProfileModeHybridDesc: 'Answers with documents and web search results.',
      aiProfileBadgeRag: 'RAG',
      aiProfileBadgeNoRag: 'No RAG',
      aiProfileBadgeWeb: 'Web',
      aiProfileBadgeNoWeb: 'No Web',
      aiProfileIdentityTitle: 'AI Identity',
      aiProfileIdentitySubtitle: 'Define the personality and instructions for the AI\'s behavior.',
      aiProfileIdentitySubtitleDocuments: 'Dedicated system prompt for document-based answers.',
      aiProfileIdentitySubtitleWeb: 'Dedicated system prompt for web-based answers.',
      aiProfileIdentitySubtitleHybrid: 'Dedicated system prompt for answers using documents and web.',
      aiProfileDifferentPromptInfoTitle: 'Different system prompt per Knowledge Source',
      aiProfileDifferentPromptInfoDesc: 'Switching the selected mode loads the dedicated system prompt for that mode.',
      aiProfileActivePromptBadge: 'Active prompt: {mode}',
      aiProfileSystemPromptLabel: 'System Prompt: {mode}',
      aiProfileSystemPromptLabelPlain: 'System Prompt',
      aiProfileRegexDetectionLabel: 'Render Reference UI',
      disclaimerToggleLabel: 'Privacy disclaimer',
      disclaimerToggleDescription: 'Show a privacy notice above the chat prompt.',
      disclaimerTextLabel: 'Disclaimer text',
      disclaimerTextHelp: 'The notice appears directly above the prompt field.',
      disclaimerLearnMoreContentLabel: 'Learn more content',
      disclaimerLearnMoreContentHelp: 'Content shown in the modal after clicking "Learn more".',
      disclaimerIconLabel: 'Icon',
      disclaimerInfoNotice: 'The disclaimer is shown whenever a chat is opened. Users can dismiss it for the current visit; it appears again when they reopen the chat.',
      disclaimerPreviewLabel: 'Preview',
      disclaimerLearnMoreLink: 'Learn more',
      disclaimerModalTitle: 'More information',
      toggleOn: 'On',
      toggleOff: 'Off',
      aiProfileSystemPromptHelp: 'Technical prompt that controls exactly how the AI answers in this mode. Leave empty to use the default.',
      aiProfileSystemPromptPlaceholder: 'System prompt for {mode}',
      aiProfileNameLabel: 'AI Profile Name',
      aiProfileNamePlaceholder: 'Contract review agent',
      aiProfileDescriptionLabel: 'Description',
      aiProfileDescriptionPlaceholder: 'You are an experienced contract analyst AI that reviews legal documents with precision and attention to detail. Provide clear, structured answers based exclusively on the document content. Highlight risks, obligations, and key terms. Format your responses clearly with bullet points and section headers.',
      aiProfileDescriptionHelp: 'This prompt sets the tone and behavior for all AI responses',
      aiProfileModelTitle: 'Model & Intelligence Settings',
      aiProfileModelSubtitle: 'Choose the AI model and reasoning level that best fits your use case.',
      aiProfileModelLabel: 'Model',
      aiProfileModelSelectPlaceholder: 'Select model',
      aiProfileReasoningLabel: 'Reasoning Effort',
      aiProfileResponseLengthLabel: 'Response length limit',
      aiProfileResponseLengthHelp: 'Maximum number of tokens in the response. Higher values allow longer responses. GPT-5.1 supports up to 65,536 tokens. Default: 1,000',
      aiProfileResponseLengthExceeded: 'Maximum allowed value is {max} — higher values cannot be entered.',
      aiProfileSourceSettingsTitle: 'Source Settings',
      aiProfileSourceSettingsSubtitleDocuments: 'Configure document retrieval settings.',
      aiProfileSourceSettingsSubtitleWeb: 'Configure web search settings.',
      aiProfileSourceSettingsSubtitleHybrid: 'Configure document retrieval and web search settings.',
      aiProfileWebSourceSettingsTitle: 'Web Search Settings',
      aiProfileWebSourceSettingsSubtitle: 'Controls public web search results used by the AI.',
      aiProfileWebSourceBadge: 'WEB SOURCE',
      aiProfileWebSearchProviderLabel: 'Web Search Provider',
      aiProfileWebSearchProviderValue: 'Azure OpenAI Web Search',
      aiProfileSearchContextSizeLabel: 'Search Context Size',
      aiProfileSearchContextSizeHelp: 'Controls how much web content Azure uses to ground the answer — not an exact number of sources or citations.',
      aiProfileSearchContextLowLabel: 'Low',
      aiProfileSearchContextLowDesc: 'Faster, lighter search',
      aiProfileSearchContextMediumLabel: 'Medium',
      aiProfileSearchContextMediumDesc: 'Balanced default',
      aiProfileSearchContextHighLabel: 'High',
      aiProfileSearchContextHighDesc: 'More thorough, detailed search',
      aiProfileUseWebChatHistoryLabel: 'Use chat history for Web Search',
      aiProfileUseWebChatHistoryHelp: 'Includes previous questions and answers in the web search. Off by default, as this can degrade answer quality.',
      aiProfileVerbosityLabel: 'Verbosity',
      aiProfileVerbosityOffLabel: 'Off',
      aiProfileVerbosityOffDesc: 'Not sent',
      aiProfileVerbosityLowLabel: 'Low',
      aiProfileVerbosityLowDesc: 'Short answers',
      aiProfileVerbosityMediumLabel: 'Medium',
      aiProfileVerbosityMediumDesc: 'Balanced answers',
      aiProfileVerbosityHighLabel: 'High',
      aiProfileVerbosityHighDesc: 'Detailed answers',
      aiProfileAdvancedWebSearchTitle: 'Advanced Web Search Parameters',
      aiProfileForceWebSearchLabel: 'Force Web Search',
      aiProfileForceWebSearchHelp: 'On: every request searches the web (tool_choice: required). Off: the model decides for itself (tool_choice: auto).',
      aiProfileStreamResponseLabel: 'Stream Response',
      aiProfileStreamResponseHelp: 'On (default): shows the "searching"/"generating" progress indicator while the request is in flight. Off: a single, non-streamed request.',
      aiProfileIncludeSearchSourcesLabel: 'Log Search Sources',
      aiProfileIncludeSearchSourcesHelp: 'On: the sources the model searched are additionally logged server-side (debugging only, does not change the sources shown to users).',
      aiProfileShowReasoningLabel: 'Show Reasoning',
      aiProfileShowReasoningHelp: "On: shows the user, live, what the model is thinking about before the answer appears (like Claude's own Thinking display).",
      aiProfileShowReasoningEffortWarning: 'Only takes effect at Reasoning Effort "Medium" or "High" — at "Standard" or "Low", Azure produces no reasoning summary.',
      aiProfileShowReasoningStreamWarning: 'Also requires "Stream Response" (above) to be on — without streaming, the reasoning can\'t be shown to the user live.',
      aiProfileMaxToolCallsLabel: 'Max Tool Calls',
      aiProfileMaxToolCallsPlaceholder: 'Unlimited',
      aiProfileMaxToolCallsHelp: 'Caps how many searches/page reads Azure may make per answer. Empty: no cap sent.',
      aiProfileAzureTimeoutLabel: 'Timeout (seconds)',
      aiProfileAzureTimeoutHelp: 'How long to wait for an Azure response before giving up. Default: 180 seconds.',
      aiProfileAzureMaxRetriesLabel: 'Max Retries',
      aiProfileAzureMaxRetriesHelp: 'How many times Azure automatically retries a failed request. Default: 0 (no auto-retry — each retry re-uses the full timeout again).',
      aiProfileMaxOutputTokensLabel: 'Max Output Tokens: {mode}',
      aiProfileMaxOutputTokensHelp: 'Caps the answer length for this mode. Default: 1500. Maximum allowed: 65536 — higher values are automatically clamped down.',
      aiProfileMaxOutputTokensExceeded: 'Maximum allowed value is {max} — higher values are automatically clamped down when saved.',
      aiProfileAllowedDomainsLabel: 'Allowed Domains',
      aiProfileBlockedDomainsLabel: 'Blocked Domains',
      aiProfileDomainsPlaceholder: 'e.g. wikipedia.org, example.com',
      aiProfileDomainsHelp: 'Comma-separated or one domain per line. Empty: no filter sent.',
      aiProfileUserLocationLabel: 'Location Targeting',
      aiProfileUserLocationHelp: 'On: an approximate location is passed to the web search tool to improve local results.',
      aiProfileUserLocationCountryLabel: 'Country (ISO code)',
      aiProfileUserLocationCountryPlaceholder: 'e.g. DE',
      aiProfileUserLocationCityLabel: 'City',
      aiProfileUserLocationCityPlaceholder: 'e.g. Berlin',
      aiProfileUserLocationRegionLabel: 'Region',
      aiProfileUserLocationRegionPlaceholder: 'e.g. Berlin',
      aiProfileUserLocationTimezoneLabel: 'Timezone',
      aiProfileUserLocationTimezonePlaceholder: 'e.g. Europe/Berlin',
      aiProfileContextTitle: 'Context & retrieval settings',
      aiProfileContextSubtitle: 'Defines how much context the AI uses before generating responses.',
      aiProfileSearchStrategyLabel: 'Search strategy',
      aiProfileSearchStrategyPlaceholder: 'Select strategy',
      aiProfileTopKLabel: 'Document contexts (Top K)',
      aiProfileUseCustom: 'Use Custom',
      aiProfileTopKDefaultHint: 'When disabled, the default number of contexts is used',
      aiProfileVectorKLabel: 'Candidate pool (Vector K)',
      aiProfileVectorKDefaultHint: 'When disabled, the global default value is used',
      aiProfileQaPairsLabel: 'Q/A pairs as context',
      aiProfileQaPairsDefaultHint: 'When disabled, no Q/A pairs are used as context',
      aiProfileWebSearchLabel: 'Web Search',
      aiProfileWebSearchDescription: 'Searches the internet for current information. Files and instructions are hidden in chat.',
      aiProfileWebSearchEnabled: 'Enabled',
      aiProfileWebSearchDisabled: 'Disabled',
      aiProfileWebSearchCustomPromptLabel: 'Custom search prompt',
      aiProfileWebSearchCustomPromptDescription: 'Added in front of the user\'s query on every web search. Only visible to Super Administrators.',
      aiProfileWebSearchCustomPromptPlaceholder: 'e.g. Only search for sources from the last 12 months...',
      // DefaultFiles (Standard files tab)
      filesImportSuccess: '{count} file(s) imported successfully',
      filesImportError: 'Error importing',
      filesAutoSyncSubfoldersEnabled: 'Auto-sync (with subfolders) enabled',
      filesAutoSyncFolderOnlyEnabled: 'Auto-sync (folder only) enabled',
      filesAutoSyncEnableError: 'Error enabling auto-sync',
      filesInheritedSyncNotice: 'This item is synchronized through a parent folder. Disable the parent folder.',
      filesAutoSyncDisabled: 'Auto-sync disabled',
      filesAutoSyncDisableError: 'Error disabling',
      filesSpItemDeleted: 'Item deleted successfully',
      filesSpItemDeleteError: 'Error deleting',
      filesLastSyncNever: 'Not synchronized',
      filesLastSyncJustNow: 'Just now',
      filesLastSyncMinutes: '{mins} min ago',
      filesLastSyncHours: '{hrs} h ago',
      filesLastSyncDays: '{days} day(s) ago',
      filesRelToday: 'Today',
      filesRelYesterday: 'Yesterday',
      filesRelDaysAgo: '{days} days ago',
      filesRelWeekAgo: '1 week ago',
      filesRelWeeksAgo: '{weeks} weeks ago',
      filesRelMonthAgo: '1 month ago',
      filesRelMonthsAgo: '{months} months ago',
      filesRelYearAgo: '1 year ago',
      filesRelYearsAgo: '{years} years ago',
      filesGeneralFolder: 'General',
      filesInvalidFileType: '{count} file(s) skipped. Only PDF, DOC and DOCX are allowed.',
      filesFolderNameRequired: 'Please enter a folder name',
      filesAddAtLeastOne: 'Please add at least one file',
      filesSelectTaskRequired: 'Please select a task',
      filesAddedToFolderSuccess: '{count} file(s) added to "{folderName}" successfully',
      filesFolderCreatedSuccess: 'Folder "{folderName}" with {count} file(s) created successfully',
      filesPartialSuccess: '{success} files successful, {errors} errors',
      filesUploadError: 'Error uploading files',
      filesMonthlyPageLimitExceeded: 'The file upload limit for this month is {limit} pages. You have tried to upload {attempted} pages. Please try again and only upload {limit} pages.',
      filesCreateFolderError: 'Error creating folder',
      filesDeleteFileSuccess: 'File deleted successfully',
      filesDeleteFileError: 'Error deleting file',
      filesDeleteFolderSuccess: 'Folder "{folderName}" deleted successfully',
      filesSelectTaskHint: 'Please select a task to manage standard files',
      filesTabAll: 'All files',
      filesTabLocal: 'Local files',
      filesTabSharePoint: 'SharePoint',
      filesSubTabOverview: 'Sites',
      filesSubTabImportReports: 'Import reports',
      filesImportReportsSubtitle: 'One report per import, newest first.',
      filesImportReportsRefresh: 'Refresh',
      filesImportReportsEmpty: 'No import reports yet',
      filesImportReportsEmptyHint: 'Reports are saved automatically after every import.',
      filesImportReportsColSite: 'Site',
      filesImportReportsColStarted: 'Started',
      filesSubTabProtocol: 'Deletion protocol',
      filesDeletionReportsSubtitle: 'One report per deletion, newest first.',
      filesDeletionReportsEmpty: 'No deletion reports yet',
      filesDeletionReportsEmptyHint: 'Reports are saved automatically after every "Delete imported data" or "Remove site" action.',
      filesViewDeletionReport: 'View deletion report',
      filesCountSingular: 'file',
      filesCountPlural: 'files',
      filesItemSingular: 'item',
      filesItemPlural: 'items',
      filesHeaderAllSubtitle: 'Local and SharePoint files in a unified list.',
      filesHeaderLocalSubtitle: 'Files stored and managed directly in DocAI.',
      filesConnectedSitesSingular: '{count} site assigned to this agent',
      filesConnectedSitesPlural: '{count} sites assigned to this agent',
      filesUploadFile: 'Upload file',
      filesAddFolder: 'Add folder',
      filesSearchPlaceholder: 'Search',
      filesFilterLabel: 'Filter',
      filesFilterLabelWithCount: 'Filter (1)',
      filesNoResultsFound: 'No results found',
      filesNoFiles: 'No files available',
      filesColName: 'File name',
      filesColType: 'File type',
      filesColCreated: 'Created',
      filesColModified: 'Modified',
      filesTypeSharePoint: 'SharePoint File',
      filesTypeLocal: 'Local File',
      filesDelete: 'Delete',
      filesPageOf: 'Page {current} of {total}',
      filesPrevious: 'Previous',
      filesNext: 'Next',
      filesSyncProtocolTitle: 'Deletion protocol',
      filesSyncProtocolSubtitle: 'Audit log of all deletion events across all sites',
      filesNoSyncActions: 'No sync actions logged',
      filesColSite: 'Site',
      filesColPath: 'Path',
      filesColTimestamp: 'Timestamp',
      filesColRuleType: 'Rule type',
      filesColEvent: 'Event',
      filesColStatus: 'Status',
      filesSiteOverview: 'SharePoint sites',
      filesFilesAndFolders: 'Files & folders',
      filesSyncedSuffix: '{count} synchronized',
      filesImporting: 'Importing...',
      filesImport: 'Import',
      filesImportWithCount: 'Import ({count})',
      filesNoItems: 'No files or folders',
      filesDisableSync: 'Disable Sync',
      filesEnableAutoSyncFolderOnly: 'Enable Auto-Sync (folder only)',
      filesEnableAutoSync: 'Enable Auto-Sync',
      filesItemTypeFolder: 'Folder',
      filesItemTypeFile: 'File',
      filesMetaModified: 'Modified: {date}',
      filesColNameSp: 'Name',
      filesColTypeSp: 'Type',
      filesColSpFolder: 'folder',
      filesColSpFile: 'File',
      filesNoConnectedSites: 'No SharePoint sites yet',
      filesNoConnectedSitesHint: 'Add a site your organisation admin has connected, choose which libraries and Lists to use, then start an import.',
      filesAddSiteButton: 'Add site',
      filesAddSiteTitle: 'Add SharePoint site',
      filesAddSiteSubtitle: 'Sites your organisation admin has connected. Adding a site does not import anything.',
      filesScheduleSyncButtonTitle: 'Schedule sync',
      filesScheduleSyncTitle: 'Sync schedule',
      filesScheduleSyncSubtitle: 'Choose a date and time when all configured sites will sync automatically.',
      filesScheduleSyncDateLabel: 'Date',
      filesScheduleSyncTimeLabel: 'Time',
      filesScheduleSyncRepeatLabel: 'Repeat automatically',
      filesScheduleSyncRepeatDaysLabel: 'Every (days)',
      filesScheduleSyncSaveButton: 'Save schedule',
      filesScheduleSyncRemoveButton: 'Remove schedule',
      filesScheduleSyncNextRunLabel: 'Next sync:',
      filesScheduleSyncNoScheduleLabel: 'No schedule set',
      filesScheduleSyncPastDateError: 'Date and time must be in the future',
      filesScheduleSyncSelectDatePlaceholder: 'Select date',
      filesScheduleSyncSelectTimePlaceholder: 'Select time',
      filesScheduleSyncClearButton: 'Clear',
      filesScheduleSyncTodayButton: 'Today',
      filesAddSiteNoKnowledgeArea: 'No knowledge area',
      filesAddSiteEmpty: 'No more sites available — either all have already been added, or your organisation admin hasn\'t connected one matching your area yet.',
      filesAddSiteSubmit: 'Add site',
      filesSiteAddedToAgent: 'Site added to agent',
      filesSourcesUpdated: 'Sources updated',
      filesImportedBrowserTitle: 'Imported files — {siteName}',
      filesImportedBrowserRoot: 'All folders',
      filesImportedBrowserBack: 'Back',
      filesImportedBrowserEmpty: 'No files at this location',
      filesImportedBrowserFileCount: '{count} files',
      filesAddSiteNext: 'Next',
      filesAddSiteSourcesTitle: 'Sources in {siteName}',
      filesAddSiteSourcesSubtitle: 'Document libraries and Lists with attachments in this site. Only what you select can be imported.',
      filesAddSiteDocumentLibraries: 'Document libraries',
      filesAddSiteItemCount: '{count} items',
      filesAddSiteLists: 'Lists with attachments',
      filesAddSiteListsHint: 'Lists can\'t be selected yet. Importing them requires certificate-based authentication that hasn\'t been set up yet — this selection will be enabled once that\'s available.',
      filesAddSiteNoSources: 'No document libraries or Lists found on this site.',
      filesAddSiteSourcesFooterHint: 'Saving stores the selection. Nothing is imported until you start an import.',
      filesAddSiteSelectAtLeastOne: 'Select at least one source.',
      filesOpenSite: 'Open site',
      filesStatsFiles: 'Files',
      filesStatsFolders: 'Folders',
      filesStatsSubfolders: 'Subfolders',
      filesStatsLastSync: 'Last sync',
      filesStatusImported: 'Imported',
      filesSourcesChangedBadge: 'Sources changed',
      filesSourcesChangedHint: 'The selected sources have changed since the last import. Import again to apply the change.',
      filesEditSources: 'Edit sources',
      filesImportAgain: 'Import again',
      filesStatsFilesImported: 'files imported',
      filesStatsSourceCountSingular: '{count} source',
      filesStatsSourceCountPlural: '{count} sources',
      filesStatsLastImport: 'last import',
      filesStatsSkippedFailed: '{skipped} skipped · {failed} failed',
      filesViewImportReport: 'View import report',
      filesImportInfoBanner: 'Imports run only when you start them. Files added, changed or deleted in SharePoint afterwards are not picked up until the next import.',
      filesDeleteImportedData: 'Delete imported data',
      filesRemoveSiteFromAgent: 'Remove site from agent',
      filesSiteRemovedFromAgent: 'Site removed from agent',
      filesRemoveSiteError: 'Failed to remove site from agent',
      filesDeleteImportedDataConfirmTitle: 'Delete imported data?',
      filesDeleteImportedDataConfirmMessage: 'All files imported from {siteName} will be removed — embeddings, file entries and mappings. This action cannot be undone.',
      filesDeleteImportedDataError: 'Failed to delete imported data. Please try again later.',
      filesRemoveSiteConfirmTitle: 'Remove site?',
      filesRemoveSiteConfirmMessage: 'The site {siteName} will be removed from the agent and all data imported from it — documents, OCR results, embeddings and structure — will be deleted. This action cannot be undone.',
      filesRemoveSiteConfirmButton: 'Remove site',
      filesImportReportTitle: 'Import report — {siteName}',
      filesImportReportStarted: 'Started {date}',
      filesImportReportSources: 'Sources',
      filesImportReportFileTypes: 'File types',
      filesImportReportPageLimit: 'Page limit',
      filesImportReportMaxPages: 'Max {max}',
      filesImportReportNoLimit: 'No limit',
      filesImportReportImported: 'Imported',
      filesImportReportSkipped: 'Skipped',
      filesImportReportFailed: 'Failed',
      filesImportReportFileCount: '{count} files',
      filesImportReportDuration: 'Duration',
      filesImportReportStoppedNote: 'This import was stopped early. {count} file(s) were kept, the rest were skipped.',
      filesImportReportStoppedNoteWithTotal: 'This import was stopped early. {count} file(s) were kept in this run (the site now has {total} files in total).',
      filesImportReportDiscardedNote: 'This import was cancelled and discarded. Nothing from this run was kept.',
      filesImportReportBadgeStopped: 'Stopped',
      filesImportReportBadgeDiscarded: 'Discarded',
      embedErrorNoTextExtracted: 'No text extracted (likely a scanned PDF)',
      filesImportReportLastImport: 'Last import',
      filesImportReportNoDetailHint: 'These files were imported one at a time — there is no detailed report with file types, page limit, or a per-file history for this.',
      filesImportReportSkippedTitle: 'Skipped by your import settings',
      filesImportReportSkippedHint: 'These were excluded on purpose. To include them, change the settings and import again.',
      filesImportReportFailedTitle: 'Failed',
      filesImportReportFailedHint: 'These could not be processed. Retrying reads only these files again.',
      filesDeletionReportTitle: 'Deletion report — {siteName}',
      filesImportReportRemoved: 'Removed',
      filesDeletionReportRemovedTitle: 'Removed files',
      filesDeletionReportFailedTitle: 'Failed',
      filesDeletionReportFailedHint: 'These files could not be deleted.',
      filesRetryFailedFiles: 'Retry failed files ({count})',
      filesRetryReportNotCurrent: 'This report no longer describes the site\'s current state and can no longer be retried.',
      filesRetryImportRunning: 'An import is already running for this site. Please wait for it to finish.',
      filesRetryFailedError: 'Retry failed. Please try again later.',
      filesStartImportTitle: 'Start import',
      filesStartImportFileTypes: 'File types',
      filesStartImportPageLimit: 'Page limit per file',
      filesStartImportNoLimit: 'No limit',
      filesStartImportPageLimitHint: 'Files with more pages are skipped and listed in the import report.',
      filesStartImportReplaceWarningBold: 'This site was imported on {date}.',
      filesStartImportReplaceWarningRest: 'Importing again replaces the {count} documents, OCR results and embeddings currently stored for it.',
      filesStartImportSourcesAddedLabel: 'Sources added:',
      filesStartImportSourcesRemovedLabel: 'Sources removed (their files will be deleted):',
      filesStartImportInfoStatic: 'The import reads the selected sources completely. Files deleted or changed in SharePoint afterwards stay as they are here until you import again.',
      filesStartImportSubmit: 'Start import',
      filesImportCompleted: 'Import completed for {site}',
      filesImportFailed: 'Import failed',
      filesImportRunning: 'Import running',
      filesImportReconnecting: 'Reconnecting…',
      filesImportProcessedOf: '{processed} of {total} files processed',
      filesImportCancel: 'Cancel',
      filesImportCancelling: 'Cancelling…',
      filesImportCancelTitle: 'Cancel import?',
      filesImportCancelMessage: 'Do you want to keep the files already imported and only stop the rest, or undo the entire import so far?',
      filesImportCancelStop: 'Stop (keep files so far)',
      filesImportCancelDiscard: 'Discard all',
      filesImportCancelBack: 'Back',
      filesImportCancelError: 'Failed to cancel import',
      filesImportCancelProcessing: 'Processing…',
      filesEmptyInFolder: 'No files in this folder',
      filesEmptyFolders: 'No folders found',
      filesEmptyLocalHint: 'Click "Add folder" to create a new folder with files.',
      filesEmptySharePointHint: 'Import files from SharePoint to display them here.',
      filesCreatedOn: 'Created: {date}',
      filesModifiedOn: 'Modified: {date}',
      filesColFileName: 'File name',
      filesColFolderName: 'Folder name',
      filesColUploadedAt: 'Uploaded on',
      filesColSize: 'Size',
      filesColUsedIn: 'Used in',
      filesColFiles: 'Files',
      filesColCreatedAt: 'Created on',
      filesColLastModified: 'Last modified',
      filesSourceDeleted: 'Source deleted',
      filesProjectSingular: 'AI request',
      filesProjectPlural: 'AI requests',
      filesAddToFolderTitle: 'Add files to "{folderName}"',
      filesCreateNewFolderTitle: 'Create new folder',
      filesAddToFolderSubtitle: 'Upload new files to this folder.',
      filesCreateNewFolderSubtitle: 'Create a folder and add files.',
      filesFolderNameLabel: 'Folder name *',
      filesFolderNamePlaceholder: 'e.g. contracts, templates, reports...',
      filesAddFilesLabel: 'Add files *',
      filesDragOrClick: 'Drag files here or click to select',
      filesSupportedTypes: 'PDF, DOC, DOCX (multiple files possible)',
      filesSelectedFiles: 'Selected files ({count})',
      filesRemoveAll: 'Remove all',
      filesCancel: 'Cancel',
      filesUploadingStatus: 'Uploading...',
      filesCreatingStatus: 'Creating...',
      filesUpload: 'Upload',
      filesCreateFolder: 'Create folder',
      filesUploadProgressUploading: 'Files are being uploaded...',
      filesUploadProgressCreating: 'Folder is being created...',
      filesFileOfTotal: 'File {current} of {total}',
      filesPreparing: 'Preparing...',
      filesPercentDone: '{percent}% completed',
      filesDeleteFolderTitle: 'Delete folder',
      filesDeleteFileTitle: 'Delete file',
      filesDeleteFolderMessage: 'Are you sure you want to delete the folder "{name}" with {count} file(s)? This action cannot be undone.',
      filesDeleteSpItemMessage: 'Are you sure you want to delete "{name}"? This action cannot be undone.',
      filesDeleteFileMessage: 'Are you sure you want to delete "{name}"? This action cannot be undone.',
      filesDeleting: 'Deleting...',
      filesFilterTitle: 'Filter',
      filesFilterFileTypeLabel: 'File type',
      filesFilterOptLocal: 'Local File',
      filesFilterOptSharePoint: 'SharePoint File',
      filesFilterSortByLabel: 'Sort by',
      filesSortNewest: 'Newest first',
      filesSortOldest: 'Oldest first',
      filesSortMostProgress: 'Most progress',
      filesSortLeastProgress: 'Least progress',
      filesResetAll: 'Reset all',
      filesApply: 'Apply',
      // TaskShortcuts (Shortcuts tab)
      shortcutsTabEmoji: 'Emoji',
      shortcutsTabCustom: 'Custom',
      shortcutsCustomEmojiLabel: 'Enter any emoji:',
      shortcutsCustomEmojiPlaceholder: 'Paste emoji here...',
      shortcutsUseButton: 'Use',
      shortcutsModalEditTitle: 'Edit shortcut',
      shortcutsModalAddTitle: 'Add new shortcut',
      shortcutsNameLabel: 'Shortcut name',
      shortcutsNamePlaceholder: 'e.g. Simplify language',
      shortcutsIconLabel: 'Icon',
      shortcutsIconChange: 'Click to change icon',
      shortcutsDescriptionLabel: 'Description',
      shortcutsDescriptionPlaceholder: 'Describe what this shortcut does...',
      shortcutsOrderLabel: 'Display order',
      shortcutsActiveLabel: 'Active',
      shortcutsCancel: 'Cancel',
      shortcutsSaving: 'Saving...',
      shortcutsSaveButton: 'Save shortcut',
      shortcutsUpdateSuccess: 'Shortcut updated successfully',
      shortcutsCreateSuccess: 'Shortcut created successfully',
      shortcutsSaveError: 'Error saving shortcut',
      shortcutsDeleteSuccess: 'Shortcut deleted successfully',
      shortcutsDeleteError: 'Error deleting shortcut',
      shortcutsOrderSaveSuccess: 'Order saved successfully',
      shortcutsOrderSaveError: 'Error saving order',
      shortcutsSelectTaskHint: 'Please select a task to manage shortcuts',
      shortcutsHeaderTitle: 'Shortcuts',
      shortcutsHeaderSubtitle: 'Create and manage reusable AI shortcuts to quickly transform content during analysis.',
      shortcutsListTitle: 'Shortcut list',
      shortcutsAddButton: 'Add shortcut',
      shortcutsEmptyTitle: 'No shortcuts available',
      shortcutsEmptyHint: 'Click "Add shortcut" to create your first shortcut.',
      shortcutsInactiveBadge: 'Inactive',
      shortcutsEditTooltip: 'Edit',
      shortcutsDeleteTooltip: 'Delete',
      shortcutsInfoTitle: 'How shortcuts work',
      shortcutsInfoText: 'Shortcuts appear below AI responses in the analysis area. When you click one, the system reprocesses the response with the shortcut prompt and shows the result next to the original answer. This lets users quickly get translations, simplifications, summaries, or other transformations you configure.',
      shortcutsDeleteModalTitle: 'Delete shortcut',
      shortcutsDeleteModalMessage: 'Are you sure you want to delete "{name}"? This action cannot be undone.',
      // ProjectOverviewBuilder (Project overview tab)
      overviewAdded: 'Added',
      overviewAdd: 'Add',
      overviewNoAgent: 'No agent selected.',
      overviewEnableTitle: 'Enable AI request overview',
      overviewEnableSubtitle: 'When off, the AI Request Overview tab is hidden in the AI request view for this agent, and users go directly to chat.',
      overviewAvailableWidgets: 'Available widgets',
      overviewAvailableSubtitle: 'Browse and add widgets to customize the AI request overview layout.',
      overviewPreviewTitle: 'AI request overview preview',
      overviewPreviewSubtitle: 'This layout will be applied to all AI requests using this agent.',
      overviewUnsavedChanges: 'Unsaved changes',
      overviewCancel: 'Cancel',
      overviewSaving: 'Saving…',
      overviewSaveLayout: 'Save layout',
      overviewEmpty: 'No widgets added. Add widgets from the list above.',
    },

    projectSetup: {
      title: 'AI request setup',
      subtitle: 'Configure fields that will be shown in the AI request creation form.',
      formBuilderTitle: 'Form builder',
      addField: 'Add field',
      emptyState: 'No fields defined yet. Click "Add field" to start.',
      saveChanges: 'Save changes',
      savedSuccess: 'Fields saved successfully',
      saveError: 'Failed to save fields',
      moveUp: 'Move up',
      moveDown: 'Move down',
      addFieldTitle: 'Add field',
      editFieldTitle: 'Edit field',
      fieldTypeLabel: 'Field type',
      fieldLabelLabel: 'Field label',
      fieldLabelPlaceholder: 'e.g. AI request name',
      requiredLabel: 'Required field',
      requiredHelp: 'Users must fill this field to proceed.',
      requiredError: 'Please fill all required fields.',
      helperLabel: 'Helper text',
      helperPlaceholder: 'Small description shown to users',
      defaultValueLabel: 'Default value',
      defaultValuePlaceholder: 'Optional default value',
      optionsLabel: 'Options',
      optionPlaceholder: 'Add an option',
      addOption: 'Add',
      saveField: 'Save field',
      fieldTypeText: 'Text input',
      fieldTypeTextarea: 'Multi-line text',
      fieldTypeNumber: 'Number',
      fieldTypeDate: 'Date',
      fieldTypeDropdown: 'Dropdown',
      fieldTypeImage: 'Image upload',
      fieldTypeFile: 'File upload',
      directChatModeLabel: 'Direct Chat Mode',
      directChatModeEnabledBadge: 'Enabled',
      directChatModeDesc: 'When a user selects this agent, they are taken straight into a live chat — no session setup required.',
      fieldProjectName: 'AI Request Name',
      fieldProjectNameDesc: 'Text field for the AI request name — saved as AI request title.',
      fieldProjectImage: 'AI Request Image',
      fieldProjectImageDesc: 'Image upload for the AI request thumbnail.',
      fieldUploadMethods: 'Upload Methods',
      fieldUploadMethodsDesc: 'Configure how users can upload documents.',
      uploadSourceComputer: 'Computer',
      uploadSourceSharePoint: 'SharePoint',
      uploadSourceBoth: 'Both',
      uploadSourceLabel: 'Upload source',
      uploadSourceHelp: 'Choose how users will provide files for this field.',
      sourceComputerTitle: 'Upload from Computer',
      sourceComputerSub: 'User uploads files directly from their local device.',
      sourceSharePointTitle: 'SharePoint Folder',
      sourceSharePointSub: 'User selects a folder from a connected SharePoint site.',
      sourceBothTitle: 'Both Methods',
      sourceBothSub: 'Let user choose between local upload or SharePoint.',
      imageUploadSettings: 'Image upload settings',
      acceptedFormatsLabel: 'Accepted formats',
      acceptedFormatsHelp: 'Select which image formats users can upload.',
      multipleLabel: 'Allow multiple images',
      multipleHelp: 'User can upload more than one image.',
      maxImageCountLabel: 'Max image count',
      imagesMax: 'Images max',
      imageConstraintsLabel: 'Image constraints',
      optional: 'optional',
      aspectRatioLabel: 'Aspect ratio',
      arAny: 'Any', arAnySub: 'No constraint',
      ar11: '1:1', ar11Sub: 'Square',
      ar43: '4:3', ar43Sub: 'Standard',
      ar169: '16:9', ar169Sub: 'Widescreen',
      ar32: '3:2', ar32Sub: 'Photo',
      minDimensionsLabel: 'Minimum dimensions',
      widthPx: 'Width (px)',
      heightPx: 'Height (px)',
      outputQualityLabel: 'Output quality',
      qOriginal: 'Original', qOriginalSub: 'No compression',
      qHigh: 'High', qHighSub: '~80% quality',
      qMedium: 'Medium', qMediumSub: '~60% quality',
      qLow: 'Low', qLowSub: '~40% quality',
      maxFileSizeLabel: 'Max file size',
      uploadImageBtn: 'Upload image',
      uploadImagesBtn: 'Upload {remaining} more image(s)',
      errMaxCount: 'Maximum {count} images allowed.',
      errFormat: 'Format not allowed: {name}',
      errSize: '{name} exceeds {mb} MB.',
      errMinDim: '{name}: minimum {w}×{h} px required.',
      errAspect: '{name}: aspect ratio {ratio} required.',
    },

    widgets: {
      notGenerated: 'Not yet generated.',
      noSubmissionDate: 'No submission date extracted',
      documents: 'Documents',
      titles: {
        summary: 'AI Summary',
        checklist_progress: 'Checklist Progress',
        location: 'AI Request Location',
        analysis_score: 'Analysis Score',
        document_count: 'Document Count',
        participants: 'Participants',
        submission_date: 'Deadline Countdown',
      },
    },

    settings: {
      title: 'Settings',
      tabAdministrator: 'Administrator',
      tabOrganization: 'Organization',
      tabLanguage: 'Language',
      tabManageTasks: 'Manage Tasks',
    },

    organization: {
      // Toolbar
      searchPlaceholder: 'Search',
      addButton: 'New organization',
      // Table headers
      columnLogo: 'Logo',
      columnName: 'Organization name',
      columnDescription: 'Description',
      columnSharePoint: 'SharePoint',
      columnColor: 'Color',
      columnSubscription: 'Subscription',
      // Cell values
      logoFallback: 'Logo',
      spConnected: 'Connected',
      spError: 'Error',
      spNotConnected: 'Not connected',
      subscriptionActive: 'Active',
      subscriptionInactive: 'Inactive',
      // Actions
      actionManage: 'Manage',
      actionEdit: 'Edit organization',
      actionDelete: 'Delete organization',
      // Pagination
      pageOf: 'Page {current} of {total}',
      paginationPrev: 'Previous',
      paginationNext: 'Next',
      // Create/Edit Modal
      modalCreateTitle: 'Create new organization',
      modalEditTitle: 'Edit organization',
      logoLabel: 'Organization logo',
      logoAlt: 'Logo',
      logoNone: 'No logo',
      logoUpload: 'Upload logo',
      nameLabel: 'Organization name *',
      namePlaceholder: 'Enter organization name',
      descriptionLabel: 'Description (optional)',
      descriptionPlaceholder: 'Short description shown to users',
      footerAddressLabel: 'Footer address (optional)',
      footerAddressPlaceholder: 'Company AG\nStreet 1, City\nPhone 000 000 00 00\nEmail info@company.com',
      footerAddressHelp: "Shown in the footer of this organization's generated Word documents. Each line appears as its own line.",
      primaryColorLabel: 'Primary color',
      uploadingState: 'Uploading...',
      savingState: 'Saving...',
      submitCreate: 'Create organization',
      submitSave: 'Save',
      // Delete modal
      deleteTitle: 'Delete organization',
      deleteMessage: 'Are you sure you want to delete the organization "{name}"?',
      // Subscription assignment modal
      subModalTitle: 'Assign subscription to "{name}"',
      subModalSelectLabel: 'Select subscription',
      subModalSelectPlaceholder: 'Select subscription...',
      subModalNone: 'No subscription',
      subModalOptionInfo: '{name} - {tasks} tasks, {pages} pages',
      // Toasts
      toastCreated: 'Organization created successfully',
      toastUpdated: 'Organization updated successfully',
      toastDeleted: 'Organization deleted',
      toastSaveError: 'Failed to save',
      toastDeleteError: 'Failed to delete',
      toastSubAssigned: 'Subscription assigned successfully',
      toastSubAssignError: 'Failed to assign subscription',
    },

    manageOrganization: {
      // Breadcrumb / header
      breadcrumbSettings: 'Settings',
      breadcrumbManage: 'Manage organization',
      loading: 'Loading...',
      // Summary card
      summaryDepartments: 'User groups',
      summaryUsers: 'Users',
      summarySubscription: 'Subscription',
      summaryActive: 'Active',
      summaryInactive: 'Inactive',
      // Tabs
      tabDepartments: 'User groups',
      tabSubscriptions: 'Subscriptions',
      tabAgentOrder: 'Agent order',
      tabSharepoint: 'Link SharePoint',
      // Departments tab
      deptSearchPlaceholder: 'Search',
      deptAddButton: 'Add user group',
      deptEmptyTitle: 'No user groups yet',
      deptEmptyHint: 'Create your first user group.',
      noResultsTitle: 'No results found',
      noResultsHint: 'Try different search terms.',
      deptColumnName: 'User group',
      deptColumnDescription: 'Description',
      deptColumnCreated: 'Created',
      deptActionEdit: 'Edit user group',
      deptActionDelete: 'Delete user group',
      // Department modal
      deptModalCreateTitle: 'Create user group',
      deptModalEditTitle: 'Edit user group',
      deptModalNameLabel: 'User group name*',
      deptModalNamePlaceholder: 'e.g. Sales',
      deptModalDescriptionLabel: 'Description (optional)',
      deptModalDescriptionPlaceholder: 'Short description of this user group’s purpose.',
      deptModalSubmitCreate: 'Create user group',
      deptModalSubmitSave: 'Save',
      deptModalCreating: 'Creating...',
      deptModalSaving: 'Saving...',
      // Department delete
      deptDeleteTitle: 'Delete user group',
      deptDeleteMessage: 'Deleting this user group will remove its assignment from agents. Agents will remain active but will no longer belong to this user group.',
      // Department toasts
      deptToastCreated: 'User group created successfully',
      deptToastUpdated: 'User group updated successfully',
      deptToastDeleted: 'User group deleted successfully',
      deptToastSaveError: 'Failed to save user group',
      deptToastDeleteError: 'Failed to delete user group',
      // Subscriptions tab
      subSearchPlaceholder: 'Search',
      subAddButton: 'New subscription',
      subEmptyTitle: 'No subscriptions yet',
      subEmptyHint: 'Create your first subscription.',
      subColumnName: 'Subscription name',
      subColumnDescription: 'Description',
      subActionViewCredits: 'View credit usage',
      subActionViewUsage: 'View usage',
      subActionEdit: 'Edit',
      subActionDelete: 'Delete',
      // Subscription modal
      subModalCreateTitle: 'Create new subscription',
      subModalEditTitle: 'Edit subscription',
      subModalInfoSection: 'Subscription information',
      subModalNameLabel: 'Subscription name *',
      subModalNamePlaceholder: 'e.g. xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
      subModalDescriptionLabel: 'Description (optional)',
      subModalDescriptionPlaceholder: 'Enter description',
      subModalOrgLabel: 'Organization *',
      subModalOrgPlaceholder: 'Select organization...',
      subModalNoOrgs: 'No organizations available',
      subModalUsageSection: 'Usage settings',
      subModalUsageHint: 'Control the usage behavior for this subscription.',
      subModalTransferableLabel: 'Carry unused credits over to next month',
      subModalTransferableHint: 'When enabled, unused credits are carried over to the next billing period.',
      subModalMaxPagesLabel: 'Maximum pages per session',
      subModalAgentLimitsSection: 'Agent limits',
      subModalAgentLimitsHint: 'Set the two usage settings for each AI agent. Leave blank for unlimited.',
      subModalAgentColumn: 'Agent',
      subModalMaxRunsColumn: 'Max runs',
      subModalMaxPagesColumn: 'Max pages',
      subModalStandardPagesColumn: 'Standard-file pages per month',
      subModalUploadPagesColumn: 'Upload pages per session',
      subModalUnlimitedPlaceholder: 'Unlimited',
      subModalNoAgents: 'No agents found for this organization.',
      subModalSaving: 'Saving...',
      subModalSubmit: 'Save',
      subModalOcrSection: 'OCR page limits',
      subModalOcrHint: 'Global limits for OCR processing. Leave blank for no limit.',
      subModalOcrDaily: 'Daily (pages)',
      subModalOcrWeekly: 'Weekly (pages)',
      subModalOcrMonthly: 'Monthly (pages)',
      subModalOcrPlaceholder: 'No limit',
      // Subscription delete
      subDeleteTitle: 'Delete subscription',
      subDeleteMessage: 'Are you sure you want to delete the subscription "{name}"? All associated limits will be removed as well.',
      // Subscription toasts
      subToastCreated: 'Subscription created successfully',
      subToastUpdated: 'Subscription updated successfully',
      subToastDeleted: 'Subscription deleted successfully',
      subToastSaveError: 'Failed to save subscription',
      subToastDeleteError: 'Failed to delete subscription',
      // Credit usage view
      creditBackButton: 'Subscriptions',
      creditTitle: 'Credit usage',
      creditSubtitle: 'Current standard-file pages and upload setting per agent.',
      creditGrantButton: 'Grant temporary credits',
      creditColumnAgent: 'Agent',
      creditColumnRunsUsed: 'Runs used',
      creditColumnTempRuns: 'Temp. runs granted',
      creditColumnPagesUsed: 'Pages used',
      creditColumnTempPages: 'Temp. pages granted',
      creditColumnStandardPages: 'Standard-file pages (this month)',
      creditUnlimitedLabel: '(unlimited)',
      creditTaskFallback: 'Task {id}',
      creditBreakdown: '({monthly} monthly + {temp} temp.)',
      creditEmptyTitle: 'No agent limits',
      creditEmptyHint: 'No agent limits are assigned to this subscription.',
      // Grant credits modal
      grantTitle: 'Grant temporary credits',
      grantSubscriptionLabel: 'Subscription',
      grantAgentLabel: 'Agent *',
      grantAgentPlaceholder: 'Select agent...',
      grantNoAgents: 'No agents available',
      grantRunsLabel: 'Temporary runs',
      grantRunsHint: 'Grant additional runs to this agent.',
      grantPagesLabel: 'Temporary pages',
      grantPagesHint: 'Grant additional pages to this agent.',
      grantNoteTitle: 'Note',
      grantNoteBody: 'Temporary credits do not change subscription limits and are consumed first, before regular allocations are used.',
      grantSubmit: 'Grant credits',
      grantSaving: 'Granting...',
      grantToastSuccess: 'Temporary credit granted successfully',
      grantToastError: 'Failed to grant credit',
      // SharePoint tab
      spSearchPlaceholder: 'Search',
      spEditButton: 'Edit SharePoint',
      spConnectSiteButton: 'Connect site',
      spConnectButton: 'Connect SharePoint',
      spEmptyTitle: 'No connected sites',
      spEmptyHint: 'Create an AI request to upload files and run analyses.',
      spColumnSiteName: 'Site name',
      spColumnUrl: 'URL',
      spColumnStatus: 'Status',
      spColumnSyncInterval: 'Sync interval',
      spColumnSite: 'Site',
      spColumnKnowledgeArea: 'Knowledge area',
      spColumnUsedByAgents: 'Used by agents',
      spAllKnowledgeAreas: 'All knowledge areas',
      spUsedByAgentsSingular: '{count} agent',
      spUsedByAgentsPlural: '{count} agents',
      spNotUsed: 'Not used',
      spCanBeDisconnected: 'Can be disconnected',
      spInfoBannerTitle: 'Connecting a site grants access only.',
      spInfoBannerBody: 'Nothing is scanned, monitored or imported on its own — no scheduled runs, no change notifications, no OCR, no embeddings. Content is imported only when an agent admin starts an import, and it stays at the state of that import until someone starts another one.',
      spStatusSynced: 'Synced',
      spStatusError: 'Error',
      spStatusPending: 'Pending',
      spStatusConnected: 'Connected',
      spStatusReadOnlyAccess: 'Read-only access',
      spIntervalMinutes: '{count} min.',
      spActionEditInterval: 'Edit sync interval',
      spActionDelete: 'Delete',
      spActionViewDetails: 'View site details',
      spActionEditNameArea: 'Edit name and knowledge area',
      spActionCheckAccessNow: 'Check access now',
      spActionCopySiteId: 'Copy site ID',
      spActionDisconnect: 'Disconnect site',
      spToastAccessConfirmed: 'Access confirmed — read-only, no content read',
      spToastAccessFailed: 'Access check failed: {error}',
      spToastSiteIdCopied: 'Site ID copied',
      spToastDisconnectBlocked: 'Cannot disconnect — still used by: {agents}',
      spConnLoading: 'Loading...',
      spConnEditTitle: 'Edit SharePoint',
      spConnCreateTitle: 'Connect SharePoint',
      spConnTenantIdLabel: 'Tenant ID',
      spConnTenantIdPlaceholder: 'e.g. xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
      spConnClientIdLabel: 'Client ID',
      spConnClientIdPlaceholder: 'Enter Azure App Registration Client ID',
      spConnClientSecretLabel: 'Client Secret',
      spConnClientSecretPlaceholderEdit: '••••••••',
      spConnClientSecretPlaceholderCreate: 'Enter Azure App Client Secret',
      spConnSecurityNote: 'These credentials are stored securely and used only to connect to your SharePoint site.',
      spConnDisconnectButton: 'Disconnect SharePoint',
      spConnSavingButton: 'Saving...',
      spConnSaveButton: 'Save connection',
      spConnDisconnectConfirmTitle: 'Really disconnect SharePoint?',
      spConnDisconnectConfirmMessage: 'All connected sites and their mappings will be removed. Already-imported files and embeddings are kept, but nothing more can be synced from SharePoint until a new connection is established.',
      spConnDisconnectingButton: 'Disconnecting...',
      spConnDisconnectConfirmButton: 'Yes, disconnect',
      spErrorInvalidSecret: 'The SharePoint client secret is invalid or has expired. Please update the connection credentials.',
      spErrorAppNotFound: 'The Azure app registration could not be found for this tenant. Please check the Tenant ID and Client ID.',
      spErrorTenantNotFound: 'The Tenant ID could not be found. Please check it and try again.',
      spErrorAuthFailed: 'SharePoint sign-in failed. Please check the connection credentials.',
      spErrorPermissionDenied: 'Access denied. The Azure app is missing the required SharePoint permissions.',
      spErrorSiteNotFound: 'The SharePoint site could not be found.',
      spErrorGeneric: 'Connection to SharePoint failed. Please check the connection settings.',
      spDetailsSubtitle: 'Stored connection data. Nothing here is refreshed in the background.',
      spDetailsSetInPlatform: 'Set in this platform',
      spDetailsKnowledgeArea: 'Knowledge area',
      spDetailsSharePointName: 'SharePoint name',
      spDetailsIdentifiesSite: 'Identifies the site',
      spDetailsKeyBadge: 'KEY',
      spDetailsAccessScope: 'Read-only · Sites.Selected',
      spDetailsConnectedBy: 'Connected by',
      spDetailsConnectedOn: 'Connected on',
      spDetailsAccessLastChecked: 'Access last checked',
      spDetailsCheckedByFormat: '{datetime} by {name}',
      spNeverChecked: 'Never checked',
      spDetailsCheckAccessNote: 'Access checks verify the permission grant and refresh the SharePoint name and URL. They read no content.',
      spEditSiteSaveError: 'Failed to save changes',
      spAgentsModalViewTitle: 'Agents using this site',
      spAgentsModalViewSubtitle: 'Open any of these agents directly.',
      spAgentsModalBlockedTitle: 'Site is still in use',
      spAgentsModalBlockedSubtitle: 'Remove this site from the agents below, then disconnect it.',
      spAgentsModalWarningSingular: '{siteName} is assigned to {count} agent. Disconnecting now would break its imported content.',
      spAgentsModalWarningPlural: '{siteName} is assigned to {count} agents. Disconnecting now would break their imported content.',
      spAgentsModalFileCountSingular: '{count} file',
      spAgentsModalFileCountPlural: '{count} files',
      spAgentsModalImportedOn: 'imported {date}',
      spAgentsModalOpenAgent: 'Open agent',
      spAgentsModalDeleteNote: 'Deleting an agent releases its site assignments automatically.',
      spAgentsModalGoToFirst: 'Go to first agent',
      // Connect site modal
      connectSiteTitle: 'Connect site',
      connectSiteUrlPlaceholder: '',
      connectSiteInvalidIdFormat: 'Invalid Site ID format. Expected: hostname.sharepoint.com,siteCollectionGuid,webGuid',
      connectSiteAdd: 'Add',
      connectSiteSaveCredentials: 'Save credentials',
      connectSiteSubtitle: 'Only sites your Microsoft 365 admin has granted read access to can be connected.',
      connectSiteStep1: 'Enter site ID',
      connectSiteStep2: 'Confirm and name',
      connectSiteIdLabel: 'Site ID',
      connectSiteIdHint: 'Each site needs its own ID and its own access grant — including sites that sit under a hub site.',
      connectSiteHelpTitle: 'Where do I find the site ID?',
      connectSiteHelpStep1: 'Open the SharePoint site in your browser.',
      connectSiteHelpStep2: 'Add /_api/site/id to the site address and press Enter, for example https://contoso.sharepoint.com/sites/Finance/_api/site/id.',
      connectSiteHelpStep3: 'Copy the GUID from the response and put it after the hostname, separated by a comma.',
      connectSiteCheckAccess: 'Check access',
      connectSiteUrlColumn: 'URL',
      connectSiteAccessColumn: 'Access',
      connectSiteAccessConfirmed: 'Read-only · confirmed',
      connectSiteDisplayNameLabel: 'Display name',
      connectSiteDisplayNameHint: 'Shown to agent admins and in chat.',
      connectSiteKnowledgeAreaLabel: 'Knowledge area (optional)',
      connectSiteKnowledgeAreaSelectPrompt: 'Select knowledge area',
      connectSiteKnowledgeAreaEmpty: 'No knowledge areas yet — create one below.',
      connectSiteKnowledgeAreaHint: 'Groups related sites in this list and in agent settings. Has no effect on access or imports.',
      knowledgeAreaCreateNew: 'Create new Knowledge Area',
      knowledgeAreaManageButton: 'Manage Knowledge Areas',
      knowledgeAreaManagerTitle: 'Knowledge Areas',
      knowledgeAreaManagerSubtitle: 'Knowledge Areas organize SharePoint sites and are independent of User Groups — every agent can see every Knowledge Area.',
      knowledgeAreaNameLabel: 'Knowledge Area name',
      knowledgeAreaNamePlaceholder: 'e.g. IT, Finance, HR',
      knowledgeAreaEnterHint: 'Press Enter or click "Create" to add it.',
      knowledgeAreaCreateButton: 'Create',
      knowledgeAreaEmptyError: 'Please enter a name',
      knowledgeAreaExistsError: 'This Knowledge Area already exists',
      knowledgeAreaCreateError: 'Failed to create knowledge area',
      connectSiteConsentLabel: 'I understand that once this site is imported into an agent, everyone with access to that agent can retrieve any imported file — including files that only some people can open in SharePoint.',
      connectSiteSubmit: 'Connect site',
      connectSiteDuplicateSite: 'This site is already connected: {name}',
      // Sync interval modal
      syncIntervalTitle: 'Sync interval',
      syncIntervalLabel: 'Interval (minutes)',
      syncIntervalHint: 'Recommended: 15 minutes for normal sites, 5 minutes for frequently updated sites',
      syncIntervalSaving: 'Saving...',
      syncIntervalSubmit: 'Save',
      // Delete site modal
      deleteSiteTitle: 'Disconnect site',
      deleteSiteMessage: 'Are you sure you want to disconnect "{name}"? This action cannot be undone.',
      deleteSiteDeleting: 'Disconnecting...',
    },

    users: {
      // Page toolbar
      pageSearchPlaceholder: 'Search',
      pageFilterButton: 'Filter',
      // Table column headers
      columnFullName: 'Full name',
      columnEmail: 'Email',
      columnOrganization: 'Organization',
      columnUserGroup: 'User Group',
      columnCreatedAt: 'Created at',
      columnRole: 'Role',
      columnAuthProvider: 'Provider',
      authProviderEmail: 'Email',
      authProviderMicrosoft: 'Microsoft',
      authProviderLocal: 'Local',
      // Role badges
      roleSuperAdmin: 'Super Admin',
      roleAdmin: 'Admin',
      roleNormal: 'Normal',
      roleNoLicense: 'No license',
      // Row action menu
      actionAssignRole: 'Assign role',
      actionAssignOrganization: 'Assign Orga + User Group',
      actionAssignDepartment: 'Assign User Group',
      actionCheckActivity: 'Check activity',
      actionResendInvite: 'Resend invitation',
      actionDeactivateUser: 'Deactivate User',
      actionActivateUser: 'Activate User',
      statusInactive: 'Inactive',
      deactivateConfirmTitle: 'Deactivate User',
      deactivateConfirmMessage: 'Are you sure you want to deactivate {name}?',
      activateConfirmTitle: 'Activate User',
      activateConfirmMessage: 'Are you sure you want to activate {name}?',
      toastUserDeactivated: 'User has been deactivated',
      toastUserActivated: 'User has been activated',
      toastStatusChangeError: 'Failed to change user status',
      activityModalTitle: 'Login activity',
      activityColMethod: 'Method',
      activityColStatus: 'Status',
      activityColIp: 'IP address',
      activityColTime: 'Time',
      activityStatusSuccess: 'Success',
      activityStatusFailed: 'Failed',
      activityEmpty: 'No login attempts found.',
      activityError: 'Could not load activity.',
      activityMethodPassword: 'Password',
      activityMethodOtp: 'Email code',
      activityMethodMicrosoft: 'Microsoft',
      activityMethodMfa: 'Two-factor code',
      actionResetMfa: "Reset 2FA",
      activityMethodMfaRecovery: "Recovery code",
      activityMethodMfaReset: "2FA reset",
      activityMethodMfaSetup: "2FA set up",
      activityPerformedBy: "by {email}",
      resetMfaConfirmMessage: "Two-factor authentication will be removed for {name}. They will have to set up their authenticator app again at their next login.",
      resetMfaConfirmTitle: "Reset two-factor authentication",
      toastMfaReset: "Two-factor authentication has been reset",
      // Filter modal
      filterModalTitle: 'Filter',
      filterUserGroupLabel: 'User Group',
      filterUserGroupPlaceholder: 'Select user groups',
      filterOrganizationLabel: 'Organisation',
      filterOrganizationPlaceholder: 'Select organisations',
      filterRoleLabel: 'Role',
      filterSortByLabel: 'Sort By',
      filterSortNewest: 'Newest first',
      filterSortOldest: 'Oldest first',
      filterSortNameAsc: 'Name (A–Z)',
      filterSortNameDesc: 'Name (Z–A)',
      filterClearAll: 'Clear all',
      filterApply: 'Apply',
      filterNoOptions: 'No options available',
      // Status
      errorPrefix: 'Error: {error}',
      // Role modal
      roleModalTitle: 'Assign role',
      roleModalNameLabel: 'Name:',
      roleModalCurrentRoleLabel: 'Current role:',
      roleModalSelectLabel: 'Select new role',
      roleModalSelectPlaceholder: 'Select new role',
      roleModalNoOtherRoles: 'No other roles available',
      roleModalSaving: 'Saving...',
      // Organization modal
      orgModalTitle: 'Assign organization',
      orgModalSelectLabel: 'Select organization',
      orgModalNoOrganization: 'No organization',
      orgModalAssignUserGroupLabel: 'Assign User Group',
      orgModalAssignUserGroupPlaceholder: 'Assign User Group',
      orgModalSaving: 'Saving...',
      userGroupMoreLabel: '+{count} more',
      // Toasts
      toastRoleUpdated: 'Role updated successfully',
      toastRoleUpdateError: 'Failed to update role',
      toastOrgAssigned: 'Organization assigned successfully',
      toastOrgAssignError: 'Failed to assign organization',
      // Invite modal
      inviteButtonLabel: 'Invite user',
      inviteModalTitle: 'Invite user',
      inviteModalSubtitle: 'The user will receive an invitation email and can register afterwards.',
      inviteModalEmailLabel: 'Email address',
      inviteModalRoleLabel: 'Role',
      inviteModalRolePlaceholder: 'Select a role',
      inviteModalSendButton: 'Send invitation',
      inviteModalSending: 'Sending...',
      inviteModalSuccessToast: 'Invitation sent successfully',
      inviteModalErrorToast: 'Failed to send invitation',
      resendInviteSuccessToast: 'Invitation resent',
      resendInviteErrorToast: 'Failed to resend invitation',
      inviteModalFieldRequired: 'Please provide an email address and role',
      inviteEmailAlreadyExists: 'This user already exists in the system',
      addUserMenuLabel: 'Add user',
      inviteUserMenuLabel: 'Invite user',
      newUserDefaultsAllGroups: 'All groups',
      newUserDefaultsTitle: 'Defaults for new users',
      newUserDefaultsSubtitle: 'Organization, role and groups assigned on first Microsoft sign-in.',
      newUserDefaultsButton: 'Defaults',
      addUserModalTitle: 'Add new user',
      addUserModalSubtitle: 'User will be created directly and assigned to the organization.',
      addUserFullnameLabel: 'Full name',
      addUserOrganizationLabel: 'Organization',
      addUserGenderLabel: 'Gender',
      addUserGenderPlaceholder: 'Please select',
      addUserPasswordLabel: 'Password',
      addUserRoleLabel: 'Role',
      addUserRolePlaceholder: 'Select role',
      addUserSubmitButton: 'Create user',
      addUserSubmitting: 'Creating...',
      addUserSuccessToast: 'User created successfully',
      addUserErrorToast: 'Failed to create user',
      addUserFieldRequired: 'Please fill in all fields',
      addUserNoOrgError: 'Your account has no organization assigned. Please ask a Super Administrator to set your organization first.',
      addUserSelectOrgPlaceholder: 'Select organization',
      addUserSelectOrgRequired: 'Please select an organization',
      changePasswordTitle: 'Set password',
      changePasswordSubtitle: 'Set a new password for your account.',
      changePasswordButton: 'Save password',
      statusPending: 'Invited',
      statusExpired: 'Expired',
    },

    language: {
      pageTitle: 'Language settings',
      pageSubtitle: 'Choose your preferred language for the user interface.',
      unsavedChanges: 'Unsaved changes',
      saveButton: 'Save language settings',
      cancelButton: 'Cancel',
      searchPlaceholder: 'Search',
      currentBadge: 'Current',
      defaultBadge: 'Default',
    },

    pagination: {
      pageOf: 'Page {current} of {total}',
    },

    toast: {
      commentSaved: 'Comment saved',
      fileUploaded: 'Files uploaded successfully',
      fileUpdated: 'Files updated successfully',
      projectCreated: 'AI Request created successfully!',
      projectFailed: 'Failed to create AI request',
      chooseAgent: 'Please choose an agent and enter an AI request name',
    },

    deleteModal: {
      singleTitle: 'Delete AI request',
      bulkTitle: 'Delete {count} AI requests',
      singleDescription: 'Are you sure you want to delete this AI request? This action cannot be undone.',
      bulkDescription: 'Are you sure you want to delete {count} selected AI requests? This action cannot be undone.',
      deleting: 'Deleting...',
      defaultTitle: 'Confirm deletion',
      defaultMessage: 'Are you sure you want to delete this item?',
      cancelButton: 'Cancel',
      deleteButton: 'Delete',
    },

    agentOrder: {
      hint: 'Drag and drop the agents or enter a position number.',
      unsavedChanges: 'Unsaved changes',
      cancel: 'Cancel',
      save: 'Save',
      colPosition: 'Position',
      colAgent: 'Agent',
      colPlacement: 'Place on homepage',
      posTopLeft: 'Top left',
      posTopRight: 'Top right',
      posSecondLeft: 'Second row left',
      posSecondRight: 'Second row right',
      posBehindShowAll: 'Under “Show all”',
      noDepartments: 'This organization has no user groups yet.',
      noAgents: 'No agents are assigned to this user group.',
      toastSaved: 'Order has been saved',
      toastError: 'Order could not be saved',
      loadError: 'Data could not be loaded',
      retry: 'Try again',
      discardConfirm: 'There are unsaved changes to the order. Do you want to discard them?',
      toastNoPermission: 'You do not have permission to change the order of these agents.',
    },
    usageHistory: {
      title: 'Usage History',
      subtitle: 'Monthly usage per agent — visible to super admins only',
      cardRunsLabel: 'Runs',
      cardRunsSub: 'of {max} runs in {year}',
      cardQuestionsLabel: 'Questions',
      cardTokensLabel: 'Tokens',
      cardTokensSub: 'Input and output combined',
      cardUserPagesLabel: 'User-uploaded pages',
      cardStandardPagesLabel: 'Standard-file pages',
      cardPagesLabel: 'Pages Used',
      cardPagesSub: 'of {max} pages in {year}',
      cardCreditsLabel: 'Temp. Credits',
      cardCreditsSub: 'additionally granted',
      orgPlaceholder: 'Select organisation...',
      allMonths: 'All months',
      refresh: 'Refresh',
      colMonth: 'Month',
      colAgent: 'Agent',
      colRuns: 'Runs',
      colQuestions: 'Questions',
      colTokens: 'Tokens',
      colUserPages: 'User pages',
      colStandardPages: 'Standard-file pages',
      unlimitedSuffix: '(unlimited)',
      colRunsUsed: 'Runs Used',
      colPagesUsed: 'Pages Used',
      colTempCredits: 'Temp. Credits',
      colTempPages: 'Temp. Pages',
      colAction: 'Action',
      entry: 'entry',
      entries: 'entries',
      remaining: 'remaining',
      actionCarryOver: 'Carry-over',
      actionReset: 'Reset',
      emptyOrgTitle: 'Select organisation',
      emptyOrgSubtitle: 'Select an organisation to view the monthly usage history.',
      emptyDataTitle: 'No data for',
      emptyDataSubtitle: 'No usage data has been recorded for this period.',
      loadError: 'Error loading usage history',
      months: 'January,February,March,April,May,June,July,August,September,October,November,December',
      unknownAgent: 'Unknown Agent',
      liveTitle: 'Current Usage',
      liveBadge: 'Live',
      liveSubtitle: 'Real-time · Current Month',
      liveUpdated: 'Updated',
      deletedBadge: 'Deleted',
    },

    notifications: {
      pageTitle: 'Notifications',
      subtitle: 'All activities for your AI requests, documents, agents and integrations.',
      markAllRead: 'Mark all as read',
      tabAll: 'All',
      tabUnread: 'Unread',
      tabSystem: 'System',
      empty: 'No notifications',
      emptyHint: "You're all caught up.",
      groupToday: 'TODAY',
      groupYesterday: 'YESTERDAY',
      groupEarlier: 'EARLIER',
      // Action labels
      viewDocument: 'View document',
      viewSyncReport: 'View sync report',
      viewChanges: 'View changes',
      viewFile: 'View file',
      openProject: 'Open AI request',
      viewSite: 'View site',
      viewNews: 'View release notes',
      fixIssue: 'Fix issue',
      reviewActivity: 'Review activity',
      // Time phrases
      justNow: 'Just now',
      minuteAgo: '1 minute ago',
      minutesAgo: '{m} minutes ago',
      hourAgo: '1 hour ago',
      hoursAgo: '{h} hours ago',
      yesterdayAt: 'Yesterday, {time}',
      // Sample titles & descriptions
      t1Title: 'Document analysis completed',
      t1Desc: 'AI completed the analysis of Service_Contract.pdf for City Center Agreement Review.',
      t2Title: 'SharePoint sync completed',
      t2Desc: '143 files successfully synced from the Contracts / Legal Agreements folder.',
      t3Title: 'Agent settings updated',
      t3Desc: 'Changes made to the Contract Review Agent — temperature and prompt updated.',
      t4Title: 'New AI request created',
      t4Desc: 'AI Request "Downtown Plaza" was created and assigned to the Contracts user group.',
      t5Title: 'Document uploaded',
      t5Desc: 'Lease_Agreement_2024.docx was uploaded to the Standard Files library.',
      t6Title: 'Analysis completed',
      t6Desc: 'Full analysis of AI request "Skyline Tower" completed — 98% confidence score.',
      t7Title: 'SharePoint site connected',
      t7Desc: 'Contoso Legal Hub was successfully connected as a data source.',
      t8Title: 'Document processed',
      t8Desc: 'NDA_Template_v3.pdf was processed and is ready for AI analysis.',
      t9Title: 'Platform update — v2.4.0',
      t9Desc: 'New features: improved SharePoint sync speed, enhanced citation badges and AI profile builder.',
      t10Title: 'New agent created',
      t10Desc: '"Compliance Checker" agent was created by Olivia Rhye in the Contracts workspace.',
      t11Title: 'Sync error detected',
      t11Desc: 'Syncing 3 files from the HR documents folder failed. Access denied.',
      t12Title: 'Security alert',
      t12Desc: 'A new sign-in from an unknown device was detected. Please review your account.',
      t13Title: 'Scheduled maintenance',
      t13Desc: 'The platform will be under maintenance on March 10, 2026 from 02:00–04:00 UTC. Services may be briefly unavailable.',
    },

    dataAnalysis: {
      hint: "As Data Analysis",
      activeHint: "Data Analysis is active for this request",
      preview: "Preview",
      closePreview: "Close preview",
      previewLoading: "Loading preview…",
      previewError: "The preview could not be loaded. You can download the file instead.",
      skippedLabel: "Not used",
      skipReasonMissing: "file not found",
      skipReasonUnreadable: "could not be read",
      skipReasonTooLarge: "too large",
      skipReasonStandardFile: "standard file, not supported",
      modeName: "Data Analysis",
      tooltip: "Analyze files with advanced calculations and data tools",
      composerMeta: "Stays on until you turn it off · selected files limit the context",
      usingSessionFiles: "Using eligible files from this chat",
      usingSelectedFiles: "Using selected files only",
      selectedForRequest: "Selected for this request",
      sessionForRequest: "Eligible files from this chat",
      noFilesForRequest: "No files – calculations only",
      workingTitle: "Analyzing your data…",
      completedTitle: "Data analysis completed",
      statusAnalyzing: "Analyzing",
      statusReady: "Ready",
      statusFailed: "Failed",
      stepPreparing: "Preparing files…",
      stepInspecting: "Checking data structure…",
      stepCalculating: "Running calculations…",
      stepResults: "Preparing results…",
      stepRetrying: "Retrying…",
      progressCompleted: "Analysis complete",
      completedHelper: "The result appears as a regular chat answer; any created file is added to Generated files.",
      failedTitle: "The data analysis failed",
      failedHelper: "Please try again.",
      contentFilterHelper: "The answer was blocked by the content filter. Please rephrase the request or select other files.",
      filesUnavailableHelper: "The files for this analysis could not be read. Please upload them again.",
      tokenLimitHelper: "The answer reached the agent's token limit. Please increase the limit in the agent settings.",
      retry: "Try again",
      basedOnSelected: "Analysis based on the selected files",
      basedOnSession: "Analysis based on the files in this chat",
      basedOnNone: "Analysis without files",
      filesUsedLabel: "Files used",
      generatedFilesLabel: "Created files",
      download: "Download",
      downloadError: "The file could not be downloaded.",
      alreadyRunningError: "A data analysis is already running in this chat. Please wait for it to finish.",
      notEnabledError: "Data Analysis is turned off for this agent.",
      modeSaveError: "The Data Analysis switch could not be saved.",
      startFailedError: "The data analysis could not be started.",
    },
    chatZoom: {
      open: 'Open zoom controls',
      close: 'Close zoom controls',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      reset: 'Reset to 100%',
    },
    share: {
      button: 'Share',
      shareProject: 'Share AI Request',
      modalTitle: 'Share AI Request',
      modalSubtitle: 'Add people or copy the link',
      modalDescription: 'Share this AI request with others in read-only mode. Anyone with this link can view AI request details, files, AI answers, and sources, but they cannot make changes.',
      notifOnly: 'In-app notification only · No email sent',
      peopleLabel: 'People',
      permissionLabel: 'Permission',
      shareButton: 'Create & Copy Link',
      shareAndNotify: 'Share & Notify',
      shareLinkPlaceholder: 'Link will appear after sharing...',
      linkNote: 'Anyone with this link can view this AI request only. They cannot edit, upload files, add comments, or ask new AI questions.',
      searchPlaceholder: 'Search name or email...',
      noResults: 'No users found',
      addedLabel: 'Added',
      cancel: 'Cancel',
      copyLink: 'Copy link',
      linkCopied: 'Copied!',
      shareLinkLabel: 'Share link',
      shareSuccess: 'AI Request shared successfully',
      removeFailed: 'Could not remove recipient',
      readOnlyBadge: 'Read only',
      commentsTitle: 'Comments',
      commentsEmpty: 'No comments yet.',
      commentsLoading: 'Loading comments …',
      commentsPlaceholder: 'Write a comment …',
      commentsAdd: 'Comment',
      commentsSave: 'Save',
      commentsCancel: 'Cancel',
      commentsEdit: 'Edit',
      commentsDelete: 'Delete',
      commentsDeleteConfirm: 'Delete this comment?',
      commentsEdited: 'edited',
      commentsUnknownAuthor: 'Unknown user',
      commentsLoadError: 'Comments could not be loaded.',
      commentsSaveError: 'Comment could not be saved.',
      sharedBy: 'Shared by',
      readOnlyHint: 'This view is read-only — you cannot ask questions.',
      openProject: 'Open AI request',
      loadingError: 'Could not load shared AI request.',
      notFound: 'This share link is invalid or has expired.',
      accessRevoked: 'Your access to this AI request has been removed.',
      currentRecipients: 'Currently shared with',
      noRecipients: 'Not shared yet',
      revokeAccess: 'Revoke access',
      revokeShare: 'End sharing',
      revokeConfirm: 'Are you sure you want to end sharing for this AI request? All recipients will lose access.',
      revokeSuccess: 'Sharing ended successfully',
      inviteDescription: 'Invite people by entering their name or email.',
      invitePeople: 'Invite People',
      sendInvite: 'Send Invite',
      whoCanView: 'Who can view this AI request',
      viewAll: 'View All',
      allViewers: 'All Viewers',
      allViewersDesc: 'People who currently have access to view this AI request.',
      removeAccess: 'Remove access',
      youLabel: 'You',
      nPeople: '{count} people',
      viewPermission: 'View Only',
      viewButton: 'View',
      alreadyShared: 'Already shared',
      done: 'Done',
    },

    agents: {
      pageTitle: 'Agents',
      createAgent: 'Create agent',
      selectAll: 'Select all',
      loading: 'Loading agents...',
      noneFound: 'No agents found',
      noneYet: 'No agents yet',
      noneFoundHint: 'No agents match your search.',
      noneYetHint: 'Create an agent to start analyzing files and data.',
      duplicate: 'Duplicate agent',
      agentNameLabel: 'Agent name',
      userGroupLabel: 'User group',
      organizationLabel: 'Organization',
      lastUpdatedLabel: 'Last updated',
      createModalTitle: 'Create New Agent',
      createModalImageLabel: 'Agent image',
      createModalUploadImage: 'Upload image',
      createModalChangeImage: 'Upload new image',
      createModalRemoveImage: 'Remove',
      createModalNoFileSelected: 'No file selected',
      createModalSelectOrg: 'Select organization',
      createModalSelectDept: 'Select user group',
      createModalDescLabel: 'Description',
      createModalDescPlaceholder: 'Describe the main purpose of this agent...',
      createModalDescHelp: 'One or two sentences explaining how this agent helps users.',
      createModalCancel: 'Cancel',
      createModalSubmit: 'Add agent',
      createModalSubmitting: 'Creating...',
      createModalNameRequired: 'Please enter an agent name',
    },

    filterModal: {
      title: 'Filter',
      agentsLabel: 'Agents',
      agentsPlaceholder: 'Select agents',
      creationDateLabel: 'Creation date',
      today: 'Today',
      last7days: 'Last 7 days',
      last30days: 'Last 30 days',
      monthPlaceholder: 'Select month',
      progressLabel: 'Q&A progress',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      sortByLabel: 'Sort by',
      newestFirst: 'Newest first',
      oldestFirst: 'Oldest first',
      mostProgress: 'Most progress',
      leastProgress: 'Least progress',
      resetAll: 'Reset all',
      apply: 'Apply',
      organizationLabel: 'Organization',
      organizationPlaceholder: 'Select organization',
      userGroupLabel: 'User group',
      userGroupPlaceholder: 'Select user group',
      lastUpdateLabel: 'Last update',
      clearAll: 'Clear all',
    },

    months: {
      january: 'January',
      february: 'February',
      march: 'March',
      april: 'April',
      may: 'May',
      june: 'June',
      july: 'July',
      august: 'August',
      september: 'September',
      october: 'October',
      november: 'November',
      december: 'December',
    },

    createProjectModal: {
      title: 'Create new AI request',
      agentLabel: 'Agent',
      agentPlaceholder: 'Select agent',
      noAgentsAvailable: 'No agents available',
      projectImageLabel: 'AI request image',
      uploadImage: 'Upload image',
      uploadNewImage: 'Upload new image',
      noFileSelected: 'No file selected',
      projectNameLabel: 'AI request name',
      projectNamePlaceholder: 'AI request name',
      uploadMethodLabel: 'Upload method',
      uploadFromDevice: 'Upload from my device',
      uploadFromDeviceHint: 'Add files directly from your computer',
      connectSharePoint: 'Connect SharePoint folder',
      connectSharePointHint: 'Use files from your AI request workspace',
      analyze: 'Analyze',
      creating: 'Creating...',
    },

    helpCenter: {
      title: 'Help Center',
      tabFaqs: "FAQ's",
      tabSupport: 'Contact Support',
      sidebarFaqs: 'FAQs',
      sidebarSupport: 'Support',
      sidebarFooterCopyright: '© 2026 AI Spaces GmbH',

      supportInfoTitle: 'Support Information',
      supportInfoSubtitle: 'Provide information users will see in the Help Center when they need assistance.',
      supportMessageLabel: 'Support Message',
      supportMessageDefault: 'Here you can find information about our support. Our team is happy to assist you.',
      supportRequestLinkLabel: 'Support Request Link',
      supportNoLinkConfigured: 'No link configured',
      supportHoursLabel: 'Support Hours',
      supportHoursDaysDefault: 'Monday - Friday',
      supportHoursTimeDefault: '07:00 - 17:00',
      supportErrorPrefix: 'Error: {message}',
      supportMessagePlaceholder: 'Write a short message explaining how users can get help from your support team.',
      supportCharactersRemaining: '{count} characters remaining',
      supportLinkPlaceholder: 'Paste the support form or feedback request link',
      supportDaysLabel: 'Days',
      supportTimeLabel: 'Time',
      supportDaysPlaceholder: 'e.g., Monday - Friday',
      supportTimePlaceholder: 'e.g., 07:00 - 17:00',
      supportSaving: 'Saving...',
      // Per-day hours (toggle-based UI)
      supportDayMonday: 'Monday',
      supportDayTuesday: 'Tuesday',
      supportDayWednesday: 'Wednesday',
      supportDayThursday: 'Thursday',
      supportDayFriday: 'Friday',
      supportDaySaturday: 'Saturday',
      supportDaySunday: 'Sunday',
      supportClosed: 'Closed',
      supportOpenLabel: 'Open',
      supportCloseLabel: 'Close',
      supportAddHours: 'Add a set of hours',
      supportRemoveHours: 'Remove time slot',
      // Time & Date Settings card + modal
      tdsCardTitle: 'Time & Date Settings',
      tdsCardSubtitle: 'Configure how time, date, and week information is displayed across the platform.',
      tdsTimezoneLabel: 'Timezone',
      tdsDateFormatLabel: 'Date Format',
      tdsTimeFormatLabel: 'Time Format',
      tdsWeekStartsOnLabel: 'Week Starts On',
      tdsTime12Label: '12-hour',
      tdsTime12Example: '2:30 PM',
      tdsTime24Label: '24-hour',
      tdsTime24Example: '14:30',
      tdsModalTitle: 'Time & Date Settings',
      tdsSaving: 'Saving...',

      faqPageTitle: 'Frequently Asked Questions',
      faqPageSubtitle: 'Find answers to common questions about using DocAI, managing agents, and analyzing documents.',
      faqManageTitle: 'Manage FAQs',
      faqAddButton: 'Add question',
      faqEmptyTitle: 'No FAQs yet',
      faqEmptySubtitle: 'No questions have been added yet.',
      faqDrawerAddTitle: 'Add question',
      faqDrawerEditTitle: 'Edit question',
      faqQuestionLabel: 'Question',
      faqQuestionPlaceholder: 'Question',
      faqAnswerLabel: 'Answer',
      faqAnswerPlaceholder: 'Answer',
      faqMediaLabel: 'Upload video/image (optional)',
      faqUploading: 'Uploading...',
      faqFileUploaded: 'File uploaded',
      faqRemoveFile: 'Remove',
      faqDropHint: 'Drag your video/image here or {browse}',
      faqBrowse: 'browse',
      faqSaving: 'Saving...',
      faqDeleteTitle: 'Delete FAQ',
      faqDeleteMessage: 'Are you sure you want to delete this FAQ?',
      faqMediaAlt: 'FAQ media',
      faqToastFetchError: 'Failed to load FAQs',
      faqToastDeleted: 'FAQ deleted successfully',
      faqToastDeleteError: 'Failed to delete FAQ',
      faqToastFileUploaded: 'File uploaded successfully',
      faqToastUploadError: 'Failed to upload file',
      faqValidationRequired: 'Question and answer are required',
      faqValidationUploadWait: 'Please wait until the upload is complete.',
      faqToastUpdated: 'FAQ updated successfully',
      faqToastCreated: 'FAQ created successfully',
      faqToastSaveError: 'Failed to save FAQ',
      faqToastChangesSaved: 'Changes saved',
      faqToastChangesSaveError: 'Failed to save',
    },

    auth: {
      loginTitle: 'Log in or sign up',
      loginSubtitle: 'Sign in to access your AI requests.',
      continueWithMicrosoft: 'Continue with Microsoft',
      or: 'OR',
      orContinueWith: 'OR CONTINUE WITH',
      emailPlaceholder: 'Email address',
      checkInboxTitle: 'Check your inbox',
      checkInboxSubtitle: 'Enter the verification code we just sent to {email}',
      codePlaceholder: 'Code',
      resendEmail: 'Resend email',
      continueWithPassword: 'Continue with password',
      enterPasswordTitle: 'Enter your password',
      emailLabel: 'Email address',
      edit: 'Edit',
      passwordLabel: 'Password',
      forgotPassword: 'Forgot password?',
      loginWithOtp: 'Log in with a one-time code',
      resetPasswordTitle: 'Forgot your password?',
      resetPasswordSubtitle: 'Click "Continue" to reset your password for {email}',
      resetPasswordEmailPrompt: "Enter your email address and we'll send you a reset link.",
      setNewPasswordSubtitle: 'Must be at least 8 characters.',
      setNewPasswordTitle: 'Set new password',
      newPasswordPlaceholder: 'New password',
      confirmPasswordPlaceholder: 'Confirm password',
      passwordMismatch: 'Passwords do not match.',
      sendResetLinkButton: 'Send Reset Link',
      resetPasswordButton: 'Reset Password',
      footerPrivacyPolicy: 'Privacy Policy',
      footerTermsOfService: 'Terms of Service',
      footerSupport: 'Support',
      resetLinkSentTitle: 'Check your email',
      resetLinkSentSubtitle: "We've sent a password reset link to {email}. Please check your inbox.",
      backToLogin: 'Back to login',
      invalidEmail: 'Please enter a valid email address',
      otpSent: 'Code has been sent',
      otpResent: 'Code has been resent',
      genericError: 'Something went wrong. Please try again.',
      fieldRequired: 'Please fill in all fields.',
      invalidPhone: 'Please enter a valid phone number.',
      passwordTooShort: 'Password must be at least 8 characters long.',
      today: 'Today',
      clear: 'Clear',
      completeProfileTitle: 'Complete your profile',
      completeProfileSubtitle: 'Just a few more details to set up your account.',
      fullnameLabel: 'Full name',
      genderLabel: 'Gender',
      genderPlaceholder: 'Please select',
      genderMale: 'Male',
      genderFemale: 'Female',
      genderDiverse: 'Other',
      dobLabel: 'Date of birth',
      mobileLabel: 'Mobile number',
      passwordOptionalLabel: 'Password (optional)',
      loginButton: 'Log in',
      welcomeBack: 'Welcome back',
      loginPageSubtitle: 'Log in to your account to continue.',
      rememberMe: 'Remember me',
      noAccount: "Don't have an account?",
      signUpLink: 'Sign up',
      noPasswordHint: "Don't have a password? We'll email you a verification code instead.",
      loginWithPasswordLink: 'Log in with password',
      setPasswordTitle: 'Set a password',
      setPasswordSubtitle: 'Welcome back, {fullname}. Set a password so you can log in directly with your email next time.',
      mfaVerifyTitle: 'Two-factor authentication',
      mfaAuthenticatorRowTitle: "Authenticator app",
      mfaConfirmText: "Enter the 6-digit code your app is showing now.",
      mfaConfirmTitle: "Confirm code",
      mfaEnrollStep: "Step {current} of {total}",
      mfaInstallText: "Install an authenticator app such as Microsoft Authenticator or Google Authenticator on your smartphone.",
      mfaInstallTitle: "Install an authenticator app",
      mfaManualKeyButton: "Enter key manually",
      mfaManualKeyText: "Enter this key in your app (time-based):",
      mfaRecoveryCopied: "Copied",
      mfaRecoveryCopy: "Copy",
      mfaRecoveryDone: "Done",
      mfaRecoveryDownload: "Download",
      mfaRecoveryFileHeader: "DocAI Platform – recovery codes (each code can only be used once)",
      mfaRecoverySavedCheckbox: "I have saved these codes securely",
      mfaRecoverySectionText: "Sign in with one of these codes if you lose access to your authenticator app.",
      mfaRecoverySectionTitle: "Recovery codes",
      mfaRecoveryText: "If you lose access to your app, you can sign in with one of these codes. Each code can only be used once. Keep them somewhere safe.",
      mfaRecoveryTitle: "Save your recovery codes",
      mfaRecoveryVerifySubtitle: "Enter one of your recovery codes.",
      mfaRegenerateButton: "Generate new codes",
      mfaRegenerateConfirmTitle: "Generate new recovery codes",
      mfaRegenerateSubmit: "Generate codes",
      mfaRegenerateSubtitle: "Enter the 6-digit code from your authenticator app.",
      mfaRegenerateSuccess: "New recovery codes have been generated.",
      mfaRegenerateWarning: "Your previous recovery codes will stop working.",
      mfaRequiredForAll: "Two-factor authentication is mandatory for all users and cannot be disabled.",
      mfaScanText: "Open the app, add a new account and scan this QR code. The entry will appear as “{issuer}”.",
      mfaScanTitle: "Scan the QR code",
      mfaUseAuthenticatorCode: "Use a code from the authenticator app",
      mfaUseRecoveryCode: "Use a recovery code",
      mfaVerifySubtitle: 'Enter the 6-digit code from your authenticator app.',
      mfaCodeLabel: 'Code',
      mfaSetupTitle: 'Set up two-factor authentication',
      mfaSetupSubtitle: 'Scan this QR code with an authenticator app (e.g. Google or Microsoft Authenticator) and enter the code shown to continue.',
      mfaSectionTitle: 'Two-factor authentication',
      mfaSectionDescription: 'Add extra protection to your account with an authenticator app.',
      mfaStatusEnabled: 'Enabled',
      mfaStatusDisabled: 'Disabled',
      mfaEnableButton: 'Enable',
      mfaDisableButton: 'Disable',
      mfaEnabledSuccess: 'Two-factor authentication has been enabled.',
      mfaDisabledSuccess: 'Two-factor authentication has been disabled.',
      mfaRequiredForRole: 'Two-factor authentication is required for your role and cannot be disabled.',
    },
  },

  cs: {
    common: {
      save: 'Uložit',
      cancel: 'Zrušit',
      delete: 'Smazat',
      edit: 'Upravit',
      close: 'Zavřít',
      confirm: 'Potvrdit',
      back: 'Zpět',
      next: 'Další',
      previous: 'Předchozí',
      search: 'Hledat',
      loading: 'Načítání...',
      upload: 'Nahrát',
      download: 'Stáhnout',
      open: 'Otevřít',
      yes: 'Ano',
      no: 'Ne',
      add: 'Přidat',
      remove: 'Odebrat',
      create: 'Vytvořit',
      continue: 'Pokračovat',
      proceed: 'Pokračovat',
      retry: 'Opakovat',
      refresh: 'Obnovit',
      error: 'Chyba',
      success: 'Úspěch',
      warning: 'Varování',
      info: 'Informace',
    },

    nav: {
      homepage: 'Úvodní stránka',
      projects: 'Projekty',
      agents: 'Agenti',
      notifications: 'Oznámení',
      helpCenter: 'Centrum nápovědy',
      recentProjects: 'Nedávné projekty',
      settings: 'Nastavení',
      account: 'Účet',
      logout: 'Odhlásit se',
      logoutConfirm: 'Opravdu se chcete odhlásit?',
    },

    sidebar: {
      fragen: 'Pokyny',
      dateien: 'Soubory',
      uploadFile: 'Nahrát soubor',
      loadingQuestions: 'Pokyny se načítají…',
      refreshFiles: 'Obnovit soubory',
      closeSidebar: 'Zavřít postranní panel',
      openSidebar: 'Otevřít postranní panel',
      noFilesUploaded: 'Zatím nebyly nahrány žádné soubory',
      searchFilesPlaceholder: 'Hledat soubory',
      sharepointSection: 'SharePoint',
      noKnowledgeArea: 'Bez oblasti znalostí',
      noFilesFound: 'Nebyly nalezeny žádné soubory',
      maxFilesInfo: 'Pro analýzu je povoleno maximálně {count} souborů ({used}/{count})',
      selectedFiles: 'Vybrané soubory:',
      addFiles: 'Přidat soubory',
      standardBadge: 'Standardní',
      sharepointBadge: 'SharePoint',
      uploadedBadge: 'Nahráno',
      deleteFileTitle: 'Smazat soubor?',
      deleteFileMessage: 'Opravdu chcete odstranit „{filename}" z analýzy? Tuto akci nelze vrátit zpět.',
      statusPending: 'Čeká',
      statusDone: 'Hotovo',
      standardSection: 'Standardní',
      uploadingState: 'Nahrávání...',
      convertingState: 'Konverze...',
      youSection: 'Vy',
      totalPages: 'Celkový počet stránek: {count}',
      pdfLoading: 'Načítání PDF...',
      imageLoading: 'Načítání obrázku...',
      imageLoadError: 'Obrázek se nepodařilo načíst',
      sourceFileNotFound: 'Soubor se nepodařilo otevřít. Možná již není k dispozici.',
      sourceFileStale: 'Tento soubor SharePoint byl od vytvoření této odpovědi aktualizován nebo odstraněn ze zdroje.',
      sourceFileTimeout: 'Načítání souboru trvalo příliš dlouho. Zkuste to prosím znovu.',
      sourceFileLoadFailed: 'Soubor se nepodařilo načíst. Zkontrolujte prosím připojení a zkuste to znovu.',
      sourceFileDecryptionFailed: 'Tento soubor se nepodařilo dešifrovat. Šifrovací klíč se mohl změnit nebo je soubor poškozen — obraťte se prosím na administrátora.',
      backToFiles: 'Zpět na soubory',
      refreshAlt: 'Obnovit',
      noFileSelected: 'Není vybrán žádný soubor',
      invalidFileType: 'Vyberte prosím pouze soubory typu {types}',
      pdfProcessingError: 'Chyba při zpracování {filename}: Neplatný soubor PDF',
      fileValidationError: 'Chyba u {filename}: {error}',
      convertFileError: 'Soubor se nepodařilo převést',
      noOrganization: 'Uživatel není přiřazen k žádné organizaci',
      uploadExceedsPages: 'Nahrání překračuje dostupné stránky',
      selectFilesToUpload: 'Vyberte prosím soubory k nahrání',
      uploadFilesError: 'Soubory se nepodařilo nahrát',
      cannotRefresh: 'Soubory nelze obnovit: chybí informace o uživateli nebo historii',
      refreshFilesError: 'Soubory se nepodařilo obnovit',
      deleteFileError: 'Soubor se nepodařilo smazat',
      filesLoadError: 'Chyba: {error}',
      maxFilesReached: 'Analyzovat lze pouze {max} souborů. Již jste nahráli {uploaded} soubor(ů). {rejected} soubor(ů) nebyl(o) přidán(o).',
      maxFilesReachedShort: 'Analyzovat lze pouze {max} souborů. Již jste nahráli {uploaded} soubor(ů).',
      uploadSuccess: 'Soubory byly úspěšně nahrány',
      refreshSuccess: 'Soubory byly úspěšně obnoveny',
      // Generated files tab
      uploadedTab: 'Nahráno',
      generatedFilesTab: 'Vygenerované soubory {count}',
      tableName: 'Název',
      tableCreated: 'Vytvořeno',
      tableSize: 'Velikost',
      tableActions: 'Akce',
      noGeneratedFiles: 'Zatím žádné vygenerované soubory',
      today: 'Dnes',
      yesterday: 'Včera',
      previewAction: 'Náhled',
      downloadWordAction: 'Stáhnout Word',
      downloadPdfAction: 'Stáhnout PDF',
      deleteGeneratedFileTitle: 'Smazat soubor?',
      deleteGeneratedFileMessage: 'Opravdu chcete smazat „{filename}"? Tuto akci nelze vrátit zpět.',
      deleteGeneratedFileError: 'Soubor se nepodařilo smazat',
    },

    homepage: {
      title: 'Úvodní stránka',
      recentProjects: 'Nedávné projekty',
      createNewProject: 'Vytvořit nový projekt',
      noProjectsCreated: 'Nebyly vytvořeny žádné projekty',
      noProjectsHint: 'Vytvořte projekt pro nahrání souborů a spuštění analýz.',
      tableTitle: 'Název',
      tableAgents: 'Agenti',
      tableCreatedAt: 'Datum vytvoření',
      tableProgress: 'Průběh Q&A',
      tableAnswerStatus: 'Stav odpovědí',
      answeredOf: '{done} / {total} zodpovězeno',
      agentsTitle: 'Agenti',
      agentsSubtitleLine1: 'Objevujte a pracujte s AI agenty navrženými pro různé skupiny uživatelů.',
      agentsSubtitleLine2: 'Každý agent vám pomáhá rychleji analyzovat dokumenty, data a rozhodnutí.',
      noAgentsAvailable: 'Žádní agenti nejsou k dispozici. Kontaktujte prosím svého administrátora.',
      noAgentsYet: 'Zatím žádní agenti',
      noAgentsYetHint: 'Vytvořte agenta a začněte analyzovat soubory a data.',
      createAgentFirst: 'Nejprve prosím vytvořte agenta.',
      agentCardDefaultDescription: 'AI agent pro podporu vašich úkolů.',
      newChatPlaceholderName: 'Nový chat',
      historyLimitReached: 'Dosáhli jste maximálního počtu {limit} historií chatu. Před vytvořením nové historie prosím smažte některou existující.',
      createProjectError: 'Projekt se nepodařilo vytvořit.',
      startDirectChatError: 'Chat se nepodařilo spustit.',
      startingDirectChat: 'Spouští se chat...',
      showAll: 'Zobrazit vše',
      showLess: 'Zobrazit méně',
      noDepartment: 'Bez oddělení',
    },

    history: {
      pageTitle: 'Projekty',
      tableTitle: 'Název',
      tableAgents: 'Agenti',
      tableCreatedAt: 'Datum vytvoření',
      tableProgress: 'Průběh Q&A',
      tableAnswerStatus: 'Stav odpovědí',
      answeredOf: '{done} / {total} zodpovězeno',
      agentLabel: 'Agenti',
      titleLabel: 'Název',
      creationDateLabel: 'Datum vytvoření',
      progressLabel: 'Průběh Q&A',
      noProjectsFound: 'Nebyly nalezeny žádné projekty',
      noProjectsCreated: 'Nebyly vytvořeny žádné projekty',
      noProjectsSearchHint: 'Žádné projekty neodpovídají vašemu vyhledávání.',
      createForAgent: 'Vytvořte projekt pro {agent}.',
      createHint: 'Vytvořte projekt pro nahrání souborů a spuštění analýz.',
      continue: 'Pokračovat',
      view: 'Zobrazit',
      projectDetails: 'Podrobnosti žádosti AI',
      deleteAll: 'Smazat vše',
      newProjectTitle: 'Vytvořit nový projekt',
      createProject: 'Vytvořit projekt',
      agentProjects: 'Projekty agenta',
      clearFilter: 'Zrušit filtr',
      filter: 'Filtr',
      tableAccess: 'Přístup',
      accessOwner: 'Vlastník',
      accessViewOnly: 'Sdíleno',
      accessInvited: 'Pozváno',
      accessCanEdit: 'Může upravovat',
      tabMyProjects: 'Moje projekty',
      tabSharedProjects: 'Sdílené projekty',
      tabInvitedProjects: 'Pozvané projekty',
    },

    analysis: {
      breadcrumbHome: 'Úvodní stránka',
      breadcrumbProjects: 'Projekty',
      breadcrumbProjectDetail: 'Otevřít přehled projektu',
      breadcrumbChatLeaf: 'Chat',
      export: 'Exportovat',
      exportWithComments: 'Export s komentáři',
      exportWithoutComments: 'Export bez komentářů',
      agentFallback: 'Agent',
      projectFallback: 'Projekt',
      openSidebar: 'Otevřít postranní panel',
      chatPlaceholder: 'Zeptejte se na něco',
      sources: 'Zdroje',
      reference: 'Reference',
      webSourcesCount: '{count} webových zdrojů',
      sourcesCount: '{count} zdrojů',
      document: 'Dokument',
      documentNumber: 'Dokument {index}',
      source: 'Zdroj',
      sourceLabel: 'Zdroj: {filename} (stránka {page})',
      page: 'Stránka',
      pageWithNumber: 'Stránka {page}',
      questionTab: 'Otázky',
      fileTab: 'Soubory',
      projectLoading: 'Načítání projektu...',
      pdfBrowserTitle: 'AI Spaces AI shrnutí',
      pdfSummarySuffix: 'AI shrnutí',
      pdfUploadedDocsHeading: 'Nahrané dokumenty:',
    },

    chatActions: {
      listen: 'Poslechnout',
      stop: 'Zastavit',
      loading: 'Načítání...',
      copy: 'Kopírovat',
      addComment: 'Přidat komentář',
      editComment: 'Upravit komentář',
      quickActions: 'Rychlé akce',
      quickActionsResult: 'Výsledek rychlé akce',
      setStatus: 'Nastavit stav',
      statusSelect: 'Vybrat stav',
      aiDisclaimer: 'Odpověď generovaná AI. Vždy si prosím ověřte výsledky.',
      wordToggle: 'Vygenerovat Word dokument',
      wordCreating: 'Dokument se vytváří…',
      wordCreatingSubtitle: 'Na základě aktuálního požadavku a dostupných zdrojů',
      wordCreatingStatusPill: 'Vytváří se',
      wordCreatingRendering: 'Vytváří se Word dokument…',
      wordCreatingInProgress: 'Probíhá',
      wordCreatingHelper: 'Může to trvat několik minut. Mezitím můžete pokračovat v chatu.',
      wordNothingFound: 'Téma nebylo v dostupných dokumentech nalezeno.',
      wordTruncated: 'Dokument se nepodařilo dokončit. Zkuste to prosím znovu.',
      wordFailed: 'Generování dokumentu se nezdařilo.',
      wordDownloadWord: 'Stáhnout Word',
      wordDownloadPdf: 'Stáhnout PDF',
      wordDocumentCreated: 'Dokument Word byl vytvořen',
      wordReady: 'Připraveno',
      wordCompletedLabel: 'Dokončeno',
      wordReadyHelper: 'Dokument je připraven ke stažení.',
      wordPreview: 'Náhled',
      wordDownloadWordShort: 'Word',
      wordDownloadPdfShort: 'PDF',
      wordDownloadError: 'Stahování se nezdařilo. Zkuste to prosím znovu.',
      wordHint: 'Jako dokument Word',
      wordModeLabel: 'Word',
      wordComposerMeta: "Pouze jeden požadavek · vytvoří dokument Word",
      wordNotEnabledError: 'Generování Word dokumentů není pro tohoto agenta povoleno.',
      wordAlreadyRunningError: 'Pro tohoto agenta se již vytváří Word dokument. Počkejte prosím, až bude hotový.',
      saving: 'Ukládání...',
      saveComment: 'Uložit',
      commentBy: 'Komentář od {name}',
      readAloud: 'Přečíst nahlas',
      close: 'Zavřít',
      commentPlaceholder: 'Přidat komentář',
      defaultUser: 'Uživatel',
      askPlaceholder: 'Zeptejte se na něco',
      chatDisabled: 'Chat je deaktivován',
      voiceInput: 'Hlasový vstup',
      uploadFile: 'Nahrát soubor',
      enterProjectName: 'Pojmenujte projekt',
      convertToPdf: 'Konverze do PDF...',
      processing: 'Zpracování...',
      uploading: 'Nahrávání...',
      startAnalysis: 'Spustit analýzu',
      addFiles: 'Přidat soubory',
      newAnalysisTitle: 'Nová analýza?',
      newAnalysisConfirm: 'Opravdu chcete zavřít aktuální analýzu a zahájit novou?',
      removeFile: 'Odebrat soubor',
      filePickerRoot: 'Kořenová složka',
      filePickerClear: 'Zrušit výběr',
      filePickerTrigger: 'Vybrat soubor (/)',
      filePickerSearchPlaceholder: 'Hledat v této složce',
      filePickerSelected: 'vybráno',
      onlyOneFileAllowed: 'Pro úvodní analýzu je povolen pouze jeden soubor.',
      tooManyImages: 'Maximálně {max} obrázky na nahrání.',
      filesStillProcessing: 'Před položením otázky k nahraným souborům počkejte, až se dokončí jejich zpracování.',
      defaultImageOnlyMessage: 'Prosím analyzujte přiložený obrázek.',
      analyzingImage: 'Analyzuji obrázek…',
      transcriptionFailed: 'Transkripce selhala',
      microphoneDenied: 'Přístup k mikrofonu byl zamítnut',
      uploadSuccess: 'Soubor byl úspěšně nahrán',
      uploadFailed: 'Nahrávání selhalo',
      uploadExceedsPages: 'Nahrání překračuje dostupné stránky',
      uploadPageLimitExceeded: 'Limit pro nahrávání souborů v této relaci je {limit} stránek. Pokusili jste se nahrát {attempted} stránek. Zkuste to prosím znovu a nahrajte pouze {limit} stránek.',
      pageLimitsVerificationFailed: 'Nepodařilo se ověřit limity stránek',
      invalidFileType: 'Vyberte prosím pouze soubory typu {types}',
      invalidPdfProcessing: 'Chyba při zpracování {name}: Neplatný soubor PDF',
      fileConvertFailed: 'Soubor se nepodařilo převést',
      noFileSelected: 'Není vybrán žádný soubor',
      fileErrorFor: 'Chyba u {name}: {error}',
      genericError: 'Došlo k chybě.',
      noOrganization: 'Uživatel není přiřazen k žádné organizaci',
      tasksLoadFailed: 'Nepodařilo se načíst úkoly: {error}',
      agentQuestionPrefix: 'Ot: {title}',
      webSearchStep1: 'Rozumím vaší otázce',
      webSearchStep2: 'Prohledávám internet',
      webSearchStep3: 'Kontroluji relevantní výsledky',
      webSearchStep4: 'Kontroluji užitečné zdroje',
      webSearchStep5: 'Připravuji odpověď',
      webSearchFoundQuery: 'Hledáno: „{query}"',
      aiThinkingLabel: 'AI přemýšlí:',
      retryTimeoutLabel: 'Zkusit znovu',
    },

    answerStatus: {
      accepted: 'Přijato',
      review: 'Vyžaduje další přezkoumání',
      missing: 'Informace chybí',
      rejected: 'Zamítnuto',
    },

    projectDetail: {
      breadcrumb: 'Detail projektu',
      tabOverview: 'Přehled',
      tabDocuments: 'Dokumenty',
      tabQuestions: 'Pokyny',
      tabActivities: 'Aktivity',
      agentLabel: 'Agent: {name}',
      defaultAgentName: 'Agent pro kontrolu smluv',
      aiSummary: 'AI shrnutí',
      checklistProgress: 'Průběh kontrolního seznamu',
      projectLocation: 'Umístění AI žádosti',
      analysisScore: 'Skóre analýzy',
      documentCount: 'Počet dokumentů',
      participants: 'Účastníci',
      documents: 'Dokumenty',
      noLocationExtracted: 'Dosud neextrahováno',
      questionsCategory: 'Kategorie',
      questionsTitle: 'Název otázky',
      questionsDescription: 'Popis',
      questionsAnswer: 'AI odpověď',
      questionsStatus: 'Stav',
      questionsLoading: 'Načítání otázek...',
      defaultCategory: 'Obecné',
      acceptedOrOpen: 'Přijato',
      openAction: 'Otevřít',
      projectCreated: 'Projekt vytvořen',
      newProjectStarted: 'Nový projekt zahájen',
      activitiesEmpty: 'Aktivity projektu se zde zobrazí po nahrání dokumentů, provedení analýzy a zodpovězení otázek.',
      activitiesEmptyTitle: 'Zatím žádná aktivita',
      activityFileUploaded: 'Soubor nahrán',
      activityFileProcessed: 'Zpracováno',
      activityQuestionAsked: 'Otázka položena',
      activityQuestionAskedFallback: 'Uživatel položil otázku',
      activityAnalysisPerformed: 'Analýza provedena',
      activityAnalysisFallback: 'AI analýza dokončena',
      activityDefaultUser: 'Uživatel',
      activityAiAgent: 'AI Agent',
      activityTodayLabel: 'Dnes',
      activityYesterdayLabel: 'Včera',
      activityShowOlder: 'Zobrazit starší',
      activityShowLess: 'Zobrazit méně',
      uploadFilesHint: 'Nahrajte soubory do tohoto projektu, aby je agent mohl zkontrolovat a analyzovat.',
      yourDocuments: 'Vaše dokumenty',
      uploadDocument: 'Nahrát dokument',
      uploading: 'Nahrávání...',
      agentReferenceFiles: 'Referenční soubory agenta',
      documentName: 'Název dokumentu',
      pages: 'Stránky',
      uploadDate: 'Datum nahrání',
      documentsLoading: 'Načítání dokumentů...',
      noDocumentsUploaded: 'Žádné dokumenty nebyly nahrány',
      noQuestionsAssigned: 'Žádné otázky nebyly přiřazeny',
      noQuestionsHint: 'Otázky se zde zobrazí po jejich přidání k tomuto agentovi.',
    },

    agentSettings: {
      breadcrumbAgents: 'Agenti',
      breadcrumbSettings: 'Nastavení agenta',
      agentFallback: 'Agent',
      departmentFallback: 'Uživatelská skupina',
      tabGeneral: 'Obecná nastavení',
      tabQuestions: 'Pokyny',
      tabAiProfile: 'AI profil',
      tabTools: 'Nástroje',
      tabFiles: 'Standardní soubory',
      tabProjectOverview: 'Přehled projektu',
      tabProjectSetup: 'Nastavení projektu',
      tabShortcuts: 'Zkratky',
      // Tools tab — Word generation
      toolsTitle: 'Nástroje',
      toolsSubtitle: 'Povolte volitelné funkce pro tohoto agenta. Generování Word dokumentů využívá stávající konfiguraci vyhledávání agenta (dokumenty, web nebo kombinaci).',
      toolsWordGenTitle: 'Generování Word dokumentu',
      toolsWordGenSubtitle: 'Uživatelé mohou vytvořit brandovaný Word dokument zapnutím funkce Word pro jednotlivý chatový požadavek.',
      toolsCodeInterpreterTitle: "Code Interpreter (Analýza dat)",
      toolsCodeInterpreterSubtitle: "Umožňuje přesné výpočty, tabulky a grafy z nahraných souborů – zapíná se pro každý chat a zůstává zapnutý, dokud ho nevypnete.",
      toolsCiInstructionLabel: "Pokyny pro analýzu",
      toolsCiInstructionPlaceholder: "např. Všechny částky uvádějte v CHF a každou tabulku exportujte také jako soubor Excel.",
      toolsCiInstructionHelp: "Doplňuje pevná pravidla analýzy tohoto agenta.",
      toolsCiMaxOutputTokensLabel: "Maximální délka odpovědi (tokeny)",
      toolsCiMaxOutputTokensPlaceholder: "Bez omezení",
      toolsCiMaxOutputTokensHelp: "Ponechte prázdné pro žádné omezení. Velmi nízké hodnoty mohou analýzu předčasně ukončit.",
      toolsCiSessionScopeLabel: "Pracovní prostředí",
      toolsCiSessionScopeSession: "Znovu použít v rámci chatu (doporučeno)",
      toolsCiSessionScopeRequest: "Nové pro každý požadavek",
      toolsCiSessionScopeSessionDesc: "Navazující požadavky používají stejné soubory a jsou výrazně rychlejší.",
      toolsCiSessionScopeRequestDesc: "Každá analýza začíná v novém prostředí a soubory nahrává znovu.",
      toolsInstructionLabel: 'Pokyn',
      toolsInstructionPlaceholder: 'Pokyn pro generování Word dokumentu',
      toolsInstructionHelp: 'Toto nahrazuje část promptu agenta, která formuje odpověď, pro generování Word dokumentů. Samotné vyhledávání zůstává beze změny.',
      toolsMaxOutputTokensLabel: 'Maximální počet výstupních tokenů',
      toolsMaxOutputTokensHelp: 'Samostatný rozpočet výstupu pro generování Word dokumentů. Velkorysá výchozí hodnota zabraňuje tichému zkrácení dokumentu; backend přesto ověřuje nastavenou hodnotu vůči podporovanému limitu modelu.',
      toolsBehaviourTitle: 'Chování generování Word dokumentu',
      toolsBehaviourItem1: 'Tlačítko Word se v chatovém vstupu zobrazí pouze tehdy, je-li tento nástroj povolen.',
      toolsBehaviourItem2: 'Přepínač Word platí pro jednu zprávu a po odeslání nebo otevření jiného chatu se resetuje.',
      toolsBehaviourItem3: 'Vybrané dokumenty omezují věcné zdroje použité pro generovaný dokument.',
      toolsBehaviourItem4: 'Hotový DOCX a náhled PDF se ukládají v sekci Vygenerované soubory a nikdy se nepřidávají do vyhledávacího indexu.',
      toolsFutureToolsNote: 'Budoucí nástroje: Analýza dat / Code Interpreter zde mohou být později přidány jako další samostatný nástroj.',
      toolsFixedLayoutTitle: 'Pevné rozvržení dokumentu · není nutná žádná konfigurace',
      toolsBrandingCardTitle: 'Branding organizace',
      toolsBrandingCardText: 'Logo, název organizace a primární barva se použijí automaticky.',
      toolsSourcesCardTitle: 'Zdroje a jazyk',
      toolsSourcesCardText: 'Ověřené zdroje a pevné popisky se řídí jazykem dokumentu/požadavku.',
      toolsTransparencyCardTitle: 'Transparentnost AI',
      toolsTransparencyCardText: 'Upozornění na obsah generovaný AI je uvedeno v zápatí a ve vlastnostech souboru Word.',
      toolsLayoutFooterNote: 'V1 používá standardní brandované rozvržení. Vlastní šablony, více šablon a editor dokumentů jsou záměrně mimo rozsah.',
      toolsSaveButton: 'Uložit změny',
      generalTitle: 'Obecná nastavení',
      generalSubtitle: 'Definujte, jak tento agent funguje a jaký obchodní problém řeší.',
      unsavedChanges: 'Neuložené změny',
      cancel: 'Zrušit',
      save: 'Uložit',
      saving: 'Ukládání...',
      basicInfoTitle: 'Základní informace',
      basicInfoSubtitle: 'Základní údaje pro organizaci a identifikaci tohoto agenta.',
      agentImageLabel: 'Obrázek agenta',
      editImage: 'Upravit obrázek',
      removeImage: 'Odebrat obrázek',
      noFileSelected: 'Není vybrán žádný soubor',
      agentNameLabel: 'Název agenta',
      agentNamePlaceholder: 'Zadejte název agenta',
      organizationLabel: 'Organizace',
      selectOrganization: 'Vybrat organizaci',
      departmentLabel: 'Uživatelská skupina',
      selectDepartment: 'Vybrat uživatelskou skupinu',
      descriptionLabel: 'Popis',
      descriptionPlaceholder: 'Popište hlavní přínos tohoto agenta...',
      descriptionHelp: 'Jedna nebo dvě věty vysvětlující, jak tento agent pomáhá uživatelům.',
      useCaseTitle: 'Případ použití',
      useCaseSubtitle: 'Popište problém, který tento agent řeší, a jak pomáhá vašemu týmu.',
      problemLabel: 'Popis problému*',
      problemPlaceholder: 'Jakou obchodní výzvu tento agent řeší?',
      problemHelp: 'Příklad: Právní týmy tráví hodiny manuální kontrolou smluv kvůli dodržování předpisů...',
      solutionLabel: 'Popis řešení*',
      solutionPlaceholder: 'Jak tento agent řeší problém?',
      solutionHelp: 'Příklad: Tento AI agent automaticky prohledává smlouvy a identifikuje klíčové pojmy...',
      goalsTitle: 'Cíle a KPI',
      goalsSubtitle: 'Definujte metriky úspěchu a kdo je za tento případ použití zodpovědný.',
      goalLabel: 'Cíl případu použití*',
      goalPlaceholder: 'např. Snížit dobu kontroly smluv o 80 % s 99% přesností',
      goalHelp: 'Toto je nápovědný text pro uživatele.',
      kpisLabel: 'Klíčové ukazatele výkonnosti (KPI)',
      kpiPlaceholder: 'Zadejte nový KPI...',
      kpiAdd: 'Přidat',
      kpiHelp: 'Stiskněte Enter nebo klikněte na Přidat pro přidání nového KPI.',
      managerLabel: 'Procesní manažer',
      managerPlaceholder: 'Jméno procesního manažera',
      managerHelp: 'Kdo je za tento proces zodpovědný?',
      toastNoTask: 'Nebyl vybrán žádný úkol',
      toastNoPermission: 'Nemáte oprávnění upravovat tento úkol',
      toastUpdateSuccess: 'Úkol byl úspěšně aktualizován',
      toastUpdateError: 'Chyba při aktualizaci',
      addCategoryTitle: 'Vytvořit novou kategorii',
      addCategorySubtitle: 'Přidejte kategorii pro organizaci pokynů a usnadnění správy a filtrování analýzy.',
      addCategoryNameLabel: 'Název kategorie',
      addCategoryNamePlaceholder: 'Název kategorie',
      addCategoryEnterHint: 'Stiskněte Enter pro přidání nové kategorie.',
      addCategoryEmptyError: 'Zadejte prosím název kategorie.',
      addCategoryExistsError: 'Tato kategorie již existuje.',
      addCategoryNoAgentError: 'Nebyl vybrán žádný agent.',
      addCategoryCreateError: 'Chyba při vytváření kategorie.',
      addCategoryDeleteError: 'Chyba při mazání kategorie.',
      addCategoryConfirm: 'Přidat kategorii',
      addQuestionTitle: 'Vytvořit nový pokyn',
      editQuestionTitle: 'Upravit pokyn',
      questionTitleLabel: 'Název pokynu',
      questionTitlePlaceholder: 'Název pokynu',
      questionCategoryLabel: 'Kategorie',
      questionCategoryPlaceholder: 'Kategorie',
      questionDescriptionLabel: 'Popis',
      questionDescriptionPlaceholder: 'Zadejte popis...',
      questionEmptyTitleError: 'Zadejte prosím název pokynu.',
      questionEmptyDescriptionError: 'Zadejte prosím popis.',
      questionOrgPermissionError: 'Můžete upravovat pouze pokyny pro úkoly patřící vaší organizaci.',
      questionGenericError: 'Došlo k chybě. Zkuste to prosím znovu.',
      addQuestionConfirm: 'Přidat pokyn',
      updateQuestionConfirm: 'Aktualizovat pokyn',
      edit: 'Upravit',
      delete: 'Smazat',
      searchPlaceholder: 'Hledat',
      previous: 'Předchozí',
      next: 'Další',
      pageOf: 'Stránka {current} z {total}',
      showProjectDetailTitle: 'Aktivovat pokyny',
      showProjectDetailSubtitle: 'Pokud je vypnuto, karta pokynů v chatu je skryta.',
      showFilesTabTitle: 'Zobrazit kartu souborů v chatu',
      showFilesTabSubtitle: 'Pokud je vypnuto, karta souborů v postranním panelu chatu je pro tohoto agenta skryta.',
      allowLocalFilesTitle: 'Povolit nahrávání místních souborů',
      allowLocalFilesSubtitle: 'Pokud je vypnuto, uživatelé nemohou nahrávat místní soubory. Zobrazují se pouze soubory ze SharePointu.',
      questionsSelectTaskHint: 'Vyberte prosím úkol pro zobrazení jeho pokynů',
      questionsAddCategoriesButton: 'Přidat kategorie',
      questionsAddQuestionButton: 'Přidat pokyn',
      questionsReorderButton: 'Změnit pořadí',
      questionsReorderDoneButton: 'Hotovo',
      questionsReorderHint: 'Zobrazí se všechny pokyny. Přetáhněte pokyn na požadovanou pozici, i přes původní stránky.',
      questionsColCategories: 'Kategorie',
      questionsColTitle: 'Název pokynu',
      questionsColDescription: 'Popis',
      questionsEmptyTitle: 'Nebyly nalezeny žádné pokyny',
      questionsEmptyHint: 'Zatím nebyly přidány žádné pokyny. Klikněte na „Přidat pokyn" pro vytvoření nového pokynu.',
      questionsDeleteTitle: 'Smazat pokyn',
      questionsDeleteMessage: 'Opravdu chcete smazat tento pokyn?',
      aiProfileLoadError: 'Chyba při načítání AI profilu',
      aiProfileSaveError: 'Chyba při ukládání AI profilu',
      aiProfileNoPermission: 'Můžete upravovat AI profily pouze pro úkoly vaší organizace',
      aiProfileSaveSuccess: 'AI profil byl úspěšně uložen',
      aiProfileLoading: 'Načítání AI profilu...',
      aiProfileHeaderTitle: 'AI profil',
      aiProfileHeaderSubtitle: 'Nakonfigurujte, jak AI agent chápe kontext, generuje odpovědi a chová se během analýzy.',
      aiProfileModelDefaultLabel: 'Default (from environment variables)',
      aiProfileModelDefaultDesc: 'Používá výchozí konfiguraci',
      aiProfileModelGpt51Label: 'GPT-5.1',
      aiProfileModelGpt51Desc: 'Nejvýkonnější, vyvážený výkon',
      aiProfileModelGpt56LunaLabel: 'GPT-5.6-Luna',
      aiProfileModelGpt56LunaDesc: 'Experimentální, pouze webové vyhledávání',
      aiProfileModelGpt56TerraLabel: 'GPT-5.6-Terra',
      aiProfileModelGpt56TerraDesc: 'Experimentální, pouze pro webové vyhledávání',
      aiProfileReasoningLowLabel: 'Low',
      aiProfileReasoningLowDesc: 'Nejrychlejší odpovědi, základní uvažování',
      aiProfileReasoningStandardLabel: 'Standard',
      aiProfileReasoningStandardDesc: 'Standardní uvažování',
      aiProfileReasoningMediumLabel: 'Medium',
      aiProfileReasoningMediumDesc: 'Vyvážená rychlost a kvalita',
      aiProfileReasoningHighLabel: 'High',
      aiProfileReasoningHighDesc: 'Nejlepší přesnost, pomalejší odpovědi',
      aiProfileSearchHybrid: 'Hybrid',
      aiProfileSearchSemantic: 'Hybrid + Sémantické',
      aiProfileSearchVector: 'Vector',
      aiProfileSearchKeyword: 'Keyword',
      aiProfileKnowledgeSourceTitle: 'Zdroj znalostí',
      aiProfileKnowledgeSourceSubtitle: 'Vyberte, jaké zdroje může tento AI profil používat.',
      aiProfileModeDocumentsLabel: 'Pouze dokumenty',
      aiProfileModeDocumentsDesc: 'Odpovědi pouze na základě indexovaných dokumentů a Q/A párů.',
      aiProfileModeWebLabel: 'Pouze web',
      aiProfileModeWebDesc: 'Odpovědi pouze na základě výsledků webového vyhledávání.',
      aiProfileModeHybridLabel: 'Dokumenty + Web',
      aiProfileModeHybridDesc: 'Odpovědi s dokumenty i výsledky webového vyhledávání.',
      aiProfileBadgeRag: 'RAG',
      aiProfileBadgeNoRag: 'Bez RAG',
      aiProfileBadgeWeb: 'Web',
      aiProfileBadgeNoWeb: 'Bez webu',
      aiProfileIdentityTitle: 'AI Identity',
      aiProfileIdentitySubtitle: 'Definujte osobnost a instrukce pro chování AI.',
      aiProfileIdentitySubtitleDocuments: 'Dedikovaný systémový prompt pro odpovědi na základě dokumentů.',
      aiProfileIdentitySubtitleWeb: 'Dedikovaný systémový prompt pro odpovědi na základě webu.',
      aiProfileIdentitySubtitleHybrid: 'Dedikovaný systémový prompt pro odpovědi využívající dokumenty a web.',
      aiProfileDifferentPromptInfoTitle: 'Odlišný systémový prompt pro každý zdroj znalostí',
      aiProfileDifferentPromptInfoDesc: 'Při přepnutí vybraného režimu se načte dedikovaný systémový prompt pro daný režim.',
      aiProfileActivePromptBadge: 'Aktivní prompt: {mode}',
      aiProfileSystemPromptLabel: 'Systémový prompt: {mode}',
      aiProfileSystemPromptLabelPlain: 'Systémový prompt',
      aiProfileRegexDetectionLabel: 'Zobrazit UI reference',
      disclaimerToggleLabel: 'Upozornění na ochranu soukromí',
      disclaimerToggleDescription: 'Zobrazí upozornění na ochranu soukromí nad chatovým polem.',
      disclaimerTextLabel: 'Text upozornění',
      disclaimerTextHelp: 'Upozornění se zobrazí přímo nad polem pro dotaz.',
      disclaimerLearnMoreContentLabel: 'Obsah pro „Zjistit více"',
      disclaimerLearnMoreContentHelp: 'Obsah zobrazený v okně po kliknutí na „Zjistit více".',
      disclaimerIconLabel: 'Ikona',
      disclaimerInfoNotice: 'Upozornění se zobrazí při každém otevření chatu. Uživatelé jej mohou zavřít pro aktuální návštěvu; při opětovném otevření chatu se zobrazí znovu.',
      disclaimerPreviewLabel: 'Náhled',
      disclaimerLearnMoreLink: 'Zjistit více',
      disclaimerModalTitle: 'Další informace',
      toggleOn: 'Zapnuto',
      toggleOff: 'Vypnuto',
      aiProfileSystemPromptHelp: 'Technický prompt, který přesně řídí, jak AI odpovídá v tomto režimu. Ponechte prázdné pro použití výchozího nastavení.',
      aiProfileSystemPromptPlaceholder: 'Systémový prompt pro {mode}',
      aiProfileNameLabel: 'AI Profile Name',
      aiProfileNamePlaceholder: 'Agent pro kontrolu smluv',
      aiProfileDescriptionLabel: 'Popis',
      aiProfileDescriptionPlaceholder: 'Jste zkušený AI analytik smluv, který kontroluje právní dokumenty s přesností a důrazem na detail. Poskytujte jasné, strukturované odpovědi založené výhradně na obsahu dokumentu. Zvýrazněte rizika, závazky a klíčové podmínky. Formátujte odpovědi přehledně pomocí odrážek a nadpisů sekcí.',
      aiProfileDescriptionHelp: 'Tento prompt nastavuje tón a chování pro všechny AI odpovědi',
      aiProfileModelTitle: 'Model & Intelligence Settings',
      aiProfileModelSubtitle: 'Vyberte AI model a úroveň uvažování, která nejlépe odpovídá vašemu případu použití.',
      aiProfileModelLabel: 'Model',
      aiProfileModelSelectPlaceholder: 'Vybrat model',
      aiProfileReasoningLabel: 'Reasoning Effort',
      aiProfileResponseLengthLabel: 'Limit délky odpovědi',
      aiProfileResponseLengthHelp: 'Maximální počet tokenů v odpovědi. Vyšší hodnoty umožňují delší odpovědi. GPT-5.1 podporuje až 65 536 tokenů. Výchozí: 1 000',
      aiProfileResponseLengthExceeded: 'Maximální povolená hodnota je {max} — vyšší hodnoty nelze zadat.',
      aiProfileSourceSettingsTitle: 'Nastavení zdrojů',
      aiProfileSourceSettingsSubtitleDocuments: 'Nakonfigurujte nastavení vyhledávání dokumentů.',
      aiProfileSourceSettingsSubtitleWeb: 'Nakonfigurujte nastavení webového vyhledávání.',
      aiProfileSourceSettingsSubtitleHybrid: 'Nakonfigurujte vyhledávání dokumentů a nastavení webového vyhledávání.',
      aiProfileWebSourceSettingsTitle: 'Nastavení webového vyhledávání',
      aiProfileWebSourceSettingsSubtitle: 'Řídí veřejné výsledky webového vyhledávání používané AI.',
      aiProfileWebSourceBadge: 'WEBOVÝ ZDROJ',
      aiProfileWebSearchProviderLabel: 'Poskytovatel webového vyhledávání',
      aiProfileWebSearchProviderValue: 'Azure OpenAI Web Search',
      aiProfileSearchContextSizeLabel: 'Velikost kontextu vyhledávání',
      aiProfileSearchContextSizeHelp: 'Řídí, kolik webového obsahu Azure použije k podložení odpovědi – nejde o přesný počet zdrojů nebo citací.',
      aiProfileSearchContextLowLabel: 'Nízká',
      aiProfileSearchContextLowDesc: 'Rychlejší, povrchnější vyhledávání',
      aiProfileSearchContextMediumLabel: 'Střední',
      aiProfileSearchContextMediumDesc: 'Vyvážené výchozí nastavení',
      aiProfileSearchContextHighLabel: 'Vysoká',
      aiProfileSearchContextHighDesc: 'Důkladnější, podrobnější vyhledávání',
      aiProfileUseWebChatHistoryLabel: 'Použít historii chatu pro webové vyhledávání',
      aiProfileUseWebChatHistoryHelp: 'Zahrne předchozí otázky a odpovědi do webového vyhledávání. Ve výchozím nastavení vypnuto, protože to může zhoršit kvalitu odpovědí.',
      aiProfileVerbosityLabel: 'Podrobnost',
      aiProfileVerbosityOffLabel: 'Vypnuto',
      aiProfileVerbosityOffDesc: 'Neposíláno',
      aiProfileVerbosityLowLabel: 'Nízká',
      aiProfileVerbosityLowDesc: 'Stručné odpovědi',
      aiProfileVerbosityMediumLabel: 'Střední',
      aiProfileVerbosityMediumDesc: 'Vyvážené odpovědi',
      aiProfileVerbosityHighLabel: 'Vysoká',
      aiProfileVerbosityHighDesc: 'Podrobné odpovědi',
      aiProfileAdvancedWebSearchTitle: 'Pokročilé parametry webového vyhledávání',
      aiProfileForceWebSearchLabel: 'Vynutit webové vyhledávání',
      aiProfileForceWebSearchHelp: 'Zapnuto: každý požadavek prohledá web (tool_choice: required). Vypnuto: model se rozhodne sám (tool_choice: auto).',
      aiProfileStreamResponseLabel: 'Streamovat odpověď',
      aiProfileStreamResponseHelp: 'Zapnuto (výchozí): zobrazuje indikátor průběhu „vyhledávání“/„generování“ během požadavku. Vypnuto: jeden, nestreamovaný požadavek.',
      aiProfileIncludeSearchSourcesLabel: 'Protokolovat zdroje vyhledávání',
      aiProfileIncludeSearchSourcesHelp: 'Zapnuto: zdroje, které model prohledal, se navíc zaznamenají na serveru (pouze pro debugování, nemění zdroje zobrazené uživatelům).',
      aiProfileShowReasoningLabel: 'Zobrazit uvažování',
      aiProfileShowReasoningHelp: 'Zapnuto: zobrazí uživateli živě, o čem model právě přemýšlí, než se objeví odpověď (podobně jako zobrazení „Přemýšlení" u Claude).',
      aiProfileShowReasoningEffortWarning: 'Funguje pouze při úrovni uvažování „Střední" nebo „Vysoká" — u „Výchozí" nebo „Nízká" Azure žádné shrnutí uvažování nevytvoří.',
      aiProfileShowReasoningStreamWarning: 'Vyžaduje také zapnuté „Streamovat odpověď" (výše) — bez streamování nelze uvažování uživateli zobrazit živě.',
      aiProfileMaxToolCallsLabel: 'Max. počet volání nástroje',
      aiProfileMaxToolCallsPlaceholder: 'Neomezeno',
      aiProfileMaxToolCallsHelp: 'Omezuje, kolik vyhledávání/načtení stránek může Azure provést na jednu odpověď. Prázdné: limit se neposílá.',
      aiProfileAzureTimeoutLabel: 'Časový limit (sekundy)',
      aiProfileAzureTimeoutHelp: 'Jak dlouho čekat na odpověď Azure, než se požadavek vzdá. Výchozí: 180 sekund.',
      aiProfileAzureMaxRetriesLabel: 'Maximální počet opakování',
      aiProfileAzureMaxRetriesHelp: 'Kolikrát Azure automaticky zopakuje neúspěšný požadavek. Výchozí: 0 (bez automatického opakování — každé opakování znovu využije celý časový limit).',
      aiProfileMaxOutputTokensLabel: 'Max. výstupní tokeny: {mode}',
      aiProfileMaxOutputTokensHelp: 'Omezuje délku odpovědi pro tento režim. Výchozí: 1500. Maximálně povoleno: 65536 — vyšší hodnoty se automaticky omezí.',
      aiProfileMaxOutputTokensExceeded: 'Maximální povolená hodnota je {max} — vyšší hodnoty se při uložení automaticky omezí.',
      aiProfileAllowedDomainsLabel: 'Povolené domény',
      aiProfileBlockedDomainsLabel: 'Blokované domény',
      aiProfileDomainsPlaceholder: 'např. wikipedia.org, example.com',
      aiProfileDomainsHelp: 'Oddělené čárkou nebo jedna domína na řádek. Prázdné: filtr se neposílá.',
      aiProfileUserLocationLabel: 'Cílení na polohu',
      aiProfileUserLocationHelp: 'Zapnuto: přibližná poloha se odešle nástroji pro webové vyhledávání ke zlepšení místních výsledků.',
      aiProfileUserLocationCountryLabel: 'Země (ISO kód)',
      aiProfileUserLocationCountryPlaceholder: 'např. DE',
      aiProfileUserLocationCityLabel: 'Město',
      aiProfileUserLocationCityPlaceholder: 'např. Berlín',
      aiProfileUserLocationRegionLabel: 'Region',
      aiProfileUserLocationRegionPlaceholder: 'např. Berlín',
      aiProfileUserLocationTimezoneLabel: 'Časové pásmo',
      aiProfileUserLocationTimezonePlaceholder: 'např. Europe/Berlin',
      aiProfileContextTitle: 'Nastavení kontextu a vyhledávání',
      aiProfileContextSubtitle: 'Definuje, kolik kontextu AI použije před generováním odpovědí.',
      aiProfileSearchStrategyLabel: 'Strategie vyhledávání',
      aiProfileSearchStrategyPlaceholder: 'Vybrat strategii',
      aiProfileTopKLabel: 'Kontexty dokumentů (Top K)',
      aiProfileUseCustom: 'Use Custom',
      aiProfileTopKDefaultHint: 'Při deaktivaci se použije výchozí počet kontextů',
      aiProfileVectorKLabel: 'Fond kandidátů (Vector K)',
      aiProfileVectorKDefaultHint: 'Při deaktivaci se použije globální výchozí hodnota',
      aiProfileQaPairsLabel: 'Q/A páry jako kontext',
      aiProfileQaPairsDefaultHint: 'Při deaktivaci se nepoužijí žádné Q/A páry jako kontext',
      aiProfileWebSearchLabel: 'Webové vyhledávání',
      aiProfileWebSearchDescription: 'Vyhledává na internetu aktuální informace. Soubory a pokyny budou v chatu skryty.',
      aiProfileWebSearchEnabled: 'Aktivováno',
      aiProfileWebSearchDisabled: 'Deaktivováno',
      aiProfileWebSearchCustomPromptLabel: 'Vlastní vyhledávací prompt',
      aiProfileWebSearchCustomPromptDescription: 'Při každém webovém vyhledávání se přidá před dotaz uživatele. Viditelné pouze pro super administrátory.',
      aiProfileWebSearchCustomPromptPlaceholder: 'např. Hledej pouze zdroje z posledních 12 měsíců...',
      filesImportSuccess: '{count} soubor(ů) úspěšně importováno',
      filesImportError: 'Chyba při importu',
      filesAutoSyncSubfoldersEnabled: 'Auto-sync (s podsložkami) aktivován',
      filesAutoSyncFolderOnlyEnabled: 'Auto-sync (pouze složka) aktivován',
      filesAutoSyncEnableError: 'Chyba při aktivaci auto-sync',
      filesInheritedSyncNotice: 'Tato položka je synchronizována prostřednictvím nadřazené složky. Deaktivujte nadřazenou složku.',
      filesAutoSyncDisabled: 'Auto-sync deaktivován',
      filesAutoSyncDisableError: 'Chyba při deaktivaci',
      filesSpItemDeleted: 'Položka byla úspěšně smazána',
      filesSpItemDeleteError: 'Chyba při mazání',
      filesLastSyncNever: 'Nesynchronizováno',
      filesLastSyncJustNow: 'Právě teď',
      filesLastSyncMinutes: 'před {mins} min.',
      filesLastSyncHours: 'před {hrs} hod.',
      filesLastSyncDays: 'před {days} dny',
      filesRelToday: 'Dnes',
      filesRelYesterday: 'Včera',
      filesRelDaysAgo: 'před {days} dny',
      filesRelWeekAgo: 'před 1 týdnem',
      filesRelWeeksAgo: 'před {weeks} týdny',
      filesRelMonthAgo: 'před 1 měsícem',
      filesRelMonthsAgo: 'před {months} měsíci',
      filesRelYearAgo: 'před 1 rokem',
      filesRelYearsAgo: 'před {years} lety',
      filesGeneralFolder: 'Obecné',
      filesInvalidFileType: '{count} soubor(ů) přeskočeno. Povoleny jsou pouze PDF, DOC a DOCX.',
      filesFolderNameRequired: 'Zadejte prosím název složky',
      filesAddAtLeastOne: 'Přidejte prosím alespoň jeden soubor',
      filesSelectTaskRequired: 'Vyberte prosím úkol',
      filesAddedToFolderSuccess: '{count} soubor(ů) úspěšně přidáno do „{folderName}"',
      filesFolderCreatedSuccess: 'Složka „{folderName}" s {count} soubor(y) úspěšně vytvořena',
      filesPartialSuccess: '{success} souborů úspěšně, {errors} chyb',
      filesUploadError: 'Chyba při nahrávání souborů',
      filesMonthlyPageLimitExceeded: 'Limit pro nahrávání souborů v tomto měsíci je {limit} stránek. Pokusili jste se nahrát {attempted} stránek. Zkuste to prosím znovu a nahrajte pouze {limit} stránek.',
      filesCreateFolderError: 'Chyba při vytváření složky',
      filesDeleteFileSuccess: 'Soubor byl úspěšně smazán',
      filesDeleteFileError: 'Chyba při mazání souboru',
      filesDeleteFolderSuccess: 'Složka „{folderName}" byla úspěšně smazána',
      filesSelectTaskHint: 'Vyberte prosím úkol pro správu standardních souborů',
      filesTabAll: 'Všechny soubory',
      filesTabLocal: 'Místní soubory',
      filesTabSharePoint: 'SharePoint',
      filesSubTabOverview: 'Weby',
      filesSubTabImportReports: 'Zprávy o importu',
      filesImportReportsSubtitle: 'Jedna zpráva na import, nejnovější první.',
      filesImportReportsRefresh: 'Obnovit',
      filesImportReportsEmpty: 'Zatím žádné zprávy o importu',
      filesImportReportsEmptyHint: 'Zprávy se ukládají automaticky po každém importu.',
      filesImportReportsColSite: 'Web',
      filesImportReportsColStarted: 'Zahájeno',
      filesSubTabProtocol: 'Protokol mazání',
      filesDeletionReportsSubtitle: 'Jedna zpráva na smazání, nejnovější první.',
      filesDeletionReportsEmpty: 'Zatím žádné zprávy o smazání',
      filesDeletionReportsEmptyHint: 'Zprávy se ukládají automaticky po každé akci "Smazat importovaná data" nebo "Odebrat site".',
      filesViewDeletionReport: 'Zobrazit zprávu o smazání',
      filesCountSingular: 'soubor',
      filesCountPlural: 'soubory',
      filesItemSingular: 'položka',
      filesItemPlural: 'položky',
      filesHeaderAllSubtitle: 'Místní a SharePoint soubory v jednom seznamu.',
      filesHeaderLocalSubtitle: 'Soubory uložené a spravované přímo v DocAI.',
      filesConnectedSitesSingular: '{count} web přiřazen k tomuto agentovi',
      filesConnectedSitesPlural: '{count} webů přiřazeno k tomuto agentovi',
      filesUploadFile: 'Nahrát soubor',
      filesAddFolder: 'Přidat složku',
      filesSearchPlaceholder: 'Hledat',
      filesFilterLabel: 'Filtr',
      filesFilterLabelWithCount: 'Filtr (1)',
      filesNoResultsFound: 'Nebyly nalezeny žádné výsledky',
      filesNoFiles: 'Žádné soubory nejsou k dispozici',
      filesColName: 'Název souboru',
      filesColType: 'Typ souboru',
      filesColCreated: 'Vytvořeno',
      filesColModified: 'Upraveno',
      filesTypeSharePoint: 'SharePoint File',
      filesTypeLocal: 'Local File',
      filesDelete: 'Smazat',
      filesPageOf: 'Stránka {current} z {total}',
      filesPrevious: 'Předchozí',
      filesNext: 'Další',
      filesSyncProtocolTitle: 'Protokol mazání',
      filesSyncProtocolSubtitle: 'Auditní záznam všech mazání napříč weby',
      filesNoSyncActions: 'Žádné synchronizační akce nebyly zaznamenány',
      filesColSite: 'Web',
      filesColPath: 'Cesta',
      filesColTimestamp: 'Časové razítko',
      filesColRuleType: 'Typ pravidla',
      filesColEvent: 'Událost',
      filesColStatus: 'Stav',
      filesSiteOverview: 'SharePoint weby',
      filesFilesAndFolders: 'Soubory a složky',
      filesSyncedSuffix: '{count} synchronizováno',
      filesImporting: 'Importování...',
      filesImport: 'Import',
      filesImportWithCount: 'Import ({count})',
      filesNoItems: 'Žádné soubory nebo složky',
      filesDisableSync: 'Disable Sync',
      filesEnableAutoSyncFolderOnly: 'Enable Auto-Sync (folder only)',
      filesEnableAutoSync: 'Enable Auto-Sync',
      filesItemTypeFolder: 'Složka',
      filesItemTypeFile: 'Soubor',
      filesMetaModified: 'Upraveno: {date}',
      filesColNameSp: 'Název',
      filesColTypeSp: 'Typ',
      filesColSpFolder: 'folder',
      filesColSpFile: 'File',
      filesNoConnectedSites: 'Zatím žádné SharePoint weby',
      filesNoConnectedSitesHint: 'Přidejte web, který připojil váš organizační administrátor, vyberte knihovny a seznamy, které chcete použít, a poté spusťte import.',
      filesAddSiteButton: 'Přidat web',
      filesAddSiteTitle: 'Přidat SharePoint web',
      filesAddSiteSubtitle: 'Weby, které připojil váš organizační administrátor. Přidáním webu se nic neimportuje.',
      filesScheduleSyncButtonTitle: 'Naplánovat synchronizaci',
      filesScheduleSyncTitle: 'Plán synchronizace',
      filesScheduleSyncSubtitle: 'Vyberte datum a čas, kdy se všechny nakonfigurované weby automaticky synchronizují.',
      filesScheduleSyncDateLabel: 'Datum',
      filesScheduleSyncTimeLabel: 'Čas',
      filesScheduleSyncRepeatLabel: 'Automaticky opakovat',
      filesScheduleSyncRepeatDaysLabel: 'Každých (dní)',
      filesScheduleSyncSaveButton: 'Uložit plán',
      filesScheduleSyncRemoveButton: 'Odebrat plán',
      filesScheduleSyncNextRunLabel: 'Další synchronizace:',
      filesScheduleSyncNoScheduleLabel: 'Není nastaven žádný plán',
      filesScheduleSyncPastDateError: 'Datum a čas musí být v budoucnosti',
      filesScheduleSyncSelectDatePlaceholder: 'Vyberte datum',
      filesScheduleSyncSelectTimePlaceholder: 'Vyberte čas',
      filesScheduleSyncClearButton: 'Vymazat',
      filesScheduleSyncTodayButton: 'Dnes',
      filesAddSiteNoKnowledgeArea: 'Bez oblasti znalostí',
      filesAddSiteEmpty: 'Žádné další weby k dispozici — buď byly všechny již přidány, nebo váš organizační administrátor ještě nepřipojil web odpovídající vaší oblasti.',
      filesAddSiteSubmit: 'Přidat web',
      filesSiteAddedToAgent: 'Web přidán k agentovi',
      filesSourcesUpdated: 'Zdroje aktualizovány',
      filesImportedBrowserTitle: 'Importované soubory — {siteName}',
      filesImportedBrowserRoot: 'Všechny složky',
      filesImportedBrowserBack: 'Zpět',
      filesImportedBrowserEmpty: 'Na tomto místě nejsou žádné soubory',
      filesImportedBrowserFileCount: '{count} souborů',
      filesAddSiteNext: 'Další',
      filesAddSiteSourcesTitle: 'Zdroje ve {siteName}',
      filesAddSiteSourcesSubtitle: 'Knihovny dokumentů a seznamy s přílohami na tomto webu. Importovat lze pouze to, co vyberete.',
      filesAddSiteDocumentLibraries: 'Knihovny dokumentů',
      filesAddSiteItemCount: '{count} položek',
      filesAddSiteLists: 'Seznamy s přílohami',
      filesAddSiteListsHint: 'Seznamy zatím nelze vybrat. Jejich import vyžaduje autentizaci založenou na certifikátu, která zatím není nastavena — tento výběr bude povolen, jakmile bude k dispozici.',
      filesAddSiteNoSources: 'Na tomto webu nebyly nalezeny žádné knihovny dokumentů ani seznamy.',
      filesAddSiteSourcesFooterHint: 'Uložení si zapamatuje výběr. Import se spustí až po jeho zahájení.',
      filesAddSiteSelectAtLeastOne: 'Vyberte alespoň jeden zdroj.',
      filesOpenSite: 'Otevřít web',
      filesStatsFiles: 'Soubory',
      filesStatsFolders: 'Složky',
      filesStatsSubfolders: 'Podsložky',
      filesStatsLastSync: 'Poslední synchronizace',
      filesStatusImported: 'Importováno',
      filesSourcesChangedBadge: 'Zdroje změněny',
      filesSourcesChangedHint: 'Vybrané zdroje se od posledního importu změnily. Znovu importujte, aby se změna projevila.',
      filesEditSources: 'Upravit zdroje',
      filesImportAgain: 'Importovat znovu',
      filesStatsFilesImported: 'souborů importováno',
      filesStatsSourceCountSingular: '{count} zdroj',
      filesStatsSourceCountPlural: '{count} zdrojů',
      filesStatsLastImport: 'poslední import',
      filesStatsSkippedFailed: '{skipped} přeskočeno · {failed} selhalo',
      filesViewImportReport: 'Zobrazit zprávu o importu',
      filesImportInfoBanner: 'Importy se spouštějí pouze tehdy, když je zahájíte. Soubory přidané, změněné nebo smazané v SharePointu poté nebudou zachyceny až do dalšího importu.',
      filesDeleteImportedData: 'Smazat importovaná data',
      filesRemoveSiteFromAgent: 'Odebrat web z agenta',
      filesSiteRemovedFromAgent: 'Web odebrán z agenta',
      filesRemoveSiteError: 'Web se nepodařilo odebrat z agenta',
      filesDeleteImportedDataConfirmTitle: 'Smazat importovaná data?',
      filesDeleteImportedDataConfirmMessage: 'Všechny soubory importované z {siteName} budou odebrány — embeddingy, záznamy souborů a mapování. Tuto akci nelze vrátit zpět.',
      filesDeleteImportedDataError: 'Importovaná data se nepodařilo smazat. Zkuste to prosím později.',
      filesRemoveSiteConfirmTitle: 'Odebrat web?',
      filesRemoveSiteConfirmMessage: 'Web {siteName} bude odebrán z agenta a všechna z něj importovaná data — dokumenty, výsledky OCR, embeddingy a struktura — budou smazána. Tuto akci nelze vrátit zpět.',
      filesRemoveSiteConfirmButton: 'Odebrat web',
      filesImportReportTitle: 'Zpráva o importu — {siteName}',
      filesImportReportStarted: 'Zahájeno {date}',
      filesImportReportSources: 'Zdroje',
      filesImportReportFileTypes: 'Typy souborů',
      filesImportReportPageLimit: 'Limit stránek',
      filesImportReportMaxPages: 'Max. {max}',
      filesImportReportNoLimit: 'Bez limitu',
      filesImportReportImported: 'Importováno',
      filesImportReportSkipped: 'Přeskočeno',
      filesImportReportFailed: 'Selhalo',
      filesImportReportFileCount: '{count} souborů',
      filesImportReportDuration: 'Doba trvání',
      filesImportReportStoppedNote: 'Tento import byl předčasně zastaven. {count} soubor(ů) bylo zachováno, zbytek byl přeskočen.',
      filesImportReportStoppedNoteWithTotal: 'Tento import byl předčasně zastaven. V tomto běhu bylo zachováno {count} soubor(ů) (web má nyní celkem {total} souborů).',
      filesImportReportDiscardedNote: 'Tento import byl zrušen a zahozen. Z tohoto běhu nebylo nic zachováno.',
      filesImportReportBadgeStopped: 'Zastaveno',
      filesImportReportBadgeDiscarded: 'Zahozeno',
      embedErrorNoTextExtracted: 'Nebyl extrahován žádný text (pravděpodobně naskenované PDF)',
      filesImportReportLastImport: 'Poslední import',
      filesImportReportNoDetailHint: 'Tyto soubory byly importovány jednotlivě — pro to neexistuje podrobná zpráva s typy souborů, limitem stránek ani historií jednotlivých souborů.',
      filesImportReportSkippedTitle: 'Přeskočeno kvůli nastavení importu',
      filesImportReportSkippedHint: 'Tyto byly záměrně vyloučeny. Chcete-li je zahrnout, změňte nastavení a importujte znovu.',
      filesImportReportFailedTitle: 'Selhalo',
      filesImportReportFailedHint: 'Tyto nebylo možné zpracovat. Nový pokus přečte pouze tyto soubory.',
      filesDeletionReportTitle: 'Zpráva o smazání — {siteName}',
      filesImportReportRemoved: 'Odebráno',
      filesDeletionReportRemovedTitle: 'Odebrané soubory',
      filesDeletionReportFailedTitle: 'Selhalo',
      filesDeletionReportFailedHint: 'Tyto soubory se nepodařilo smazat.',
      filesRetryFailedFiles: 'Zkusit znovu selhané soubory ({count})',
      filesRetryReportNotCurrent: 'Tato zpráva již nepopisuje aktuální stav lokality a nelze ji znovu zkusit.',
      filesRetryImportRunning: 'Pro tuto lokalitu již probíhá import. Počkejte prosím na jeho dokončení.',
      filesRetryFailedError: 'Nový pokus se nezdařil. Zkuste to prosím později.',
      filesStartImportTitle: 'Zahájit import',
      filesStartImportFileTypes: 'Typy souborů',
      filesStartImportPageLimit: 'Limit stránek na soubor',
      filesStartImportNoLimit: 'Bez limitu',
      filesStartImportPageLimitHint: 'Soubory s více stránkami jsou přeskočeny a uvedeny ve zprávě o importu.',
      filesStartImportReplaceWarningBold: 'Tato lokalita byla naposledy importována {date}.',
      filesStartImportReplaceWarningRest: 'Opětovný import nahradí {count} dokumentů, výsledky OCR a embeddingy, které jsou pro ni aktuálně uloženy.',
      filesStartImportSourcesAddedLabel: 'Přidané zdroje:',
      filesStartImportSourcesRemovedLabel: 'Odebrané zdroje (jejich soubory budou smazány):',
      filesStartImportInfoStatic: 'Import kompletně načte vybrané zdroje. Soubory následně smazané nebo změněné v SharePointu zůstanou zde beze změny, dokud znovu neprovedete import.',
      filesStartImportSubmit: 'Zahájit import',
      filesImportCompleted: 'Import pro {site} dokončen',
      filesImportFailed: 'Import se nezdařil',
      filesImportRunning: 'Import probíhá',
      filesImportReconnecting: 'Obnovuje se připojení…',
      filesImportProcessedOf: 'Zpracováno {processed} z {total} souborů',
      filesImportCancel: 'Zrušit',
      filesImportCancelling: 'Ruší se…',
      filesImportCancelTitle: 'Zrušit import?',
      filesImportCancelMessage: 'Chcete ponechat již importované soubory a zastavit pouze zbytek, nebo zcela vrátit dosavadní import?',
      filesImportCancelStop: 'Zastavit (ponechat dosavadní soubory)',
      filesImportCancelDiscard: 'Zahodit vše',
      filesImportCancelBack: 'Zpět',
      filesImportCancelError: 'Import se nepodařilo zrušit',
      filesImportCancelProcessing: 'Zpracovává se…',
      filesEmptyInFolder: 'V této složce nejsou žádné soubory',
      filesEmptyFolders: 'Nebyly nalezeny žádné složky',
      filesEmptyLocalHint: 'Klikněte na „Přidat složku" pro vytvoření nové složky se soubory.',
      filesEmptySharePointHint: 'Importujte soubory ze SharePoint pro jejich zobrazení zde.',
      filesCreatedOn: 'Vytvořeno: {date}',
      filesModifiedOn: 'Upraveno: {date}',
      filesColFileName: 'Název souboru',
      filesColFolderName: 'Název složky',
      filesColUploadedAt: 'Nahráno dne',
      filesColSize: 'Velikost',
      filesColUsedIn: 'Použito v',
      filesColFiles: 'Soubory',
      filesColCreatedAt: 'Vytvořeno dne',
      filesColLastModified: 'Naposledy upraveno',
      filesSourceDeleted: 'Zdroj smazán',
      filesProjectSingular: 'projekt',
      filesProjectPlural: 'projekty',
      filesAddToFolderTitle: 'Přidat soubory do „{folderName}"',
      filesCreateNewFolderTitle: 'Vytvořit novou složku',
      filesAddToFolderSubtitle: 'Nahrajte nové soubory do této složky.',
      filesCreateNewFolderSubtitle: 'Vytvořte složku a přidejte soubory.',
      filesFolderNameLabel: 'Název složky *',
      filesFolderNamePlaceholder: 'např. smlouvy, šablony, zprávy...',
      filesAddFilesLabel: 'Přidat soubory *',
      filesDragOrClick: 'Přetáhněte soubory sem nebo klikněte pro výběr',
      filesSupportedTypes: 'PDF, DOC, DOCX (možno více souborů)',
      filesSelectedFiles: 'Vybrané soubory ({count})',
      filesRemoveAll: 'Odebrat vše',
      filesCancel: 'Zrušit',
      filesUploadingStatus: 'Nahrávání...',
      filesCreatingStatus: 'Vytváření...',
      filesUpload: 'Nahrát',
      filesCreateFolder: 'Vytvořit složku',
      filesUploadProgressUploading: 'Soubory se nahrávají...',
      filesUploadProgressCreating: 'Složka se vytváří...',
      filesFileOfTotal: 'Soubor {current} z {total}',
      filesPreparing: 'Příprava...',
      filesPercentDone: '{percent} % dokončeno',
      filesDeleteFolderTitle: 'Smazat složku',
      filesDeleteFileTitle: 'Smazat soubor',
      filesDeleteFolderMessage: 'Opravdu chcete smazat složku „{name}" s {count} soubor(y)? Tuto akci nelze vrátit zpět.',
      filesDeleteSpItemMessage: 'Opravdu chcete smazat „{name}"? Tuto akci nelze vrátit zpět.',
      filesDeleteFileMessage: 'Opravdu chcete smazat „{name}"? Tuto akci nelze vrátit zpět.',
      filesDeleting: 'Mazání...',
      filesFilterTitle: 'Filtr',
      filesFilterFileTypeLabel: 'Typ souboru',
      filesFilterOptLocal: 'Local File',
      filesFilterOptSharePoint: 'SharePoint File',
      filesFilterSortByLabel: 'Řadit podle',
      filesSortNewest: 'Nejnovější',
      filesSortOldest: 'Nejstarší',
      filesSortMostProgress: 'Most progress',
      filesSortLeastProgress: 'Least progress',
      filesResetAll: 'Obnovit vše',
      filesApply: 'Použít',
      shortcutsTabEmoji: 'Emoji',
      shortcutsTabCustom: 'Vlastní',
      shortcutsCustomEmojiLabel: 'Zadejte libovolné emoji:',
      shortcutsCustomEmojiPlaceholder: 'Vložte emoji sem...',
      shortcutsUseButton: 'Použít',
      shortcutsModalEditTitle: 'Upravit zkratku',
      shortcutsModalAddTitle: 'Přidat novou zkratku',
      shortcutsNameLabel: 'Název zkratky',
      shortcutsNamePlaceholder: 'např. Zjednodušit jazyk',
      shortcutsIconLabel: 'Ikona',
      shortcutsIconChange: 'Klikněte pro změnu ikony',
      shortcutsDescriptionLabel: 'Popis',
      shortcutsDescriptionPlaceholder: 'Popište, co tato zkratka dělá...',
      shortcutsOrderLabel: 'Pořadí zobrazení',
      shortcutsActiveLabel: 'Aktivní',
      shortcutsCancel: 'Zrušit',
      shortcutsSaving: 'Ukládání...',
      shortcutsSaveButton: 'Uložit zkratku',
      shortcutsUpdateSuccess: 'Zkratka byla úspěšně aktualizována',
      shortcutsCreateSuccess: 'Zkratka byla úspěšně vytvořena',
      shortcutsSaveError: 'Chyba při ukládání zkratky',
      shortcutsDeleteSuccess: 'Zkratka byla úspěšně smazána',
      shortcutsDeleteError: 'Chyba při mazání zkratky',
      shortcutsOrderSaveSuccess: 'Pořadí bylo úspěšně uloženo',
      shortcutsOrderSaveError: 'Chyba při ukládání pořadí',
      shortcutsSelectTaskHint: 'Vyberte prosím úkol pro správu zkratek',
      shortcutsHeaderTitle: 'Zkratky',
      shortcutsHeaderSubtitle: 'Vytvářejte a spravujte opakovaně použitelné AI zkratky pro rychlou transformaci obsahu během analýzy.',
      shortcutsListTitle: 'Seznam zkratek',
      shortcutsAddButton: 'Přidat zkratku',
      shortcutsEmptyTitle: 'Žádné zkratky nejsou k dispozici',
      shortcutsEmptyHint: 'Klikněte na „Přidat zkratku" pro vytvoření vaší první zkratky.',
      shortcutsInactiveBadge: 'Neaktivní',
      shortcutsEditTooltip: 'Upravit',
      shortcutsDeleteTooltip: 'Smazat',
      shortcutsInfoTitle: 'Jak zkratky fungují',
      shortcutsInfoText: 'Zkratky se zobrazují pod AI odpověďmi v oblasti analýzy. Když na jednu kliknete, systém znovu zpracuje odpověď s promptem zkratky a zobrazí výsledek vedle původní odpovědi. To umožňuje uživatelům rychle získat překlady, zjednodušení, shrnutí nebo jiné transformace, které nakonfigurujete.',
      shortcutsDeleteModalTitle: 'Smazat zkratku',
      shortcutsDeleteModalMessage: 'Opravdu chcete smazat „{name}"? Tuto akci nelze vrátit zpět.',
      overviewAdded: 'Přidáno',
      overviewAdd: 'Přidat',
      overviewNoAgent: 'Nebyl vybrán žádný agent.',
      overviewAvailableWidgets: 'Dostupné widgety',
      overviewAvailableSubtitle: 'Procházejte a přidávejte widgety pro přizpůsobení rozvržení přehledu projektu.',
      overviewPreviewTitle: 'Náhled přehledu projektu',
      overviewPreviewSubtitle: 'Toto rozvržení bude aplikováno na všechny projekty používající tohoto agenta.',
      overviewUnsavedChanges: 'Neuložené změny',
      overviewCancel: 'Zrušit',
      overviewSaving: 'Ukládání…',
      overviewSaveLayout: 'Uložit rozvržení',
      overviewEmpty: 'Nebyly přidány žádné widgety. Přidejte widgety ze seznamu výše.',
    },

    projectSetup: {
      title: 'Nastavení projektu',
      subtitle: 'Nakonfigurujte pole, která se zobrazí ve formuláři pro vytvoření projektu.',
      formBuilderTitle: 'Tvůrce formuláře',
      addField: 'Přidat pole',
      emptyState: 'Zatím nejsou definována žádná pole. Klikněte na „Přidat pole".',
      saveChanges: 'Uložit změny',
      savedSuccess: 'Pole úspěšně uložena',
      saveError: 'Chyba při ukládání polí',
      moveUp: 'Posunout nahoru',
      moveDown: 'Posunout dolů',
      addFieldTitle: 'Přidat pole',
      editFieldTitle: 'Upravit pole',
      fieldTypeLabel: 'Typ pole',
      fieldLabelLabel: 'Popisek pole',
      fieldLabelPlaceholder: 'např. Název projektu',
      requiredLabel: 'Povinné pole',
      requiredHelp: 'Uživatelé musí toto pole vyplnit.',
      requiredError: 'Vyplňte prosím všechna povinná pole.',
      helperLabel: 'Nápověda',
      helperPlaceholder: 'Krátký popis pro uživatele',
      defaultValueLabel: 'Výchozí hodnota',
      defaultValuePlaceholder: 'Volitelná výchozí hodnota',
      optionsLabel: 'Možnosti',
      optionPlaceholder: 'Přidat možnost',
      addOption: 'Přidat',
      saveField: 'Uložit pole',
      fieldTypeText: 'Textové pole',
      fieldTypeTextarea: 'Víceřádkový text',
      fieldTypeNumber: 'Číslo',
      fieldTypeDate: 'Datum',
      fieldTypeDropdown: 'Rozbalovací nabídka',
      fieldTypeImage: 'Nahrání obrázku',
      fieldTypeFile: 'Nahrání souboru',
      directChatModeLabel: 'Přímý chatovací režim',
      directChatModeEnabledBadge: 'Aktivováno',
      directChatModeDesc: 'Když uživatel vybere tohoto agenta, dostane se přímo do živého chatu — není potřeba žádné nastavení požadavku.',
      fieldProjectName: 'Název projektu',
      fieldProjectNameDesc: 'Textové pole pro název projektu – uloženo jako titulek projektu.',
      fieldProjectImage: 'Obrázek projektu',
      fieldProjectImageDesc: 'Nahrání obrázku pro miniaturu projektu.',
      fieldUploadMethods: 'Metody nahrávání',
      fieldUploadMethodsDesc: 'Nakonfigurujte, jak mohou uživatelé nahrávat dokumenty.',
      uploadSourceComputer: 'Počítač',
      uploadSourceSharePoint: 'SharePoint',
      uploadSourceBoth: 'Obojí',
      uploadSourceLabel: 'Zdroj nahrávání',
      uploadSourceHelp: 'Vyberte, jak uživatelé poskytnou soubory pro toto pole.',
      sourceComputerTitle: 'Nahrát z počítače',
      sourceComputerSub: 'Uživatel nahraje soubory přímo z lokálního zařízení.',
      sourceSharePointTitle: 'Složka SharePoint',
      sourceSharePointSub: 'Uživatel vybere složku z připojeného webu SharePoint.',
      sourceBothTitle: 'Obě metody',
      sourceBothSub: 'Uživatel si vybere mezi lokálním nahráním a SharePointem.',
      imageUploadSettings: 'Nastavení nahrávání obrázků',
      acceptedFormatsLabel: 'Povolené formáty',
      acceptedFormatsHelp: 'Vyberte, které formáty obrázků mohou uživatelé nahrát.',
      multipleLabel: 'Povolit více obrázků',
      multipleHelp: 'Uživatel může nahrát více než jeden obrázek.',
      maxImageCountLabel: 'Maximální počet obrázků',
      imagesMax: 'Obrázků max.',
      imageConstraintsLabel: 'Omezení obrázků',
      optional: 'volitelné',
      aspectRatioLabel: 'Poměr stran',
      arAny: 'Libovolný', arAnySub: 'Bez omezení',
      ar11: '1:1', ar11Sub: 'Čtverec',
      ar43: '4:3', ar43Sub: 'Standard',
      ar169: '16:9', ar169Sub: 'Širokoúhlý',
      ar32: '3:2', ar32Sub: 'Foto',
      minDimensionsLabel: 'Minimální rozměry',
      widthPx: 'Šířka (px)',
      heightPx: 'Výška (px)',
      outputQualityLabel: 'Kvalita výstupu',
      qOriginal: 'Originál', qOriginalSub: 'Bez komprese',
      qHigh: 'Vysoká', qHighSub: '~80% kvalita',
      qMedium: 'Střední', qMediumSub: '~60% kvalita',
      qLow: 'Nízká', qLowSub: '~40% kvalita',
      maxFileSizeLabel: 'Max. velikost souboru',
      uploadImageBtn: 'Nahrát obrázek',
      uploadImagesBtn: 'Nahrát další obrázky ({remaining})',
      errMaxCount: 'Maximálně {count} obrázků povoleno.',
      errFormat: 'Formát není povolen: {name}',
      errSize: '{name} překračuje {mb} MB.',
      errMinDim: '{name}: vyžadováno minimálně {w}×{h} px.',
      errAspect: '{name}: vyžadován poměr stran {ratio}.',
    },

    widgets: {
      notGenerated: 'Dosud nevygenerováno.',
      noSubmissionDate: 'Nebylo extrahováno žádné datum odevzdání',
      documents: 'Dokumenty',
      titles: {
        summary: 'AI shrnutí',
        checklist_progress: 'Průběh kontrolního seznamu',
        location: 'Umístění AI žádosti',
        analysis_score: 'Skóre analýzy',
        document_count: 'Počet dokumentů',
        participants: 'Účastníci',
        submission_date: 'Odpočet do termínu',
      },
    },

    settings: {
      title: 'Nastavení',
      tabAdministrator: 'Administrátor',
      tabOrganization: 'Organizace',
      tabLanguage: 'Jazyk',
      tabManageTasks: 'Spravovat úkoly',
    },

    organization: {
      searchPlaceholder: 'Hledat',
      addButton: 'Nová organizace',
      columnLogo: 'Logo',
      columnName: 'Název organizace',
      columnDescription: 'Popis',
      columnSharePoint: 'SharePoint',
      columnColor: 'Barva',
      columnSubscription: 'Předplatné',
      logoFallback: 'Logo',
      spConnected: 'Připojeno',
      spError: 'Chyba',
      spNotConnected: 'Nepřipojeno',
      subscriptionActive: 'Aktivní',
      subscriptionInactive: 'Neaktivní',
      actionManage: 'Spravovat',
      actionEdit: 'Upravit organizaci',
      actionDelete: 'Smazat organizaci',
      pageOf: 'Stránka {current} z {total}',
      paginationPrev: 'Předchozí',
      paginationNext: 'Další',
      modalCreateTitle: 'Vytvořit novou organizaci',
      modalEditTitle: 'Upravit organizaci',
      logoLabel: 'Logo organizace',
      logoAlt: 'Logo',
      logoNone: 'Žádné logo',
      logoUpload: 'Nahrát logo',
      nameLabel: 'Název organizace *',
      namePlaceholder: 'Zadejte název organizace',
      descriptionLabel: 'Popis (nepovinné)',
      descriptionPlaceholder: 'Krátký popis zobrazený uživatelům',
      footerAddressLabel: 'Adresa v zápatí (nepovinné)',
      footerAddressPlaceholder: 'Firma s.r.o.\nUlice 1, Město\nTelefon 000 000 000\nE-mail info@firma.cz',
      footerAddressHelp: 'Zobrazuje se v zápatí Word dokumentů generovaných touto organizací. Každý řádek se zobrazí na samostatném řádku.',
      primaryColorLabel: 'Primární barva',
      uploadingState: 'Nahrávání...',
      savingState: 'Ukládání...',
      submitCreate: 'Vytvořit organizaci',
      submitSave: 'Uložit',
      deleteTitle: 'Smazat organizaci',
      deleteMessage: 'Opravdu chcete smazat organizaci „{name}"?',
      subModalTitle: 'Přiřadit předplatné k „{name}"',
      subModalSelectLabel: 'Vybrat předplatné',
      subModalSelectPlaceholder: 'Vybrat předplatné...',
      subModalNone: 'Žádné předplatné',
      subModalOptionInfo: '{name} – {tasks} úkolů, {pages} stránek',
      toastCreated: 'Organizace byla úspěšně vytvořena',
      toastUpdated: 'Organizace byla úspěšně aktualizována',
      toastDeleted: 'Organizace byla smazána',
      toastSaveError: 'Nepodařilo se uložit',
      toastDeleteError: 'Nepodařilo se smazat',
      toastSubAssigned: 'Předplatné bylo úspěšně přiřazeno',
      toastSubAssignError: 'Nepodařilo se přiřadit předplatné',
    },

    manageOrganization: {
      breadcrumbSettings: 'Nastavení',
      breadcrumbManage: 'Správa organizace',
      loading: 'Načítání...',
      summaryDepartments: 'Uživatelské skupiny',
      summaryUsers: 'Uživatelé',
      summarySubscription: 'Předplatné',
      summaryActive: 'Aktivní',
      summaryInactive: 'Neaktivní',
      tabDepartments: 'Uživatelské skupiny',
      tabSubscriptions: 'Předplatná',
      tabAgentOrder: 'Pořadí agentů',
      tabSharepoint: 'Propojit SharePoint',
      deptSearchPlaceholder: 'Hledat',
      deptAddButton: 'Přidat uživatelskou skupinu',
      deptEmptyTitle: 'Zatím žádné uživatelské skupiny',
      deptEmptyHint: 'Vytvořte svou první uživatelskou skupinu.',
      noResultsTitle: 'Nebyly nalezeny žádné výsledky',
      noResultsHint: 'Zkuste jiné vyhledávací výrazy.',
      deptColumnName: 'Uživatelská skupina',
      deptColumnDescription: 'Popis',
      deptColumnCreated: 'Vytvořeno',
      deptActionEdit: 'Upravit uživatelskou skupinu',
      deptActionDelete: 'Smazat uživatelskou skupinu',
      deptModalCreateTitle: 'Vytvořit uživatelskou skupinu',
      deptModalEditTitle: 'Upravit uživatelskou skupinu',
      deptModalNameLabel: 'Název uživatelské skupiny*',
      deptModalNamePlaceholder: 'např. Obchod',
      deptModalDescriptionLabel: 'Popis (nepovinné)',
      deptModalDescriptionPlaceholder: 'Krátký popis účelu této uživatelské skupiny.',
      deptModalSubmitCreate: 'Vytvořit uživatelskou skupinu',
      deptModalSubmitSave: 'Uložit',
      deptModalCreating: 'Vytváření...',
      deptModalSaving: 'Ukládání...',
      deptDeleteTitle: 'Smazat uživatelskou skupinu',
      deptDeleteMessage: 'Smazáním této uživatelské skupiny bude odstraněno její přiřazení k agentům. Agenti zůstanou aktivní, ale nebudou již patřit do této uživatelské skupiny.',
      deptToastCreated: 'Uživatelská skupina byla úspěšně vytvořena',
      deptToastUpdated: 'Uživatelská skupina byla úspěšně aktualizována',
      deptToastDeleted: 'Uživatelská skupina byla úspěšně smazána',
      deptToastSaveError: 'Nepodařilo se uložit uživatelskou skupinu',
      deptToastDeleteError: 'Nepodařilo se smazat uživatelskou skupinu',
      subSearchPlaceholder: 'Hledat',
      subAddButton: 'Nové předplatné',
      subEmptyTitle: 'Zatím žádná předplatná',
      subEmptyHint: 'Vytvořte své první předplatné.',
      subColumnName: 'Název předplatného',
      subColumnDescription: 'Popis',
      subActionViewCredits: 'Zobrazit využití kreditů',
      subActionViewUsage: 'Zobrazit využití',
      subActionEdit: 'Upravit',
      subActionDelete: 'Smazat',
      subModalCreateTitle: 'Vytvořit nové předplatné',
      subModalEditTitle: 'Upravit předplatné',
      subModalInfoSection: 'Informace o předplatném',
      subModalNameLabel: 'Název předplatného *',
      subModalNamePlaceholder: 'např. xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
      subModalDescriptionLabel: 'Popis (nepovinné)',
      subModalDescriptionPlaceholder: 'Zadejte popis',
      subModalOrgLabel: 'Organizace *',
      subModalOrgPlaceholder: 'Vybrat organizaci...',
      subModalNoOrgs: 'Žádné organizace nejsou k dispozici',
      subModalUsageSection: 'Nastavení využití',
      subModalUsageHint: 'Řiďte chování využití pro toto předplatné.',
      subModalTransferableLabel: 'Převést nevyužité kredity do dalšího měsíce',
      subModalTransferableHint: 'Při aktivaci se nevyužité kredity převedou do dalšího fakturačního období.',
      subModalMaxPagesLabel: 'Maximální počet stránek na relaci',
      subModalAgentLimitsSection: 'Limity agentů',
      subModalAgentLimitsHint: 'Nastavte pro každého AI agenta dvě nastavení využití. Prázdné pole znamená neomezeno.',
      subModalAgentColumn: 'Agent',
      subModalMaxRunsColumn: 'Max běhů',
      subModalMaxPagesColumn: 'Max stránek',
      subModalStandardPagesColumn: 'Stránky standardních souborů za měsíc',
      subModalUploadPagesColumn: 'Stránky nahrané za relaci',
      subModalUnlimitedPlaceholder: 'Neomezeno',
      subModalNoAgents: 'Pro tuto organizaci nebyli nalezeni žádní agenti.',
      subModalSaving: 'Ukládání...',
      subModalSubmit: 'Uložit',
      subModalOcrSection: 'Limity stránek OCR',
      subModalOcrHint: 'Globální limity pro zpracování OCR. Ponechte prázdné pro žádný limit.',
      subModalOcrDaily: 'Denně (stránky)',
      subModalOcrWeekly: 'Týdně (stránky)',
      subModalOcrMonthly: 'Měsíčně (stránky)',
      subModalOcrPlaceholder: 'Bez limitu',
      subDeleteTitle: 'Smazat předplatné',
      subDeleteMessage: 'Opravdu chcete smazat předplatné „{name}"? Budou odstraněny také všechny přiřazené limity.',
      subToastCreated: 'Předplatné bylo úspěšně vytvořeno',
      subToastUpdated: 'Předplatné bylo úspěšně aktualizováno',
      subToastDeleted: 'Předplatné bylo úspěšně smazáno',
      subToastSaveError: 'Nepodařilo se uložit předplatné',
      subToastDeleteError: 'Nepodařilo se smazat předplatné',
      creditBackButton: 'Předplatná',
      creditTitle: 'Využití kreditů',
      creditSubtitle: 'Aktuální stav stránek standardních souborů a nastavení nahrávání pro každého agenta.',
      creditGrantButton: 'Udělit dočasné kredity',
      creditColumnAgent: 'Agent',
      creditColumnRunsUsed: 'Využité běhy',
      creditColumnTempRuns: 'Dočasné běhy uděleny',
      creditColumnPagesUsed: 'Využité stránky',
      creditColumnTempPages: 'Dočasné stránky uděleny',
      creditColumnStandardPages: 'Stránky standardních souborů (tento měsíc)',
      creditUnlimitedLabel: '(neomezeno)',
      creditTaskFallback: 'Úkol {id}',
      creditBreakdown: '({monthly} měsíčně + {temp} dočasně)',
      creditEmptyTitle: 'Žádné limity agentů',
      creditEmptyHint: 'Tomuto předplatnému nejsou přiřazeny žádné limity agentů.',
      grantTitle: 'Udělit dočasné kredity',
      grantSubscriptionLabel: 'Předplatné',
      grantAgentLabel: 'Agent *',
      grantAgentPlaceholder: 'Vybrat agenta...',
      grantNoAgents: 'Žádní agenti nejsou k dispozici',
      grantRunsLabel: 'Dočasné běhy',
      grantRunsHint: 'Udělte tomuto agentovi další běhy.',
      grantPagesLabel: 'Dočasné stránky',
      grantPagesHint: 'Udělte tomuto agentovi další stránky.',
      grantNoteTitle: 'Poznámka',
      grantNoteBody: 'Dočasné kredity nemění limity předplatného a jsou spotřebovány jako první, než se použije běžná alokace.',
      grantSubmit: 'Udělit kredity',
      grantSaving: 'Udělování...',
      grantToastSuccess: 'Dočasný kredit byl úspěšně udělen',
      grantToastError: 'Nepodařilo se udělit kredit',
      spSearchPlaceholder: 'Hledat',
      spEditButton: 'Upravit SharePoint',
      spConnectSiteButton: 'Připojit web',
      spConnectButton: 'Připojit SharePoint',
      spEmptyTitle: 'Žádné připojené weby',
      spEmptyHint: 'Vytvořte projekt pro nahrání souborů a spuštění analýz.',
      spColumnSiteName: 'Název webu',
      spColumnUrl: 'URL',
      spColumnStatus: 'Stav',
      spColumnSyncInterval: 'Interval synchronizace',
      spColumnSite: 'Web',
      spColumnKnowledgeArea: 'Znalostní oblast',
      spColumnUsedByAgents: 'Používáno agenty',
      spAllKnowledgeAreas: 'Všechny znalostní oblasti',
      spUsedByAgentsSingular: '{count} agent',
      spUsedByAgentsPlural: '{count} agentů',
      spNotUsed: 'Nepoužíváno',
      spCanBeDisconnected: 'Lze odpojit',
      spInfoBannerTitle: 'Připojení webu pouze uděluje přístup.',
      spInfoBannerBody: 'Nic se automaticky neskenuje, nesleduje ani neimportuje — žádné plánované spouštění, žádná upozornění na změny, žádné OCR, žádné embeddingy. Obsah se importuje pouze tehdy, když administrátor agenta spustí import, a zůstává ve stavu tohoto importu, dokud jej někdo znovu nespustí.',
      spStatusSynced: 'Synchronizováno',
      spStatusError: 'Chyba',
      spStatusPending: 'Čeká',
      spStatusConnected: 'Připojeno',
      spStatusReadOnlyAccess: 'Přístup pouze pro čtení',
      spIntervalMinutes: '{count} min.',
      spActionEditInterval: 'Upravit interval synchronizace',
      spActionDelete: 'Smazat',
      spActionViewDetails: 'Zobrazit podrobnosti webu',
      spActionEditNameArea: 'Upravit název a znalostní oblast',
      spActionCheckAccessNow: 'Zkontrolovat přístup nyní',
      spActionCopySiteId: 'Kopírovat ID webu',
      spActionDisconnect: 'Odpojit web',
      spToastAccessConfirmed: 'Přístup potvrzen — pouze pro čtení, žádný obsah nebyl přečten',
      spToastAccessFailed: 'Kontrola přístupu selhala: {error}',
      spToastSiteIdCopied: 'ID webu zkopírováno',
      spToastDisconnectBlocked: 'Nelze odpojit — stále používáno těmito agenty: {agents}',
      spConnLoading: 'Načítání...',
      spConnEditTitle: 'Upravit SharePoint',
      spConnCreateTitle: 'Připojit SharePoint',
      spConnTenantIdLabel: 'Tenant ID',
      spConnTenantIdPlaceholder: 'např. xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
      spConnClientIdLabel: 'Client ID',
      spConnClientIdPlaceholder: 'Zadejte Client ID registrace aplikace Azure',
      spConnClientSecretLabel: 'Client Secret',
      spConnClientSecretPlaceholderEdit: '••••••••',
      spConnClientSecretPlaceholderCreate: 'Zadejte Client Secret aplikace Azure',
      spConnSecurityNote: 'Tyto přihlašovací údaje jsou bezpečně uloženy a používány výhradně k připojení k vaší SharePoint site.',
      spConnDisconnectButton: 'Odpojit SharePoint',
      spConnSavingButton: 'Ukládání...',
      spConnSaveButton: 'Uložit připojení',
      spConnDisconnectConfirmTitle: 'Opravdu odpojit SharePoint?',
      spConnDisconnectConfirmMessage: 'Všechny připojené sites a jejich mapování budou odebrány. Již importované soubory a embeddingy zůstanou zachovány, ale ze SharePointu již nebude možné nic synchronizovat, dokud nebude vytvořeno nové připojení.',
      spConnDisconnectingButton: 'Odpojování...',
      spConnDisconnectConfirmButton: 'Ano, odpojit',
      spErrorInvalidSecret: 'Client Secret pro SharePoint je neplatný nebo vypršel. Aktualizujte prosím údaje připojení.',
      spErrorAppNotFound: 'Registrace aplikace Azure nebyla pro tento tenant nalezena. Zkontrolujte prosím Tenant ID a Client ID.',
      spErrorTenantNotFound: 'Tenant ID nebylo nalezeno. Zkontrolujte jej prosím a zkuste to znovu.',
      spErrorAuthFailed: 'Přihlášení k SharePointu se nezdařilo. Zkontrolujte prosím údaje připojení.',
      spErrorPermissionDenied: 'Přístup odepřen. Aplikaci Azure chybí potřebná oprávnění pro SharePoint.',
      spErrorSiteNotFound: 'SharePoint site nebyla nalezena.',
      spErrorGeneric: 'Připojení k SharePointu se nezdařilo. Zkontrolujte prosím nastavení připojení.',
      spDetailsSubtitle: 'Uložená data připojení. Nic zde se na pozadí neaktualizuje.',
      spDetailsSetInPlatform: 'Nastaveno v této platformě',
      spDetailsKnowledgeArea: 'Znalostní oblast',
      spDetailsSharePointName: 'Název v SharePointu',
      spDetailsIdentifiesSite: 'Identifikuje web',
      spDetailsKeyBadge: 'KLÍČ',
      spDetailsAccessScope: 'Pouze pro čtení · Sites.Selected',
      spDetailsConnectedBy: 'Připojil(a)',
      spDetailsConnectedOn: 'Připojeno dne',
      spDetailsAccessLastChecked: 'Přístup naposledy zkontrolován',
      spDetailsCheckedByFormat: '{datetime}, {name}',
      spNeverChecked: 'Nikdy nezkontrolováno',
      spDetailsCheckAccessNote: 'Kontroly přístupu ověřují udělení oprávnění a aktualizují název a URL v SharePointu. Nečtou žádný obsah.',
      spEditSiteSaveError: 'Uložení změn se nezdařilo',
      spAgentsModalViewTitle: 'Agenti používající tento web',
      spAgentsModalViewSubtitle: 'Otevřete kteréhokoli z těchto agentů přímo.',
      spAgentsModalBlockedTitle: 'Web se stále používá',
      spAgentsModalBlockedSubtitle: 'Nejprve odeberte tento web z níže uvedených agentů a poté jej odpojte.',
      spAgentsModalWarningSingular: '{siteName} je přiřazen {count} agentovi. Odpojení nyní by poškodilo jeho importovaný obsah.',
      spAgentsModalWarningPlural: '{siteName} je přiřazen {count} agentům. Odpojení nyní by poškodilo jejich importovaný obsah.',
      spAgentsModalFileCountSingular: '{count} soubor',
      spAgentsModalFileCountPlural: '{count} souborů',
      spAgentsModalImportedOn: 'importováno {date}',
      spAgentsModalOpenAgent: 'Otevřít agenta',
      spAgentsModalDeleteNote: 'Smazáním agenta se jeho přiřazení k webům automaticky uvolní.',
      spAgentsModalGoToFirst: 'Přejít na prvního agenta',
      connectSiteTitle: 'Připojit web',
      connectSiteUrlPlaceholder: '',
      connectSiteInvalidIdFormat: 'Neplatný formát ID webu. Očekáváno: hostname.sharepoint.com,siteCollectionGuid,webGuid',
      connectSiteAdd: 'Přidat',
      connectSiteSaveCredentials: 'Uložit přihlašovací údaje',
      connectSiteSubtitle: 'Připojit lze pouze weby, ke kterým váš administrátor Microsoft 365 udělil přístup pro čtení.',
      connectSiteStep1: 'Zadat ID webu',
      connectSiteStep2: 'Potvrdit a pojmenovat',
      connectSiteIdLabel: 'ID webu',
      connectSiteIdHint: 'Každý web potřebuje své vlastní ID a vlastní udělení přístupu — včetně webů, které patří pod centrální web (hub).',
      connectSiteHelpTitle: 'Kde najdu ID webu?',
      connectSiteHelpStep1: 'Otevřete SharePoint web ve svém prohlížeči.',
      connectSiteHelpStep2: 'Přidejte /_api/site/id k adrese webu a stiskněte Enter, například https://contoso.sharepoint.com/sites/Finance/_api/site/id.',
      connectSiteHelpStep3: 'Zkopírujte GUID z odpovědi a vložte jej za název hostitele, oddělený čárkou.',
      connectSiteCheckAccess: 'Zkontrolovat přístup',
      connectSiteUrlColumn: 'URL',
      connectSiteAccessColumn: 'Přístup',
      connectSiteAccessConfirmed: 'Pouze pro čtení · potvrzeno',
      connectSiteDisplayNameLabel: 'Zobrazovaný název',
      connectSiteDisplayNameHint: 'Zobrazuje se administrátorům agentů a v chatu.',
      connectSiteKnowledgeAreaLabel: 'Znalostní oblast (volitelné)',
      connectSiteKnowledgeAreaSelectPrompt: 'Vybrat oblast znalostí',
      connectSiteKnowledgeAreaEmpty: 'Zatím žádné oblasti znalostí — vytvořte ji níže.',
      connectSiteKnowledgeAreaHint: 'Seskupuje související weby v tomto seznamu a v nastavení agentů. Nemá vliv na přístup ani importy.',
      knowledgeAreaCreateNew: 'Vytvořit novou oblast znalostí',
      knowledgeAreaManageButton: 'Spravovat oblasti znalostí',
      knowledgeAreaManagerTitle: 'Oblasti znalostí',
      knowledgeAreaManagerSubtitle: 'Oblasti znalostí organizují weby SharePointu a jsou nezávislé na uživatelských skupinách — každý agent vidí každou oblast znalostí.',
      knowledgeAreaNameLabel: 'Název oblasti znalostí',
      knowledgeAreaNamePlaceholder: 'např. IT, Finance, HR',
      knowledgeAreaEnterHint: 'Stiskněte Enter nebo klikněte na „Vytvořit" pro přidání.',
      knowledgeAreaCreateButton: 'Vytvořit',
      knowledgeAreaEmptyError: 'Zadejte prosím název',
      knowledgeAreaExistsError: 'Tato oblast znalostí již existuje',
      knowledgeAreaCreateError: 'Nepodařilo se vytvořit oblast znalostí',
      connectSiteConsentLabel: 'Rozumím tomu, že jakmile je tento web importován do agenta, kdokoli s přístupem k tomuto agentovi může získat kterýkoli importovaný soubor — včetně souborů, které jsou v SharePointu přístupné jen některým lidem.',
      connectSiteSubmit: 'Připojit web',
      connectSiteDuplicateSite: 'Tento web je již připojen: {name}',
      syncIntervalTitle: 'Interval synchronizace',
      syncIntervalLabel: 'Interval (minuty)',
      syncIntervalHint: 'Doporučeno: 15 minut pro běžné weby, 5 minut pro často aktualizované weby',
      syncIntervalSaving: 'Ukládání...',
      syncIntervalSubmit: 'Uložit',
      deleteSiteTitle: 'Odpojit web',
      deleteSiteMessage: 'Opravdu chcete odpojit „{name}"? Tuto akci nelze vrátit zpět.',
      deleteSiteDeleting: 'Odpojování...',
    },

    users: {
      pageSearchPlaceholder: 'Hledat',
      pageFilterButton: 'Filtr',
      columnFullName: 'Celé jméno',
      columnEmail: 'E-mail',
      columnOrganization: 'Organizace',
      columnUserGroup: 'Uživatelská skupina',
      columnCreatedAt: 'Datum vytvoření',
      columnRole: 'Role',
      columnAuthProvider: 'Poskytovatel',
      authProviderEmail: 'E-mail',
      authProviderMicrosoft: 'Microsoft',
      authProviderLocal: 'Lokální',
      roleSuperAdmin: 'Super Admin',
      roleAdmin: 'Admin',
      roleNormal: 'Normální',
      roleNoLicense: 'Bez licence',
      actionAssignRole: 'Přiřadit roli',
      actionAssignOrganization: 'Přiřadit org. + uživatelskou skupinu',
      actionAssignDepartment: 'Přiřadit skupinu uživatelů',
      actionCheckActivity: 'Zkontrolovat aktivitu',
      actionResendInvite: 'Odeslat pozvánku znovu',
      actionDeactivateUser: 'Deaktivovat uživatele',
      actionActivateUser: 'Aktivovat uživatele',
      statusInactive: 'Neaktivní',
      deactivateConfirmTitle: 'Deaktivovat uživatele',
      deactivateConfirmMessage: 'Opravdu chcete deaktivovat uživatele {name}?',
      activateConfirmTitle: 'Aktivovat uživatele',
      activateConfirmMessage: 'Opravdu chcete aktivovat uživatele {name}?',
      toastUserDeactivated: 'Uživatel byl deaktivován',
      toastUserActivated: 'Uživatel byl aktivován',
      toastStatusChangeError: 'Stav uživatele se nepodařilo změnit',
      activityModalTitle: 'Přihlašovací aktivita',
      activityColMethod: 'Metoda',
      activityColStatus: 'Stav',
      activityColIp: 'IP adresa',
      activityColTime: 'Čas',
      activityStatusSuccess: 'Úspěšné',
      activityStatusFailed: 'Neúspěšné',
      activityEmpty: 'Žádné přihlašovací pokusy nebyly nalezeny.',
      activityError: 'Aktivitu se nepodařilo načíst.',
      activityMethodPassword: 'Heslo',
      activityMethodOtp: 'E-mailový kód',
      activityMethodMicrosoft: 'Microsoft',
      activityMethodMfa: 'Dvoufaktorový kód',
      actionResetMfa: "Resetovat 2FA",
      activityMethodMfaRecovery: "Obnovovací kód",
      activityMethodMfaReset: "2FA resetováno",
      activityMethodMfaSetup: "2FA nastaveno",
      activityPerformedBy: "provedl(a) {email}",
      resetMfaConfirmMessage: "Dvoufaktorové ověření uživatele {name} bude odstraněno. Při dalším přihlášení bude nutné aplikaci pro ověřování nastavit znovu.",
      resetMfaConfirmTitle: "Resetovat dvoufaktorové ověření",
      toastMfaReset: "Dvoufaktorové ověření bylo resetováno",
      errorPrefix: 'Chyba: {error}',
      roleModalTitle: 'Přiřadit roli',
      roleModalNameLabel: 'Jméno:',
      roleModalCurrentRoleLabel: 'Aktuální role:',
      roleModalSelectLabel: 'Vybrat novou roli',
      roleModalSelectPlaceholder: 'Vybrat novou roli',
      roleModalNoOtherRoles: 'Žádné další role nejsou k dispozici',
      roleModalSaving: 'Ukládání...',
      orgModalTitle: 'Přiřadit organizaci',
      orgModalSelectLabel: 'Vybrat organizaci',
      orgModalNoOrganization: 'Žádná organizace',
      orgModalAssignUserGroupLabel: 'Přiřadit uživatelskou skupinu',
      orgModalAssignUserGroupPlaceholder: 'Přiřadit uživatelskou skupinu',
      orgModalSaving: 'Ukládání...',
      userGroupMoreLabel: '+{count} další',
      toastRoleUpdated: 'Role byla úspěšně aktualizována',
      toastRoleUpdateError: 'Nepodařilo se aktualizovat roli',
      toastOrgAssigned: 'Organizace byla úspěšně přiřazena',
      toastOrgAssignError: 'Nepodařilo se přiřadit organizaci',
      // Invite modal
      inviteButtonLabel: 'Pozvat uživatele',
      inviteModalTitle: 'Pozvat uživatele',
      inviteModalSubtitle: 'Uživatel obdrží pozvánkový e-mail a může se následně zaregistrovat.',
      inviteModalEmailLabel: 'E-mailová adresa',
      inviteModalRoleLabel: 'Role',
      inviteModalRolePlaceholder: 'Vyberte roli',
      inviteModalSendButton: 'Odeslat pozvánku',
      inviteModalSending: 'Odesílání...',
      inviteModalSuccessToast: 'Pozvánka byla úspěšně odeslána',
      inviteModalErrorToast: 'Nepodařilo se odeslat pozvánku',
      resendInviteSuccessToast: 'Pozvánka byla znovu odeslána',
      resendInviteErrorToast: 'Nepodařilo se znovu odeslat pozvánku',
      inviteModalFieldRequired: 'Zadejte e-mailovou adresu a roli',
      inviteEmailAlreadyExists: 'Tento uživatel již v systému existuje',
      addUserMenuLabel: 'Přidat uživatele',
      inviteUserMenuLabel: 'Pozvat uživatele',
      newUserDefaultsAllGroups: 'Všechny skupiny',
      newUserDefaultsTitle: 'Výchozí hodnoty pro nové uživatele',
      newUserDefaultsSubtitle: 'Organizace, role a skupiny přiřazené při prvním přihlášení přes Microsoft.',
      newUserDefaultsButton: 'Výchozí hodnoty',
      addUserModalTitle: 'Přidat nového uživatele',
      addUserModalSubtitle: 'Uživatel bude vytvořen přímo a přiřazen do organizace.',
      addUserFullnameLabel: 'Celé jméno',
      addUserOrganizationLabel: 'Organizace',
      addUserGenderLabel: 'Pohlaví',
      addUserGenderPlaceholder: 'Vyberte prosím',
      addUserPasswordLabel: 'Heslo',
      addUserRoleLabel: 'Role',
      addUserRolePlaceholder: 'Vyberte roli',
      addUserSubmitButton: 'Vytvořit uživatele',
      addUserSubmitting: 'Vytváření...',
      addUserSuccessToast: 'Uživatel úspěšně vytvořen',
      addUserErrorToast: 'Nepodařilo se vytvořit uživatele',
      addUserFieldRequired: 'Vyplňte prosím všechna pole',
      addUserNoOrgError: 'Váš účet nemá přiřazenou organizaci. Požádejte prosím super-administrátora, aby nejprve nastavil vaši organizaci.',
      addUserSelectOrgPlaceholder: 'Vyberte organizaci',
      addUserSelectOrgRequired: 'Prosím vyberte organizaci',
      changePasswordTitle: 'Nastavit heslo',
      changePasswordSubtitle: 'Nastavte nové heslo pro váš účet.',
      changePasswordButton: 'Uložit heslo',
      statusPending: 'Pozváno',
      statusExpired: 'Vypršelo',
    },

    language: {
      pageTitle: 'Nastavení jazyka',
      pageSubtitle: 'Vyberte preferovaný jazyk uživatelského rozhraní.',
      unsavedChanges: 'Neuložené změny',
      saveButton: 'Uložit nastavení jazyka',
      cancelButton: 'Zrušit',
      searchPlaceholder: 'Hledat',
      currentBadge: 'Aktuální',
      defaultBadge: 'Výchozí',
    },

    pagination: {
      pageOf: 'Stránka {current} z {total}',
    },

    toast: {
      commentSaved: 'Komentář uložen',
      fileUploaded: 'Soubory byly úspěšně nahrány',
      fileUpdated: 'Soubory byly úspěšně aktualizovány',
      projectCreated: 'Projekt byl úspěšně vytvořen!',
      projectFailed: 'Nepodařilo se vytvořit projekt',
      chooseAgent: 'Vyberte prosím agenta a zadejte název projektu',
    },

    deleteModal: {
      singleTitle: 'Smazat AI žádost',
      bulkTitle: 'Smazat {count} AI žádostí',
      singleDescription: 'Opravdu chcete smazat tuto AI žádost? Tuto akci nelze vrátit zpět.',
      bulkDescription: 'Opravdu chcete smazat {count} vybraných AI žádostí? Tuto akci nelze vrátit zpět.',
      deleting: 'Mazání...',
      defaultTitle: 'Potvrdit smazání',
      defaultMessage: 'Opravdu chcete smazat tuto položku?',
      cancelButton: 'Zrušit',
      deleteButton: 'Smazat',
    },

    agentOrder: {
      hint: 'Přetáhněte agenty myší nebo zadejte číslo pozice.',
      unsavedChanges: 'Neuložené změny',
      cancel: 'Zrušit',
      save: 'Uložit',
      colPosition: 'Pozice',
      colAgent: 'Agent',
      colPlacement: 'Umístění na úvodní stránce',
      posTopLeft: 'Nahoře vlevo',
      posTopRight: 'Nahoře vpravo',
      posSecondLeft: 'Druhá řada vlevo',
      posSecondRight: 'Druhá řada vpravo',
      posBehindShowAll: 'Pod „Zobrazit vše“',
      noDepartments: 'Tato organizace zatím nemá žádné skupiny uživatelů.',
      noAgents: 'K této skupině uživatelů nejsou přiřazeni žádní agenti.',
      toastSaved: 'Pořadí bylo uloženo',
      toastError: 'Pořadí se nepodařilo uložit',
      loadError: 'Data se nepodařilo načíst',
      retry: 'Zkusit znovu',
      discardConfirm: 'Pořadí obsahuje neuložené změny. Chcete je zahodit?',
      toastNoPermission: 'Nemáte oprávnění měnit pořadí těchto agentů.',
    },
    usageHistory: {
      title: 'Historie využití',
      subtitle: 'Měsíční využití podle agenta — viditelné pouze pro super administrátory',
      cardRunsLabel: 'Běhy',
      cardRunsSub: 'z {max} běhů v {year}',
      cardQuestionsLabel: 'Dotazy',
      cardTokensLabel: 'Tokeny',
      cardTokensSub: 'Vstup a výstup dohromady',
      cardUserPagesLabel: 'Stránky nahrané uživatelem',
      cardStandardPagesLabel: 'Stránky standardních souborů',
      cardPagesLabel: 'Použité stránky',
      cardPagesSub: 'z {max} stránek v {year}',
      cardCreditsLabel: 'Dočasné kredity',
      cardCreditsSub: 'navíc uděleno',
      orgPlaceholder: 'Vyberte organizaci...',
      allMonths: 'Všechny měsíce',
      refresh: 'Obnovit',
      colMonth: 'Měsíc',
      colAgent: 'Agent',
      colRuns: 'Běhy',
      colQuestions: 'Dotazy',
      colTokens: 'Tokeny',
      colUserPages: 'Stránky uživatele',
      colStandardPages: 'Stránky standardních souborů',
      unlimitedSuffix: '(neomezeno)',
      colRunsUsed: 'Použité běhy',
      colPagesUsed: 'Použité stránky',
      colTempCredits: 'Dočasné kredity',
      colTempPages: 'Dočasné stránky',
      colAction: 'Akce',
      entry: 'záznam',
      entries: 'záznamy',
      remaining: 'zbývá',
      actionCarryOver: 'Přenos',
      actionReset: 'Resetováno',
      emptyOrgTitle: 'Vyberte organizaci',
      emptyOrgSubtitle: 'Vyberte organizaci pro zobrazení měsíční historie využití.',
      emptyDataTitle: 'Žádná data pro',
      emptyDataSubtitle: 'Pro toto období nebyla zaznamenána žádná data o využití.',
      loadError: 'Chyba při načítání historie využití',
      months: 'Leden,Únor,Březen,Duben,Květen,Červen,Červenec,Srpen,Září,Říjen,Listopad,Prosinec',
      unknownAgent: 'Neznámý agent',
      liveTitle: 'Aktuální využití',
      liveBadge: 'Živě',
      liveSubtitle: 'Aktuální data · Probíhající měsíc',
      liveUpdated: 'Aktualizováno',
      deletedBadge: 'Smazáno',
    },

    notifications: {
      pageTitle: 'Oznámení',
      subtitle: 'Všechny aktivity týkající se vašich projektů, dokumentů, agentů a integrací.',
      markAllRead: 'Označit vše jako přečtené',
      tabAll: 'Vše',
      tabUnread: 'Nepřečtené',
      tabSystem: 'Systém',
      empty: 'Žádná oznámení',
      emptyHint: 'Máte vše přečteno.',
      groupToday: 'DNES',
      groupYesterday: 'VČERA',
      groupEarlier: 'DŘÍVE',
      viewDocument: 'Zobrazit dokument',
      viewSyncReport: 'Zobrazit zprávu o synchronizaci',
      viewChanges: 'Zobrazit změny',
      viewFile: 'Zobrazit soubor',
      openProject: 'Otevřít projekt',
      viewSite: 'Zobrazit web',
      viewNews: 'Zobrazit novinky',
      fixIssue: 'Opravit problém',
      reviewActivity: 'Zkontrolovat aktivitu',
      justNow: 'Právě teď',
      minuteAgo: 'před 1 minutou',
      minutesAgo: 'před {m} minutami',
      hourAgo: 'před 1 hodinou',
      hoursAgo: 'před {h} hodinami',
      yesterdayAt: 'Včera, {time}',
      t1Title: 'Analýza dokumentu dokončena',
      t1Desc: 'AI dokončila analýzu Service_Contract.pdf pro City Center Agreement Review.',
      t2Title: 'Synchronizace SharePoint dokončena',
      t2Desc: '143 souborů úspěšně synchronizováno ze složky Contracts / Legal Agreements.',
      t3Title: 'Nastavení agenta aktualizováno',
      t3Desc: 'Provedeny změny agenta Contract Review Agent — aktualizována teplota a prompt.',
      t4Title: 'Nový projekt vytvořen',
      t4Desc: 'Projekt „Downtown Plaza" byl vytvořen a přiřazen uživatelské skupině Contracts.',
      t5Title: 'Dokument nahrán',
      t5Desc: 'Lease_Agreement_2024.docx byl nahrán do knihovny standardních souborů.',
      t6Title: 'Analýza dokončena',
      t6Desc: 'Kompletní analýza projektu „Skyline Tower" dokončena — 98% skóre spolehlivosti.',
      t7Title: 'SharePoint web připojen',
      t7Desc: 'Contoso Legal Hub byl úspěšně připojen jako zdroj dat.',
      t8Title: 'Dokument zpracován',
      t8Desc: 'NDA_Template_v3.pdf byl zpracován a je připraven pro AI analýzu.',
      t9Title: 'Aktualizace platformy — v2.4.0',
      t9Desc: 'Nové funkce: vylepšená rychlost synchronizace SharePoint, rozšířené citační štítky a AI profile builder.',
      t10Title: 'Nový agent vytvořen',
      t10Desc: 'Agent „Compliance Checker" byl vytvořen uživatelem Olivia Rhye v pracovním prostoru Contracts.',
      t11Title: 'Zjištěna chyba synchronizace',
      t11Desc: 'Synchronizace 3 souborů ze složky HR dokumentů selhala. Přístup zamítnut.',
      t12Title: 'Bezpečnostní upozornění',
      t12Desc: 'Bylo zjištěno nové přihlášení z neznámého zařízení. Zkontrolujte prosím svůj účet.',
      t13Title: 'Plánovaná údržba',
      t13Desc: 'Platforma bude v údržbě 10. března 2026 od 02:00 do 04:00 UTC. Služby mohou být krátce nedostupné.',
    },

    dataAnalysis: {
      hint: "Jako analýza dat",
      activeHint: "Analýza dat je pro tento požadavek aktivní",
      preview: "Náhled",
      closePreview: "Zavřít náhled",
      previewLoading: "Načítá se náhled…",
      previewError: "Náhled se nepodařilo načíst. Soubor si můžete stáhnout.",
      skippedLabel: "Nepoužito",
      skipReasonMissing: "soubor nenalezen",
      skipReasonUnreadable: "nelze přečíst",
      skipReasonTooLarge: "příliš velký",
      skipReasonStandardFile: "standardní soubor, není podporován",
      modeName: "Analýza dat",
      tooltip: "Analyzovat soubory pomocí pokročilých výpočtů a datových nástrojů",
      composerMeta: "Zůstává zapnuté, dokud ho nevypnete · vybrané soubory omezují kontext",
      usingSessionFiles: "Používají se vhodné soubory z tohoto chatu",
      usingSelectedFiles: "Používají se pouze vybrané soubory",
      selectedForRequest: "Vybráno pro tento požadavek",
      sessionForRequest: "Vhodné soubory z tohoto chatu",
      noFilesForRequest: "Žádné soubory – pouze výpočty",
      workingTitle: "Analyzují se vaše data…",
      completedTitle: "Analýza dat dokončena",
      statusAnalyzing: "Analyzuje se",
      statusReady: "Hotovo",
      statusFailed: "Selhalo",
      stepPreparing: "Připravují se soubory…",
      stepInspecting: "Kontroluje se struktura dat…",
      stepCalculating: "Probíhají výpočty…",
      stepResults: "Připravují se výsledky…",
      stepRetrying: "Opakování pokusu…",
      progressCompleted: "Analýza dokončena",
      completedHelper: "Výsledek se zobrazí jako běžná odpověď v chatu; vytvořené soubory se uloží do Vygenerovaných souborů.",
      failedTitle: "Analýza dat se nezdařila",
      failedHelper: "Zkuste to prosím znovu.",
      contentFilterHelper: "Odpověď byla zablokována filtrem obsahu. Přeformulujte prosím požadavek nebo vyberte jiné soubory.",
      filesUnavailableHelper: "Soubory pro tuto analýzu se nepodařilo načíst. Nahrajte je prosím znovu.",
      tokenLimitHelper: "Odpověď dosáhla limitu tokenů agenta. Zvyšte prosím limit v nastavení agenta.",
      retry: "Zkusit znovu",
      basedOnSelected: "Analýza založená na vybraných souborech",
      basedOnSession: "Analýza založená na souborech tohoto chatu",
      basedOnNone: "Analýza bez souborů",
      filesUsedLabel: "Použité soubory",
      generatedFilesLabel: "Vytvořené soubory",
      download: "Stáhnout",
      downloadError: "Soubor se nepodařilo stáhnout.",
      alreadyRunningError: "V tomto chatu již probíhá analýza dat. Počkejte prosím, až bude dokončena.",
      notEnabledError: "Analýza dat je pro tohoto agenta vypnutá.",
      modeSaveError: "Přepínač analýzy dat se nepodařilo uložit.",
      startFailedError: "Analýzu dat se nepodařilo spustit.",
    },
    chatZoom: {
      open: 'Otevřít ovládání zoomu',
      close: 'Zavřít ovládání zoomu',
      zoomIn: 'Přiblížit',
      zoomOut: 'Oddálit',
      reset: 'Obnovit na 100 %',
    },
    share: {
      button: 'Sdílet',
      shareProject: 'Sdílet projekt',
      modalTitle: 'Sdílet projekt',
      modalSubtitle: 'Přidat osoby nebo zkopírovat odkaz',
      modalDescription: 'Sdílejte tento projekt s ostatními v režimu pouze pro čtení. Kdokoli s tímto odkazem může zobrazit podrobnosti projektu, soubory, odpovědi AI a zdroje, ale nemůže provádět změny.',
      notifOnly: 'Pouze oznámení v aplikaci · Žádný e-mail',
      peopleLabel: 'Osoby',
      permissionLabel: 'Oprávnění',
      shareButton: 'Vytvořit a zkopírovat odkaz',
      shareAndNotify: 'Sdílet a upozornit',
      shareLinkPlaceholder: 'Odkaz se zobrazí po sdílení...',
      linkNote: 'Kdokoli s tímto odkazem může projekt pouze zobrazit. Nelze upravovat, nahrávat soubory, přidávat komentáře ani klást nové otázky AI.',
      searchPlaceholder: 'Hledat jméno nebo e-mail...',
      noResults: 'Žádní uživatelé nenalezeni',
      addedLabel: 'Přidáno',
      cancel: 'Zrušit',
      copyLink: 'Kopírovat odkaz',
      linkCopied: 'Zkopírováno!',
      shareLinkLabel: 'Odkaz ke sdílení',
      shareSuccess: 'Projekt byl úspěšně sdílen',
      removeFailed: 'Příjemce se nepodařilo odebrat',
      readOnlyBadge: 'Pouze čtení',
      commentsTitle: 'Komentáře',
      commentsEmpty: 'Zatím žádné komentáře.',
      commentsLoading: 'Načítání komentářů …',
      commentsPlaceholder: 'Napsat komentář …',
      commentsAdd: 'Komentovat',
      commentsSave: 'Uložit',
      commentsCancel: 'Zrušit',
      commentsEdit: 'Upravit',
      commentsDelete: 'Smazat',
      commentsDeleteConfirm: 'Smazat tento komentář?',
      commentsEdited: 'upraveno',
      commentsUnknownAuthor: 'Neznámý uživatel',
      commentsLoadError: 'Komentáře se nepodařilo načíst.',
      commentsSaveError: 'Komentář se nepodařilo uložit.',
      sharedBy: 'Sdíleno od',
      readOnlyHint: 'Toto zobrazení je pouze pro čtení – otázky nelze pokládat.',
      openProject: 'Otevřít projekt',
      loadingError: 'Sdílený projekt nelze načíst.',
      notFound: 'Tento odkaz ke sdílení je neplatný nebo vypršel.',
      accessRevoked: 'Váš přístup k tomuto projektu byl odebrán.',
      currentRecipients: 'Aktuálně sdíleno s',
      noRecipients: 'Zatím nesdíleno',
      revokeAccess: 'Odvolat přístup',
      revokeShare: 'Ukončit sdílení',
      revokeConfirm: 'Opravdu chcete ukončit sdílení tohoto projektu? Všichni příjemci ztratí přístup.',
      revokeSuccess: 'Sdílení úspěšně ukončeno',
      inviteDescription: 'Pozvěte lidi zadáním jejich jména nebo e-mailu.',
      invitePeople: 'Pozvat lidi',
      sendInvite: 'Odeslat pozvánku',
      whoCanView: 'Kdo může zobrazit tento projekt',
      viewAll: 'Zobrazit vše',
      allViewers: 'Všichni prohlížeči',
      allViewersDesc: 'Lidé, kteří mají aktuálně přístup k tomuto projektu.',
      removeAccess: 'Odebrat přístup',
      youLabel: 'Vy',
      nPeople: '{count} lidí',
      viewPermission: 'Pouze zobrazení',
      viewButton: 'Zobrazit',
      alreadyShared: 'Již sdíleno',
      done: 'Hotovo',
    },

    agents: {
      pageTitle: 'Agenti',
      createAgent: 'Vytvořit agenta',
      selectAll: 'Vybrat vše',
      loading: 'Načítání agentů...',
      noneFound: 'Nebyli nalezeni žádní agenti',
      noneYet: 'Zatím žádní agenti',
      noneFoundHint: 'Žádní agenti neodpovídají vašemu vyhledávání.',
      noneYetHint: 'Vytvořte agenta pro zahájení analýzy souborů a dat.',
      duplicate: 'Duplikovat agenta',
      agentNameLabel: 'Název agenta',
      userGroupLabel: 'Uživatelská skupina',
      organizationLabel: 'Organizace',
      lastUpdatedLabel: 'Naposledy aktualizováno',
      createModalTitle: 'Vytvořit nového agenta',
      createModalImageLabel: 'Obrázek agenta',
      createModalUploadImage: 'Nahrát obrázek',
      createModalChangeImage: 'Nahrát nový obrázek',
      createModalRemoveImage: 'Odstranit',
      createModalNoFileSelected: 'Nebyl vybrán žádný soubor',
      createModalSelectOrg: 'Vyberte organizaci',
      createModalSelectDept: 'Vyberte uživatelskou skupinu',
      createModalDescLabel: 'Popis',
      createModalDescPlaceholder: 'Popište hlavní přínos tohoto agenta...',
      createModalDescHelp: 'Jedna nebo dvě věty vysvětlující, jak tento agent pomáhá uživatelům.',
      createModalCancel: 'Zrušit',
      createModalSubmit: 'Přidat agenta',
      createModalSubmitting: 'Vytváří se...',
      createModalNameRequired: 'Zadejte prosím název agenta',
    },

    filterModal: {
      title: 'Filtr',
      agentsLabel: 'Agenti',
      agentsPlaceholder: 'Vybrat agenty',
      creationDateLabel: 'Datum vytvoření',
      today: 'Dnes',
      last7days: 'Posledních 7 dní',
      last30days: 'Posledních 30 dní',
      monthPlaceholder: 'Vybrat měsíc',
      progressLabel: 'Průběh Q&A',
      low: 'Nízký',
      medium: 'Střední',
      high: 'Vysoký',
      sortByLabel: 'Řadit podle',
      newestFirst: 'Nejnovější',
      oldestFirst: 'Nejstarší',
      mostProgress: 'Největší pokrok',
      leastProgress: 'Nejmenší pokrok',
      resetAll: 'Obnovit vše',
      apply: 'Použít',
      organizationLabel: 'Organizace',
      organizationPlaceholder: 'Vybrat organizaci',
      userGroupLabel: 'Uživatelská skupina',
      userGroupPlaceholder: 'Vybrat uživatelskou skupinu',
      lastUpdateLabel: 'Poslední aktualizace',
      clearAll: 'Vymazat vše',
    },

    months: {
      january: 'Leden',
      february: 'Únor',
      march: 'Březen',
      april: 'Duben',
      may: 'Květen',
      june: 'Červen',
      july: 'Červenec',
      august: 'Srpen',
      september: 'Září',
      october: 'Říjen',
      november: 'Listopad',
      december: 'Prosinec',
    },

    createProjectModal: {
      title: 'Vytvořit nový projekt',
      agentLabel: 'Agent',
      agentPlaceholder: 'Vybrat agenta',
      noAgentsAvailable: 'Žádní agenti nejsou k dispozici',
      projectImageLabel: 'Obrázek projektu',
      uploadImage: 'Nahrát obrázek',
      uploadNewImage: 'Nahrát nový obrázek',
      noFileSelected: 'Není vybrán žádný soubor',
      projectNameLabel: 'Název KI požadavku',
      projectNamePlaceholder: 'Název KI požadavku',
      uploadMethodLabel: 'Způsob nahrání',
      uploadFromDevice: 'Nahrát z mého zařízení',
      uploadFromDeviceHint: 'Přidejte soubory přímo z vašeho počítače',
      connectSharePoint: 'Připojit SharePoint složku',
      connectSharePointHint: 'Použijte soubory z vašeho projektového pracovního prostoru',
      analyze: 'Analyzovat',
      creating: 'Vytváření...',
    },

    helpCenter: {
      title: 'Centrum nápovědy',
      tabFaqs: 'FAQ',
      tabSupport: 'Kontaktovat podporu',
      sidebarFaqs: 'FAQ',
      sidebarSupport: 'Podpora',
      sidebarFooterCopyright: '© 2026 AI Spaces GmbH',
      supportInfoTitle: 'Informace o podpoře',
      supportInfoSubtitle: 'Poskytněte informace, které uživatelé uvidí v centru nápovědy, když potřebují pomoc.',
      supportMessageLabel: 'Zpráva podpory',
      supportMessageDefault: 'Zde najdete informace o naší podpoře. Náš tým vám rád pomůže.',
      supportRequestLinkLabel: 'Odkaz na žádost o podporu',
      supportNoLinkConfigured: 'Žádný odkaz nebyl nakonfigurován',
      supportHoursLabel: 'Provozní doba podpory',
      supportHoursDaysDefault: 'Pondělí – Pátek',
      supportHoursTimeDefault: '07:00 – 17:00',
      supportErrorPrefix: 'Chyba: {message}',
      supportMessagePlaceholder: 'Napište krátkou zprávu vysvětlující, jak mohou uživatelé získat pomoc od vašeho týmu podpory.',
      supportCharactersRemaining: 'Zbývá {count} znaků',
      supportLinkPlaceholder: 'Vložte odkaz na formulář podpory nebo žádost o zpětnou vazbu',
      supportDaysLabel: 'Dny',
      supportTimeLabel: 'Čas',
      supportDaysPlaceholder: 'např. Pondělí – Pátek',
      supportTimePlaceholder: 'např. 07:00 – 17:00',
      supportSaving: 'Ukládání...',
      faqPageTitle: 'Často kladené otázky',
      faqPageSubtitle: 'Najděte odpovědi na běžné otázky týkající se používání DocAI, správy agentů a analýzy dokumentů.',
      faqManageTitle: 'Správa FAQ',
      faqAddButton: 'Přidat otázku',
      faqEmptyTitle: 'Zatím žádné FAQ',
      faqEmptySubtitle: 'Zatím nebyly přidány žádné otázky.',
      faqDrawerAddTitle: 'Přidat otázku',
      faqDrawerEditTitle: 'Upravit otázku',
      faqQuestionLabel: 'Otázka',
      faqQuestionPlaceholder: 'Otázka',
      faqAnswerLabel: 'Odpověď',
      faqAnswerPlaceholder: 'Odpověď',
      faqMediaLabel: 'Nahrát video/obrázek (nepovinné)',
      faqUploading: 'Nahrávání...',
      faqFileUploaded: 'Soubor nahrán',
      faqRemoveFile: 'Odebrat',
      faqDropHint: 'Přetáhněte video/obrázek sem nebo {browse}',
      faqBrowse: 'procházet',
      faqSaving: 'Ukládání...',
      faqDeleteTitle: 'Smazat FAQ',
      faqDeleteMessage: 'Opravdu chcete smazat toto FAQ?',
      faqMediaAlt: 'FAQ média',
      faqToastFetchError: 'Nepodařilo se načíst FAQ',
      faqToastDeleted: 'FAQ bylo úspěšně smazáno',
      faqToastDeleteError: 'Nepodařilo se smazat FAQ',
      faqToastFileUploaded: 'Soubor byl úspěšně nahrán',
      faqToastUploadError: 'Nepodařilo se nahrát soubor',
      faqValidationRequired: 'Otázka a odpověď jsou povinné',
      faqValidationUploadWait: 'Počkejte prosím, než se nahrání dokončí.',
      faqToastUpdated: 'FAQ bylo úspěšně aktualizováno',
      faqToastCreated: 'FAQ bylo úspěšně vytvořeno',
      faqToastSaveError: 'Nepodařilo se uložit FAQ',
      faqToastChangesSaved: 'Změny uloženy',
      faqToastChangesSaveError: 'Nepodařilo se uložit',
    },

    auth: {
      loginTitle: 'Přihlásit se nebo registrovat',
      loginSubtitle: 'Přihlaste se pro přístup k vašim AI dotazům.',
      continueWithMicrosoft: 'Pokračovat s Microsoft',
      or: 'NEBO',
      orContinueWith: 'NEBO POKRAČUJTE S',
      emailPlaceholder: 'E-mailová adresa',
      checkInboxTitle: 'Zkontrolujte svou schránku',
      checkInboxSubtitle: 'Zadejte ověřovací kód, který jsme právě odeslali na {email}',
      codePlaceholder: 'Kód',
      resendEmail: 'Odeslat e-mail znovu',
      continueWithPassword: 'Pokračovat s heslem',
      enterPasswordTitle: 'Zadejte heslo',
      emailLabel: 'E-mailová adresa',
      edit: 'Upravit',
      passwordLabel: 'Heslo',
      forgotPassword: 'Zapomenuté heslo?',
      loginWithOtp: 'Přihlásit se jednorázovým kódem',
      resetPasswordTitle: 'Zapomněli jste heslo?',
      resetPasswordSubtitle: 'Klikněte na "Pokračovat" pro obnovení hesla pro {email}',
      resetPasswordEmailPrompt: 'Zadejte svou e-mailovou adresu a my vám zašleme odkaz pro obnovení hesla.',
      setNewPasswordSubtitle: 'Musí mít alespoň 8 znaků.',
      setNewPasswordTitle: 'Nastavit nové heslo',
      newPasswordPlaceholder: 'Nové heslo',
      confirmPasswordPlaceholder: 'Potvrďte heslo',
      passwordMismatch: 'Hesla se neshodují.',
      sendResetLinkButton: 'Odeslat odkaz pro obnovení',
      resetPasswordButton: 'Obnovit heslo',
      footerPrivacyPolicy: 'Ochrana osobních údajů',
      footerTermsOfService: 'Podmínky služby',
      footerSupport: 'Podpora',
      resetLinkSentTitle: 'Zkontrolujte svůj e-mail',
      resetLinkSentSubtitle: 'Odeslali jsme odkaz pro obnovení hesla na {email}. Zkontrolujte prosím svou schránku.',
      backToLogin: 'Zpět na přihlášení',
      invalidEmail: 'Zadejte prosím platnou e-mailovou adresu',
      otpSent: 'Kód byl odeslán',
      otpResent: 'Kód byl znovu odeslán',
      genericError: 'Něco se pokazilo. Zkuste to znovu.',
      fieldRequired: 'Vyplňte prosím všechna pole.',
      invalidPhone: 'Zadejte prosím platné telefonní číslo.',
      passwordTooShort: 'Heslo musí mít alespoň 8 znaků.',
      today: 'Dnes',
      clear: 'Vymazat',
      completeProfileTitle: 'Dokončete svůj profil',
      completeProfileSubtitle: 'Ještě pár údajů k nastavení vašeho účtu.',
      fullnameLabel: 'Celé jméno',
      genderLabel: 'Pohlaví',
      genderPlaceholder: 'Vyberte prosím',
      genderMale: 'Muž',
      genderFemale: 'Žena',
      genderDiverse: 'Jiné',
      dobLabel: 'Datum narození',
      mobileLabel: 'Mobilní telefonní číslo',
      passwordOptionalLabel: 'Heslo (nepovinné)',
      loginButton: 'Přihlásit se',
      welcomeBack: 'Vítejte zpět',
      loginPageSubtitle: 'Přihlaste se ke svému účtu a pokračujte.',
      rememberMe: 'Zůstat přihlášen',
      noAccount: 'Nemáte účet?',
      signUpLink: 'Registrovat se',
      noPasswordHint: 'Nemáte heslo? Pošleme vám ověřovací kód e-mailem.',
      loginWithPasswordLink: 'Přihlásit se heslem',
      setPasswordTitle: 'Nastavit heslo',
      setPasswordSubtitle: 'Vítejte zpět, {fullname}. Nastavte si heslo, abyste se příště mohli přihlásit přímo svou e-mailovou adresou.',
      mfaVerifyTitle: 'Dvoufaktorové ověření',
      mfaAuthenticatorRowTitle: "Aplikace pro ověřování",
      mfaConfirmText: "Zadejte 6místný kód, který aplikace právě zobrazuje.",
      mfaConfirmTitle: "Potvrďte kód",
      mfaEnrollStep: "Krok {current} z {total}",
      mfaInstallText: "Nainstalujte si do chytrého telefonu aplikaci pro ověřování, například Microsoft Authenticator nebo Google Authenticator.",
      mfaInstallTitle: "Nainstalujte aplikaci pro ověřování",
      mfaManualKeyButton: "Zadat klíč ručně",
      mfaManualKeyText: "Zadejte tento klíč do aplikace (časově založený):",
      mfaRecoveryCopied: "Zkopírováno",
      mfaRecoveryCopy: "Kopírovat",
      mfaRecoveryDone: "Hotovo",
      mfaRecoveryDownload: "Stáhnout",
      mfaRecoveryFileHeader: "DocAI Platforma – obnovovací kódy (každý kód lze použít pouze jednou)",
      mfaRecoverySavedCheckbox: "Kódy jsem si bezpečně uložil(a)",
      mfaRecoverySectionText: "Pokud ztratíte přístup k aplikaci pro ověřování, přihlaste se jedním z těchto kódů.",
      mfaRecoverySectionTitle: "Obnovovací kódy",
      mfaRecoveryText: "Pokud ztratíte přístup k aplikaci, můžete se přihlásit jedním z těchto kódů. Každý kód lze použít pouze jednou. Uložte je na bezpečné místo.",
      mfaRecoveryTitle: "Uložte si obnovovací kódy",
      mfaRecoveryVerifySubtitle: "Zadejte jeden ze svých obnovovacích kódů.",
      mfaRegenerateButton: "Vytvořit nové kódy",
      mfaRegenerateConfirmTitle: "Vytvořit nové obnovovací kódy",
      mfaRegenerateSubmit: "Vytvořit kódy",
      mfaRegenerateSubtitle: "Zadejte 6místný kód z aplikace pro ověřování.",
      mfaRegenerateSuccess: "Nové obnovovací kódy byly vytvořeny.",
      mfaRegenerateWarning: "Vaše dosavadní obnovovací kódy přestanou platit.",
      mfaRequiredForAll: "Dvoufaktorové ověření je povinné pro všechny uživatele a nelze jej deaktivovat.",
      mfaScanText: "Otevřete aplikaci, přidejte nový účet a naskenujte tento QR kód. Záznam se zobrazí jako „{issuer}“.",
      mfaScanTitle: "Naskenujte QR kód",
      mfaUseAuthenticatorCode: "Použít kód z aplikace pro ověřování",
      mfaUseRecoveryCode: "Použít obnovovací kód",
      mfaVerifySubtitle: 'Zadejte 6místný kód z vaší aplikace pro ověřování.',
      mfaCodeLabel: 'Kód',
      mfaSetupTitle: 'Nastavit dvoufaktorové ověření',
      mfaSetupSubtitle: 'Naskenujte tento QR kód aplikací pro ověřování (např. Google nebo Microsoft Authenticator) a zadejte zobrazený kód pro pokračování.',
      mfaSectionTitle: 'Dvoufaktorové ověření',
      mfaSectionDescription: 'Chraňte svůj účet navíc pomocí aplikace pro ověřování.',
      mfaStatusEnabled: 'Aktivní',
      mfaStatusDisabled: 'Neaktivní',
      mfaEnableButton: 'Aktivovat',
      mfaDisableButton: 'Deaktivovat',
      mfaEnabledSuccess: 'Dvoufaktorové ověření bylo aktivováno.',
      mfaDisabledSuccess: 'Dvoufaktorové ověření bylo deaktivováno.',
      mfaRequiredForRole: 'Dvoufaktorové ověření je pro vaši roli povinné a nelze jej deaktivovat.',
    },
  },

  es: {
    common: {
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      edit: 'Editar',
      close: 'Cerrar',
      confirm: 'Confirmar',
      back: 'Atrás',
      next: 'Siguiente',
      previous: 'Anterior',
      search: 'Buscar',
      loading: 'Cargando...',
      upload: 'Subir',
      download: 'Descargar',
      open: 'Abrir',
      yes: 'Sí',
      no: 'No',
      add: 'Añadir',
      remove: 'Eliminar',
      create: 'Crear',
      continue: 'Continuar',
      proceed: 'Continuar',
      retry: 'Reintentar',
      refresh: 'Actualizar',
      error: 'Error',
      success: 'Éxito',
      warning: 'Advertencia',
      info: 'Información',
    },

    nav: {
      homepage: 'Página de inicio',
      projects: 'Proyectos',
      agents: 'Agentes',
      notifications: 'Notificaciones',
      helpCenter: 'Centro de ayuda',
      recentProjects: 'Proyectos recientes',
      settings: 'Configuración',
      account: 'Cuenta',
      logout: 'Cerrar sesión',
      logoutConfirm: '¿Está seguro de que desea cerrar sesión?',
    },

    sidebar: {
      fragen: 'Instrucciones',
      dateien: 'Archivos',
      uploadFile: 'Subir archivo',
      loadingQuestions: 'Cargando instrucciones…',
      refreshFiles: 'Actualizar archivos',
      closeSidebar: 'Cerrar panel lateral',
      openSidebar: 'Abrir panel lateral',
      noFilesUploaded: 'Aún no se han subido archivos',
      searchFilesPlaceholder: 'Buscar archivos',
      sharepointSection: 'SharePoint',
      noKnowledgeArea: 'Sin área de conocimiento',
      noFilesFound: 'No se encontraron archivos',
      maxFilesInfo: 'Máximo {count} archivos permitidos para el análisis ({used}/{count})',
      selectedFiles: 'Archivos seleccionados:',
      addFiles: 'Añadir archivos',
      standardBadge: 'Estándar',
      sharepointBadge: 'SharePoint',
      uploadedBadge: 'Subido',
      deleteFileTitle: '¿Eliminar archivo?',
      deleteFileMessage: '¿Está seguro de que desea eliminar "{filename}" del análisis? Esta acción no se puede deshacer.',
      statusPending: 'Pendiente',
      statusDone: 'Listo',
      standardSection: 'Estándar',
      uploadingState: 'Subiendo...',
      convertingState: 'Convirtiendo...',
      youSection: 'Usted',
      totalPages: 'Total de páginas: {count}',
      pdfLoading: 'Cargando PDF...',
      imageLoading: 'Cargando imagen...',
      imageLoadError: 'No se pudo cargar la imagen',
      sourceFileNotFound: 'No se pudo abrir el archivo. Puede que ya no esté disponible.',
      sourceFileStale: 'Este archivo de SharePoint se ha actualizado o eliminado en el origen desde que se generó esta respuesta.',
      sourceFileTimeout: 'La carga del archivo tardó demasiado. Por favor, inténtelo de nuevo.',
      sourceFileLoadFailed: 'No se pudo cargar el archivo. Compruebe su conexión e inténtelo de nuevo.',
      sourceFileDecryptionFailed: 'Este archivo no se pudo descifrar. La clave de cifrado puede haber cambiado o el archivo puede estar dañado — por favor contacte a un administrador.',
      backToFiles: 'Volver a archivos',
      refreshAlt: 'Actualizar',
      noFileSelected: 'Ningún archivo seleccionado',
      invalidFileType: 'Seleccione solo archivos de tipo {types}',
      pdfProcessingError: 'Error al procesar {filename}: Archivo PDF no válido',
      fileValidationError: 'Error en {filename}: {error}',
      convertFileError: 'No se pudo convertir el archivo',
      noOrganization: 'El usuario no está asociado a ninguna organización',
      uploadExceedsPages: 'La subida excede las páginas disponibles',
      selectFilesToUpload: 'Seleccione archivos para subir',
      uploadFilesError: 'No se pudieron subir los archivos',
      cannotRefresh: 'No se pueden actualizar los archivos: falta información del usuario o del historial',
      refreshFilesError: 'No se pudieron actualizar los archivos',
      deleteFileError: 'No se pudo eliminar el archivo',
      filesLoadError: 'Error: {error}',
      maxFilesReached: 'Solo se pueden analizar {max} archivos. Ya ha subido {uploaded} archivo(s). {rejected} archivo(s) no fueron añadidos.',
      maxFilesReachedShort: 'Solo se pueden analizar {max} archivos. Ya ha subido {uploaded} archivo(s).',
      uploadSuccess: 'Archivos subidos correctamente',
      refreshSuccess: 'Archivos actualizados correctamente',
      // Generated files tab
      uploadedTab: 'Subidos',
      generatedFilesTab: 'Archivos generados {count}',
      tableName: 'Nombre',
      tableCreated: 'Creado',
      tableSize: 'Tamaño',
      tableActions: 'Acciones',
      noGeneratedFiles: 'Aún no hay archivos generados',
      today: 'Hoy',
      yesterday: 'Ayer',
      previewAction: 'Vista previa',
      downloadWordAction: 'Descargar Word',
      downloadPdfAction: 'Descargar PDF',
      deleteGeneratedFileTitle: '¿Eliminar archivo?',
      deleteGeneratedFileMessage: '¿Está seguro de que desea eliminar "{filename}"? Esta acción no se puede deshacer.',
      deleteGeneratedFileError: 'No se pudo eliminar el archivo',
    },

    homepage: {
      title: 'Página de inicio',
      recentProjects: 'Proyectos recientes',
      createNewProject: 'Crear nuevo proyecto',
      noProjectsCreated: 'No se han creado proyectos',
      noProjectsHint: 'Cree un proyecto para subir archivos y ejecutar análisis.',
      tableTitle: 'Título',
      tableAgents: 'Agentes',
      tableCreatedAt: 'Fecha de creación',
      tableProgress: 'Progreso de Q&A',
      tableAnswerStatus: 'Estado de respuestas',
      answeredOf: '{done} / {total} respondidas',
      agentsTitle: 'Agentes',
      agentsSubtitleLine1: 'Descubra y trabaje con agentes de IA diseñados para diferentes grupos de usuarios.',
      agentsSubtitleLine2: 'Cada agente le ayuda a analizar documentos, datos y decisiones más rápido.',
      noAgentsAvailable: 'No hay agentes disponibles. Contacte a su administrador.',
      noAgentsYet: 'Aún no hay agentes',
      noAgentsYetHint: 'Cree un agente para empezar a analizar archivos y datos.',
      createAgentFirst: 'Por favor, cree primero un agente.',
      agentCardDefaultDescription: 'Agente de IA para apoyar sus tareas.',
      newChatPlaceholderName: 'Nuevo chat',
      historyLimitReached: 'Ha alcanzado el número máximo de {limit} historiales de chat. Elimine un historial existente antes de crear uno nuevo.',
      createProjectError: 'No se pudo crear el proyecto.',
      startDirectChatError: 'No se pudo iniciar el chat.',
      startingDirectChat: 'Iniciando chat...',
      showAll: 'Mostrar todos',
      showLess: 'Mostrar menos',
      noDepartment: 'Sin departamento',
    },

    history: {
      pageTitle: 'Proyectos',
      tableTitle: 'Título',
      tableAgents: 'Agentes',
      tableCreatedAt: 'Fecha de creación',
      tableProgress: 'Progreso de Q&A',
      tableAnswerStatus: 'Estado de respuestas',
      answeredOf: '{done} / {total} respondidas',
      agentLabel: 'Agentes',
      titleLabel: 'Título',
      creationDateLabel: 'Fecha de creación',
      progressLabel: 'Progreso de Q&A',
      noProjectsFound: 'No se encontraron proyectos',
      noProjectsCreated: 'No se han creado proyectos',
      noProjectsSearchHint: 'Ningún proyecto coincide con su búsqueda.',
      createForAgent: 'Cree un proyecto para {agent}.',
      createHint: 'Cree un proyecto para subir archivos y ejecutar análisis.',
      continue: 'Continuar',
      view: 'Ver',
      projectDetails: 'Detalles de solicitud de IA',
      deleteAll: 'Eliminar todo',
      newProjectTitle: 'Crear nuevo proyecto',
      createProject: 'Crear proyecto',
      agentProjects: 'Proyectos del agente',
      clearFilter: 'Borrar filtro',
      filter: 'Filtro',
      tableAccess: 'Acceso',
      accessOwner: 'Propietario',
      accessViewOnly: 'Compartido',
      accessInvited: 'Invitado',
      accessCanEdit: 'Puede editar',
      tabMyProjects: 'Mis proyectos',
      tabSharedProjects: 'Proyectos compartidos',
      tabInvitedProjects: 'Proyectos invitados',
    },

    analysis: {
      breadcrumbHome: 'Página de inicio',
      breadcrumbProjects: 'Proyectos',
      breadcrumbProjectDetail: 'Abrir resumen del proyecto',
      breadcrumbChatLeaf: 'Chat',
      export: 'Exportar',
      exportWithComments: 'Exportar con comentarios',
      exportWithoutComments: 'Exportar sin comentarios',
      agentFallback: 'Agente',
      projectFallback: 'Proyecto',
      openSidebar: 'Abrir panel lateral',
      chatPlaceholder: 'Pregunte algo',
      sources: 'Fuentes',
      reference: 'Referencia',
      webSourcesCount: '{count} fuentes web',
      sourcesCount: '{count} fuentes',
      document: 'Documento',
      documentNumber: 'Documento {index}',
      source: 'Fuente',
      sourceLabel: 'Fuente: {filename} (página {page})',
      page: 'Página',
      pageWithNumber: 'Página {page}',
      questionTab: 'Preguntas',
      fileTab: 'Archivos',
      projectLoading: 'Cargando proyecto...',
      pdfBrowserTitle: 'AI Spaces resumen de IA',
      pdfSummarySuffix: 'Resumen de IA',
      pdfUploadedDocsHeading: 'Documentos subidos:',
    },

    chatActions: {
      listen: 'Escuchar',
      stop: 'Detener',
      loading: 'Cargando...',
      copy: 'Copiar',
      addComment: 'Añadir comentario',
      editComment: 'Editar comentario',
      quickActions: 'Acciones rápidas',
      quickActionsResult: 'Resultado de acción rápida',
      setStatus: 'Establecer estado',
      statusSelect: 'Seleccionar estado',
      aiDisclaimer: 'Respuesta generada por IA. Verifique siempre los resultados.',
      wordToggle: 'Generar un documento Word',
      wordCreating: 'Creando documento…',
      wordCreatingSubtitle: 'Basado en la solicitud actual y las fuentes disponibles',
      wordCreatingStatusPill: 'Creando',
      wordCreatingRendering: 'Generando documento Word…',
      wordCreatingInProgress: 'En curso',
      wordCreatingHelper: 'Esto puede tardar unos minutos. Puede seguir chateando mientras se crea el documento.',
      wordNothingFound: 'No se encontró el tema en los documentos disponibles.',
      wordTruncated: 'No se pudo completar el documento. Inténtelo de nuevo.',
      wordFailed: 'Error al generar el documento.',
      wordDownloadWord: 'Descargar Word',
      wordDownloadPdf: 'Descargar PDF',
      wordDocumentCreated: 'Documento Word creado',
      wordReady: 'Listo',
      wordCompletedLabel: 'Completado',
      wordReadyHelper: 'El documento está listo para descargar.',
      wordPreview: 'Vista previa',
      wordDownloadWordShort: 'Word',
      wordDownloadPdfShort: 'PDF',
      wordDownloadError: 'Error al descargar. Inténtelo de nuevo.',
      wordHint: 'Como documento de Word',
      wordModeLabel: 'Word',
      wordComposerMeta: "Solo una solicitud · crea un documento Word",
      wordNotEnabledError: 'La generación de documentos Word no está habilitada para este agente.',
      wordAlreadyRunningError: 'Ya se está generando un documento Word para este agente. Espere a que finalice.',
      saving: 'Guardando...',
      saveComment: 'Guardar',
      commentBy: 'Comentario de {name}',
      readAloud: 'Leer en voz alta',
      close: 'Cerrar',
      commentPlaceholder: 'Añadir un comentario',
      defaultUser: 'Usuario',
      askPlaceholder: 'Pregunte algo',
      chatDisabled: 'El chat está desactivado',
      voiceInput: 'Entrada de voz',
      uploadFile: 'Subir archivo',
      enterProjectName: 'Nombre del proyecto',
      convertToPdf: 'Convirtiendo a PDF...',
      processing: 'Procesando...',
      uploading: 'Subiendo...',
      startAnalysis: 'Iniciar análisis',
      addFiles: 'Añadir archivos',
      newAnalysisTitle: '¿Nuevo análisis?',
      newAnalysisConfirm: '¿Está seguro de que desea cerrar el análisis actual e iniciar uno nuevo?',
      removeFile: 'Eliminar archivo',
      filePickerRoot: 'Carpeta raíz',
      filePickerClear: 'Quitar selección',
      filePickerTrigger: 'Seleccionar un archivo (/)',
      filePickerSearchPlaceholder: 'Buscar en esta carpeta',
      filePickerSelected: 'seleccionado',
      onlyOneFileAllowed: 'Solo se permite un archivo para el análisis inicial.',
      tooManyImages: 'Máximo {max} imágenes por carga.',
      filesStillProcessing: 'Espere a que los archivos subidos terminen de procesarse antes de hacer una pregunta sobre ellos.',
      defaultImageOnlyMessage: 'Por favor, analiza la imagen adjunta.',
      analyzingImage: 'Analizando imagen…',
      transcriptionFailed: 'La transcripción falló',
      microphoneDenied: 'Acceso al micrófono denegado',
      uploadSuccess: 'Archivo subido correctamente',
      uploadFailed: 'Error al subir',
      uploadExceedsPages: 'La subida excede las páginas disponibles',
      uploadPageLimitExceeded: 'El límite de subida de archivos para esta sesión es de {limit} páginas. Ha intentado subir {attempted} páginas. Vuelva a intentarlo y suba solo {limit} páginas.',
      pageLimitsVerificationFailed: 'No se pudieron verificar los límites de páginas',
      invalidFileType: 'Seleccione solo archivos de tipo {types}',
      invalidPdfProcessing: 'Error al procesar {name}: Archivo PDF no válido',
      fileConvertFailed: 'No se pudo convertir el archivo',
      noFileSelected: 'Ningún archivo seleccionado',
      fileErrorFor: 'Error en {name}: {error}',
      genericError: 'Ha ocurrido un error.',
      noOrganization: 'El usuario no está asociado a ninguna organización',
      tasksLoadFailed: 'No se pudieron cargar las tareas: {error}',
      agentQuestionPrefix: 'P: {title}',
      webSearchStep1: 'Entendiendo su pregunta',
      webSearchStep2: 'Buscando en la web',
      webSearchStep3: 'Revisando resultados relevantes',
      webSearchStep4: 'Comprobando fuentes útiles',
      webSearchStep5: 'Preparando la respuesta',
      webSearchFoundQuery: 'Buscado: "{query}"',
      aiThinkingLabel: 'La IA está pensando:',
      retryTimeoutLabel: 'Reintentar',
    },

    answerStatus: {
      accepted: 'Aceptada',
      review: 'Requiere revisión adicional',
      missing: 'Falta información',
      rejected: 'Rechazada',
    },

    projectDetail: {
      breadcrumb: 'Detalles del proyecto',
      tabOverview: 'Resumen',
      tabDocuments: 'Documentos',
      tabQuestions: 'Instrucciones',
      tabActivities: 'Actividades',
      agentLabel: 'Agente: {name}',
      defaultAgentName: 'Agente de revisión de contratos',
      aiSummary: 'Resumen de IA',
      checklistProgress: 'Progreso de la lista de verificación',
      projectLocation: 'Ubicación de solicitud de IA',
      analysisScore: 'Puntuación del análisis',
      documentCount: 'Cantidad de documentos',
      participants: 'Participantes',
      documents: 'Documentos',
      noLocationExtracted: 'Aún no extraída',
      questionsCategory: 'Categorías',
      questionsTitle: 'Título de la pregunta',
      questionsDescription: 'Descripción',
      questionsAnswer: 'Respuesta de IA',
      questionsStatus: 'Estado',
      questionsLoading: 'Cargando preguntas...',
      defaultCategory: 'General',
      acceptedOrOpen: 'Aceptada',
      openAction: 'Abrir',
      projectCreated: 'Proyecto creado',
      newProjectStarted: 'Nuevo proyecto iniciado',
      activitiesEmpty: 'Las actividades del proyecto aparecerán aquí una vez que se suban documentos, se realicen análisis y se respondan preguntas.',
      activitiesEmptyTitle: 'Sin actividad aún',
      activityFileUploaded: 'Archivo subido',
      activityFileProcessed: 'Procesado',
      activityQuestionAsked: 'Pregunta realizada',
      activityQuestionAskedFallback: 'El usuario realizó una pregunta',
      activityAnalysisPerformed: 'Análisis realizado',
      activityAnalysisFallback: 'Análisis de IA completado',
      activityDefaultUser: 'Usuario',
      activityAiAgent: 'Agente de IA',
      activityTodayLabel: 'Hoy',
      activityYesterdayLabel: 'Ayer',
      activityShowOlder: 'Mostrar más antiguos',
      activityShowLess: 'Mostrar menos',
      uploadFilesHint: 'Suba archivos a este proyecto para que el agente pueda revisarlos y analizarlos.',
      yourDocuments: 'Sus documentos',
      uploadDocument: 'Subir documento',
      uploading: 'Subiendo...',
      agentReferenceFiles: 'Archivos de referencia del agente',
      documentName: 'Nombre del documento',
      pages: 'Páginas',
      uploadDate: 'Fecha de subida',
      documentsLoading: 'Cargando documentos...',
      noDocumentsUploaded: 'No se han subido documentos',
      noQuestionsAssigned: 'No se han asignado preguntas',
      noQuestionsHint: 'Las preguntas aparecerán aquí después de ser añadidas a este agente.',
    },

    agentSettings: {
      breadcrumbAgents: 'Agentes',
      breadcrumbSettings: 'Configuración del agente',
      agentFallback: 'Agente',
      departmentFallback: 'Grupo de usuarios',
      tabGeneral: 'Configuración general',
      tabQuestions: 'Instrucciones',
      tabAiProfile: 'Perfil de IA',
      tabTools: 'Herramientas',
      tabFiles: 'Archivos estándar',
      tabProjectOverview: 'Resumen del proyecto',
      tabProjectSetup: 'Configuración del proyecto',
      tabShortcuts: 'Atajos',
      // Tools tab — Word generation
      toolsTitle: 'Herramientas',
      toolsSubtitle: 'Active funciones opcionales para este agente. La generación de Word utiliza la configuración de recuperación existente del agente (documentos, web o combinada).',
      toolsWordGenTitle: 'Generación de Word',
      toolsWordGenSubtitle: 'Los usuarios pueden crear un documento Word con la marca de la organización activando Word para una solicitud de chat individual.',
      toolsCodeInterpreterTitle: "Code Interpreter (Análisis de datos)",
      toolsCodeInterpreterSubtitle: "Permite cálculos exactos, tablas y gráficos a partir de archivos subidos; se activa por chat y permanece activo hasta que lo desactive.",
      toolsCiInstructionLabel: "Instrucciones de análisis",
      toolsCiInstructionPlaceholder: "p. ej., Mostrar todos los importes en CHF y exportar también cada tabla como archivo Excel.",
      toolsCiInstructionHelp: "Se añade a las reglas de análisis fijas de este agente.",
      toolsCiMaxOutputTokensLabel: "Longitud máxima de respuesta (tokens)",
      toolsCiMaxOutputTokensPlaceholder: "Sin límite",
      toolsCiMaxOutputTokensHelp: "Déjelo vacío para no tener límite. Valores muy bajos pueden detener el análisis antes de tiempo.",
      toolsCiSessionScopeLabel: "Entorno de trabajo",
      toolsCiSessionScopeSession: "Reutilizar dentro del chat (recomendado)",
      toolsCiSessionScopeRequest: "Nuevo para cada solicitud",
      toolsCiSessionScopeSessionDesc: "Las solicitudes posteriores reutilizan los mismos archivos y son mucho más rápidas.",
      toolsCiSessionScopeRequestDesc: "Cada análisis comienza en un entorno nuevo y vuelve a subir los archivos.",
      toolsInstructionLabel: 'Instrucción',
      toolsInstructionPlaceholder: 'Instrucción para la generación de Word',
      toolsInstructionHelp: 'Esto reemplaza la parte del prompt del agente que da forma a la respuesta para la generación de Word. La recuperación en sí no cambia.',
      toolsMaxOutputTokensLabel: 'Tokens de salida máximos',
      toolsMaxOutputTokensHelp: 'Presupuesto de salida independiente para la generación de Word. Un valor predeterminado generoso evita documentos truncados silenciosamente; el backend igualmente valida el valor configurado frente al límite del modelo compatible.',
      toolsBehaviourTitle: 'Comportamiento de la generación de Word',
      toolsBehaviourItem1: 'Word solo se muestra en el campo de chat cuando esta herramienta está activada.',
      toolsBehaviourItem2: 'El interruptor de Word se aplica a un mensaje y se restablece después de enviarlo o al abrir otro chat.',
      toolsBehaviourItem3: 'Los documentos seleccionados restringen las fuentes factuales utilizadas para el documento generado.',
      toolsBehaviourItem4: 'El DOCX final y la vista previa en PDF se guardan en Archivos generados y nunca se añaden al índice de búsqueda.',
      toolsFutureToolsNote: 'Herramientas futuras: Análisis de datos / Code Interpreter podrán añadirse aquí más adelante como otra herramienta independiente.',
      toolsFixedLayoutTitle: 'Diseño de documento fijo · no requiere configuración',
      toolsBrandingCardTitle: 'Marca de la organización',
      toolsBrandingCardText: 'El logotipo, el nombre de la organización y el color principal se aplican automáticamente.',
      toolsSourcesCardTitle: 'Fuentes e idioma',
      toolsSourcesCardText: 'Las fuentes validadas y las etiquetas fijas siguen el idioma del documento/solicitud.',
      toolsTransparencyCardTitle: 'Transparencia de IA',
      toolsTransparencyCardText: 'El aviso de contenido generado por IA se incluye en el pie de página y en las propiedades del archivo Word.',
      toolsLayoutFooterNote: 'V1 utiliza el diseño estándar con marca. Las plantillas personalizadas, varias plantillas y un editor de documentos están deliberadamente fuera de alcance.',
      toolsSaveButton: 'Guardar cambios',
      generalTitle: 'Configuración general',
      generalSubtitle: 'Defina cómo funciona este agente y qué problema empresarial resuelve.',
      unsavedChanges: 'Cambios sin guardar',
      cancel: 'Cancelar',
      save: 'Guardar',
      saving: 'Guardando...',
      basicInfoTitle: 'Información básica',
      basicInfoSubtitle: 'Datos básicos para organizar e identificar este agente.',
      agentImageLabel: 'Imagen del agente',
      editImage: 'Editar imagen',
      removeImage: 'Eliminar imagen',
      noFileSelected: 'Ningún archivo seleccionado',
      agentNameLabel: 'Nombre del agente',
      agentNamePlaceholder: 'Ingrese el nombre del agente',
      organizationLabel: 'Organización',
      selectOrganization: 'Seleccionar organización',
      departmentLabel: 'Grupo de usuarios',
      selectDepartment: 'Seleccionar grupo de usuarios',
      descriptionLabel: 'Descripción',
      descriptionPlaceholder: 'Describa el valor principal de este agente...',
      descriptionHelp: 'Una o dos oraciones explicando cómo este agente ayuda a los usuarios.',
      useCaseTitle: 'Caso de uso',
      useCaseSubtitle: 'Describa el problema que este agente resuelve y cómo ayuda a su equipo.',
      problemLabel: 'Descripción del problema*',
      problemPlaceholder: '¿Qué desafío empresarial aborda este agente?',
      problemHelp: 'Ejemplo: Los equipos legales pasan horas revisando contratos manualmente para problemas de cumplimiento...',
      solutionLabel: 'Descripción de la solución*',
      solutionPlaceholder: '¿Cómo resuelve este agente el problema?',
      solutionHelp: 'Ejemplo: Este agente de IA escanea automáticamente contratos e identifica términos clave...',
      goalsTitle: 'Objetivos y KPIs',
      goalsSubtitle: 'Defina métricas de éxito y quién es responsable de este caso de uso.',
      goalLabel: 'Objetivo del caso de uso*',
      goalPlaceholder: 'p. ej. Reducir el tiempo de revisión de contratos en un 80 % con 99 % de precisión',
      goalHelp: 'Este es un texto de ayuda para el usuario.',
      kpisLabel: 'Indicadores clave de rendimiento (KPIs)',
      kpiPlaceholder: 'Ingrese un nuevo KPI...',
      kpiAdd: 'Añadir',
      kpiHelp: 'Presione Enter o haga clic en Añadir para agregar un nuevo KPI.',
      managerLabel: 'Gerente de proceso',
      managerPlaceholder: 'Nombre del gerente de proceso',
      managerHelp: '¿Quién es responsable de este proceso?',
      toastNoTask: 'No se seleccionó ninguna tarea',
      toastNoPermission: 'No tiene permiso para editar esta tarea',
      toastUpdateSuccess: 'Tarea actualizada correctamente',
      toastUpdateError: 'Error al actualizar',
      addCategoryTitle: 'Crear nueva categoría',
      addCategorySubtitle: 'Añada una categoría para organizar instrucciones y facilitar la gestión y el filtrado del análisis.',
      addCategoryNameLabel: 'Nombre de la categoría',
      addCategoryNamePlaceholder: 'Nombre de la categoría',
      addCategoryEnterHint: 'Presione Enter para añadir una nueva categoría.',
      addCategoryEmptyError: 'Ingrese un nombre de categoría.',
      addCategoryExistsError: 'Esta categoría ya existe.',
      addCategoryNoAgentError: 'No se seleccionó ningún agente.',
      addCategoryCreateError: 'Error al crear la categoría.',
      addCategoryDeleteError: 'Error al eliminar la categoría.',
      addCategoryConfirm: 'Añadir categoría',
      addQuestionTitle: 'Crear nueva instrucción',
      editQuestionTitle: 'Editar instrucción',
      questionTitleLabel: 'Título de la instrucción',
      questionTitlePlaceholder: 'Título de la instrucción',
      questionCategoryLabel: 'Categoría',
      questionCategoryPlaceholder: 'Categoría',
      questionDescriptionLabel: 'Descripción',
      questionDescriptionPlaceholder: 'Ingrese la descripción...',
      questionEmptyTitleError: 'Ingrese un título de instrucción.',
      questionEmptyDescriptionError: 'Ingrese una descripción.',
      questionOrgPermissionError: 'Solo puede editar instrucciones para tareas pertenecientes a su organización.',
      questionGenericError: 'Ha ocurrido un error. Inténtelo de nuevo.',
      addQuestionConfirm: 'Añadir instrucción',
      updateQuestionConfirm: 'Actualizar instrucción',
      edit: 'Editar',
      delete: 'Eliminar',
      searchPlaceholder: 'Buscar',
      previous: 'Anterior',
      next: 'Siguiente',
      pageOf: 'Página {current} de {total}',
      showProjectDetailTitle: 'Activar instrucciones',
      showProjectDetailSubtitle: 'Cuando está desactivado, la pestaña de instrucciones está oculta en el chat.',
      showFilesTabTitle: 'Mostrar pestaña de archivos en el chat',
      showFilesTabSubtitle: 'Cuando está desactivado, la pestaña de archivos en la barra lateral del chat está oculta para este agente.',
      allowLocalFilesTitle: 'Permitir carga de archivos locales',
      allowLocalFilesSubtitle: 'Cuando está desactivado, los usuarios no pueden cargar archivos locales. Solo se muestran los archivos de SharePoint.',
      questionsSelectTaskHint: 'Seleccione una tarea para ver sus instrucciones',
      questionsAddCategoriesButton: 'Añadir categorías',
      questionsAddQuestionButton: 'Añadir instrucción',
      questionsReorderButton: 'Reordenar',
      questionsReorderDoneButton: 'Listo',
      questionsReorderHint: 'Se muestran todas las instrucciones. Arrastra una instrucción a la posición que quieras, incluso entre páginas anteriores.',
      questionsColCategories: 'Categorías',
      questionsColTitle: 'Título de la instrucción',
      questionsColDescription: 'Descripción',
      questionsEmptyTitle: 'No se encontraron instrucciones',
      questionsEmptyHint: 'Aún no se han añadido instrucciones. Haga clic en "Añadir instrucción" para crear una nueva instrucción.',
      questionsDeleteTitle: 'Eliminar instrucción',
      questionsDeleteMessage: '¿Está seguro de que desea eliminar esta instrucción?',
      aiProfileLoadError: 'Error al cargar el perfil de IA',
      aiProfileSaveError: 'Error al guardar el perfil de IA',
      aiProfileNoPermission: 'Solo puede editar perfiles de IA para tareas de su organización',
      aiProfileSaveSuccess: 'Perfil de IA guardado correctamente',
      aiProfileLoading: 'Cargando perfil de IA...',
      aiProfileHeaderTitle: 'Perfil de IA',
      aiProfileHeaderSubtitle: 'Configure cómo el agente de IA entiende el contexto, genera respuestas y se comporta durante el análisis.',
      aiProfileModelDefaultLabel: 'Default (from environment variables)',
      aiProfileModelDefaultDesc: 'Utiliza la configuración predeterminada',
      aiProfileModelGpt51Label: 'GPT-5.1',
      aiProfileModelGpt51Desc: 'El más potente, rendimiento equilibrado',
      aiProfileModelGpt56LunaLabel: 'GPT-5.6-Luna',
      aiProfileModelGpt56LunaDesc: 'Experimental, solo búsqueda web',
      aiProfileModelGpt56TerraLabel: 'GPT-5.6-Terra',
      aiProfileModelGpt56TerraDesc: 'Experimental, solo para búsqueda web',
      aiProfileReasoningLowLabel: 'Low',
      aiProfileReasoningLowDesc: 'Respuestas más rápidas, razonamiento básico',
      aiProfileReasoningStandardLabel: 'Standard',
      aiProfileReasoningStandardDesc: 'Razonamiento estándar',
      aiProfileReasoningMediumLabel: 'Medium',
      aiProfileReasoningMediumDesc: 'Velocidad y calidad equilibradas',
      aiProfileReasoningHighLabel: 'High',
      aiProfileReasoningHighDesc: 'Mejor precisión, respuestas más lentas',
      aiProfileSearchHybrid: 'Hybrid',
      aiProfileSearchSemantic: 'Hybrid + Semántico',
      aiProfileSearchVector: 'Vector',
      aiProfileSearchKeyword: 'Keyword',
      aiProfileKnowledgeSourceTitle: 'Fuente de conocimiento',
      aiProfileKnowledgeSourceSubtitle: 'Elija qué fuentes puede usar este perfil de IA.',
      aiProfileModeDocumentsLabel: 'Solo documentos',
      aiProfileModeDocumentsDesc: 'Respuestas solo con documentos indexados y pares de preguntas/respuestas.',
      aiProfileModeWebLabel: 'Solo web',
      aiProfileModeWebDesc: 'Respuestas solo con resultados de búsqueda web.',
      aiProfileModeHybridLabel: 'Documentos + Web',
      aiProfileModeHybridDesc: 'Respuestas con documentos y resultados de búsqueda web.',
      aiProfileBadgeRag: 'RAG',
      aiProfileBadgeNoRag: 'Sin RAG',
      aiProfileBadgeWeb: 'Web',
      aiProfileBadgeNoWeb: 'Sin web',
      aiProfileIdentityTitle: 'AI Identity',
      aiProfileIdentitySubtitle: 'Defina la personalidad e instrucciones para el comportamiento de la IA.',
      aiProfileIdentitySubtitleDocuments: 'Prompt de sistema dedicado para respuestas basadas en documentos.',
      aiProfileIdentitySubtitleWeb: 'Prompt de sistema dedicado para respuestas basadas en la web.',
      aiProfileIdentitySubtitleHybrid: 'Prompt de sistema dedicado para respuestas usando documentos y web.',
      aiProfileDifferentPromptInfoTitle: 'Prompt de sistema diferente por fuente de conocimiento',
      aiProfileDifferentPromptInfoDesc: 'Cambiar el modo seleccionado carga el prompt de sistema dedicado para ese modo.',
      aiProfileActivePromptBadge: 'Prompt activo: {mode}',
      aiProfileSystemPromptLabel: 'Prompt de sistema: {mode}',
      aiProfileSystemPromptLabelPlain: 'Prompt de sistema',
      aiProfileRegexDetectionLabel: 'Mostrar UI de referencias',
      disclaimerToggleLabel: 'Aviso de privacidad',
      disclaimerToggleDescription: 'Muestra un aviso de privacidad encima del campo de chat.',
      disclaimerTextLabel: 'Texto del aviso',
      disclaimerTextHelp: 'El aviso aparece justo encima del campo de la pregunta.',
      disclaimerLearnMoreContentLabel: 'Contenido de "Más información"',
      disclaimerLearnMoreContentHelp: 'Contenido mostrado en la ventana al hacer clic en "Más información".',
      disclaimerIconLabel: 'Icono',
      disclaimerInfoNotice: 'El aviso se muestra cada vez que se abre un chat. Los usuarios pueden cerrarlo para la sesión actual; vuelve a aparecer al reabrir el chat.',
      disclaimerPreviewLabel: 'Vista previa',
      disclaimerLearnMoreLink: 'Más información',
      disclaimerModalTitle: 'Más información',
      toggleOn: 'Activado',
      toggleOff: 'Desactivado',
      aiProfileSystemPromptHelp: 'Prompt técnico que controla exactamente cómo responde la IA en este modo. Déjelo vacío para usar el valor predeterminado.',
      aiProfileSystemPromptPlaceholder: 'Prompt de sistema para {mode}',
      aiProfileNameLabel: 'AI Profile Name',
      aiProfileNamePlaceholder: 'Agente de revisión de contratos',
      aiProfileDescriptionLabel: 'Descripción',
      aiProfileDescriptionPlaceholder: 'Usted es un experimentado analista de contratos de IA que revisa documentos legales con precisión y atención al detalle. Proporcione respuestas claras y estructuradas basadas exclusivamente en el contenido del documento. Destaque riesgos, obligaciones y términos clave. Formatee sus respuestas claramente con viñetas y encabezados de sección.',
      aiProfileDescriptionHelp: 'Este prompt establece el tono y comportamiento para todas las respuestas de IA',
      aiProfileModelTitle: 'Model & Intelligence Settings',
      aiProfileModelSubtitle: 'Elija el modelo de IA y el nivel de razonamiento que mejor se adapte a su caso de uso.',
      aiProfileModelLabel: 'Modelo',
      aiProfileModelSelectPlaceholder: 'Seleccionar modelo',
      aiProfileReasoningLabel: 'Reasoning Effort',
      aiProfileResponseLengthLabel: 'Límite de longitud de respuesta',
      aiProfileResponseLengthHelp: 'Número máximo de tokens en la respuesta. Valores más altos permiten respuestas más largas. GPT-5.1 admite hasta 65.536 tokens. Predeterminado: 1.000',
      aiProfileResponseLengthExceeded: 'El valor máximo permitido es {max} — no se pueden introducir valores más altos.',
      aiProfileSourceSettingsTitle: 'Configuración de fuentes',
      aiProfileSourceSettingsSubtitleDocuments: 'Configure los ajustes de recuperación de documentos.',
      aiProfileSourceSettingsSubtitleWeb: 'Configure los ajustes de búsqueda web.',
      aiProfileSourceSettingsSubtitleHybrid: 'Configure la recuperación de documentos y los ajustes de búsqueda web.',
      aiProfileWebSourceSettingsTitle: 'Configuración de búsqueda web',
      aiProfileWebSourceSettingsSubtitle: 'Controla los resultados de búsqueda web pública utilizados por la IA.',
      aiProfileWebSourceBadge: 'FUENTE WEB',
      aiProfileWebSearchProviderLabel: 'Proveedor de búsqueda web',
      aiProfileWebSearchProviderValue: 'Azure OpenAI Web Search',
      aiProfileSearchContextSizeLabel: 'Tamaño del contexto de búsqueda',
      aiProfileSearchContextSizeHelp: 'Controla cuánto contenido web usa Azure para fundamentar la respuesta, no un número exacto de fuentes o citas.',
      aiProfileSearchContextLowLabel: 'Bajo',
      aiProfileSearchContextLowDesc: 'Búsqueda más rápida y superficial',
      aiProfileSearchContextMediumLabel: 'Medio',
      aiProfileSearchContextMediumDesc: 'Predeterminado equilibrado',
      aiProfileSearchContextHighLabel: 'Alto',
      aiProfileSearchContextHighDesc: 'Búsqueda más exhaustiva y detallada',
      aiProfileUseWebChatHistoryLabel: 'Usar historial de chat para la búsqueda web',
      aiProfileUseWebChatHistoryHelp: 'Incluye preguntas y respuestas anteriores en la búsqueda web. Desactivado de forma predeterminada, ya que puede degradar la calidad de las respuestas.',
      aiProfileVerbosityLabel: 'Nivel de detalle',
      aiProfileVerbosityOffLabel: 'Desactivado',
      aiProfileVerbosityOffDesc: 'No se envía',
      aiProfileVerbosityLowLabel: 'Bajo',
      aiProfileVerbosityLowDesc: 'Respuestas breves',
      aiProfileVerbosityMediumLabel: 'Medio',
      aiProfileVerbosityMediumDesc: 'Respuestas equilibradas',
      aiProfileVerbosityHighLabel: 'Alto',
      aiProfileVerbosityHighDesc: 'Respuestas detalladas',
      aiProfileAdvancedWebSearchTitle: 'Parámetros avanzados de búsqueda web',
      aiProfileForceWebSearchLabel: 'Forzar búsqueda web',
      aiProfileForceWebSearchHelp: 'Activado: cada solicitud busca en la web (tool_choice: required). Desactivado: el modelo decide por sí mismo (tool_choice: auto).',
      aiProfileStreamResponseLabel: 'Transmitir respuesta',
      aiProfileStreamResponseHelp: 'Activado (predeterminado): muestra el indicador de progreso "buscando"/"generando" mientras la solicitud está en curso. Desactivado: una única solicitud sin transmisión.',
      aiProfileIncludeSearchSourcesLabel: 'Registrar fuentes de búsqueda',
      aiProfileIncludeSearchSourcesHelp: 'Activado: las fuentes que el modelo buscó también se registran en el servidor (solo para depuración, no cambia las fuentes mostradas a los usuarios).',
      aiProfileShowReasoningLabel: 'Mostrar razonamiento',
      aiProfileShowReasoningHelp: 'Activado: muestra al usuario, en vivo, en qué está pensando el modelo antes de que aparezca la respuesta (como la visualización de "Pensamiento" de Claude).',
      aiProfileShowReasoningEffortWarning: 'Solo tiene efecto con Esfuerzo de Razonamiento "Medio" o "Alto" — con "Estándar" o "Bajo", Azure no genera ningún resumen de razonamiento.',
      aiProfileShowReasoningStreamWarning: 'También requiere que "Transmitir respuesta" (arriba) esté activado — sin transmisión, el razonamiento no se puede mostrar al usuario en vivo.',
      aiProfileMaxToolCallsLabel: 'Máximo de llamadas a herramientas',
      aiProfileMaxToolCallsPlaceholder: 'Sin límite',
      aiProfileMaxToolCallsHelp: 'Limita cuántas búsquedas/lecturas de páginas puede hacer Azure por respuesta. Vacío: no se envía límite.',
      aiProfileAzureTimeoutLabel: 'Tiempo de espera (segundos)',
      aiProfileAzureTimeoutHelp: 'Cuánto tiempo esperar una respuesta de Azure antes de desistir. Predeterminado: 180 segundos.',
      aiProfileAzureMaxRetriesLabel: 'Reintentos máximos',
      aiProfileAzureMaxRetriesHelp: 'Cuántas veces Azure reintenta automáticamente una solicitud fallida. Predeterminado: 0 (sin reintento automático — cada reintento vuelve a usar el tiempo de espera completo).',
      aiProfileMaxOutputTokensLabel: 'Máx. tokens de salida: {mode}',
      aiProfileMaxOutputTokensHelp: 'Limita la longitud de la respuesta para este modo. Predeterminado: 1500. Máximo permitido: 65536 — valores más altos se limitan automáticamente.',
      aiProfileMaxOutputTokensExceeded: 'El valor máximo permitido es {max} — los valores más altos se limitan automáticamente al guardar.',
      aiProfileAllowedDomainsLabel: 'Dominios permitidos',
      aiProfileBlockedDomainsLabel: 'Dominios bloqueados',
      aiProfileDomainsPlaceholder: 'p. ej. wikipedia.org, example.com',
      aiProfileDomainsHelp: 'Separados por comas o uno por línea. Vacío: no se envía filtro.',
      aiProfileUserLocationLabel: 'Segmentación por ubicación',
      aiProfileUserLocationHelp: 'Activado: se envía una ubicación aproximada a la herramienta de búsqueda web para mejorar los resultados locales.',
      aiProfileUserLocationCountryLabel: 'País (código ISO)',
      aiProfileUserLocationCountryPlaceholder: 'p. ej. DE',
      aiProfileUserLocationCityLabel: 'Ciudad',
      aiProfileUserLocationCityPlaceholder: 'p. ej. Berlín',
      aiProfileUserLocationRegionLabel: 'Región',
      aiProfileUserLocationRegionPlaceholder: 'p. ej. Berlín',
      aiProfileUserLocationTimezoneLabel: 'Zona horaria',
      aiProfileUserLocationTimezonePlaceholder: 'p. ej. Europe/Berlin',
      aiProfileContextTitle: 'Configuración de contexto y recuperación',
      aiProfileContextSubtitle: 'Define cuánto contexto utiliza la IA antes de generar respuestas.',
      aiProfileSearchStrategyLabel: 'Estrategia de búsqueda',
      aiProfileSearchStrategyPlaceholder: 'Seleccionar estrategia',
      aiProfileTopKLabel: 'Contextos de documentos (Top K)',
      aiProfileUseCustom: 'Use Custom',
      aiProfileTopKDefaultHint: 'Cuando está desactivado, se utiliza el número predeterminado de contextos',
      aiProfileVectorKLabel: 'Pool de candidatos (Vector K)',
      aiProfileVectorKDefaultHint: 'Cuando está desactivado, se utiliza el valor predeterminado global',
      aiProfileQaPairsLabel: 'Pares Q/A como contexto',
      aiProfileQaPairsDefaultHint: 'Cuando está desactivado, no se utilizan pares Q/A como contexto',
      aiProfileWebSearchLabel: 'Búsqueda web',
      aiProfileWebSearchDescription: 'Busca en internet información actualizada. Los archivos e instrucciones se ocultarán en el chat.',
      aiProfileWebSearchEnabled: 'Activado',
      aiProfileWebSearchDisabled: 'Desactivado',
      aiProfileWebSearchCustomPromptLabel: 'Prompt de búsqueda personalizado',
      aiProfileWebSearchCustomPromptDescription: 'Se añade delante de la consulta del usuario en cada búsqueda web. Solo visible para Super Administradores.',
      aiProfileWebSearchCustomPromptPlaceholder: 'p. ej. Buscar solo fuentes de los últimos 12 meses...',
      filesImportSuccess: '{count} archivo(s) importado(s) correctamente',
      filesImportError: 'Error al importar',
      filesAutoSyncSubfoldersEnabled: 'Auto-sync (con subcarpetas) activado',
      filesAutoSyncFolderOnlyEnabled: 'Auto-sync (solo carpeta) activado',
      filesAutoSyncEnableError: 'Error al activar auto-sync',
      filesInheritedSyncNotice: 'Este elemento se sincroniza a través de una carpeta principal. Desactive la carpeta principal.',
      filesAutoSyncDisabled: 'Auto-sync desactivado',
      filesAutoSyncDisableError: 'Error al desactivar',
      filesSpItemDeleted: 'Elemento eliminado correctamente',
      filesSpItemDeleteError: 'Error al eliminar',
      filesLastSyncNever: 'No sincronizado',
      filesLastSyncJustNow: 'Ahora mismo',
      filesLastSyncMinutes: 'hace {mins} min.',
      filesLastSyncHours: 'hace {hrs} h',
      filesLastSyncDays: 'hace {days} día(s)',
      filesRelToday: 'Hoy',
      filesRelYesterday: 'Ayer',
      filesRelDaysAgo: 'hace {days} días',
      filesRelWeekAgo: 'hace 1 semana',
      filesRelWeeksAgo: 'hace {weeks} semanas',
      filesRelMonthAgo: 'hace 1 mes',
      filesRelMonthsAgo: 'hace {months} meses',
      filesRelYearAgo: 'hace 1 año',
      filesRelYearsAgo: 'hace {years} años',
      filesGeneralFolder: 'General',
      filesInvalidFileType: '{count} archivo(s) omitido(s). Solo se permiten PDF, DOC y DOCX.',
      filesFolderNameRequired: 'Ingrese un nombre de carpeta',
      filesAddAtLeastOne: 'Añada al menos un archivo',
      filesSelectTaskRequired: 'Seleccione una tarea',
      filesAddedToFolderSuccess: '{count} archivo(s) añadido(s) a "{folderName}" correctamente',
      filesFolderCreatedSuccess: 'Carpeta "{folderName}" con {count} archivo(s) creada correctamente',
      filesPartialSuccess: '{success} archivos correctos, {errors} errores',
      filesUploadError: 'Error al subir archivos',
      filesMonthlyPageLimitExceeded: 'El límite de subida de archivos para este mes es de {limit} páginas. Ha intentado subir {attempted} páginas. Vuelva a intentarlo y suba solo {limit} páginas.',
      filesCreateFolderError: 'Error al crear carpeta',
      filesDeleteFileSuccess: 'Archivo eliminado correctamente',
      filesDeleteFileError: 'Error al eliminar archivo',
      filesDeleteFolderSuccess: 'Carpeta "{folderName}" eliminada correctamente',
      filesSelectTaskHint: 'Seleccione una tarea para gestionar archivos estándar',
      filesTabAll: 'Todos los archivos',
      filesTabLocal: 'Archivos locales',
      filesTabSharePoint: 'SharePoint',
      filesSubTabOverview: 'Sitios',
      filesSubTabImportReports: 'Informes de importación',
      filesImportReportsSubtitle: 'Un informe por importación, el más reciente primero.',
      filesImportReportsRefresh: 'Actualizar',
      filesImportReportsEmpty: 'Aún no hay informes de importación',
      filesImportReportsEmptyHint: 'Los informes se guardan automáticamente después de cada importación.',
      filesImportReportsColSite: 'Sitio',
      filesImportReportsColStarted: 'Iniciado',
      filesSubTabProtocol: 'Protocolo de eliminación',
      filesDeletionReportsSubtitle: 'Un informe por eliminación, el más reciente primero.',
      filesDeletionReportsEmpty: 'Aún no hay informes de eliminación',
      filesDeletionReportsEmptyHint: 'Los informes se guardan automáticamente después de cada acción de "Eliminar datos importados" o "Quitar sitio".',
      filesViewDeletionReport: 'Ver informe de eliminación',
      filesCountSingular: 'archivo',
      filesCountPlural: 'archivos',
      filesItemSingular: 'elemento',
      filesItemPlural: 'elementos',
      filesHeaderAllSubtitle: 'Archivos locales y de SharePoint en una lista unificada.',
      filesHeaderLocalSubtitle: 'Archivos almacenados y gestionados directamente en DocAI.',
      filesConnectedSitesSingular: '{count} sitio asignado a este agente',
      filesConnectedSitesPlural: '{count} sitios asignados a este agente',
      filesUploadFile: 'Subir archivo',
      filesAddFolder: 'Añadir carpeta',
      filesSearchPlaceholder: 'Buscar',
      filesFilterLabel: 'Filtro',
      filesFilterLabelWithCount: 'Filtro (1)',
      filesNoResultsFound: 'No se encontraron resultados',
      filesNoFiles: 'No hay archivos disponibles',
      filesColName: 'Nombre del archivo',
      filesColType: 'Tipo de archivo',
      filesColCreated: 'Creado',
      filesColModified: 'Modificado',
      filesTypeSharePoint: 'SharePoint File',
      filesTypeLocal: 'Local File',
      filesDelete: 'Eliminar',
      filesPageOf: 'Página {current} de {total}',
      filesPrevious: 'Anterior',
      filesNext: 'Siguiente',
      filesSyncProtocolTitle: 'Protocolo de eliminación',
      filesSyncProtocolSubtitle: 'Registro de auditoría de todas las eliminaciones en todos los sitios',
      filesNoSyncActions: 'No se registraron acciones de sincronización',
      filesColSite: 'Sitio',
      filesColPath: 'Ruta',
      filesColTimestamp: 'Marca de tiempo',
      filesColRuleType: 'Tipo de regla',
      filesColEvent: 'Evento',
      filesColStatus: 'Estado',
      filesSiteOverview: 'Sitios de SharePoint',
      filesFilesAndFolders: 'Archivos y carpetas',
      filesSyncedSuffix: '{count} sincronizados',
      filesImporting: 'Importando...',
      filesImport: 'Importar',
      filesImportWithCount: 'Importar ({count})',
      filesNoItems: 'No hay archivos ni carpetas',
      filesDisableSync: 'Disable Sync',
      filesEnableAutoSyncFolderOnly: 'Enable Auto-Sync (folder only)',
      filesEnableAutoSync: 'Enable Auto-Sync',
      filesItemTypeFolder: 'Carpeta',
      filesItemTypeFile: 'Archivo',
      filesMetaModified: 'Modificado: {date}',
      filesColNameSp: 'Nombre',
      filesColTypeSp: 'Tipo',
      filesColSpFolder: 'folder',
      filesColSpFile: 'File',
      filesNoConnectedSites: 'Aún no hay sitios de SharePoint',
      filesNoConnectedSitesHint: 'Añada un sitio que su administrador de la organización haya conectado, elija qué bibliotecas y listas usar y luego inicie una importación.',
      filesAddSiteButton: 'Añadir sitio',
      filesAddSiteTitle: 'Añadir sitio de SharePoint',
      filesAddSiteSubtitle: 'Sitios que su administrador de la organización ha conectado. Añadir un sitio no importa nada.',
      filesScheduleSyncButtonTitle: 'Programar sincronización',
      filesScheduleSyncTitle: 'Programación de sincronización',
      filesScheduleSyncSubtitle: 'Elija la fecha y hora en que todos los sitios configurados se sincronizarán automáticamente.',
      filesScheduleSyncDateLabel: 'Fecha',
      filesScheduleSyncTimeLabel: 'Hora',
      filesScheduleSyncRepeatLabel: 'Repetir automáticamente',
      filesScheduleSyncRepeatDaysLabel: 'Cada (días)',
      filesScheduleSyncSaveButton: 'Guardar programación',
      filesScheduleSyncRemoveButton: 'Eliminar programación',
      filesScheduleSyncNextRunLabel: 'Próxima sincronización:',
      filesScheduleSyncNoScheduleLabel: 'No hay programación establecida',
      filesScheduleSyncPastDateError: 'La fecha y hora deben estar en el futuro',
      filesScheduleSyncSelectDatePlaceholder: 'Seleccionar fecha',
      filesScheduleSyncSelectTimePlaceholder: 'Seleccionar hora',
      filesScheduleSyncClearButton: 'Borrar',
      filesScheduleSyncTodayButton: 'Hoy',
      filesAddSiteNoKnowledgeArea: 'Sin área de conocimiento',
      filesAddSiteEmpty: 'No hay más sitios disponibles — o ya se añadieron todos, o su administrador de la organización aún no ha conectado uno que coincida con su área.',
      filesAddSiteSubmit: 'Añadir sitio',
      filesSiteAddedToAgent: 'Sitio añadido al agente',
      filesSourcesUpdated: 'Fuentes actualizadas',
      filesImportedBrowserTitle: 'Archivos importados — {siteName}',
      filesImportedBrowserRoot: 'Todas las carpetas',
      filesImportedBrowserBack: 'Atrás',
      filesImportedBrowserEmpty: 'No hay archivos en esta ubicación',
      filesImportedBrowserFileCount: '{count} archivos',
      filesAddSiteNext: 'Siguiente',
      filesAddSiteSourcesTitle: 'Fuentes en {siteName}',
      filesAddSiteSourcesSubtitle: 'Bibliotecas de documentos y listas con archivos adjuntos en este sitio. Solo se puede importar lo que seleccione.',
      filesAddSiteDocumentLibraries: 'Bibliotecas de documentos',
      filesAddSiteItemCount: '{count} elementos',
      filesAddSiteLists: 'Listas con archivos adjuntos',
      filesAddSiteListsHint: 'Las listas aún no se pueden seleccionar. Su importación requiere autenticación basada en certificados que todavía no está configurada — esta selección se habilitará cuando esté disponible.',
      filesAddSiteNoSources: 'No se encontraron bibliotecas de documentos ni listas en este sitio.',
      filesAddSiteSourcesFooterHint: 'Al guardar se recuerda la selección. No se importa nada hasta que inicie una importación.',
      filesAddSiteSelectAtLeastOne: 'Seleccione al menos una fuente.',
      filesOpenSite: 'Abrir sitio',
      filesStatsFiles: 'Archivos',
      filesStatsFolders: 'Carpetas',
      filesStatsSubfolders: 'Subcarpetas',
      filesStatsLastSync: 'Última sincronización',
      filesStatusImported: 'Importado',
      filesSourcesChangedBadge: 'Fuentes modificadas',
      filesSourcesChangedHint: 'Las fuentes seleccionadas han cambiado desde la última importación. Vuelva a importar para aplicar el cambio.',
      filesEditSources: 'Editar fuentes',
      filesImportAgain: 'Importar de nuevo',
      filesStatsFilesImported: 'archivos importados',
      filesStatsSourceCountSingular: '{count} fuente',
      filesStatsSourceCountPlural: '{count} fuentes',
      filesStatsLastImport: 'última importación',
      filesStatsSkippedFailed: '{skipped} omitidos · {failed} fallidos',
      filesViewImportReport: 'Ver informe de importación',
      filesImportInfoBanner: 'Las importaciones solo se ejecutan cuando usted las inicia. Los archivos añadidos, modificados o eliminados en SharePoint después no se detectarán hasta la próxima importación.',
      filesDeleteImportedData: 'Eliminar datos importados',
      filesRemoveSiteFromAgent: 'Quitar sitio del agente',
      filesSiteRemovedFromAgent: 'Sitio quitado del agente',
      filesRemoveSiteError: 'No se pudo quitar el sitio del agente',
      filesDeleteImportedDataConfirmTitle: '¿Eliminar datos importados?',
      filesDeleteImportedDataConfirmMessage: 'Se eliminarán todos los archivos importados de {siteName} — embeddings, entradas de archivos y asignaciones. Esta acción no se puede deshacer.',
      filesDeleteImportedDataError: 'No se pudieron eliminar los datos importados. Inténtelo de nuevo más tarde.',
      filesRemoveSiteConfirmTitle: '¿Quitar sitio?',
      filesRemoveSiteConfirmMessage: 'El sitio {siteName} se quitará del agente y se eliminarán todos los datos importados de él — documentos, resultados de OCR, incrustaciones y estructura. Esta acción no se puede deshacer.',
      filesRemoveSiteConfirmButton: 'Quitar sitio',
      filesImportReportTitle: 'Informe de importación — {siteName}',
      filesImportReportStarted: 'Iniciado {date}',
      filesImportReportSources: 'Fuentes',
      filesImportReportFileTypes: 'Tipos de archivo',
      filesImportReportPageLimit: 'Límite de páginas',
      filesImportReportMaxPages: 'Máx. {max}',
      filesImportReportNoLimit: 'Sin límite',
      filesImportReportImported: 'Importado',
      filesImportReportSkipped: 'Omitido',
      filesImportReportFailed: 'Fallido',
      filesImportReportFileCount: '{count} archivos',
      filesImportReportDuration: 'Duración',
      filesImportReportStoppedNote: 'Esta importación se detuvo antes de tiempo. Se conservaron {count} archivo(s), el resto se omitió.',
      filesImportReportStoppedNoteWithTotal: 'Esta importación se detuvo antes de tiempo. Se conservaron {count} archivo(s) en esta ejecución (el sitio ahora tiene {total} archivos en total).',
      filesImportReportDiscardedNote: 'Esta importación se canceló y se descartó. No se conservó nada de esta ejecución.',
      filesImportReportBadgeStopped: 'Detenido',
      filesImportReportBadgeDiscarded: 'Descartado',
      embedErrorNoTextExtracted: 'No se extrajo texto (probablemente un PDF escaneado)',
      filesImportReportLastImport: 'Última importación',
      filesImportReportNoDetailHint: 'Estos archivos se importaron uno por uno — no existe un informe detallado con tipos de archivo, límite de páginas ni historial por archivo para esto.',
      filesImportReportSkippedTitle: 'Omitidos por su configuración de importación',
      filesImportReportSkippedHint: 'Estos se excluyeron intencionalmente. Para incluirlos, cambie la configuración e importe de nuevo.',
      filesImportReportFailedTitle: 'Fallidos',
      filesImportReportFailedHint: 'Estos no se pudieron procesar. Al reintentar solo se leerán estos archivos.',
      filesDeletionReportTitle: 'Informe de eliminación — {siteName}',
      filesImportReportRemoved: 'Eliminado',
      filesDeletionReportRemovedTitle: 'Archivos eliminados',
      filesDeletionReportFailedTitle: 'Fallidos',
      filesDeletionReportFailedHint: 'No se pudieron eliminar estos archivos.',
      filesRetryFailedFiles: 'Reintentar archivos fallidos ({count})',
      filesRetryReportNotCurrent: 'Este informe ya no describe el estado actual del sitio y no se puede reintentar.',
      filesRetryImportRunning: 'Ya hay una importación en curso para este sitio. Espere a que finalice.',
      filesRetryFailedError: 'Reintento fallido. Inténtelo de nuevo más tarde.',
      filesStartImportTitle: 'Iniciar importación',
      filesStartImportFileTypes: 'Tipos de archivo',
      filesStartImportPageLimit: 'Límite de páginas por archivo',
      filesStartImportNoLimit: 'Sin límite',
      filesStartImportPageLimitHint: 'Los archivos con más páginas se omiten y se incluyen en el informe de importación.',
      filesStartImportReplaceWarningBold: 'Este sitio se importó el {date}.',
      filesStartImportReplaceWarningRest: 'Volver a importar reemplazará los {count} documentos, resultados de OCR e incrustaciones almacenados actualmente para él.',
      filesStartImportSourcesAddedLabel: 'Fuentes añadidas:',
      filesStartImportSourcesRemovedLabel: 'Fuentes eliminadas (sus archivos se eliminarán):',
      filesStartImportInfoStatic: 'La importación lee por completo las fuentes seleccionadas. Los archivos eliminados o modificados posteriormente en SharePoint permanecerán aquí sin cambios hasta que vuelva a importar.',
      filesStartImportSubmit: 'Iniciar importación',
      filesImportCompleted: 'Importación completada para {site}',
      filesImportFailed: 'Error en la importación',
      filesImportRunning: 'Importación en curso',
      filesImportReconnecting: 'Reconectando…',
      filesImportProcessedOf: '{processed} de {total} archivos procesados',
      filesImportCancel: 'Cancelar',
      filesImportCancelling: 'Cancelando…',
      filesImportCancelTitle: '¿Cancelar importación?',
      filesImportCancelMessage: '¿Desea conservar los archivos ya importados y solo detener el resto, o deshacer toda la importación realizada hasta ahora?',
      filesImportCancelStop: 'Detener (conservar archivos actuales)',
      filesImportCancelDiscard: 'Descartar todo',
      filesImportCancelBack: 'Volver',
      filesImportCancelError: 'No se pudo cancelar la importación',
      filesImportCancelProcessing: 'Procesando…',
      filesEmptyInFolder: 'No hay archivos en esta carpeta',
      filesEmptyFolders: 'No se encontraron carpetas',
      filesEmptyLocalHint: 'Haga clic en "Añadir carpeta" para crear una nueva carpeta con archivos.',
      filesEmptySharePointHint: 'Importe archivos de SharePoint para mostrarlos aquí.',
      filesCreatedOn: 'Creado: {date}',
      filesModifiedOn: 'Modificado: {date}',
      filesColFileName: 'Nombre del archivo',
      filesColFolderName: 'Nombre de la carpeta',
      filesColUploadedAt: 'Subido el',
      filesColSize: 'Tamaño',
      filesColUsedIn: 'Usado en',
      filesColFiles: 'Archivos',
      filesColCreatedAt: 'Creado el',
      filesColLastModified: 'Última modificación',
      filesSourceDeleted: 'Fuente eliminada',
      filesProjectSingular: 'proyecto',
      filesProjectPlural: 'proyectos',
      filesAddToFolderTitle: 'Añadir archivos a "{folderName}"',
      filesCreateNewFolderTitle: 'Crear nueva carpeta',
      filesAddToFolderSubtitle: 'Suba nuevos archivos a esta carpeta.',
      filesCreateNewFolderSubtitle: 'Cree una carpeta y añada archivos.',
      filesFolderNameLabel: 'Nombre de la carpeta *',
      filesFolderNamePlaceholder: 'p. ej. contratos, plantillas, informes...',
      filesAddFilesLabel: 'Añadir archivos *',
      filesDragOrClick: 'Arrastre archivos aquí o haga clic para seleccionar',
      filesSupportedTypes: 'PDF, DOC, DOCX (varios archivos posibles)',
      filesSelectedFiles: 'Archivos seleccionados ({count})',
      filesRemoveAll: 'Eliminar todos',
      filesCancel: 'Cancelar',
      filesUploadingStatus: 'Subiendo...',
      filesCreatingStatus: 'Creando...',
      filesUpload: 'Subir',
      filesCreateFolder: 'Crear carpeta',
      filesUploadProgressUploading: 'Los archivos se están subiendo...',
      filesUploadProgressCreating: 'La carpeta se está creando...',
      filesFileOfTotal: 'Archivo {current} de {total}',
      filesPreparing: 'Preparando...',
      filesPercentDone: '{percent} % completado',
      filesDeleteFolderTitle: 'Eliminar carpeta',
      filesDeleteFileTitle: 'Eliminar archivo',
      filesDeleteFolderMessage: '¿Está seguro de que desea eliminar la carpeta "{name}" con {count} archivo(s)? Esta acción no se puede deshacer.',
      filesDeleteSpItemMessage: '¿Está seguro de que desea eliminar "{name}"? Esta acción no se puede deshacer.',
      filesDeleteFileMessage: '¿Está seguro de que desea eliminar "{name}"? Esta acción no se puede deshacer.',
      filesDeleting: 'Eliminando...',
      filesFilterTitle: 'Filtro',
      filesFilterFileTypeLabel: 'Tipo de archivo',
      filesFilterOptLocal: 'Local File',
      filesFilterOptSharePoint: 'SharePoint File',
      filesFilterSortByLabel: 'Ordenar por',
      filesSortNewest: 'Más recientes',
      filesSortOldest: 'Más antiguos',
      filesSortMostProgress: 'Most progress',
      filesSortLeastProgress: 'Least progress',
      filesResetAll: 'Restablecer todo',
      filesApply: 'Aplicar',
      shortcutsTabEmoji: 'Emoji',
      shortcutsTabCustom: 'Personalizado',
      shortcutsCustomEmojiLabel: 'Ingrese cualquier emoji:',
      shortcutsCustomEmojiPlaceholder: 'Pegue el emoji aquí...',
      shortcutsUseButton: 'Usar',
      shortcutsModalEditTitle: 'Editar atajo',
      shortcutsModalAddTitle: 'Añadir nuevo atajo',
      shortcutsNameLabel: 'Nombre del atajo',
      shortcutsNamePlaceholder: 'p. ej. Simplificar lenguaje',
      shortcutsIconLabel: 'Icono',
      shortcutsIconChange: 'Haga clic para cambiar el icono',
      shortcutsDescriptionLabel: 'Descripción',
      shortcutsDescriptionPlaceholder: 'Describa lo que hace este atajo...',
      shortcutsOrderLabel: 'Orden de visualización',
      shortcutsActiveLabel: 'Activo',
      shortcutsCancel: 'Cancelar',
      shortcutsSaving: 'Guardando...',
      shortcutsSaveButton: 'Guardar atajo',
      shortcutsUpdateSuccess: 'Atajo actualizado correctamente',
      shortcutsCreateSuccess: 'Atajo creado correctamente',
      shortcutsSaveError: 'Error al guardar el atajo',
      shortcutsDeleteSuccess: 'Atajo eliminado correctamente',
      shortcutsDeleteError: 'Error al eliminar el atajo',
      shortcutsOrderSaveSuccess: 'Orden guardado correctamente',
      shortcutsOrderSaveError: 'Error al guardar el orden',
      shortcutsSelectTaskHint: 'Seleccione una tarea para gestionar atajos',
      shortcutsHeaderTitle: 'Atajos',
      shortcutsHeaderSubtitle: 'Cree y gestione atajos de IA reutilizables para transformar contenido rápidamente durante el análisis.',
      shortcutsListTitle: 'Lista de atajos',
      shortcutsAddButton: 'Añadir atajo',
      shortcutsEmptyTitle: 'No hay atajos disponibles',
      shortcutsEmptyHint: 'Haga clic en "Añadir atajo" para crear su primer atajo.',
      shortcutsInactiveBadge: 'Inactivo',
      shortcutsEditTooltip: 'Editar',
      shortcutsDeleteTooltip: 'Eliminar',
      shortcutsInfoTitle: 'Cómo funcionan los atajos',
      shortcutsInfoText: 'Los atajos aparecen debajo de las respuestas de IA en el área de análisis. Al hacer clic en uno, el sistema reprocesa la respuesta con el prompt del atajo y muestra el resultado junto a la respuesta original. Esto permite a los usuarios obtener rápidamente traducciones, simplificaciones, resúmenes u otras transformaciones que usted configure.',
      shortcutsDeleteModalTitle: 'Eliminar atajo',
      shortcutsDeleteModalMessage: '¿Está seguro de que desea eliminar "{name}"? Esta acción no se puede deshacer.',
      overviewAdded: 'Añadido',
      overviewAdd: 'Añadir',
      overviewNoAgent: 'No se seleccionó ningún agente.',
      overviewAvailableWidgets: 'Widgets disponibles',
      overviewAvailableSubtitle: 'Explore y añada widgets para personalizar el diseño del resumen del proyecto.',
      overviewPreviewTitle: 'Vista previa del resumen del proyecto',
      overviewPreviewSubtitle: 'Este diseño se aplicará a todos los proyectos que utilicen este agente.',
      overviewUnsavedChanges: 'Cambios sin guardar',
      overviewCancel: 'Cancelar',
      overviewSaving: 'Guardando…',
      overviewSaveLayout: 'Guardar diseño',
      overviewEmpty: 'No se han añadido widgets. Añada widgets de la lista anterior.',
    },

    projectSetup: {
      title: 'Configuración del proyecto',
      subtitle: 'Configure los campos que aparecerán en el formulario de creación del proyecto.',
      formBuilderTitle: 'Constructor de formularios',
      addField: 'Añadir campo',
      emptyState: 'Aún no se han definido campos. Haga clic en "Añadir campo" para empezar.',
      saveChanges: 'Guardar cambios',
      savedSuccess: 'Campos guardados correctamente',
      saveError: 'Error al guardar los campos',
      moveUp: 'Mover arriba',
      moveDown: 'Mover abajo',
      addFieldTitle: 'Añadir campo',
      editFieldTitle: 'Editar campo',
      fieldTypeLabel: 'Tipo de campo',
      fieldLabelLabel: 'Etiqueta del campo',
      fieldLabelPlaceholder: 'p. ej. Nombre del proyecto',
      requiredLabel: 'Campo obligatorio',
      requiredHelp: 'Los usuarios deben completar este campo.',
      requiredError: 'Por favor, complete todos los campos obligatorios.',
      helperLabel: 'Texto de ayuda',
      helperPlaceholder: 'Pequeña descripción mostrada a los usuarios',
      defaultValueLabel: 'Valor predeterminado',
      defaultValuePlaceholder: 'Valor predeterminado opcional',
      optionsLabel: 'Opciones',
      optionPlaceholder: 'Añadir una opción',
      addOption: 'Añadir',
      saveField: 'Guardar campo',
      fieldTypeText: 'Entrada de texto',
      fieldTypeTextarea: 'Texto multilínea',
      fieldTypeNumber: 'Número',
      fieldTypeDate: 'Fecha',
      fieldTypeDropdown: 'Lista desplegable',
      fieldTypeImage: 'Subida de imagen',
      fieldTypeFile: 'Subida de archivo',
      directChatModeLabel: 'Modo de chat directo',
      directChatModeEnabledBadge: 'Activado',
      directChatModeDesc: 'Cuando un usuario selecciona este agente, entra directamente a un chat en vivo — no se requiere configuración de solicitud.',
      fieldProjectName: 'Nombre del proyecto',
      fieldProjectNameDesc: 'Campo de texto para el nombre del proyecto – guardado como título.',
      fieldProjectImage: 'Imagen del proyecto',
      fieldProjectImageDesc: 'Subida de imagen para la miniatura del proyecto.',
      fieldUploadMethods: 'Métodos de subida',
      fieldUploadMethodsDesc: 'Configure cómo los usuarios pueden subir documentos.',
      uploadSourceComputer: 'Ordenador',
      uploadSourceSharePoint: 'SharePoint',
      uploadSourceBoth: 'Ambos',
      uploadSourceLabel: 'Origen de subida',
      uploadSourceHelp: 'Elija cómo los usuarios proporcionarán archivos para este campo.',
      sourceComputerTitle: 'Subir desde el ordenador',
      sourceComputerSub: 'El usuario sube archivos directamente desde su dispositivo local.',
      sourceSharePointTitle: 'Carpeta de SharePoint',
      sourceSharePointSub: 'El usuario selecciona una carpeta de un sitio SharePoint conectado.',
      sourceBothTitle: 'Ambos métodos',
      sourceBothSub: 'Permitir al usuario elegir entre subida local o SharePoint.',
      imageUploadSettings: 'Configuración de subida de imágenes',
      acceptedFormatsLabel: 'Formatos aceptados',
      acceptedFormatsHelp: 'Seleccione qué formatos de imagen pueden subir los usuarios.',
      multipleLabel: 'Permitir varias imágenes',
      multipleHelp: 'El usuario puede subir más de una imagen.',
      maxImageCountLabel: 'Número máximo de imágenes',
      imagesMax: 'Imágenes máx.',
      imageConstraintsLabel: 'Restricciones de imagen',
      optional: 'opcional',
      aspectRatioLabel: 'Relación de aspecto',
      arAny: 'Cualquiera', arAnySub: 'Sin restricción',
      ar11: '1:1', ar11Sub: 'Cuadrado',
      ar43: '4:3', ar43Sub: 'Estándar',
      ar169: '16:9', ar169Sub: 'Panorámico',
      ar32: '3:2', ar32Sub: 'Foto',
      minDimensionsLabel: 'Dimensiones mínimas',
      widthPx: 'Ancho (px)',
      heightPx: 'Alto (px)',
      outputQualityLabel: 'Calidad de salida',
      qOriginal: 'Original', qOriginalSub: 'Sin compresión',
      qHigh: 'Alta', qHighSub: '~80% calidad',
      qMedium: 'Media', qMediumSub: '~60% calidad',
      qLow: 'Baja', qLowSub: '~40% calidad',
      maxFileSizeLabel: 'Tamaño máximo',
      uploadImageBtn: 'Subir imagen',
      uploadImagesBtn: 'Subir {remaining} imagen(es) más',
      errMaxCount: 'Se permiten máximo {count} imágenes.',
      errFormat: 'Formato no permitido: {name}',
      errSize: '{name} supera {mb} MB.',
      errMinDim: '{name}: se requiere mínimo {w}×{h} px.',
      errAspect: '{name}: se requiere relación de aspecto {ratio}.',
    },

    widgets: {
      notGenerated: 'Aún no generado.',
      noSubmissionDate: 'No se extrajo fecha de entrega',
      documents: 'Documentos',
      titles: {
        summary: 'Resumen de IA',
        checklist_progress: 'Progreso de la lista de verificación',
        location: 'Ubicación de solicitud de IA',
        analysis_score: 'Puntuación del análisis',
        document_count: 'Cantidad de documentos',
        participants: 'Participantes',
        submission_date: 'Cuenta regresiva del plazo',
      },
    },

    settings: {
      title: 'Configuración',
      tabAdministrator: 'Administrador',
      tabOrganization: 'Organización',
      tabLanguage: 'Idioma',
      tabManageTasks: 'Gestionar tareas',
    },

    organization: {
      searchPlaceholder: 'Buscar',
      addButton: 'Nueva organización',
      columnLogo: 'Logo',
      columnName: 'Nombre de la organización',
      columnDescription: 'Descripción',
      columnSharePoint: 'SharePoint',
      columnColor: 'Color',
      columnSubscription: 'Suscripción',
      logoFallback: 'Logo',
      spConnected: 'Conectado',
      spError: 'Error',
      spNotConnected: 'No conectado',
      subscriptionActive: 'Activa',
      subscriptionInactive: 'Inactiva',
      actionManage: 'Gestionar',
      actionEdit: 'Editar organización',
      actionDelete: 'Eliminar organización',
      pageOf: 'Página {current} de {total}',
      paginationPrev: 'Anterior',
      paginationNext: 'Siguiente',
      modalCreateTitle: 'Crear nueva organización',
      modalEditTitle: 'Editar organización',
      logoLabel: 'Logo de la organización',
      logoAlt: 'Logo',
      logoNone: 'Sin logo',
      logoUpload: 'Subir logo',
      nameLabel: 'Nombre de la organización *',
      namePlaceholder: 'Ingrese el nombre de la organización',
      descriptionLabel: 'Descripción (opcional)',
      descriptionPlaceholder: 'Descripción breve mostrada a los usuarios',
      footerAddressLabel: 'Dirección de pie de página (opcional)',
      footerAddressPlaceholder: 'Empresa S.A.\nCalle 1, Ciudad\nTeléfono 000 000 000\nCorreo info@empresa.com',
      footerAddressHelp: 'Se muestra en el pie de página de los documentos Word generados por esta organización. Cada línea aparece en su propia línea.',
      primaryColorLabel: 'Color primario',
      uploadingState: 'Subiendo...',
      savingState: 'Guardando...',
      submitCreate: 'Crear organización',
      submitSave: 'Guardar',
      deleteTitle: 'Eliminar organización',
      deleteMessage: '¿Está seguro de que desea eliminar la organización "{name}"?',
      subModalTitle: 'Asignar suscripción a "{name}"',
      subModalSelectLabel: 'Seleccionar suscripción',
      subModalSelectPlaceholder: 'Seleccionar suscripción...',
      subModalNone: 'Sin suscripción',
      subModalOptionInfo: '{name} – {tasks} tareas, {pages} páginas',
      toastCreated: 'Organización creada correctamente',
      toastUpdated: 'Organización actualizada correctamente',
      toastDeleted: 'Organización eliminada',
      toastSaveError: 'Error al guardar',
      toastDeleteError: 'Error al eliminar',
      toastSubAssigned: 'Suscripción asignada correctamente',
      toastSubAssignError: 'Error al asignar suscripción',
    },

    manageOrganization: {
      breadcrumbSettings: 'Configuración',
      breadcrumbManage: 'Gestionar organización',
      loading: 'Cargando...',
      summaryDepartments: 'Grupos de usuarios',
      summaryUsers: 'Usuarios',
      summarySubscription: 'Suscripción',
      summaryActive: 'Activa',
      summaryInactive: 'Inactiva',
      tabDepartments: 'Grupos de usuarios',
      tabSubscriptions: 'Suscripciones',
      tabAgentOrder: 'Orden de agentes',
      tabSharepoint: 'Vincular SharePoint',
      deptSearchPlaceholder: 'Buscar',
      deptAddButton: 'Añadir grupo de usuarios',
      deptEmptyTitle: 'Aún no hay grupos de usuarios',
      deptEmptyHint: 'Cree su primer grupo de usuarios.',
      noResultsTitle: 'No se encontraron resultados',
      noResultsHint: 'Intente con otros términos de búsqueda.',
      deptColumnName: 'Grupo de usuarios',
      deptColumnDescription: 'Descripción',
      deptColumnCreated: 'Creado',
      deptActionEdit: 'Editar grupo de usuarios',
      deptActionDelete: 'Eliminar grupo de usuarios',
      deptModalCreateTitle: 'Crear grupo de usuarios',
      deptModalEditTitle: 'Editar grupo de usuarios',
      deptModalNameLabel: 'Nombre del grupo de usuarios*',
      deptModalNamePlaceholder: 'p. ej. Ventas',
      deptModalDescriptionLabel: 'Descripción (opcional)',
      deptModalDescriptionPlaceholder: 'Breve descripción del propósito de este grupo de usuarios.',
      deptModalSubmitCreate: 'Crear grupo de usuarios',
      deptModalSubmitSave: 'Guardar',
      deptModalCreating: 'Creando...',
      deptModalSaving: 'Guardando...',
      deptDeleteTitle: 'Eliminar grupo de usuarios',
      deptDeleteMessage: 'Eliminar este grupo de usuarios eliminará su asignación de los agentes. Los agentes permanecerán activos pero ya no pertenecerán a este grupo de usuarios.',
      deptToastCreated: 'Grupo de usuarios creado correctamente',
      deptToastUpdated: 'Grupo de usuarios actualizado correctamente',
      deptToastDeleted: 'Grupo de usuarios eliminado correctamente',
      deptToastSaveError: 'Error al guardar grupo de usuarios',
      deptToastDeleteError: 'Error al eliminar grupo de usuarios',
      subSearchPlaceholder: 'Buscar',
      subAddButton: 'Nueva suscripción',
      subEmptyTitle: 'Aún no hay suscripciones',
      subEmptyHint: 'Cree su primera suscripción.',
      subColumnName: 'Nombre de la suscripción',
      subColumnDescription: 'Descripción',
      subActionViewCredits: 'Ver uso de créditos',
      subActionViewUsage: 'Ver uso',
      subActionEdit: 'Editar',
      subActionDelete: 'Eliminar',
      subModalCreateTitle: 'Crear nueva suscripción',
      subModalEditTitle: 'Editar suscripción',
      subModalInfoSection: 'Información de la suscripción',
      subModalNameLabel: 'Nombre de la suscripción *',
      subModalNamePlaceholder: 'p. ej. xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
      subModalDescriptionLabel: 'Descripción (opcional)',
      subModalDescriptionPlaceholder: 'Ingrese la descripción',
      subModalOrgLabel: 'Organización *',
      subModalOrgPlaceholder: 'Seleccionar organización...',
      subModalNoOrgs: 'No hay organizaciones disponibles',
      subModalUsageSection: 'Configuración de uso',
      subModalUsageHint: 'Controle el comportamiento de uso para esta suscripción.',
      subModalTransferableLabel: 'Transferir créditos no utilizados al próximo mes',
      subModalTransferableHint: 'Cuando está activado, los créditos no utilizados se transfieren al siguiente período de facturación.',
      subModalMaxPagesLabel: 'Máximo de páginas por sesión',
      subModalAgentLimitsSection: 'Límites de agentes',
      subModalAgentLimitsHint: 'Defina los dos ajustes de uso para cada agente de IA. Dejar en blanco significa ilimitado.',
      subModalAgentColumn: 'Agente',
      subModalMaxRunsColumn: 'Máx. ejecuciones',
      subModalMaxPagesColumn: 'Máx. páginas',
      subModalStandardPagesColumn: 'Páginas de archivos estándar por mes',
      subModalUploadPagesColumn: 'Páginas de carga por sesión',
      subModalUnlimitedPlaceholder: 'Ilimitado',
      subModalNoAgents: 'No se encontraron agentes para esta organización.',
      subModalSaving: 'Guardando...',
      subModalSubmit: 'Guardar',
      subModalOcrSection: 'Límites de páginas OCR',
      subModalOcrHint: 'Límites globales para el procesamiento OCR. Dejar en blanco para sin límite.',
      subModalOcrDaily: 'Diario (páginas)',
      subModalOcrWeekly: 'Semanal (páginas)',
      subModalOcrMonthly: 'Mensual (páginas)',
      subModalOcrPlaceholder: 'Sin límite',
      subDeleteTitle: 'Eliminar suscripción',
      subDeleteMessage: '¿Está seguro de que desea eliminar la suscripción "{name}"? Todos los límites asociados también serán eliminados.',
      subToastCreated: 'Suscripción creada correctamente',
      subToastUpdated: 'Suscripción actualizada correctamente',
      subToastDeleted: 'Suscripción eliminada correctamente',
      subToastSaveError: 'Error al guardar la suscripción',
      subToastDeleteError: 'Error al eliminar la suscripción',
      creditBackButton: 'Suscripciones',
      creditTitle: 'Uso de créditos',
      creditSubtitle: 'Estado actual de páginas de archivos estándar y ajuste de carga por agente.',
      creditGrantButton: 'Otorgar créditos temporales',
      creditColumnAgent: 'Agente',
      creditColumnRunsUsed: 'Ejecuciones utilizadas',
      creditColumnTempRuns: 'Ejecuciones temp. otorgadas',
      creditColumnPagesUsed: 'Páginas utilizadas',
      creditColumnTempPages: 'Páginas temp. otorgadas',
      creditColumnStandardPages: 'Páginas de archivos estándar (este mes)',
      creditUnlimitedLabel: '(ilimitado)',
      creditTaskFallback: 'Tarea {id}',
      creditBreakdown: '({monthly} mensual + {temp} temp.)',
      creditEmptyTitle: 'Sin límites de agentes',
      creditEmptyHint: 'No hay límites de agentes asignados a esta suscripción.',
      grantTitle: 'Otorgar créditos temporales',
      grantSubscriptionLabel: 'Suscripción',
      grantAgentLabel: 'Agente *',
      grantAgentPlaceholder: 'Seleccionar agente...',
      grantNoAgents: 'No hay agentes disponibles',
      grantRunsLabel: 'Ejecuciones temporales',
      grantRunsHint: 'Otorgue ejecuciones adicionales a este agente.',
      grantPagesLabel: 'Páginas temporales',
      grantPagesHint: 'Otorgue páginas adicionales a este agente.',
      grantNoteTitle: 'Nota',
      grantNoteBody: 'Los créditos temporales no cambian los límites de la suscripción y se consumen primero, antes de utilizar las asignaciones regulares.',
      grantSubmit: 'Otorgar créditos',
      grantSaving: 'Otorgando...',
      grantToastSuccess: 'Crédito temporal otorgado correctamente',
      grantToastError: 'Error al otorgar el crédito',
      spSearchPlaceholder: 'Buscar',
      spEditButton: 'Editar SharePoint',
      spConnectSiteButton: 'Conectar sitio',
      spConnectButton: 'Conectar SharePoint',
      spEmptyTitle: 'No hay sitios conectados',
      spEmptyHint: 'Cree un proyecto para subir archivos y ejecutar análisis.',
      spColumnSiteName: 'Nombre del sitio',
      spColumnUrl: 'URL',
      spColumnStatus: 'Estado',
      spColumnSyncInterval: 'Intervalo de sincronización',
      spColumnSite: 'Sitio',
      spColumnKnowledgeArea: 'Área de conocimiento',
      spColumnUsedByAgents: 'Usado por agentes',
      spAllKnowledgeAreas: 'Todas las áreas de conocimiento',
      spUsedByAgentsSingular: '{count} agente',
      spUsedByAgentsPlural: '{count} agentes',
      spNotUsed: 'No utilizado',
      spCanBeDisconnected: 'Se puede desconectar',
      spInfoBannerTitle: 'Conectar un sitio solo concede acceso.',
      spInfoBannerBody: 'Nada se escanea, supervisa o importa automáticamente — sin ejecuciones programadas, sin notificaciones de cambios, sin OCR, sin embeddings. El contenido solo se importa cuando un administrador de agente inicia una importación, y permanece en el estado de esa importación hasta que alguien inicia otra.',
      spStatusSynced: 'Sincronizado',
      spStatusError: 'Error',
      spStatusPending: 'Pendiente',
      spStatusConnected: 'Conectado',
      spStatusReadOnlyAccess: 'Acceso de solo lectura',
      spIntervalMinutes: '{count} min.',
      spActionEditInterval: 'Editar intervalo de sincronización',
      spActionDelete: 'Eliminar',
      spActionViewDetails: 'Ver detalles del sitio',
      spActionEditNameArea: 'Editar nombre y área de conocimiento',
      spActionCheckAccessNow: 'Comprobar acceso ahora',
      spActionCopySiteId: 'Copiar ID del sitio',
      spActionDisconnect: 'Desconectar sitio',
      spToastAccessConfirmed: 'Acceso confirmado — solo lectura, ningún contenido leído',
      spToastAccessFailed: 'Error al comprobar el acceso: {error}',
      spToastSiteIdCopied: 'ID del sitio copiado',
      spToastDisconnectBlocked: 'No se puede desconectar — todavía en uso por: {agents}',
      spConnLoading: 'Cargando...',
      spConnEditTitle: 'Editar SharePoint',
      spConnCreateTitle: 'Conectar SharePoint',
      spConnTenantIdLabel: 'Tenant ID',
      spConnTenantIdPlaceholder: 'p. ej. xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
      spConnClientIdLabel: 'Client ID',
      spConnClientIdPlaceholder: 'Introduzca el Client ID del registro de la app de Azure',
      spConnClientSecretLabel: 'Client Secret',
      spConnClientSecretPlaceholderEdit: '••••••••',
      spConnClientSecretPlaceholderCreate: 'Introduzca el Client Secret de la app de Azure',
      spConnSecurityNote: 'Estas credenciales se almacenan de forma segura y se usan únicamente para conectarse a su sitio de SharePoint.',
      spConnDisconnectButton: 'Desconectar SharePoint',
      spConnSavingButton: 'Guardando...',
      spConnSaveButton: 'Guardar conexión',
      spConnDisconnectConfirmTitle: '¿Desconectar SharePoint realmente?',
      spConnDisconnectConfirmMessage: 'Se eliminarán todos los sitios conectados y sus asignaciones. Los archivos e incrustaciones ya importados se conservarán, pero no se podrá sincronizar nada más desde SharePoint hasta que se establezca una nueva conexión.',
      spConnDisconnectingButton: 'Desconectando...',
      spConnDisconnectConfirmButton: 'Sí, desconectar',
      spErrorInvalidSecret: 'El Client Secret de SharePoint no es válido o ha caducado. Actualice las credenciales de conexión.',
      spErrorAppNotFound: 'No se encontró el registro de la app de Azure para este tenant. Verifique el Tenant ID y el Client ID.',
      spErrorTenantNotFound: 'No se encontró el Tenant ID. Verifíquelo e inténtelo de nuevo.',
      spErrorAuthFailed: 'Error al iniciar sesión en SharePoint. Verifique las credenciales de conexión.',
      spErrorPermissionDenied: 'Acceso denegado. A la app de Azure le faltan los permisos necesarios de SharePoint.',
      spErrorSiteNotFound: 'No se pudo encontrar el sitio de SharePoint.',
      spErrorGeneric: 'Error al conectar con SharePoint. Verifique la configuración de la conexión.',
      spDetailsSubtitle: 'Datos de conexión almacenados. Nada aquí se actualiza en segundo plano.',
      spDetailsSetInPlatform: 'Establecido en esta plataforma',
      spDetailsKnowledgeArea: 'Área de conocimiento',
      spDetailsSharePointName: 'Nombre en SharePoint',
      spDetailsIdentifiesSite: 'Identifica el sitio',
      spDetailsKeyBadge: 'CLAVE',
      spDetailsAccessScope: 'Solo lectura · Sites.Selected',
      spDetailsConnectedBy: 'Conectado por',
      spDetailsConnectedOn: 'Conectado el',
      spDetailsAccessLastChecked: 'Último acceso comprobado',
      spDetailsCheckedByFormat: '{datetime} por {name}',
      spNeverChecked: 'Nunca comprobado',
      spDetailsCheckAccessNote: 'Las comprobaciones de acceso verifican la concesión de permisos y actualizan el nombre y la URL de SharePoint. No leen ningún contenido.',
      spEditSiteSaveError: 'No se pudieron guardar los cambios',
      spAgentsModalViewTitle: 'Agentes que usan este sitio',
      spAgentsModalViewSubtitle: 'Abra cualquiera de estos agentes directamente.',
      spAgentsModalBlockedTitle: 'El sitio todavía está en uso',
      spAgentsModalBlockedSubtitle: 'Elimine este sitio de los agentes que aparecen abajo y luego desconéctelo.',
      spAgentsModalWarningSingular: '{siteName} está asignado a {count} agente. Desconectarlo ahora dañaría su contenido importado.',
      spAgentsModalWarningPlural: '{siteName} está asignado a {count} agentes. Desconectarlo ahora dañaría su contenido importado.',
      spAgentsModalFileCountSingular: '{count} archivo',
      spAgentsModalFileCountPlural: '{count} archivos',
      spAgentsModalImportedOn: 'importado el {date}',
      spAgentsModalOpenAgent: 'Abrir agente',
      spAgentsModalDeleteNote: 'Eliminar un agente libera automáticamente sus asignaciones de sitios.',
      spAgentsModalGoToFirst: 'Ir al primer agente',
      connectSiteTitle: 'Conectar sitio',
      connectSiteUrlPlaceholder: '',
      connectSiteInvalidIdFormat: 'Formato de ID de sitio no válido. Se esperaba: hostname.sharepoint.com,siteCollectionGuid,webGuid',
      connectSiteAdd: 'Añadir',
      connectSiteSaveCredentials: 'Guardar credenciales',
      connectSiteSubtitle: 'Solo se pueden conectar sitios a los que su administrador de Microsoft 365 haya concedido acceso de lectura.',
      connectSiteStep1: 'Introducir ID del sitio',
      connectSiteStep2: 'Confirmar y nombrar',
      connectSiteIdLabel: 'ID del sitio',
      connectSiteIdHint: 'Cada sitio necesita su propio ID y su propia concesión de acceso — incluidos los sitios que están bajo un sitio concentrador (hub).',
      connectSiteHelpTitle: '¿Dónde encuentro el ID del sitio?',
      connectSiteHelpStep1: 'Abra el sitio de SharePoint en su navegador.',
      connectSiteHelpStep2: 'Añada /_api/site/id a la dirección del sitio y pulse Enter, por ejemplo https://contoso.sharepoint.com/sites/Finance/_api/site/id.',
      connectSiteHelpStep3: 'Copie el GUID de la respuesta y colóquelo después del nombre de host, separado por una coma.',
      connectSiteCheckAccess: 'Comprobar acceso',
      connectSiteUrlColumn: 'URL',
      connectSiteAccessColumn: 'Acceso',
      connectSiteAccessConfirmed: 'Solo lectura · confirmado',
      connectSiteDisplayNameLabel: 'Nombre para mostrar',
      connectSiteDisplayNameHint: 'Se muestra a los administradores de agentes y en el chat.',
      connectSiteKnowledgeAreaLabel: 'Área de conocimiento (opcional)',
      connectSiteKnowledgeAreaSelectPrompt: 'Seleccionar área de conocimiento',
      connectSiteKnowledgeAreaEmpty: 'Aún no hay áreas de conocimiento — cree una a continuación.',
      connectSiteKnowledgeAreaHint: 'Agrupa sitios relacionados en esta lista y en la configuración de agentes. No afecta al acceso ni a las importaciones.',
      knowledgeAreaCreateNew: 'Crear nueva área de conocimiento',
      knowledgeAreaManageButton: 'Gestionar áreas de conocimiento',
      knowledgeAreaManagerTitle: 'Áreas de conocimiento',
      knowledgeAreaManagerSubtitle: 'Las áreas de conocimiento organizan los sitios de SharePoint y son independientes de los grupos de usuarios — todos los agentes pueden ver todas las áreas de conocimiento.',
      knowledgeAreaNameLabel: 'Nombre del área de conocimiento',
      knowledgeAreaNamePlaceholder: 'p. ej. IT, Finanzas, RR. HH.',
      knowledgeAreaEnterHint: 'Pulse Enter o haga clic en "Crear" para añadirla.',
      knowledgeAreaCreateButton: 'Crear',
      knowledgeAreaEmptyError: 'Introduzca un nombre',
      knowledgeAreaExistsError: 'Esta área de conocimiento ya existe',
      knowledgeAreaCreateError: 'No se pudo crear el área de conocimiento',
      connectSiteConsentLabel: 'Entiendo que, una vez que este sitio se importe a un agente, cualquier persona con acceso a ese agente podrá recuperar cualquier archivo importado — incluidos archivos que en SharePoint solo pueden abrir algunas personas.',
      connectSiteSubmit: 'Conectar sitio',
      connectSiteDuplicateSite: 'Este sitio ya está conectado: {name}',
      syncIntervalTitle: 'Intervalo de sincronización',
      syncIntervalLabel: 'Intervalo (minutos)',
      syncIntervalHint: 'Recomendado: 15 minutos para sitios normales, 5 minutos para sitios con actualizaciones frecuentes',
      syncIntervalSaving: 'Guardando...',
      syncIntervalSubmit: 'Guardar',
      deleteSiteTitle: 'Desconectar sitio',
      deleteSiteMessage: '¿Está seguro de que desea desconectar "{name}"? Esta acción no se puede deshacer.',
      deleteSiteDeleting: 'Desconectando...',
    },

    users: {
      pageSearchPlaceholder: 'Buscar',
      pageFilterButton: 'Filtro',
      columnFullName: 'Nombre completo',
      columnEmail: 'Correo electrónico',
      columnOrganization: 'Organización',
      columnUserGroup: 'Grupo de usuarios',
      columnCreatedAt: 'Fecha de creación',
      columnRole: 'Rol',
      columnAuthProvider: 'Proveedor',
      authProviderEmail: 'Correo',
      authProviderMicrosoft: 'Microsoft',
      authProviderLocal: 'Local',
      roleSuperAdmin: 'Super Admin',
      roleAdmin: 'Admin',
      roleNormal: 'Normal',
      roleNoLicense: 'Sin licencia',
      actionAssignRole: 'Asignar rol',
      actionAssignOrganization: 'Asignar org. + grupo de usuarios',
      actionAssignDepartment: 'Asignar grupo de usuarios',
      actionCheckActivity: 'Ver actividad',
      actionResendInvite: 'Reenviar invitación',
      actionDeactivateUser: 'Desactivar usuario',
      actionActivateUser: 'Activar usuario',
      statusInactive: 'Inactivo',
      deactivateConfirmTitle: 'Desactivar usuario',
      deactivateConfirmMessage: '¿Está seguro de que desea desactivar a {name}?',
      activateConfirmTitle: 'Activar usuario',
      activateConfirmMessage: '¿Está seguro de que desea activar a {name}?',
      toastUserDeactivated: 'El usuario ha sido desactivado',
      toastUserActivated: 'El usuario ha sido activado',
      toastStatusChangeError: 'No se pudo cambiar el estado del usuario',
      activityModalTitle: 'Actividad de inicio de sesión',
      activityColMethod: 'Método',
      activityColStatus: 'Estado',
      activityColIp: 'Dirección IP',
      activityColTime: 'Fecha y hora',
      activityStatusSuccess: 'Correcto',
      activityStatusFailed: 'Fallido',
      activityEmpty: 'No se encontraron intentos de inicio de sesión.',
      activityError: 'No se pudo cargar la actividad.',
      activityMethodPassword: 'Contraseña',
      activityMethodOtp: 'Código por correo',
      activityMethodMicrosoft: 'Microsoft',
      activityMethodMfa: 'Código de dos factores',
      actionResetMfa: "Restablecer 2FA",
      activityMethodMfaRecovery: "Código de recuperación",
      activityMethodMfaReset: "2FA restablecido",
      activityMethodMfaSetup: "2FA configurado",
      activityPerformedBy: "por {email}",
      resetMfaConfirmMessage: "Se eliminará la autenticación de dos factores de {name}. En el próximo inicio de sesión deberá configurar de nuevo la aplicación de autenticación.",
      resetMfaConfirmTitle: "Restablecer la autenticación de dos factores",
      toastMfaReset: "La autenticación de dos factores se ha restablecido",
      errorPrefix: 'Error: {error}',
      roleModalTitle: 'Asignar rol',
      roleModalNameLabel: 'Nombre:',
      roleModalCurrentRoleLabel: 'Rol actual:',
      roleModalSelectLabel: 'Seleccionar nuevo rol',
      roleModalSelectPlaceholder: 'Seleccionar nuevo rol',
      roleModalNoOtherRoles: 'No hay otros roles disponibles',
      roleModalSaving: 'Guardando...',
      orgModalTitle: 'Asignar organización',
      orgModalSelectLabel: 'Seleccionar organización',
      orgModalNoOrganization: 'Sin organización',
      orgModalAssignUserGroupLabel: 'Asignar grupo de usuarios',
      orgModalAssignUserGroupPlaceholder: 'Asignar grupo de usuarios',
      orgModalSaving: 'Guardando...',
      userGroupMoreLabel: '+{count} más',
      toastRoleUpdated: 'Rol actualizado correctamente',
      toastRoleUpdateError: 'Error al actualizar el rol',
      toastOrgAssigned: 'Organización asignada correctamente',
      toastOrgAssignError: 'Error al asignar organización',
      // Invite modal
      inviteButtonLabel: 'Invitar usuario',
      inviteModalTitle: 'Invitar usuario',
      inviteModalSubtitle: 'El usuario recibirá un correo de invitación y podrá registrarse a continuación.',
      inviteModalEmailLabel: 'Dirección de correo electrónico',
      inviteModalRoleLabel: 'Rol',
      inviteModalRolePlaceholder: 'Seleccionar un rol',
      inviteModalSendButton: 'Enviar invitación',
      inviteModalSending: 'Enviando...',
      inviteModalSuccessToast: 'Invitación enviada correctamente',
      inviteModalErrorToast: 'No se pudo enviar la invitación',
      resendInviteSuccessToast: 'Invitación reenviada',
      resendInviteErrorToast: 'No se pudo reenviar la invitación',
      inviteModalFieldRequired: 'Indique una dirección de correo electrónico y un rol',
      inviteEmailAlreadyExists: 'Este usuario ya existe en el sistema',
      addUserMenuLabel: 'Agregar usuario',
      inviteUserMenuLabel: 'Invitar usuario',
      newUserDefaultsAllGroups: 'Todos los grupos',
      newUserDefaultsTitle: 'Valores predeterminados para nuevos usuarios',
      newUserDefaultsSubtitle: 'Organización, rol y grupos asignados en el primer inicio de sesión con Microsoft.',
      newUserDefaultsButton: 'Predeterminados',
      addUserModalTitle: 'Agregar nuevo usuario',
      addUserModalSubtitle: 'El usuario se creará directamente y se asignará a la organización.',
      addUserFullnameLabel: 'Nombre completo',
      addUserOrganizationLabel: 'Organización',
      addUserGenderLabel: 'Género',
      addUserGenderPlaceholder: 'Seleccione',
      addUserPasswordLabel: 'Contraseña',
      addUserRoleLabel: 'Rol',
      addUserRolePlaceholder: 'Seleccionar rol',
      addUserSubmitButton: 'Crear usuario',
      addUserSubmitting: 'Creando...',
      addUserSuccessToast: 'Usuario creado correctamente',
      addUserErrorToast: 'No se pudo crear el usuario',
      addUserFieldRequired: 'Por favor, complete todos los campos',
      addUserNoOrgError: 'Su cuenta no tiene ninguna organización asignada. Pida a un Super Administrador que configure su organización primero.',
      addUserSelectOrgPlaceholder: 'Seleccionar organización',
      addUserSelectOrgRequired: 'Por favor seleccione una organización',
      changePasswordTitle: 'Establecer contraseña',
      changePasswordSubtitle: 'Establezca una nueva contraseña para su cuenta.',
      changePasswordButton: 'Guardar contraseña',
      statusPending: 'Invitado',
      statusExpired: 'Expirado',
    },

    language: {
      pageTitle: 'Configuración de idioma',
      pageSubtitle: 'Elija su idioma preferido para la interfaz de usuario.',
      unsavedChanges: 'Cambios sin guardar',
      saveButton: 'Guardar configuración de idioma',
      cancelButton: 'Cancelar',
      searchPlaceholder: 'Buscar',
      currentBadge: 'Actual',
      defaultBadge: 'Predeterminado',
    },

    pagination: {
      pageOf: 'Página {current} de {total}',
    },

    toast: {
      commentSaved: 'Comentario guardado',
      fileUploaded: 'Archivos subidos correctamente',
      fileUpdated: 'Archivos actualizados correctamente',
      projectCreated: '¡Proyecto creado correctamente!',
      projectFailed: 'No se pudo crear el proyecto',
      chooseAgent: 'Seleccione un agente e ingrese un nombre de proyecto',
    },

    deleteModal: {
      singleTitle: 'Eliminar solicitud de IA',
      bulkTitle: 'Eliminar {count} solicitudes de IA',
      singleDescription: '¿Está seguro de que desea eliminar esta solicitud de IA? Esta acción no se puede deshacer.',
      bulkDescription: '¿Está seguro de que desea eliminar {count} solicitudes de IA seleccionadas? Esta acción no se puede deshacer.',
      deleting: 'Eliminando...',
      defaultTitle: 'Confirmar eliminación',
      defaultMessage: '¿Está seguro de que desea eliminar este elemento?',
      cancelButton: 'Cancelar',
      deleteButton: 'Eliminar',
    },

    agentOrder: {
      hint: 'Arrastre los agentes o introduzca un número de posición.',
      unsavedChanges: 'Cambios sin guardar',
      cancel: 'Cancelar',
      save: 'Guardar',
      colPosition: 'Posición',
      colAgent: 'Agente',
      colPlacement: 'Lugar en la página de inicio',
      posTopLeft: 'Arriba a la izquierda',
      posTopRight: 'Arriba a la derecha',
      posSecondLeft: 'Segunda fila a la izquierda',
      posSecondRight: 'Segunda fila a la derecha',
      posBehindShowAll: 'En «Mostrar todo»',
      noDepartments: 'Esta organización aún no tiene grupos de usuarios.',
      noAgents: 'No hay agentes asignados a este grupo de usuarios.',
      toastSaved: 'El orden se ha guardado',
      toastError: 'No se pudo guardar el orden',
      loadError: 'No se pudieron cargar los datos',
      retry: 'Reintentar',
      discardConfirm: 'Hay cambios sin guardar en el orden. ¿Desea descartarlos?',
      toastNoPermission: 'No tiene permiso para cambiar el orden de estos agentes.',
    },
    usageHistory: {
      title: 'Historial de uso',
      subtitle: 'Uso mensual por agente — visible solo para superadministradores',
      cardRunsLabel: 'Ejecuciones',
      cardRunsSub: 'de {max} ejecuciones en {year}',
      cardQuestionsLabel: 'Preguntas',
      cardTokensLabel: 'Tokens',
      cardTokensSub: 'Entrada y salida combinadas',
      cardUserPagesLabel: 'Páginas subidas por usuarios',
      cardStandardPagesLabel: 'Páginas de archivos estándar',
      cardPagesLabel: 'Páginas usadas',
      cardPagesSub: 'de {max} páginas en {year}',
      cardCreditsLabel: 'Créditos temporales',
      cardCreditsSub: 'otorgados adicionalmente',
      orgPlaceholder: 'Seleccionar organización...',
      allMonths: 'Todos los meses',
      refresh: 'Actualizar',
      colMonth: 'Mes',
      colAgent: 'Agente',
      colRuns: 'Ejecuciones',
      colQuestions: 'Preguntas',
      colTokens: 'Tokens',
      colUserPages: 'Páginas de usuario',
      colStandardPages: 'Páginas de archivos estándar',
      unlimitedSuffix: '(ilimitado)',
      colRunsUsed: 'Ejecuciones usadas',
      colPagesUsed: 'Páginas usadas',
      colTempCredits: 'Créditos temporales',
      colTempPages: 'Páginas temporales',
      colAction: 'Acción',
      entry: 'entrada',
      entries: 'entradas',
      remaining: 'restantes',
      actionCarryOver: 'Transferencia',
      actionReset: 'Restablecido',
      emptyOrgTitle: 'Seleccionar organización',
      emptyOrgSubtitle: 'Seleccione una organización para ver el historial mensual de uso.',
      emptyDataTitle: 'Sin datos para',
      emptyDataSubtitle: 'No se han registrado datos de uso para este período.',
      loadError: 'Error al cargar el historial de uso',
      months: 'Enero,Febrero,Marzo,Abril,Mayo,Junio,Julio,Agosto,Septiembre,Octubre,Noviembre,Diciembre',
      unknownAgent: 'Agente desconocido',
      liveTitle: 'Uso actual',
      liveBadge: 'En vivo',
      liveSubtitle: 'Tiempo real · Mes en curso',
      liveUpdated: 'Actualizado',
      deletedBadge: 'Eliminado',
    },

    notifications: {
      pageTitle: 'Notificaciones',
      subtitle: 'Todas las actividades de sus proyectos, documentos, agentes e integraciones.',
      markAllRead: 'Marcar todo como leído',
      tabAll: 'Todas',
      tabUnread: 'No leídas',
      tabSystem: 'Sistema',
      empty: 'Sin notificaciones',
      emptyHint: 'Está al día.',
      groupToday: 'HOY',
      groupYesterday: 'AYER',
      groupEarlier: 'ANTERIORES',
      viewDocument: 'Ver documento',
      viewSyncReport: 'Ver informe de sincronización',
      viewChanges: 'Ver cambios',
      viewFile: 'Ver archivo',
      openProject: 'Abrir proyecto',
      viewSite: 'Ver sitio',
      viewNews: 'Ver novedades',
      fixIssue: 'Solucionar problema',
      reviewActivity: 'Revisar actividad',
      justNow: 'Justo ahora',
      minuteAgo: 'hace 1 minuto',
      minutesAgo: 'hace {m} minutos',
      hourAgo: 'hace 1 hora',
      hoursAgo: 'hace {h} horas',
      yesterdayAt: 'Ayer, {time}',
      t1Title: 'Análisis de documento completado',
      t1Desc: 'La IA completó el análisis de Service_Contract.pdf para City Center Agreement Review.',
      t2Title: 'Sincronización de SharePoint completada',
      t2Desc: '143 archivos sincronizados correctamente desde la carpeta Contracts / Legal Agreements.',
      t3Title: 'Configuración del agente actualizada',
      t3Desc: 'Cambios realizados en el Contract Review Agent — temperatura y prompt actualizados.',
      t4Title: 'Nuevo proyecto creado',
      t4Desc: 'El proyecto "Downtown Plaza" fue creado y asignado al grupo de usuarios Contracts.',
      t5Title: 'Documento subido',
      t5Desc: 'Lease_Agreement_2024.docx fue subido a la biblioteca de archivos estándar.',
      t6Title: 'Análisis completado',
      t6Desc: 'Análisis completo del proyecto "Skyline Tower" completado — puntuación de confianza del 98 %.',
      t7Title: 'Sitio de SharePoint conectado',
      t7Desc: 'Contoso Legal Hub fue conectado exitosamente como fuente de datos.',
      t8Title: 'Documento procesado',
      t8Desc: 'NDA_Template_v3.pdf fue procesado y está listo para el análisis de IA.',
      t9Title: 'Actualización de la plataforma — v2.4.0',
      t9Desc: 'Nuevas funciones: velocidad de sincronización de SharePoint mejorada, insignias de citación mejoradas y constructor de perfil de IA.',
      t10Title: 'Nuevo agente creado',
      t10Desc: 'El agente "Compliance Checker" fue creado por Olivia Rhye en el espacio de trabajo Contracts.',
      t11Title: 'Error de sincronización detectado',
      t11Desc: 'La sincronización de 3 archivos de la carpeta de documentos de RR. HH. falló. Acceso denegado.',
      t12Title: 'Alerta de seguridad',
      t12Desc: 'Se detectó un nuevo inicio de sesión desde un dispositivo desconocido. Revise su cuenta.',
      t13Title: 'Mantenimiento programado',
      t13Desc: 'La plataforma estará en mantenimiento el 10 de marzo de 2026 de 02:00 a 04:00 UTC. Los servicios pueden no estar disponibles brevemente.',
    },

    dataAnalysis: {
      hint: "Como análisis de datos",
      activeHint: "El análisis de datos está activo para esta solicitud",
      preview: "Vista previa",
      closePreview: "Cerrar vista previa",
      previewLoading: "Cargando vista previa…",
      previewError: "No se pudo cargar la vista previa. Puede descargar el archivo.",
      skippedLabel: "No utilizados",
      skipReasonMissing: "archivo no encontrado",
      skipReasonUnreadable: "no se pudo leer",
      skipReasonTooLarge: "demasiado grande",
      skipReasonStandardFile: "archivo estándar, no compatible",
      modeName: "Análisis de datos",
      tooltip: "Analizar archivos con cálculos avanzados y herramientas de datos",
      composerMeta: "Permanece activo hasta que lo desactive · los archivos seleccionados limitan el contexto",
      usingSessionFiles: "Usando los archivos adecuados de este chat",
      usingSelectedFiles: "Usando solo los archivos seleccionados",
      selectedForRequest: "Seleccionados para esta solicitud",
      sessionForRequest: "Archivos adecuados de este chat",
      noFilesForRequest: "Sin archivos – solo cálculos",
      workingTitle: "Analizando sus datos…",
      completedTitle: "Análisis de datos completado",
      statusAnalyzing: "Analizando",
      statusReady: "Listo",
      statusFailed: "Fallido",
      stepPreparing: "Preparando archivos…",
      stepInspecting: "Comprobando la estructura de datos…",
      stepCalculating: "Ejecutando cálculos…",
      stepResults: "Preparando resultados…",
      stepRetrying: "Reintentando…",
      progressCompleted: "Análisis completado",
      completedHelper: "El resultado aparece como una respuesta normal del chat; los archivos creados se guardan en Archivos generados.",
      failedTitle: "El análisis de datos ha fallado",
      failedHelper: "Inténtelo de nuevo.",
      contentFilterHelper: "La respuesta fue bloqueada por el filtro de contenido. Reformule la solicitud o seleccione otros archivos.",
      filesUnavailableHelper: "No se pudieron leer los archivos de este análisis. Vuelva a subirlos.",
      tokenLimitHelper: "La respuesta alcanzó el límite de tokens del agente. Aumente el límite en la configuración del agente.",
      retry: "Reintentar",
      basedOnSelected: "Análisis basado en los archivos seleccionados",
      basedOnSession: "Análisis basado en los archivos de este chat",
      basedOnNone: "Análisis sin archivos",
      filesUsedLabel: "Archivos utilizados",
      generatedFilesLabel: "Archivos creados",
      download: "Descargar",
      downloadError: "No se pudo descargar el archivo.",
      alreadyRunningError: "Ya hay un análisis de datos en curso en este chat. Espere a que finalice.",
      notEnabledError: "El análisis de datos está desactivado para este agente.",
      modeSaveError: "No se pudo guardar el interruptor de análisis de datos.",
      startFailedError: "No se pudo iniciar el análisis de datos.",
    },
    chatZoom: {
      open: 'Abrir controles de zoom',
      close: 'Cerrar controles de zoom',
      zoomIn: 'Acercar',
      zoomOut: 'Alejar',
      reset: 'Restablecer al 100 %',
    },
    share: {
      button: 'Compartir',
      shareProject: 'Compartir proyecto',
      modalTitle: 'Compartir proyecto',
      modalSubtitle: 'Agregar personas o copiar el enlace',
      modalDescription: 'Comparte este proyecto con otros en modo de solo lectura. Cualquiera con este enlace puede ver los detalles del proyecto, archivos, respuestas de IA y fuentes, pero no puede realizar cambios.',
      notifOnly: 'Solo notificación en la app · Sin correo electrónico',
      peopleLabel: 'Personas',
      permissionLabel: 'Permiso',
      shareButton: 'Crear y copiar enlace',
      shareAndNotify: 'Compartir y notificar',
      shareLinkPlaceholder: 'El enlace aparecerá después de compartir...',
      linkNote: 'Cualquiera con este enlace solo puede ver este proyecto. No pueden editar, subir archivos, agregar comentarios ni hacer nuevas preguntas de IA.',
      searchPlaceholder: 'Buscar nombre o correo...',
      noResults: 'No se encontraron usuarios',
      addedLabel: 'Agregado',
      cancel: 'Cancelar',
      copyLink: 'Copiar enlace',
      linkCopied: '¡Copiado!',
      shareLinkLabel: 'Enlace para compartir',
      shareSuccess: 'Proyecto compartido con éxito',
      removeFailed: 'No se pudo eliminar el destinatario',
      readOnlyBadge: 'Solo lectura',
      commentsTitle: 'Comentarios',
      commentsEmpty: 'Aún no hay comentarios.',
      commentsLoading: 'Cargando comentarios …',
      commentsPlaceholder: 'Escribir un comentario …',
      commentsAdd: 'Comentar',
      commentsSave: 'Guardar',
      commentsCancel: 'Cancelar',
      commentsEdit: 'Editar',
      commentsDelete: 'Eliminar',
      commentsDeleteConfirm: '¿Eliminar este comentario?',
      commentsEdited: 'editado',
      commentsUnknownAuthor: 'Usuario desconocido',
      commentsLoadError: 'No se pudieron cargar los comentarios.',
      commentsSaveError: 'No se pudo guardar el comentario.',
      sharedBy: 'Compartido por',
      readOnlyHint: 'Esta vista es de solo lectura – no se pueden hacer preguntas.',
      openProject: 'Abrir proyecto',
      loadingError: 'No se pudo cargar el proyecto compartido.',
      notFound: 'Este enlace de compartir no es válido o ha expirado.',
      accessRevoked: 'Tu acceso a este proyecto ha sido eliminado.',
      currentRecipients: 'Actualmente compartido con',
      noRecipients: 'Aún no compartido',
      revokeAccess: 'Revocar acceso',
      revokeShare: 'Terminar compartición',
      revokeConfirm: '¿Seguro que desea terminar la compartición de este proyecto? Todos los destinatarios perderán el acceso.',
      revokeSuccess: 'Compartición terminada con éxito',
      inviteDescription: 'Invite a personas introduciendo su nombre o correo.',
      invitePeople: 'Invitar personas',
      sendInvite: 'Enviar invitación',
      whoCanView: 'Quién puede ver este proyecto',
      viewAll: 'Ver todos',
      allViewers: 'Todos los espectadores',
      allViewersDesc: 'Personas que actualmente tienen acceso a este proyecto.',
      removeAccess: 'Eliminar acceso',
      youLabel: 'Usted',
      nPeople: '{count} personas',
      viewPermission: 'Solo ver',
      viewButton: 'Ver',
      alreadyShared: 'Ya compartido',
      done: 'Listo',
    },

    agents: {
      pageTitle: 'Agentes',
      createAgent: 'Crear agente',
      selectAll: 'Seleccionar todo',
      loading: 'Cargando agentes...',
      noneFound: 'No se encontraron agentes',
      noneYet: 'Aún no hay agentes',
      noneFoundHint: 'Ningún agente coincide con su búsqueda.',
      noneYetHint: 'Cree un agente para comenzar a analizar archivos y datos.',
      duplicate: 'Duplicar agente',
      agentNameLabel: 'Nombre del agente',
      userGroupLabel: 'Grupo de usuarios',
      organizationLabel: 'Organización',
      lastUpdatedLabel: 'Última actualización',
      createModalTitle: 'Crear nuevo agente',
      createModalImageLabel: 'Imagen del agente',
      createModalUploadImage: 'Subir imagen',
      createModalChangeImage: 'Subir nueva imagen',
      createModalRemoveImage: 'Eliminar',
      createModalNoFileSelected: 'Ningún archivo seleccionado',
      createModalSelectOrg: 'Seleccionar organización',
      createModalSelectDept: 'Seleccionar grupo de usuarios',
      createModalDescLabel: 'Descripción',
      createModalDescPlaceholder: 'Describa el propósito principal de este agente...',
      createModalDescHelp: 'Una o dos frases que expliquen cómo este agente ayuda a los usuarios.',
      createModalCancel: 'Cancelar',
      createModalSubmit: 'Añadir agente',
      createModalSubmitting: 'Creando...',
      createModalNameRequired: 'Ingrese un nombre de agente',
    },

    filterModal: {
      title: 'Filtro',
      agentsLabel: 'Agentes',
      agentsPlaceholder: 'Seleccionar agentes',
      creationDateLabel: 'Fecha de creación',
      today: 'Hoy',
      last7days: 'Últimos 7 días',
      last30days: 'Últimos 30 días',
      monthPlaceholder: 'Seleccionar mes',
      progressLabel: 'Progreso de Q&A',
      low: 'Bajo',
      medium: 'Medio',
      high: 'Alto',
      sortByLabel: 'Ordenar por',
      newestFirst: 'Más recientes',
      oldestFirst: 'Más antiguos',
      mostProgress: 'Mayor progreso',
      leastProgress: 'Menor progreso',
      resetAll: 'Restablecer todo',
      apply: 'Aplicar',
      organizationLabel: 'Organización',
      organizationPlaceholder: 'Seleccionar organización',
      userGroupLabel: 'Grupo de usuarios',
      userGroupPlaceholder: 'Seleccionar grupo de usuarios',
      lastUpdateLabel: 'Última actualización',
      clearAll: 'Borrar todo',
    },

    months: {
      january: 'Enero',
      february: 'Febrero',
      march: 'Marzo',
      april: 'Abril',
      may: 'Mayo',
      june: 'Junio',
      july: 'Julio',
      august: 'Agosto',
      september: 'Septiembre',
      october: 'Octubre',
      november: 'Noviembre',
      december: 'Diciembre',
    },

    createProjectModal: {
      title: 'Crear nuevo proyecto',
      agentLabel: 'Agente',
      agentPlaceholder: 'Seleccionar agente',
      noAgentsAvailable: 'No hay agentes disponibles',
      projectImageLabel: 'Imagen del proyecto',
      uploadImage: 'Subir imagen',
      uploadNewImage: 'Subir nueva imagen',
      noFileSelected: 'Ningún archivo seleccionado',
      projectNameLabel: 'Nombre de la solicitud IA',
      projectNamePlaceholder: 'Nombre de la solicitud IA',
      uploadMethodLabel: 'Método de subida',
      uploadFromDevice: 'Subir desde mi dispositivo',
      uploadFromDeviceHint: 'Añada archivos directamente desde su computadora',
      connectSharePoint: 'Conectar carpeta de SharePoint',
      connectSharePointHint: 'Use archivos de su espacio de trabajo del proyecto',
      analyze: 'Analizar',
      creating: 'Creando...',
    },

    helpCenter: {
      title: 'Centro de ayuda',
      tabFaqs: 'FAQ',
      tabSupport: 'Contactar soporte',
      sidebarFaqs: 'FAQ',
      sidebarSupport: 'Soporte',
      sidebarFooterCopyright: '© 2026 AI Spaces GmbH',
      supportInfoTitle: 'Información de soporte',
      supportInfoSubtitle: 'Proporcione información que los usuarios verán en el centro de ayuda cuando necesiten asistencia.',
      supportMessageLabel: 'Mensaje de soporte',
      supportMessageDefault: 'Aquí encontrará información sobre nuestro soporte. Nuestro equipo estará encantado de ayudarle.',
      supportRequestLinkLabel: 'Enlace de solicitud de soporte',
      supportNoLinkConfigured: 'No se configuró ningún enlace',
      supportHoursLabel: 'Horario de soporte',
      supportHoursDaysDefault: 'Lunes – Viernes',
      supportHoursTimeDefault: '07:00 – 17:00',
      supportErrorPrefix: 'Error: {message}',
      supportMessagePlaceholder: 'Escriba un mensaje breve explicando cómo los usuarios pueden obtener ayuda de su equipo de soporte.',
      supportCharactersRemaining: '{count} caracteres restantes',
      supportLinkPlaceholder: 'Pegue el enlace al formulario de soporte o solicitud de comentarios',
      supportDaysLabel: 'Días',
      supportTimeLabel: 'Hora',
      supportDaysPlaceholder: 'p. ej. Lunes – Viernes',
      supportTimePlaceholder: 'p. ej. 07:00 – 17:00',
      supportSaving: 'Guardando...',
      faqPageTitle: 'Preguntas frecuentes',
      faqPageSubtitle: 'Encuentre respuestas a preguntas comunes sobre el uso de DocAI, la gestión de agentes y el análisis de documentos.',
      faqManageTitle: 'Gestionar FAQ',
      faqAddButton: 'Añadir pregunta',
      faqEmptyTitle: 'Aún no hay FAQ',
      faqEmptySubtitle: 'Aún no se han añadido preguntas.',
      faqDrawerAddTitle: 'Añadir pregunta',
      faqDrawerEditTitle: 'Editar pregunta',
      faqQuestionLabel: 'Pregunta',
      faqQuestionPlaceholder: 'Pregunta',
      faqAnswerLabel: 'Respuesta',
      faqAnswerPlaceholder: 'Respuesta',
      faqMediaLabel: 'Subir video/imagen (opcional)',
      faqUploading: 'Subiendo...',
      faqFileUploaded: 'Archivo subido',
      faqRemoveFile: 'Eliminar',
      faqDropHint: 'Arrastre su video/imagen aquí o {browse}',
      faqBrowse: 'explore',
      faqSaving: 'Guardando...',
      faqDeleteTitle: 'Eliminar FAQ',
      faqDeleteMessage: '¿Está seguro de que desea eliminar este FAQ?',
      faqMediaAlt: 'Medios FAQ',
      faqToastFetchError: 'No se pudieron cargar las FAQ',
      faqToastDeleted: 'FAQ eliminado correctamente',
      faqToastDeleteError: 'No se pudo eliminar el FAQ',
      faqToastFileUploaded: 'Archivo subido correctamente',
      faqToastUploadError: 'Error al subir el archivo',
      faqValidationRequired: 'La pregunta y la respuesta son obligatorias',
      faqValidationUploadWait: 'Espere hasta que se complete la subida.',
      faqToastUpdated: 'FAQ actualizado correctamente',
      faqToastCreated: 'FAQ creado correctamente',
      faqToastSaveError: 'Error al guardar el FAQ',
      faqToastChangesSaved: 'Cambios guardados',
      faqToastChangesSaveError: 'Error al guardar',
    },

    auth: {
      loginTitle: 'Iniciar sesión o registrarse',
      loginSubtitle: 'Inicie sesión para acceder a sus consultas de IA.',
      continueWithMicrosoft: 'Continuar con Microsoft',
      or: 'O',
      orContinueWith: 'O CONTINUAR CON',
      emailPlaceholder: 'Dirección de correo electrónico',
      checkInboxTitle: 'Revise su bandeja de entrada',
      checkInboxSubtitle: 'Introduzca el código de verificación que acabamos de enviar a {email}',
      codePlaceholder: 'Código',
      resendEmail: 'Reenviar correo',
      continueWithPassword: 'Continuar con contraseña',
      enterPasswordTitle: 'Introduzca su contraseña',
      emailLabel: 'Dirección de correo electrónico',
      edit: 'Editar',
      passwordLabel: 'Contraseña',
      forgotPassword: '¿Olvidó su contraseña?',
      loginWithOtp: 'Iniciar sesión con un código de un solo uso',
      resetPasswordTitle: '¿Olvidó su contraseña?',
      resetPasswordSubtitle: 'Haga clic en "Continuar" para restablecer su contraseña para {email}',
      resetPasswordEmailPrompt: 'Introduzca su dirección de correo electrónico y le enviaremos un enlace para restablecerla.',
      setNewPasswordSubtitle: 'Debe tener al menos 8 caracteres.',
      setNewPasswordTitle: 'Establecer nueva contraseña',
      newPasswordPlaceholder: 'Nueva contraseña',
      confirmPasswordPlaceholder: 'Confirmar contraseña',
      passwordMismatch: 'Las contraseñas no coinciden.',
      sendResetLinkButton: 'Enviar enlace de restablecimiento',
      resetPasswordButton: 'Restablecer contraseña',
      footerPrivacyPolicy: 'Privacidad',
      footerTermsOfService: 'Términos de servicio',
      footerSupport: 'Soporte',
      resetLinkSentTitle: 'Revise su correo electrónico',
      resetLinkSentSubtitle: 'Hemos enviado un enlace para restablecer la contraseña a {email}. Por favor, revise su bandeja de entrada.',
      backToLogin: 'Volver al inicio de sesión',
      invalidEmail: 'Introduzca una dirección de correo electrónico válida',
      otpSent: 'El código ha sido enviado',
      otpResent: 'El código ha sido reenviado',
      genericError: 'Algo salió mal. Por favor, inténtelo de nuevo.',
      fieldRequired: 'Por favor, complete todos los campos.',
      invalidPhone: 'Introduzca un número de teléfono válido.',
      passwordTooShort: 'La contraseña debe tener al menos 8 caracteres.',
      today: 'Hoy',
      clear: 'Borrar',
      completeProfileTitle: 'Complete su perfil',
      completeProfileSubtitle: 'Solo unos datos más para configurar su cuenta.',
      fullnameLabel: 'Nombre completo',
      genderLabel: 'Género',
      genderPlaceholder: 'Seleccione',
      genderMale: 'Masculino',
      genderFemale: 'Femenino',
      genderDiverse: 'Diverso',
      dobLabel: 'Fecha de nacimiento',
      mobileLabel: 'Número de teléfono móvil',
      passwordOptionalLabel: 'Contraseña (opcional)',
      loginButton: 'Iniciar sesión',
      welcomeBack: 'Bienvenido de nuevo',
      loginPageSubtitle: 'Inicie sesión en su cuenta para continuar.',
      rememberMe: 'Recordarme',
      noAccount: '¿No tiene cuenta?',
      signUpLink: 'Registrarse',
      noPasswordHint: '¿No tiene contraseña? Le enviaremos un código de verificación por correo.',
      loginWithPasswordLink: 'Iniciar sesión con contraseña',
      setPasswordTitle: 'Establecer contraseña',
      setPasswordSubtitle: 'Bienvenido de nuevo, {fullname}. Establezca una contraseña para iniciar sesión directamente con su correo la próxima vez.',
      mfaVerifyTitle: 'Autenticación de dos factores',
      mfaAuthenticatorRowTitle: "Aplicación de autenticación",
      mfaConfirmText: "Introduzca el código de 6 dígitos que muestra ahora su aplicación.",
      mfaConfirmTitle: "Confirme el código",
      mfaEnrollStep: "Paso {current} de {total}",
      mfaInstallText: "Instale en su smartphone una aplicación de autenticación como Microsoft Authenticator o Google Authenticator.",
      mfaInstallTitle: "Instale una aplicación de autenticación",
      mfaManualKeyButton: "Introducir la clave manualmente",
      mfaManualKeyText: "Introduzca esta clave en su aplicación (basada en tiempo):",
      mfaRecoveryCopied: "Copiado",
      mfaRecoveryCopy: "Copiar",
      mfaRecoveryDone: "Listo",
      mfaRecoveryDownload: "Descargar",
      mfaRecoveryFileHeader: "DocAI Plataforma – códigos de recuperación (cada código solo se puede usar una vez)",
      mfaRecoverySavedCheckbox: "He guardado estos códigos de forma segura",
      mfaRecoverySectionText: "Inicie sesión con uno de estos códigos si pierde el acceso a su aplicación de autenticación.",
      mfaRecoverySectionTitle: "Códigos de recuperación",
      mfaRecoveryText: "Si pierde el acceso a su aplicación, puede iniciar sesión con uno de estos códigos. Cada código solo se puede usar una vez. Guárdelos en un lugar seguro.",
      mfaRecoveryTitle: "Guarde sus códigos de recuperación",
      mfaRecoveryVerifySubtitle: "Introduzca uno de sus códigos de recuperación.",
      mfaRegenerateButton: "Generar nuevos códigos",
      mfaRegenerateConfirmTitle: "Generar nuevos códigos de recuperación",
      mfaRegenerateSubmit: "Generar códigos",
      mfaRegenerateSubtitle: "Introduzca el código de 6 dígitos de su aplicación de autenticación.",
      mfaRegenerateSuccess: "Se han generado nuevos códigos de recuperación.",
      mfaRegenerateWarning: "Sus códigos de recuperación anteriores dejarán de funcionar.",
      mfaRequiredForAll: "La autenticación de dos factores es obligatoria para todos los usuarios y no se puede desactivar.",
      mfaScanText: "Abra la aplicación, añada una cuenta nueva y escanee este código QR. La entrada aparecerá como «{issuer}».",
      mfaScanTitle: "Escanee el código QR",
      mfaUseAuthenticatorCode: "Usar un código de la aplicación de autenticación",
      mfaUseRecoveryCode: "Usar un código de recuperación",
      mfaVerifySubtitle: 'Introduzca el código de 6 dígitos de su aplicación de autenticación.',
      mfaCodeLabel: 'Código',
      mfaSetupTitle: 'Configurar autenticación de dos factores',
      mfaSetupSubtitle: 'Escanee este código QR con una aplicación de autenticación (p. ej. Google o Microsoft Authenticator) e introduzca el código mostrado para continuar.',
      mfaSectionTitle: 'Autenticación de dos factores',
      mfaSectionDescription: 'Proteja su cuenta adicionalmente con una aplicación de autenticación.',
      mfaStatusEnabled: 'Activada',
      mfaStatusDisabled: 'Desactivada',
      mfaEnableButton: 'Activar',
      mfaDisableButton: 'Desactivar',
      mfaEnabledSuccess: 'La autenticación de dos factores ha sido activada.',
      mfaDisabledSuccess: 'La autenticación de dos factores ha sido desactivada.',
      mfaRequiredForRole: 'La autenticación de dos factores es obligatoria para su rol y no se puede desactivar.',
    },
  },
};

export const DEFAULT_LANGUAGE = 'de';

export const AVAILABLE_LANGUAGES = [
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština', flag: '🇨🇿' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
];

export default translations;
